import { z } from "zod";

export type Locale = "he" | "en";

export const languages: {
    code: Locale;
}[] = [{ code: "he" }, { code: "en" }];

export const locales = languages.map((language) => language.code) as [
    Locale,
    ...Locale[],
];

export const zLocale = z.enum(locales);

export const rtlLanguages: Locale[] = ["he"];

export const getDirectionFromText = (text: string): "ltr" | "rtl" => {
    const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    return rtlRegex.test(text) ? "rtl" : "ltr";
};

export const defaultLocale: Locale = "he";

let currentLocale: Locale = defaultLocale;

export const setCurrentLocale = (locale?: Locale | null) => {
    const resolvedLocale =
        locale && languages.some((l) => l.code === locale)
            ? locale
            : defaultLocale;
    currentLocale = resolvedLocale;
};

export const translations: Record<string, Record<Locale, string>> = {
    languageLabel: { he: "שפה", en: "Language" },
    he: { he: "עברית", en: "Hebrew" },
    en: { he: "אנגלית", en: "English" },
    es: { he: "ספרדית", en: "Spanish" },
    fr: { he: "צרפתית", en: "French" },
    ru: { he: "רוסית", en: "Russian" },
    ar: { he: "ערבית", en: "Arabic" },
    ro: { he: "רומנית", en: "Romanian" },
    sr: { he: "סרבית", en: "Serbian" },
    tl: { he: "פיליפינית", en: "Filipino" },
    ne: { he: "נפאלית", en: "Nepali" },
    hi: { he: "הודית", en: "Hindi" },
    si: { he: "סינהלית", en: "Sinhala" },
    uk: { he: "אוקראינית", en: "Ukrainian" },
    mo: { he: "מולדובנית", en: "Moldovan" },
    am: { he: "אמהרית", en: "Amharic" },
    tr: { he: "טורקית", en: "Turkish" },
    uz: { he: "אוזבקית", en: "Uzbek" },
    ta: { he: "טמילית", en: "Tamil" },
    te: { he: "טלוגו", en: "Telugu" },
    bn: { he: "בנגלית", en: "Bengali" },
    mr: { he: "מרטהית", en: "Marathi" },
    kn: { he: "קנאדה", en: "Kannada" },
    ml: { he: "מלאיאלם", en: "Malayalam" },
    gu: { he: "גוג'ראטית", en: "Gujarati" },
    it: { he: "איטלקית", en: "Italian" },
    de: { he: "גרמנית", en: "German" },
    pt: { he: "פורטוגזית", en: "Portuguese" },
    "auth-card.auth-separator": { he: "או", en: "Or" },
    "auth.continue-with-google-btn": {
        he: "המשיכו עם גוגל",
        en: "Continue with Google",
    },
    "auth.or": { he: "או", en: "Or" },
    "chat-bots.title": { he: "סוכני AI", en: "AI agents" },
    "play.metadata.title": {
        he: "YedaLabs | מערכת שיפור למידה מבוססת AI",
        en: "YedaLabs | AI-powered learning improvement system",
    },
    "chatbot-knowledge.text.textarea-label": {
        he: "הזינו מידע כללי שתרצו שהעוזר האישי ידע",
        en: "Enter general information you want the assistant to know",
    },
    "chatbot-knowledge.websites.input-label": {
        he: "הזינו כתובת אתר",
        en: "Enter website URL",
    },
    "chatbot-knowledge.websites.input-submit-btn": {
        he: "הוספת אתר",
        en: "Add website",
    },
    "chatbot.chats.title": { he: "שיחות", en: "Chats" },
    "chatbot.chats.description": {
        he: "צפו בשיחות של המשתמשים עם הסוכן",
        en: "Browse users' conversations with the agent",
    },
    "chatbot.chats.no-chats": { he: "אין שיחות עדיין", en: "No chats yet" },
    "chatbot.chats.no-messages": {
        he: "אין הודעות בשיחה זו",
        en: "No messages in this chat",
    },
    "chatbot.chats.select-chat": {
        he: "בחרו שיחה כדי לצפות בתמלול",
        en: "Select a chat to view its transcript",
    },
    "chatbot.chats.messages-count": {
        he: "{count} הודעות",
        en: "{count} messages",
    },
    "chatbot.chats.unknown-student": {
        he: "משתמש לא ידוע",
        en: "Unknown user",
    },
    "chatbot.chats.started-at": {
        he: "התחילה ב-{date}",
        en: "Started {date}",
    },
    "chatbot.chats.load-error": {
        he: "טעינת השיחות נכשלה",
        en: "Failed to load chats",
    },
    "chatbot.chats.transcript-error": {
        he: "טעינת התמלול נכשלה",
        en: "Failed to load transcript",
    },
    "chatbot.knowledge-from-videos.add-videos": {
        he: "הוספת סרטונים",
        en: "Add videos",
    },
    "chatbot.knowledge-from-videos.only-videos-with-captions-warning": {
        he: "שימו לב - אפשר לבחור רק סרטונים שכבר נוצרו להם כתוביות.",
        en: "Note - you can only choose videos that already have captions.",
    },
    "chatbot.knowledge-from-videos.save-changes-btn": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "chatbot.knowledge.description": {
        he: "הוסיפו מקורות ידע כמו סרטונים, קבצים, אתרים ושאלות ותשובות שהסוכן יענה מתוכם.",
        en: "Add knowledge sources like videos, files, websites and Q&A for the agent to answer from.",
    },
    "chatbot.knowledge.title": { he: "ידע", en: "Knowledge" },
    "chatbot.navbar.back-btn": { he: "חזרה", en: "Back" },
    "chatbot.navbar.demo-btn": { he: "דמו", en: "Demo" },
    "chatbot.navbar.name-fallback": { he: "הסוכן שלך", en: "Your agent" },
    "chatbot.settings.description": {
        he: "ערכו את ההגדרות של העוזר האישי.",
        en: "Edit the assistant settings.",
    },
    "chatbot.settings.design.bubble-side.label": {
        he: "צד בועת הצ'אט",
        en: "Chat bubble side",
    },
    "chatbot.settings.design.description": {
        he: "כאן תמצאו את ההגדרות העיצוב של העוזר האישי",
        en: "Here you can find the assistant's design settings",
    },
    "chatbot.settings.design.logo.label": { he: "לוגו", en: "Logo" },
    "chatbot.settings.design.main-color.label": {
        he: "צבע בועת הצ'אט",
        en: "Chat bubble color",
    },
    "chatbot.settings.design.save-btn": { he: "שמירה", en: "Save" },
    "chatbot.settings.design.title": { he: "עיצוב", en: "Design" },
    "chatbot.settings.embed.description": {
        he: "כדי להטמיע את העוזר האישי הזינו את ה-chatbot id ואת ה-secret key במערכת הלמידה שלכם.",
        en: "To embed the assistant, enter the chatbot id and secret key in your LMS.",
    },
    "chatbot.settings.embed.status.label": {
        he: "סטטוס: {state}",
        en: "Status: {state}",
    },
    "chatbot.settings.embed.status.active": {
        he: "פעיל",
        en: "Active",
    },
    "chatbot.settings.embed.status.off": {
        he: "כבוי",
        en: "Off",
    },
    "chatbot.settings.embed.status.description": {
        he: "כשהסטטוס כבוי, העוזר האישי לא יוצג לתלמידים במערכת הלמידה.",
        en: "When off, the assistant won't show to students in the learning management system.",
    },
    "chatbot.settings.embed.id.label": { he: "Chatbot id", en: "Chatbot ID" },
    "chatbot.settings.embed.secret-key.label": {
        he: "Secret key",
        en: "Secret key",
    },
    "chatbot.settings.embed.title": {
        he: "הטמעה במערכת למידה",
        en: "Embed in LMS",
    },
    "chatbot.settings.general.description": {
        he: "כאן תמצאו את ההגדרות הכלליות של העוזר האישי",
        en: "Here you can find the assistant's general settings",
    },
    "chatbot.settings.general.description.label": {
        he: "תיאור (מומלץ)",
        en: "Description (recommended)",
    },
    "chatbot.settings.general.extraInstructions.label": {
        he: "הערות נוספות ל-AI",
        en: "Additional notes for AI",
    },
    "chatbot.settings.general.hideBranding.label": {
        he: "הסתרת מיתוג",
        en: "Hide branding",
    },
    "chatbot.settings.general.language.label": {
        he: "שפת הממשק",
        en: "Interface language",
    },
    "chatbot.settings.general.name.label": {
        he: "שם העוזר האישי",
        en: "Assistant name",
    },
    "chatbot.settings.general.save-btn": { he: "שמירה", en: "Save" },
    "chatbot.settings.general.title": { he: "כללי", en: "General" },
    "chatbot.settings.general.welcomeMessage.label": {
        he: "הודעת ברוכים הבאים",
        en: "Welcome message",
    },
    "chatbot.settings.nudnik.add-trigger": {
        he: "הוספת טריגר",
        en: "Add trigger",
    },
    "chatbot.settings.nudnik.description": {
        he: "הוסיפו טריגרים שבהם העוזר האישי יהיה נודניק",
        en: "Add triggers where the assistant will nudge",
    },
    "chatbot.settings.nudnik.save-btn": { he: "שמירה", en: "Save" },
    "chatbot.settings.nudnik.title": { he: "נודניק", en: "Nudnik" },
    "chatbot.settings.title": { he: "הגדרות", en: "Settings" },
    "chatbot.settings.sidenav.agent": { he: "פרומפט", en: "Prompt" },
    "chatbot.settings.sidenav.tools": { he: "כלים", en: "Tools" },
    "chatbot.settings.agent.title": { he: "פרומפט", en: "Prompt" },
    "chatbot.settings.agent.description": {
        he: "ערכו את הפרומפט המלא שמנחה את הסוכן",
        en: "Edit the full prompt that guides the agent",
    },
    "chatbot.settings.agent.save-btn": { he: "שמירה", en: "Save" },
    "chatbot.settings.agent.system-prompt.label": {
        he: "פרומפט המערכת",
        en: "System prompt",
    },
    "chatbot.settings.agent.system-prompt.description": {
        he: "הפרומפט המלא שמנחה את הסוכן. ניתן לערוך אותו במלואו.",
        en: "The full prompt that guides the agent. You can edit all of it.",
    },
    "chatbot.settings.agent.rtl": { he: "ימין לשמאל", en: "RTL" },
    "chatbot.settings.agent.model.label": { he: "מודל", en: "Model" },
    "chatbot.settings.agent.model.flash-lite": {
        he: "Gemini 3.1 Flash Lite",
        en: "Gemini 3.1 Flash Lite",
    },
    "chatbot.settings.agent.model.flash": {
        he: "Gemini 3 Flash",
        en: "Gemini 3 Flash",
    },
    "chatbot.settings.agent.model.flash-smart": {
        he: "Gemini 3.5 Flash",
        en: "Gemini 3.5 Flash",
    },
    "chatbot.settings.agent.model.credits-suffix": {
        he: "קרדיטים להודעה",
        en: "credits per message",
    },
    "chatbot.settings.agent.thinking.label": {
        he: "רמת חשיבה",
        en: "Thinking level",
    },
    "chatbot.settings.agent.thinking.unavailable": {
        he: "המודל הזה אינו תומך ברמת חשיבה",
        en: "This model does not support a thinking level",
    },
    "chatbot.settings.agent.thinking.minimal": { he: "מינימלית", en: "Minimal" },
    "chatbot.settings.agent.thinking.low": { he: "נמוכה", en: "Low" },
    "chatbot.settings.agent.thinking.medium": { he: "בינונית", en: "Medium" },
    "chatbot.settings.agent.thinking.high": { he: "גבוהה", en: "High" },
    "chatbot.settings.agent.thinking.free": { he: "חינם", en: "Free" },
    "chatbot.settings.agent.templates.reset-btn": {
        he: "איפוס לתבנית",
        en: "Reset to template",
    },
    "chatbot.settings.agent.templates.rag.label": {
        he: "תבנית RAG",
        en: "RAG template",
    },
    "chatbot.settings.agent.templates.rag.description": {
        he: "מתאימה לכלי search_knowledge — מענה באמצעות חיפוש במאגר הידע",
        en: "Built for the search_knowledge tool — answers by searching the knowledge base",
    },
    "chatbot.settings.agent.templates.tree.label": {
        he: "תבנית עץ ידע",
        en: "Knowledge tree template",
    },
    "chatbot.settings.agent.templates.tree.description": {
        he: "מתאימה לכלים list_subtopics ו-fetch_topic — מענה באמצעות ניווט בעץ הנושאים",
        en: "Built for the list_subtopics and fetch_topic tools — answers by navigating the topic tree",
    },
    "chatbot.settings.agent.templates.confirm-title": {
        he: "להחליף את הפרומפט הנוכחי?",
        en: "Replace the current prompt?",
    },
    "chatbot.settings.agent.templates.confirm-description": {
        he: "התבנית תחליף את כל הטקסט בעורך, כולל שינויים שלא נשמרו. הפרומפט השמור יידרס רק לאחר לחיצה על שמירה.",
        en: "The template replaces all text in the editor, including unsaved changes. The saved prompt is overwritten only after you press Save.",
    },
    "chatbot.settings.agent.templates.confirm-action": {
        he: "החלפה",
        en: "Replace",
    },
    "chatbot.settings.agent.templates.confirm-cancel": {
        he: "ביטול",
        en: "Cancel",
    },
    "chatbot.settings.agent.variables.title": {
        he: "משתני פרומפט",
        en: "Prompt variables",
    },
    "chatbot.settings.agent.variables.root-topics.description": {
        he: "מתחלף אוטומטית ברשימת הנושאים הראשיים של הסוכן",
        en: "Automatically replaced with the list of the agent's main topics",
    },
    "chatbot.settings.agent.variables.insert": { he: "הוספה", en: "Insert" },
    "chatbot.settings.agent.variables.copy": { he: "העתקה", en: "Copy" },
    "chatbot.settings.agent.variables.copied": { he: "הועתק", en: "Copied" },
    "chatbot.settings.agent.variables.active": { he: "פעיל", en: "Active" },
    "chatbot.settings.agent.variables.unused": {
        he: "לא בשימוש",
        en: "Not used",
    },
    "chatbot.settings.agent.variables.tooltip": {
        he: "בלי המשתנה הזה בפרומפט, רשימת הנושאים לא תוזרק לשיחה.",
        en: "Without this variable in the prompt, no topic list is injected into the conversation.",
    },
    "chatbot.settings.agent.hint.variable-without-tools": {
        he: "הפרומפט מכיל את {variable} אבל לא מוגדרים כלי עץ ידע (list_subtopics / fetch_topic) — צריך להוסיף אותם בלשונית הכלים.",
        en: "The prompt contains {variable} but no knowledge-tree tools (list_subtopics / fetch_topic) are configured — consider adding them in the Tools tab.",
    },
    "chatbot.settings.agent.hint.tools-without-variable": {
        he: "מוגדרים כלי עץ ידע אבל הפרומפט לא מכיל את {variable} — רשימת נושאי השורש לא תוזרק לפרומפט.",
        en: "Knowledge-tree tools are configured but the prompt does not contain {variable} — the root topic list will not be injected.",
    },
    "chatbot.settings.tools.title": { he: "כלים", en: "Tools" },
    "chatbot.settings.tools.description": {
        he: "הגדירו כלים מותאמים אישית שהסוכן יכול לקרוא להם בזמן השיחה",
        en: "Declare custom tools the agent can call during a conversation",
    },
    "chatbot.settings.tools.save-btn": { he: "שמירה", en: "Save" },
    "chatbot.settings.tools.add-tool": { he: "הוספת כלי", en: "Add tool" },
    "chatbot.settings.tools.type.label": { he: "סוג הכלי", en: "Tool type" },
    "chatbot.settings.tools.type.rag": {
        he: "חיפוש RAG",
        en: "RAG search",
    },
    "chatbot.settings.tools.type.list-subtopics": {
        he: "עיון בתת-נושאים (עץ ידע)",
        en: "List subtopics (knowledge tree)",
    },
    "chatbot.settings.tools.type.fetch-topic": {
        he: "שליפת נושא (עץ ידע)",
        en: "Fetch topic (knowledge tree)",
    },
    "chatbot.settings.tools.type.custom": {
        he: "מותאם אישית",
        en: "Custom",
    },
    "chatbot.settings.tools.name.label": { he: "מזהה (ID)", en: "ID" },
    "chatbot.settings.tools.name.placeholder": {
        he: "my_custom_tool",
        en: "my_custom_tool",
    },
    "chatbot.settings.tools.description.label": { he: "תיאור", en: "Description" },
    "chatbot.settings.tools.waiting-message.label": {
        he: "הודעת המתנה",
        en: "Waiting message",
    },
    "chatbot.settings.tools.waiting-message.placeholder": {
        he: "מחפש בחומרי הקורס...",
        en: "Searching the knowledge base…",
    },
    "chatbot.settings.tools.waiting-message.description": {
        he: 'מוצג בצ׳אט בזמן שהכלי פועל. אם ריק, יוצג "חושב..."',
        en: 'Shown in the chat while this tool is running. Falls back to "Thinking…" when empty.',
    },
    "chatbot.settings.tools.request-type.label": {
        he: "סוג בקשה",
        en: "Request type",
    },
    "chatbot.settings.tools.url.label": {
        he: "כתובת Endpoint",
        en: "Endpoint URL",
    },
    "chatbot.settings.tools.url.placeholder": {
        he: "https://api.example.com/endpoint",
        en: "https://api.example.com/endpoint",
    },
    "chatbot.settings.tools.timeout.label": {
        he: "זמן קצוב (שניות)",
        en: "Timeout (seconds)",
    },
    "chatbot.settings.tools.headers.label": { he: "Headers", en: "Headers" },
    "chatbot.settings.tools.add-header": { he: "הוספת Header", en: "Add header" },
    "chatbot.settings.tools.query-params.label": {
        he: "פרמטרים ב-Query",
        en: "Query parameters",
    },
    "chatbot.settings.tools.add-query-param": {
        he: "הוספת פרמטר",
        en: "Add query param",
    },
    "chatbot.settings.tools.key.label": { he: "מפתח", en: "Key" },
    "chatbot.settings.tools.value.label": { he: "ערך", en: "Value" },
    "chatbot.settings.tools.payload.label": { he: "פרמטרים", en: "Parameters" },
    "chatbot.settings.tools.payload.description": {
        he: "הפרמטרים שהמודל ימלא כשהוא קורא לכלי",
        en: "The parameters the model fills in when calling the tool",
    },
    "chatbot.settings.tools.add-parameter": {
        he: "הוספת פרמטר",
        en: "Add parameter",
    },
    "chatbot.settings.tools.param.name": { he: "שם", en: "Name" },
    "chatbot.settings.tools.param.description": { he: "תיאור", en: "Description" },
    "chatbot.settings.tools.param.type": { he: "סוג", en: "Type" },
    "chatbot.settings.tools.param.required": { he: "שדה חובה", en: "Required field" },
    "chatbot.settings.tools.rag-note": {
        he: "כלי חיפוש הידע מובנה ומקבל רשימה של עד 3 שאילתות חיפוש (queries) — ניתן לערוך רק את התיאור שלו.",
        en: "The knowledge-search tool is built in and takes a list of up to 3 search queries — only its description is editable.",
    },
    "chatbot.settings.tools.list-subtopics-note": {
        he: "כלי מובנה של עץ הידע: מקבל רשימת מזהי נושאים ומחזיר את תת-הנושאים הישירים שלהם. ניתן לערוך רק את התיאורים ואת הודעת ההמתנה.",
        en: "Built-in knowledge-tree tool: takes topic IDs and returns their direct subtopics. Only the descriptions and the waiting message are editable.",
    },
    "chatbot.settings.tools.fetch-topic-note": {
        he: "כלי מובנה של עץ הידע: מקבל רשימת מזהי נושאים ומחזיר את התוכן המלא שלהם. ניתן לערוך רק את התיאורים ואת הודעת ההמתנה.",
        en: "Built-in knowledge-tree tool: takes topic IDs and returns their full content. Only the descriptions and the waiting message are editable.",
    },
    "chatbot.settings.tools.error.name-required": {
        he: "לכל כלי חייב להיות מזהה (ID)",
        en: "Every tool must have an ID",
    },
    "chatbot.settings.tools.error.description-required": {
        he: "לכל כלי חייב להיות תיאור",
        en: "Every tool must have a description",
    },
    "chatbot.settings.tools.error.invalid-url": {
        he: "כתובת Endpoint לא תקינה",
        en: "Invalid endpoint URL",
    },
    "chatbot.settings.tools.error.invalid-param": {
        he: "לכל פרמטר חייבים להיות שם ותיאור",
        en: "Every parameter must have a name and description",
    },
    "chatbot.settings.tools.error.duplicate-id": {
        he: "לכל כלי חייב להיות מזהה (ID) ייחודי",
        en: "Every tool must have a unique ID",
    },
    "chatbot.tabs.main": { he: "ראשי", en: "Main" },
    "chatbot.tabs.knowledge": { he: "ידע", en: "Knowledge" },
    "chatbot.tabs.chats": { he: "שיחות", en: "Chats" },
    "chatbot.tabs.settings": { he: "הגדרות", en: "Settings" },
    "chatbot.settings.sidenav.general": { he: "כללי", en: "General" },
    "chatbot.settings.sidenav.design": { he: "עיצוב", en: "Design" },
    "chatbot.settings.sidenav.nudnik": { he: "נודניק", en: "Nudnik" },
    "chatbot.settings.sidenav.embed": { he: "הטמעה", en: "Embed" },
    "chatbot.settings.general.type.label": { he: "סוג", en: "Type" },
    "chatbot.settings.general.type.course-assistant": {
        he: "עוזר הוראה",
        en: "Course assistant",
    },
    "chatbot.settings.general.type.support-assistant": {
        he: "עוזר תמיכה טכנית",
        en: "Support assistant",
    },
    "chatbot.settings.general.knowledge-language.label": {
        he: "שפת הידע",
        en: "Knowledge language",
    },
    "chatbot.bubble-side.left": { he: "שמאל", en: "Left" },
    "chatbot.bubble-side.right": { he: "ימין", en: "Right" },
    "chatbot.chats.new-group": { he: "קבוצה חדשה", en: "New group" },
    "chatbot.chats.new-group.description": {
        he: "מלאו את הפרטים ולחצו יצירה.",
        en: "Fill in the details and click create.",
    },
    "chatbot.chats.new-group.name-label": {
        he: "שם הקבוצה",
        en: "Group name",
    },
    "chatbot.knowledge.tabs.videos": { he: "סרטונים", en: "Videos" },
    "chatbot.knowledge.tabs.files": { he: "קבצים", en: "Files" },
    "chatbot.knowledge.tabs.websites": { he: "אתרים", en: "Websites" },
    "chatbot.knowledge.tabs.q-and-a": { he: "שאלות ותשובות", en: "Q&A" },
    "chatbot.knowledge.hide-source": {
        he: "הסתרת מקור ידע",
        en: "Hide knowledge source",
    },
    "chatbot.knowledge.hide-all-sources": {
        he: "הסתרת כל מקורות הידע",
        en: "Hide all knowledge sources",
    },
    "chatbot.knowledge.files.supported": {
        he: "קבצים נתמכים: {fileType}",
        en: "Supported files: {fileType}",
    },
    "chatbot.knowledge.videos.select-videos": {
        he: "בחירת סרטונים",
        en: "Select videos",
    },
    "chatbot.knowledge.videos.select-videos.description": {
        he: "סמנו את הסרטונים שאתם רוצים ולחצו אישור.",
        en: "Select the videos you want and click confirm.",
    },
    "chatbot.knowledge.videos.select-folder": {
        he: "בחירת תיקייה שלמה",
        en: "Select entire folder",
    },
    "chatbot.knowledge.videos.select-folder.description": {
        he: "נווטו אל התיקייה שתרצו להוסיף את כל הסרטונים שלה ולחצו בחירה.",
        en: "Navigate to the folder you want to add and click select.",
    },
    "chatbot.knowledge.errors.title": { he: "שגיאות:", en: "Errors:" },
    "chatbot.knowledge.errors.video": {
        he: 'הוספת ידע מהסרטון: "{identifier}" נכשלה: {error}',
        en: 'Failed to add knowledge from video "{identifier}": {error}',
    },
    "chatbot.knowledge.errors.file": {
        he: 'הוספת ידע מהקובץ: "{identifier}" נכשלה: {error}',
        en: 'Failed to add knowledge from file "{identifier}": {error}',
    },
    "chatbot.knowledge.errors.website": {
        he: 'הוספת ידע מהאתר: "{identifier}" נכשלה: {error}',
        en: 'Failed to add knowledge from website "{identifier}": {error}',
    },
    "chatbot.knowledge.errors.text": {
        he: 'הוספת ידע מטקסט: "{identifier}" נכשלה: {error}',
        en: 'Failed to add knowledge from text "{identifier}": {error}',
    },
    "chatbot.knowledge.errors.unknown": {
        he: "שגיאה לא צפויה",
        en: "Unexpected error",
    },
    "chatbot.knowledge.qa.add": {
        he: "הוספת שאלה ותשובה",
        en: "Add question and answer",
    },
    "chatbot.knowledge.qa.no-questions": {
        he: "ללא שאלות",
        en: "No questions",
    },
    "chatbot.knowledge.qa.edit-title": { he: "עריכה", en: "Edit" },
    "chatbot.knowledge.qa.create-title": { he: "יצירה", en: "Create" },
    "chatbot.knowledge.qa.edit-description": {
        he: "מלאו את הפרטים ולחצו אישור",
        en: "Fill in the details and click confirm",
    },
    "chatbot.knowledge.qa.questions": { he: "שאלות", en: "Questions" },
    "chatbot.knowledge.qa.answer": { he: "תשובה", en: "Answer" },
    "chatbot.knowledge.qa.image": { he: "תמונה", en: "Image" },
    "chatbot.knowledge.qa.import.button": {
        he: "ייבוא מאקסל",
        en: "Import from Excel",
    },
    "chatbot.knowledge.qa.import.title": {
        he: "ייבוא שאלות ותשובות מאקסל",
        en: "Import Q&A from Excel",
    },
    "chatbot.knowledge.qa.import.description": {
        he: "העלו קובץ אקסל והשאלות והתשובות יתווספו לידע. כל שורה היא שאלה ותשובה, והשורה הראשונה צריכה להכיל את כותרות העמודות.",
        en: "Upload an Excel file and the questions and answers will be added to the knowledge. Each row is one question and answer, and the first row must contain the column headers",
    },
    "chatbot.knowledge.qa.import.structure-title": {
        he: "מבנה הקובץ",
        en: "File structure",
    },
    "chatbot.knowledge.qa.import.col.question": {
        he: "שאלה",
        en: "Question",
    },
    "chatbot.knowledge.qa.import.col.answer": {
        he: "תשובה",
        en: "Answer",
    },
    "chatbot.knowledge.qa.import.col.image": {
        he: "קישור לתמונה",
        en: "Image URL",
    },
    "chatbot.knowledge.qa.import.image-note": {
        he: "עמודת הקישור לתמונה אופציונלית — הזינו כתובת URL מלאה של תמונה.",
        en: "The image URL column is optional — enter a full image URL.",
    },
    "chatbot.knowledge.qa.import.download-template": {
        he: "הורדת קובץ לדוגמה",
        en: "Download template",
    },
    "chatbot.knowledge.qa.import.upload.label": {
        he: "העלאת קובץ",
        en: "Upload file",
    },
    "chatbot.knowledge.qa.import.upload.title": {
        he: "גררו לכאן קובץ אקסל או לחצו לבחירה",
        en: "Drag an Excel file here or click to select",
    },
    "chatbot.knowledge.qa.import.upload.subtitle": {
        he: "קבצי .xlsx או .xls בלבד",
        en: ".xlsx or .xls files only",
    },
    "chatbot.knowledge.qa.import.parsing": {
        he: "מעבד את הקובץ...",
        en: "Processing file...",
    },
    "chatbot.knowledge.qa.import.success": {
        he: "{count} שאלות ותשובות יובאו בהצלחה",
        en: "{count} questions and answers imported successfully",
    },
    "chatbot.knowledge.qa.import.success-with-skipped": {
        he: "{count} שאלות ותשובות יובאו, {skipped} שורות דולגו",
        en: "{count} questions and answers imported, {skipped} rows skipped",
    },
    "chatbot.knowledge.qa.import.error.empty": {
        he: "הקובץ ריק",
        en: "The file is empty",
    },
    "chatbot.knowledge.qa.import.error.parse": {
        he: "לא הצלחנו לקרוא את הקובץ. ודאו שזהו קובץ אקסל תקין",
        en: "We couldn't read the file. Make sure it's a valid Excel file",
    },
    "chatbot.knowledge.qa.import.error.missing-columns": {
        he: 'מבנה הקובץ שגוי — נדרשות עמודות "שאלה" ו"תשובה"',
        en: 'Invalid file structure — "Question" and "Answer" columns are required',
    },
    "chatbot.knowledge.qa.import.error.no-valid-rows": {
        he: "לא נמצאו שורות תקינות בקובץ",
        en: "No valid rows found in the file",
    },
    "chatbot.knowledge.qa.import.template.example-question": {
        he: "מהן שעות הפעילות?",
        en: "What are your opening hours?",
    },
    "chatbot.knowledge.qa.import.template.example-answer": {
        he: "אנחנו פתוחים בימים א'-ה' בין 9:00 ל-17:00.",
        en: "We're open Sunday to Thursday, 9:00 to 17:00.",
    },
    "chatbot.knowledge.qa.import.template.sheet-name": {
        he: "שאלות ותשובות",
        en: "Q&A",
    },
    "chatbot.knowledge.qa.import.template.filename": {
        he: "תבנית-שאלות-ותשובות",
        en: "qa-template",
    },
    "chatbot.knowledge.mode.rag": {
        he: "RAG",
        en: "RAG",
    },
    "chatbot.knowledge.mode.tree": {
        he: "עץ ידע",
        en: "Knowledge tree",
    },
    "chatbot.knowledge.mode.label": {
        he: "סוג מאגר הידע",
        en: "Knowledge base type",
    },
    "chatbot.knowledge.mode.description": {
        he: "בחירת תצוגה בלבד. שני מאגרי הידע קיימים במקביל, והסוכן משתמש בזה שהכלים שלו מוגדרים אליו.",
        en: "Display choice only. Both knowledge bases live side by side; the agent uses whichever its assigned tools read from.",
    },
    "chatbot.knowledge.mode.rag.option-description": {
        he: "מקורות ידע חופשיים כמו סרטונים, קבצים, אתרים ושו״ת שהסוכן שולף מהם.",
        en: "Free-form sources like videos, files, websites and Q&A the agent retrieves from.",
    },
    "chatbot.knowledge.mode.tree.option-description": {
        he: "עץ נושאים מסודר שהסוכן מנווט בו כדי לענות בדיוק מהידע שהגדרתם.",
        en: "A curated topic tree the agent navigates to answer strictly from your knowledge.",
    },
    "chatbot.knowledge.tree.description": {
        he: "בנו עץ נושאים מסודר, והסוכן ינווט בו כדי לענות בדיוק מהידע שהגדרתם.",
        en: "Build a curated topic tree that the agent navigates to answer strictly from your knowledge.",
    },
    "chatbot.knowledge.tree.add-root": {
        he: "הוספת נושא ראשי",
        en: "Add root topic",
    },
    "chatbot.knowledge.tree.auto-arrange": {
        he: "מירכוז",
        en: "Centralize",
    },
    "chatbot.knowledge.tree.node.agent-caption": {
        he: "הסוכן",
        en: "Agent",
    },
    "chatbot.knowledge.tree.node.agent-fallback": {
        he: "הסוכן שלך",
        en: "Your agent",
    },
    "chatbot.knowledge.tree.search-placeholder": {
        he: "חיפוש נושא...",
        en: "Search topics...",
    },
    "chatbot.knowledge.tree.topics-count": {
        he: "{count} נושאים",
        en: "{count} topics",
    },
    "chatbot.knowledge.tree.load-error": {
        he: "טעינת עץ הידע נכשלה. נסו לרענן את העמוד.",
        en: "Failed to load the knowledge tree. Try refreshing the page.",
    },
    "chatbot.knowledge.tree.empty.title": {
        he: "עדיין אין נושאים",
        en: "No topics yet",
    },
    "chatbot.knowledge.tree.empty.description": {
        he: "התחילו לבנות את עץ הידע — הוסיפו נושא ראשי ראשון.",
        en: "Start building the knowledge tree — add your first root topic.",
    },
    "chatbot.knowledge.tree.locked.title": {
        he: "עץ ידע מותאם אישית",
        en: "A curated knowledge tree",
    },
    "chatbot.knowledge.tree.locked.description": {
        he: "עץ ידע מסדר את התכנים שלכם לנושאים ברורים שהסוכן מנווט ביניהם, כדי לענות במדויק ורק מתוך הידע שלכם. הצוות שלנו יבנה אותו עבורכם מהתכנים הקיימים, בלי עבודה מצדכם.",
        en: "A knowledge tree organizes your content into clear topics your agent navigates, so it answers precisely and only from your own knowledge. Our team builds it for you from your existing content, with no work on your side.",
    },
    "chatbot.knowledge.tree.locked.feature-curated": {
        he: "עץ נושאים מותאם לתוכן שלכם",
        en: "Topics tailored to your content",
    },
    "chatbot.knowledge.tree.locked.feature-precise": {
        he: "תשובות מדויקות מבוססות הידע שלכם",
        en: "Precise, grounded answers",
    },
    "chatbot.knowledge.tree.locked.feature-built": {
        he: "אנחנו בונים אותו עבורכם",
        en: "Built for you by our team",
    },
    "chatbot.knowledge.tree.locked.contact-btn": {
        he: "דברו איתנו",
        en: "Contact us",
    },
    "chatbot.knowledge.tree.locked.footnote": {
        he: "השאירו פרטים ונחזור אליכם בהקדם.",
        en: "Leave your details and we will get back to you shortly.",
    },
    "chatbot.knowledge.tree.node.root-badge": {
        he: "ראשי",
        en: "root",
    },
    "chatbot.knowledge.tree.node.empty-label": {
        he: "ריק",
        en: "empty",
    },
    "chatbot.knowledge.tree.node.no-content": {
        he: "עדיין אין תוכן לנושא הזה",
        en: "No content yet",
    },
    "chatbot.knowledge.tree.node.add-child": {
        he: "הוספת תת־נושא",
        en: "Add subtopic",
    },
    "chatbot.knowledge.tree.edge.detach": {
        he: "ניתוק מהנושא ההורה",
        en: "Detach from parent",
    },
    "chatbot.knowledge.tree.editor.title": {
        he: "עריכת נושא",
        en: "Edit topic",
    },
    "chatbot.knowledge.tree.editor.create-title": {
        he: "נושא חדש",
        en: "New topic",
    },
    "chatbot.knowledge.tree.editor.sheet-description": {
        he: "הגדירו מה הסוכן יודע על הנושא הזה.",
        en: "Define what the agent knows about this topic.",
    },
    "chatbot.knowledge.tree.editor.title-label": {
        he: "כותרת",
        en: "Title",
    },
    "chatbot.knowledge.tree.editor.title-placeholder": {
        he: "שם הנושא",
        en: "Topic name",
    },
    "chatbot.knowledge.tree.editor.description-label": {
        he: "תיאור קצר",
        en: "Short description",
    },
    "chatbot.knowledge.tree.editor.description-helper": {
        he: "מוצג לסוכן כשהוא מדפדף בין הנושאים.",
        en: "Shown to the agent when browsing topics.",
    },
    "chatbot.knowledge.tree.editor.priority-label": {
        he: "עדיפות",
        en: "Priority",
    },
    "chatbot.knowledge.tree.editor.priority-helper": {
        he: "במקרה של סתירה בין מקורות, הסוכן מעדיף נושאים עם עדיפות גבוהה יותר.",
        en: "When sources conflict, the agent prefers topics with higher priority.",
    },
    "chatbot.knowledge.tree.editor.priority-1": {
        he: "הכי נמוכה",
        en: "Lowest",
    },
    "chatbot.knowledge.tree.editor.priority-2": {
        he: "נמוכה",
        en: "Low",
    },
    "chatbot.knowledge.tree.editor.priority-3": {
        he: "רגילה",
        en: "Normal",
    },
    "chatbot.knowledge.tree.editor.priority-4": {
        he: "גבוהה",
        en: "High",
    },
    "chatbot.knowledge.tree.editor.priority-5": {
        he: "הכי גבוהה",
        en: "Highest",
    },
    "chatbot.knowledge.tree.editor.content-label": {
        he: "תוכן",
        en: "Content",
    },
    "chatbot.knowledge.tree.editor.content-helper": {
        he: "הידע המלא שמוחזר לסוכן — אפשר לכתוב ב־Markdown.",
        en: "The full knowledge returned to the agent — markdown supported.",
    },
    "chatbot.knowledge.tree.editor.faqs-label": {
        he: "שאלות ותשובות",
        en: "FAQs",
    },
    "chatbot.knowledge.tree.editor.faqs-helper": {
        he: "תשובות מנוסחות מראש לשאלות שחשוב לענות עליהן במדויק.",
        en: "Curated answers for questions that must be answered precisely.",
    },
    "chatbot.knowledge.tree.editor.add-faq": {
        he: "הוספת שאלה",
        en: "Add FAQ",
    },
    "chatbot.knowledge.tree.editor.faq-question-placeholder": {
        he: "שאלה",
        en: "Question",
    },
    "chatbot.knowledge.tree.editor.faq-answer-placeholder": {
        he: "תשובה",
        en: "Answer",
    },
    "chatbot.knowledge.tree.editor.sources-label": {
        he: "מקורות",
        en: "Sources",
    },
    "chatbot.knowledge.tree.editor.sources-helper": {
        he: "מאיפה הגיע התוכן — כדי שתמיד אפשר יהיה לחזור למקור.",
        en: "Where the content came from, so you can always trace it back.",
    },
    "chatbot.knowledge.tree.editor.add-source": {
        he: "הוספת מקור",
        en: "Add source",
    },
    "chatbot.knowledge.tree.editor.source-label-placeholder": {
        he: "תיאור המקור",
        en: "Source label",
    },
    "chatbot.knowledge.tree.editor.source-url-placeholder": {
        he: "כתובת (URL)",
        en: "URL",
    },
    "chatbot.knowledge.tree.editor.source-type.video": {
        he: "סרטון",
        en: "Video",
    },
    "chatbot.knowledge.tree.editor.source-type.file": {
        he: "קובץ",
        en: "File",
    },
    "chatbot.knowledge.tree.editor.source-type.website": {
        he: "אתר",
        en: "Website",
    },
    "chatbot.knowledge.tree.editor.source-type.text": {
        he: "טקסט",
        en: "Text",
    },
    "chatbot.knowledge.tree.editor.source-type.other": {
        he: "אחר",
        en: "Other",
    },
    "chatbot.knowledge.tree.editor.remove-row": {
        he: "הסרת שורה",
        en: "Remove row",
    },
    "chatbot.knowledge.tree.editor.save": {
        he: "שמירה",
        en: "Save",
    },
    "chatbot.knowledge.tree.editor.delete": {
        he: "מחיקת נושא",
        en: "Delete topic",
    },
    "chatbot.knowledge.tree.editor.delete-confirm-action": {
        he: "מחיקה",
        en: "Delete",
    },
    "chatbot.knowledge.tree.editor.delete-confirm-title": {
        he: "למחוק את הנושא?",
        en: "Delete this topic?",
    },
    "chatbot.knowledge.tree.editor.delete-confirm-description": {
        he: "הנושא יימחק לצמיתות. אי אפשר לבטל את הפעולה.",
        en: "The topic will be permanently deleted. This cannot be undone.",
    },
    "chatbot.knowledge.tree.editor.delete-confirm-description-with-children": {
        he: "הנושא יימחק לצמיתות יחד עם כל {count} תתי־הנושאים שלו. אי אפשר לבטל את הפעולה.",
        en: "The topic and all {count} of its subtopics will be permanently deleted. This cannot be undone.",
    },
    "chatbot.knowledge.tree.editor.cancel": {
        he: "ביטול",
        en: "Cancel",
    },
    "chatbot.knowledge.tree.editor.discard-title": {
        he: "לבטל את השינויים?",
        en: "Discard changes?",
    },
    "chatbot.knowledge.tree.editor.discard-description": {
        he: "יש שינויים שלא נשמרו בנושא הזה. אם ממשיכים, השינויים יימחקו.",
        en: "You have unsaved changes to this topic. Continuing will discard them.",
    },
    "chatbot.knowledge.tree.editor.discard-confirm": {
        he: "ביטול השינויים",
        en: "Discard changes",
    },
    "chatbot.knowledge.tree.editor.discard-cancel": {
        he: "המשך עריכה",
        en: "Keep editing",
    },
    "chatbot.knowledge.tree.editor.new-topic-default-title": {
        he: "נושא חדש",
        en: "New topic",
    },
    "toast.success.create-chatbot-topic": {
        he: "הנושא נוצר בהצלחה!",
        en: "Topic created!",
    },
    "toast.success.save-chatbot-topic": {
        he: "הנושא נשמר בהצלחה!",
        en: "Topic saved!",
    },
    "toast.success.delete-chatbot-topic": {
        he: "הנושא נמחק בהצלחה!",
        en: "Topic deleted!",
    },
    "toast.error.topic-cycle": {
        he: "אי אפשר להעביר נושא אל מתחת לתת־נושא של עצמו",
        en: "A topic can't be moved under its own subtopic.",
    },
    "chatbot.answers.no-answer.title": {
        he: "שאלה ללא תשובה",
        en: "Unanswered question",
    },
    "chatbot.answers.no-answer.add": {
        he: "הוספת תשובה לידע",
        en: "Add answer to knowledge",
    },
    "email.knowledge-ready.subject": {
        he: "הידע של {chatbot} מוכן",
        en: "The knowledge for {chatbot} is ready",
    },
    "email.knowledge-ready.subject-partial": {
        he: "הידע של {chatbot} מוכן — חלק מהפריטים נכשלו",
        en: "The knowledge for {chatbot} is ready — some items failed",
    },
    "email.knowledge-ready.body": {
        he: "סיימנו לעבד {count} פריטי ידע עבור {chatbot}. הסוכן מוכן לשימוש.",
        en: "We finished processing {count} knowledge items for {chatbot}. The agent is ready to use.",
    },
    "email.knowledge-ready.failed-intro": {
        he: "{count} פריטים לא עובדו בהצלחה:",
        en: "{count} items could not be processed:",
    },
    "email.knowledge-ready.cta": {
        he: "מעבר לסוכן",
        en: "Open the agent",
    },
    "chatbot.knowledge.job-in-progress": {
        he: "מעבדים את הידע ברקע ({done} מתוך {total}). נשלח אליכם מייל בסיום.",
        en: "Processing knowledge in the background ({done} of {total}). We'll email you when it's done.",
    },
    "chatbot.knowledge.job-queued": {
        he: "הידע נשמר. העיבוד מתבצע ברקע ותקבלו מייל בסיום.",
        en: "Knowledge saved. Processing runs in the background and you'll get an email when it's done.",
    },
    "chatbot.answers.no-answer.dismiss": {
        he: "הסרת השאלה מהרשימה",
        en: "Dismiss question",
    },
    "chatbot.answers.add-modal.title": {
        he: "הוספת תשובה",
        en: "Add answer",
    },
    "chatbot.answers.add-modal.description": {
        he: "מלאו את הפרטים ולחצו הוספה",
        en: "Fill in the details and click add",
    },
    "chatbot.answers.add-modal.question": { he: "שאלה", en: "Question" },
    "chatbot.answers.add-modal.answer": { he: "תשובה", en: "Answer" },
    "chatbot.answers.add-modal.image": { he: "תמונה", en: "Image" },
    "chatbot.nudnik.trigger-settings.title": {
        he: "הגדרות הטריגר",
        en: "Trigger settings",
    },
    "chatbot.nudnik.trigger-settings.placeholder": {
        he: "מתי יופיע הטריגר",
        en: "When should the trigger appear",
    },
    "chatbot.nudnik.trigger-settings.label": {
        he: "מתי יופיע הטריגר",
        en: "When should the trigger appear",
    },
    "chatbot.nudnik.trigger-settings.value-placeholder": {
        he: "ערך",
        en: "Value",
    },
    "chatbot.nudnik.trigger-settings.delete": {
        he: "מחיקת טריגר",
        en: "Delete trigger",
    },
    "chatbot.nudnik.messages.title": {
        he: "הודעות של הנודניק",
        en: "Nudnik messages",
    },
    "chatbot.nudnik.messages.type-placeholder": {
        he: "סוג ההודעה",
        en: "Message type",
    },
    "chatbot.nudnik.messages.type-label": {
        he: "סוג ההודעה",
        en: "Message type",
    },
    "chatbot.nudnik.messages.message-placeholder": {
        he: "הזינו הודעה",
        en: "Enter a message",
    },
    "chatbot.nudnik.messages.video-picker-description": {
        he: "בחרו סרטון ולחצו אישור.",
        en: "Choose a video and click confirm.",
    },
    "chatbot.nudnik.messages.video-picker-placeholder": {
        he: "לחצו כדי לבחור סרטון",
        en: "Click to choose a video",
    },
    "chatbot.nudnik.messages.delete": {
        he: "מחיקת הודעה",
        en: "Delete message",
    },
    "chatbot.nudnik.messages.add": { he: "הוספת הודעה", en: "Add message" },
    "chatbot.nudnik.trigger-condition.always": {
        he: "יופיע תמיד",
        en: "Always show",
    },
    "chatbot.nudnik.trigger-condition.url-includes": {
        he: "רק אם ה-url מכיל את",
        en: "Only if the URL includes",
    },
    "chatbot.nudnik.trigger-condition.url-equals": {
        he: "רק אם ה-url שווה ל-",
        en: "Only if the URL equals",
    },
    "chatbot.nudnik.trigger-message-type.text": { he: "טקסט", en: "Text" },
    "chatbot.nudnik.trigger-message-type.video": { he: "סרטון", en: "Video" },
    "chatbots-grid.order-btn": { he: "סדר", en: "Order" },
    "chatbots-grid.search-inputs.placeholder": {
        he: "חיפוש...",
        en: "Search...",
    },
    "chatbots-grid.pagination.next": { he: "הבא", en: "Next" },
    "chatbots-grid.pagination.page-out-of-pages": {
        he: "עמוד {currentPage} מתוך {totalPages}",
        en: "Page {currentPage} of {totalPages}",
    },
    "chatbots-grid.pagination.prev": { he: "הקודם", en: "Previous" },
    "chatbots-grid.pagination.results-per-page": {
        he: "תוצאות בעמוד:",
        en: "Results per page:",
    },
    "chatbots-grid.see-all-btn": { he: "לכל התיקיות", en: "All folders" },
    "chatbots-grid.sort-btn": { he: "מיון לפי", en: "Sort by" },
    "chatbots-grid.card.questions-count": {
        he: "{count} שאלות",
        en: "{count} questions",
    },
    "chatbots-grid.card.no-answer": { he: "ללא תשובה", en: "No answer" },
    "chatbots.order-options.desc": { he: "יורד", en: "Descending" },
    "chatbots.order-options.asc": { he: "עולה", en: "Ascending" },
    "chatbots.sort-options.created-at": { he: "נוצר בתאריך", en: "Created at" },
    "chatbots.sort-options.name": { he: "שם", en: "Name" },
    "chatbots.sort-options.activity": { he: "פעילות", en: "Activity" },
    "chatbots.create-btn": { he: "סוכן AI חדש", en: "New AI agent" },
    "chatbots.delete-chatbot-modal.delete-btn": { he: "מחיקה", en: "Delete" },
    "color-picker.chosen-color.label": {
        he: "הצבע הנבחר:",
        en: "Selected color:",
    },
    "color-picker.generate-palette.btn": {
        he: "ייצור פלטה",
        en: "Generate palette",
    },
    "color-picker.gradient.from": {
        he: "מ",
        en: "From",
    },
    "color-picker.gradient.to": {
        he: "אל",
        en: "To",
    },
    "course-syllabus.lesson.record-lesson-button": {
        he: "יצירה",
        en: "Create",
    },
    "courses.create-course-btn": { he: "קורס חדש ", en: "New course" },
    "courses.tabs.overview": { he: "כללי", en: "Overview" },
    "courses.order-options.desc": { he: "יורד", en: "Descending" },
    "courses.order-options.asc": { he: "עולה", en: "Ascending" },
    "courses.sort-options.created-at": { he: "נוצר בתאריך", en: "Created at" },
    "courses.sort-options.name": { he: "שם", en: "Name" },
    "courses.grid.order-btn": { he: "סדר", en: "Order" },
    "courses.grid.search-inputs.placeholder": {
        he: "חיפוש...",
        en: "Search...",
    },
    "courses.grid.pagination.next": { he: "הבא", en: "Next" },
    "courses.grid.pagination.page-out-of-pages": {
        he: "עמוד {currentPage} מתוך {totalPages}",
        en: "Page {currentPage} of {totalPages}",
    },
    "courses.grid.pagination.prev": { he: "הקודם", en: "Previous" },
    "courses.grid.pagination.results-per-page": {
        he: "תוצאות בעמוד:",
        en: "Results per page:",
    },
    "courses.grid.sort-btn": { he: "מיון לפי", en: "Sort by" },
    "courses.nav.edit": { he: "עריכה", en: "Edit" },
    "courses.nav.syllabus": { he: "סילאבוס", en: "Syllabus" },
    "courses.nav.students": { he: "תלמידים", en: "Students" },
    "courses.nav.settings": { he: "הגדרות", en: "Settings" },
    "courses.no-name": { he: "ללא שם", en: "Untitled" },
    "courses.overview.title": { he: "הקורסים שלי", en: "My courses" },
    "courses.status.live": { he: "לייב", en: "Live" },
    "courses.status.draft": { he: "דראפט", en: "Draft" },
    "courses.progress.tooltip": {
        he: "סיימת {progress}% מהקורס",
        en: "You completed {progress}% of the course",
    },
    "courses.settings.title": { he: "הגדרות", en: "Settings" },
    "courses.settings.general.name.label": {
        he: "שם הקורס",
        en: "Course name",
    },
    "courses.settings.general.pathname.label": {
        he: "כתובת URL",
        en: "URL",
    },
    "courses.settings.general.enrollment.label": {
        he: "הרשמה",
        en: "Enrollment",
    },
    "courses.settings.price.label": { he: "מחיר", en: "Price" },
    "courses.settings.access-period.label": {
        he: "תקופת גישה",
        en: "Access period",
    },
    "courses.settings.access-period.placeholder": {
        he: "בחרו תקופת גישה",
        en: "Select access period",
    },
    "courses.settings.access-period.forever": { he: "לתמיד", en: "Forever" },
    "courses.settings.access-period.1-month": { he: "חודש", en: "1 month" },
    "courses.settings.access-period.3-months": {
        he: "3 חודשים",
        en: "3 months",
    },
    "courses.settings.access-period.12-months": {
        he: "12 חודשים",
        en: "12 months",
    },
    "courses.settings.access-period.24-months": {
        he: "24 חודשים",
        en: "24 months",
    },
    "courses.settings.access-period.36-months": {
        he: "36 חודשים",
        en: "36 months",
    },
    "courses.settings.enrollment.public.name": {
        he: "ציבורי",
        en: "Public",
    },
    "courses.settings.enrollment.public.description": {
        he: "כולם יכולים להירשם לקורס",
        en: "Anyone can enroll",
    },
    "courses.settings.enrollment.free.name": { he: "חינמי", en: "Free" },
    "courses.settings.enrollment.free.description": {
        he: "הקורס פתוח לכל מי שנרשם",
        en: "Open to anyone who enrolls",
    },
    "courses.settings.enrollment.paid.name": { he: "בתשלום", en: "Paid" },
    "courses.settings.enrollment.paid.description": {
        he: "גישה רק לאחר תשלום",
        en: "Access after payment",
    },
    "courses.settings.enrollment.subscription.name": {
        he: "מנוי",
        en: "Subscription",
    },
    "courses.settings.enrollment.subscription.description": {
        he: "גישה במסגרת מנוי פעיל",
        en: "Access with an active subscription",
    },
    "courses.settings.tabs.general": { he: "כללי", en: "General" },
    "courses.settings.tabs.coupons": { he: "קופונים", en: "Coupons" },
    "courses.navigation.new-chapter": { he: "פרק חדש", en: "New chapter" },
    "courses.navigation.new-lesson": { he: "שיעור חדש", en: "New lesson" },
    "courses.navigation.lessons-count": {
        he: "{count} שיעורים",
        en: "{count} lessons",
    },
    "courses.action-menu.lesson-settings": {
        he: "הגדרות השיעור",
        en: "Lesson settings",
    },
    "courses.action-menu.delete-lesson": {
        he: "מחיקת השיעור",
        en: "Delete lesson",
    },
    "courses.action-menu.edit-chapter": {
        he: "עריכת הפרק",
        en: "Edit chapter",
    },
    "courses.action-menu.delete-chapter": {
        he: "מחיקת הפרק",
        en: "Delete chapter",
    },
    "courses.below-lesson.navigation": { he: "ניווט", en: "Navigation" },
    "courses.below-lesson.teacher-comments": {
        he: "הערות לשיעור",
        en: "Lesson notes",
    },
    "courses.below-lesson.questions": { he: "שאלות", en: "Questions" },
    "courses.below-lesson.edit": { he: "עריכה", en: "Edit" },
    "courses.below-lesson.watch": { he: "צפייה", en: "Watch" },
    "courses.modals.chapter.edit-title": {
        he: "עריכת פרק",
        en: "Edit chapter",
    },
    "courses.modals.chapter.new-title": { he: "פרק חדש", en: "New chapter" },
    "courses.modals.chapter.description": {
        he: "בתוך הפרק תוכלו ליצור הרבה שיעורים.",
        en: "Inside a chapter you can create many lessons.",
    },
    "courses.modals.chapter.name-label": { he: "שם הפרק", en: "Chapter name" },
    "courses.modals.lesson.settings-title": {
        he: "הגדרות השיעור",
        en: "Lesson settings",
    },
    "courses.modals.lesson.new-title": { he: "שיעור חדש", en: "New lesson" },
    "courses.modals.lesson.description": {
        he: "כאן תוכלו לבחור את הגדרות השיעור.",
        en: "Choose the lesson settings here.",
    },
    "courses.modals.lesson.name-label": { he: "שם השיעור", en: "Lesson name" },
    "courses.modals.lesson.chapter-selection-label": {
        he: "הפרק אליו יתווסף השיעור",
        en: "Chapter for the lesson",
    },
    "courses.modals.lesson.chapter-selection-placeholder": {
        he: "בחירת פרק",
        en: "Select chapter",
    },
    "courses.modals.lesson.visibility-label": {
        he: "זמינות השיעור",
        en: "Lesson visibility",
    },
    "courses.modals.lesson.visibility-locked": { he: "נעול", en: "Locked" },
    "courses.modals.lesson.visibility-locked-description": {
        he: "- רק מי ששילם יוכל לצפות בשיעור",
        en: "- only paying students can watch",
    },
    "courses.modals.lesson.visibility-open": { he: "פתוח", en: "Open" },
    "courses.modals.lesson.visibility-open-description": {
        he: "- כולם יוכלו לצפות בשיעור",
        en: "- everyone can watch",
    },
    "courses.modals.delete-lesson.title": {
        he: "מחיקת שיעור",
        en: "Delete lesson",
    },
    "courses.modals.delete-lesson.confirm-prefix": {
        he: "אתם בטוחים שתרצו למחוק את השיעור:",
        en: "Are you sure you want to delete the lesson:",
    },
    "courses.modals.delete-lesson.confirm-suffix": { he: "?", en: "?" },
    "courses.modals.delete-chapter.title": {
        he: "מחיקת פרק",
        en: "Delete chapter",
    },
    "courses.modals.delete-chapter.confirm-prefix": {
        he: "אתם בטוחים שתרצו למחוק את הפרק:",
        en: "Are you sure you want to delete the chapter:",
    },
    "courses.modals.delete-chapter.confirm-suffix": { he: "?", en: "?" },
    "courses.modals.delete-chapter.warning": {
        he: "מחיקת הפרק תמחק גם את כל השיעורים שבתוכו.",
        en: "Deleting the chapter will also delete all lessons inside it.",
    },
    "courses.lesson-types.empty": { he: "ללא תוכן", en: "No content" },
    "courses.lesson.change-type": {
        he: "שינוי סוג השיעור",
        en: "Change lesson type",
    },
    "courses.lesson.choose-type.title": {
        he: "בחרו את סוג השיעור",
        en: "Choose the lesson type",
    },
    "courses.lesson.empty.title": {
        he: "אין לכם עדיין שיעורים",
        en: "You don't have any lessons yet",
    },
    "courses.lesson.empty.description": {
        he: "התחילו בליצור פרק חדש ובתוכו תיצרו שיעור",
        en: "Start by creating a new chapter and add a lesson inside it",
    },
    "courses.lesson.no-permission.purchase-title": {
        he: "רכשו את הקורס",
        en: "Purchase the course",
    },
    "courses.lesson.no-permission.no-access-title": {
        he: "אין לך גישה",
        en: "No access",
    },
    "courses.lesson.no-permission.purchase-description": {
        he: "רכשו את הקורס כדי לצפות בכל השיעורים",
        en: "Purchase the course to watch all lessons",
    },
    "courses.lesson.no-permission.signup-description": {
        he: "הירשמו כדי לצפות בכל השיעורים",
        en: "Sign up to watch all lessons",
    },
    "courses.lesson.no-permission.purchase-cta": {
        he: "לרכישת הקורס",
        en: "Purchase course",
    },
    "courses.lesson.no-permission.signup-cta": { he: "להרשמה", en: "Sign up" },
    "courses.questions.title": { he: "שאלות", en: "Questions" },
    "courses.questions.new-question": { he: "שאלה חדשה", en: "New question" },
    "courses.questions.title-placeholder": {
        he: "כותרת השאלה",
        en: "Question title",
    },
    "courses.questions.details-placeholder": {
        he: "פירוט השאלה",
        en: "Question details",
    },
    "courses.questions.back-to-questions": {
        he: "לכל השאלות",
        en: "All questions",
    },
    "courses.questions.comments-count": {
        he: "{count} תגובות",
        en: "{count} comments",
    },
    "courses.questions.add-comment": { he: "הוספת תגובה", en: "Add comment" },
    "courses.questions.add-comment-placeholder": {
        he: "הוסיפו תגובה לדיון",
        en: "Add a comment to the discussion",
    },
    "courses.lesson.video.title": { he: "וידאו", en: "Video" },
    "courses.lesson.video.description": {
        he: "הדביקו לינק לסרטון חיצוני או בחרו סרטון מהאחסון שלכם באקסייטר.",
        en: "Paste an external video link or pick a video from your Xsiter storage.",
    },
    "courses.lesson.video.my-videos": { he: "הסרטונים שלי", en: "My videos" },
    "courses.lesson.video.record-now": { he: "הקלט עכשיו", en: "Record now" },
    "courses.lesson.video.url-label": { he: "לינק לסרטון", en: "Video URL" },
    "courses.lesson.text.title": { he: "טקסט", en: "Text" },
    "courses.lesson.text.description": {
        he: "כתבו כאן את תוכן השיעור",
        en: "Write the lesson content here",
    },
    "courses.lesson.image.title": { he: "תמונה", en: "Image" },
    "courses.lesson.image.description": {
        he: "העלו לכאן תמונה שתוצג לתלמידים שלכם בתור השיעור",
        en: "Upload an image that will be shown as the lesson",
    },
    "courses.image-lesson.upload-image.title": {
        he: "העלאת תמונה",
        en: "Upload image",
    },
    "courses.image-lesson.upload-image.subtitle": {
        he: "גררו לכאן תמונה, או לחצו כדי לבחור תמונה",
        en: "Drag an image here or click to choose",
    },
    "courses.lesson-types.video.name": { he: "וידאו", en: "Video" },
    "courses.lesson-types.video.description": {
        he: "הטמיעו לינק לסרטון או העלו ישירות לאחסון של אקסייטר",
        en: "Embed a video link or upload directly to Xsiter storage",
    },
    "courses.lesson-types.image.name": { he: "תמונה", en: "Image" },
    "courses.lesson-types.image.description": {
        he: "העלו תמונה לאחסון של אקסייטר והציגו אותה בתור השיעור",
        en: "Upload an image to Xsiter storage and display it as the lesson",
    },
    "courses.lesson-types.text.name": { he: "טקסט", en: "Text" },
    "courses.lesson-types.text.description": {
        he: "שיעור כתוב - כותרות, רשימות, טקסטים, טבלאות ועוד",
        en: "Written lesson with headings, lists, text, tables, and more",
    },
    "courses.lesson-types.live.name": { he: "מפגש לייב", en: "Live session" },
    "courses.lesson-types.live.description": {
        he: "תזמון מפגש עתידי כמו זום בתוך הקורס שלכם",
        en: "Schedule a future meeting like Zoom inside your course",
    },
    "courses.lesson-types.test.name": { he: "מבחן", en: "Test" },
    "courses.lesson-types.test.description": {
        he: "בנו מבחן לתלמידים שלכם כדי לבדוק את הידע שלהם",
        en: "Create a test for your students to assess their knowledge",
    },
    "courses.lesson-types.upsell.name": { he: "Upsell", en: "Upsell" },
    "courses.lesson-types.upsell.description": {
        he: "הציעו למכירה מוצר המשך לקורס הזה",
        en: "Offer a follow-up product for sale after this course",
    },
    "create-chatbot-modal.header.description": {
        he: "בחרו שם שיעזור לכם לזהות את העוזר האישי.",
        en: "Choose a name to help you identify the personal assistant.",
    },
    "create-chatbot-modal.header.title": {
        he: "יצירת סוכן AI",
        en: "Create AI agent",
    },
    "create-chatbot-modal.name-input.label": { he: "שם", en: "Name" },
    "create-chatbot-modal.submit-btn": { he: "יצירה", en: "Create" },
    "data-table.loading-state.title": { he: "טוען...", en: "Loading..." },
    "data-table.no-results.title": {
        he: "לא נמצאו תוצאות.",
        en: "No results found.",
    },
    "delete-chatbot-modal.cancel-btn": { he: "ביטול", en: "Cancel" },
    "delete-chatbot-modal.header.description": {
        he: "אתם בטוחים שתרצו למחוק את הצאטבוט: {chatbotName}? פעולה זו לא ניתנת לשחזר.",
        en: "Are you sure you want to delete the chatbot: {chatbotName}? This action cannot be undone.",
    },
    "export-modal.upload-progress.description.progress": {
        he: "תהליך הייצוא יכול לקחת עד 15 דקות.",
        en: "The export process can take up to 15 minutes.",
    },
    "floating-footer-menu.webcam-position.menu-label": {
        he: "מיקום מצלמה",
        en: "Camera position",
    },
    "floating-footer-menu.webcam-position.full": {
        he: "מסך מלא",
        en: "Fullscreen",
    },
    "floating-footer-menu.webcam-position.top-right": {
        he: "ימין למעלה",
        en: "Top right",
    },
    "floating-footer-menu.webcam-position.bottom-right": {
        he: "ימין למטה",
        en: "Bottom right",
    },
    "floating-footer-menu.webcam-position.bottom-left": {
        he: "שמאל למטה",
        en: "Bottom left",
    },
    "floating-footer-menu.webcam-position.top-left": {
        he: "שמאל למעלה",
        en: "Top left",
    },
    "floating-footer-menu.webcam-position.free": {
        he: "חופשי",
        en: "Free",
    },
    "floating-footer-menu.webcam-position.hidden": {
        he: "הסתרה",
        en: "Hidden",
    },
    "funnels.ai-presentation.step-2.slides-length.auto": {
        he: "אוטומטי (מומלץ)",
        en: "Automatic (recommended)",
    },
    "funnels.ai-upload-videos.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "funnels.create-presentation.step-5.title": {
        he: "המצגת שלך כמעט מוכנה",
        en: "Your presentation is almost ready",
    },
    "funnels.onboarding.name.label": { he: "שם מלא", en: "Full name" },
    "funnels.onboarding.persona.label": {
        he: "מה מתאר אותך הכי נכון",
        en: "What describes you best",
    },
    "funnels.onboarding.phone.label": { he: "טלפון", en: "Phone" },
    "funnels.onboarding.step-1.options.dont-know-option": {
        he: "אני לא יודע/ת עדיין, אני רק רוצה לראות את הפלטפורמה.",
        en: "I don't know yet, I just want to see the platform.",
    },
    "funnels.render-lomda.step-2.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "funnels.render-lomda.step-2.choose-folder.placeholder": {
        he: "לחצו כדי לבחור תיקייה",
        en: "Click to choose a folder",
    },
    "funnels.render-lomda.step-2.choose-folder.description": {
        he: "נווטו לתיקייה שבה תרצו לשמור את הסרטונים ולחצו אישור.",
        en: "Navigate to the folder where you want to save the videos and click Confirm.",
    },
    "funnels.render-lomda.step-2.description": {
        he: "בחרו תיקיית יעד והגדרות ליצירת הסרטון",
        en: "Choose a destination folder and settings for video creation",
    },
    "funnels.render-lomda.step-2.submit-btn": { he: "סיום", en: "Finish" },
    "funnels.render-lomda.step-2.title": {
        he: "הגדרות יצירת סרטון",
        en: "Video creation settings",
    },
    "funnels.render-lomda.step-2.choose-folder.label": {
        he: "בחירת תיקייה",
        en: "Choose folder",
    },
    "funnels.lomda-render.step-3.submit-btn": {
        he: "מעבר לאחסון",
        en: "Go to storage",
    },
    "funnels.lomda-render.step-3.title": {
        he: "מתבצעת יצירת וידאו",
        en: "Video generation in progress",
    },
    "funnels.lomda.render.step1": { he: "הגדרות", en: "Settings" },
    "funnels.lomda.render.step2": { he: "עיבוד", en: "Processing" },
    "funnels.lomda.render.step3": { he: "יצירה", en: "Creation" },
    "funnels.render-lomda.enhance-scripts.title": {
        he: "שיפור טקסטים לקריינות עם AI (מומלץ)",
        en: "Enhance scripts for voice-over with AI (recommended)",
    },
    "funnels.render-lomda.step-2.voice-tone.label": {
        he: "טון דיבור",
        en: "Voice tone",
    },
    "funnels.render-lomda.step-2.voice-instructions.label": {
        he: "הנחיות לקריינות (אופציונלי)",
        en: "Voice-over instructions (optional)",
    },
    "funnels.render-lomda.step-2.voice-instructions.placeholder": {
        he: "למשל: דברו בצורה חמה ומזמינה, עם הפסקות קצרות לפני מושגים חשובים...",
        en: "e.g., Speak warmly and invitingly, with short pauses before key concepts...",
    },
    "funnels.lomda-v2.render.step1": {
        he: "הגדרות כלליות",
        en: "General settings",
    },
    "funnels.lomda-v2.render.step-chapters": {
        he: "פרקים",
        en: "Chapters",
    },
    "funnels.lomda-v2.render.step-chapters.title": {
        he: "פרקים",
        en: "Chapters",
    },
    "funnels.lomda-v2.render.step-chapters.description": {
        he: "חלקו את הלומדה לפרקים ותנו לכל אחד שם משמעותי.",
        en: "Split the lomda into chapters and give each one a meaningful name.",
    },
    "funnels.lomda-v2.render.step2": {
        he: "אפשרויות עיבוד",
        en: "Post processing options",
    },
    "funnels.lomda-v2.render.step3": {
        he: "התקדמות",
        en: "Progress",
    },
    "funnels.lomda-v2.render.step1.title": {
        he: "הגדרות כלליות",
        en: "General settings",
    },
    "funnels.lomda-v2.render.step1.description": {
        he: "הגדירו את שם הסרטון, התיקייה והגדרות הדיבוב (מודל, קול, הוראות וניסוי קול)",
        en: "Set video name, folder, and dubbing defaults (model, voice, instructions, voice playground)",
    },
    "funnels.lomda-v2.render.step1.video-name": {
        he: "שם הסרטון",
        en: "Video name",
    },
    "funnels.lomda-v2.render.step1.video-name-placeholder": {
        he: "הזינו שם לסרטון",
        en: "Enter video name",
    },
    "funnels.lomda-v2.render.step1.video-folder": {
        he: "תיקיית הסרטון",
        en: "Video folder",
    },
    "funnels.lomda-v2.render.step1.narrator-instructions": {
        he: "הנחיות לקריין",
        en: "Narrator instructions",
    },
    "funnels.lomda-v2.render.step1.voice": {
        he: "קול הקריין",
        en: "Narrator voice",
    },
    "lomda.voice.female": {
        he: "קול נשי",
        en: "Female voice",
    },
    "lomda.voice.male": {
        he: "קול גברי",
        en: "Male voice",
    },
    "funnels.lomda-v2.render.step1.narrator-instructions-placeholder": {
        he: "למשל: דברו בחום ובסבלנות, האטו לפני הגדרות חשובות והדגישו מונחים מקצועיים...",
        en: "e.g., Speak warmly and patiently, slow down before key definitions, and emphasize technical terms...",
    },
    "funnels.lomda-v2.render.step1.regen-strategy.title": {
        he: "מה לעשות עם הקריינות הקיימת?",
        en: "What about the existing voiceovers?",
    },
    "funnels.lomda-v2.render.step1.narrator-instructions.save-default": {
        he: "שמור כברירת מחדל ללומדה",
        en: "Save as lomda default",
    },
    "lomda.narrator-instructions.words": {
        he: "מילים",
        en: "words",
    },
    "lomda.narrator-instructions.limit-reached": {
        he: "הגעתם למגבלת המילים — מחקו כדי להוסיף עוד.",
        en: "Word limit reached — delete some to add more.",
    },
    "funnels.lomda-v2.render.step1.regen-strategy.option.all.title": {
        he: "יצירה מחדש של כל הקריינות עם ההנחיות החדשות",
        en: "Regenerate all voiceovers with the new instructions",
    },
    "funnels.lomda-v2.render.step1.regen-strategy.option.none.title": {
        he: "יצירת קריינות עם ההנחיות החדשות רק לסצנות בלי קריינות",
        en: "Keep existing voiceovers",
    },
    "funnels.lomda-v2.render.step2.title": {
        he: "אפשרויות עיבוד וידאו",
        en: "Video post processing options",
    },
    "funnels.lomda-v2.render.step2.description": {
        he: "בחרו כתוביות, פרקים, אינטראקציות, תרגומים ודיבובים — כמו בלומדה הראשונה",
        en: "Choose captions, chapters, interactions, translations, and dubbing — same options as Lomda v1",
    },
    "funnels.lomda-v2.render.step2.submit-btn": {
        he: "רינדור",
        en: "Render",
    },
    "funnels.lomda-v2.render.step3.title": {
        he: "יוצרים את הלומדה שלך",
        en: "Creating your lomda",
    },
    "funnels.lomda-v2.render.step3.description": {
        he: "התהליך עשוי להימשך מספר דקות. ניתן לעזוב את הדף, העבודה תימשך ברקע.",
        en: "This may take a few minutes. Feel free to leave this page, we'll keep working in the background.",
    },
    "funnels.lomda-v2.render.step3.no-doc-hint": {
        he: "חזרו לשלב הקודם והתחילו שוב את יצירת הסרטון.",
        en: "Go back to the previous step and start video generation again.",
    },
    "funnels.stepper.progress": {
        he: "שלב {currentStep} מתוך {totalSteps}",
        en: "Step {currentStep} of {totalSteps}",
    },
    "funnels.dev.prev": { he: "הקודם", en: "Previous" },
    "funnels.dev.next": { he: "הבא", en: "Next" },
    "funnels.generic-footer.prev-btn": { he: "הקודם", en: "Previous" },
    "funnels.generic-footer.next-btn": { he: "הבא", en: "Next" },
    "funnels.generic-footer.skip-btn": { he: "דילוג", en: "Skip" },
    "funnels.onboarding.step-1.title": {
        he: "איך נוכל לעזור לך?",
        en: "How can we help you?",
    },
    "funnels.onboarding.step-1.description": {
        he: "בחר/י את האפשרות המתאימה ביותר ולחצ/י המשך",
        en: "Choose the option that fits you best and click Next",
    },
    "funnels.onboarding.step-1.options.course.title": {
        he: "קורס דיגיטלי",
        en: "Digital course",
    },
    "funnels.onboarding.step-1.options.course.description": {
        he: "אני רוצה ליצור קורס דיגיטלי איכותי ולשתף את הידע שלי עם העולם.",
        en: "I want to create a high-quality digital course and share my knowledge with the world.",
    },
    "funnels.onboarding.step-1.options.presentations.title": {
        he: "מצגת",
        en: "Presentation",
    },
    "funnels.onboarding.step-1.options.presentations.description": {
        he: "אני רוצה לעצב מצגת מרשימה עם אנימציות שמדברות לקהל שלי.",
        en: "I want to design an impressive presentation with animations that speak to my audience.",
    },
    "funnels.onboarding.step-1.options.interactive-videos.title": {
        he: "סרטון אינטראקטיבי",
        en: "Interactive video",
    },
    "funnels.onboarding.step-1.options.interactive-videos.description": {
        he: "אני רוצה להפוך את הסרטונים שלי לאינטראקטיביים.",
        en: "I want to make my videos interactive.",
    },
    "funnels.onboarding.step-1.options.dont-know.title": {
        he: "אחר",
        en: "Other",
    },
    "funnels.onboarding.step-1.options.dont-know.description": {
        he: "אני לא יודע/ת עדיין, אני רק רוצה לראות את הפלטפורמה.",
        en: "I don't know yet, I just want to see the platform.",
    },
    "funnels.onboarding.step-2.title": {
        he: "פרטים אישיים",
        en: "Personal details",
    },
    "funnels.onboarding.step-2.description": {
        he: "ספר/י לנו קצת על עצמך",
        en: "Tell us a bit about yourself",
    },
    "funnels.onboarding.persona.placeholder": {
        he: "בחר/י אפשרות",
        en: "Choose an option",
    },
    "funnels.onboarding.step-3.loading": {
        he: "כבר מתחילים",
        en: "Getting started",
    },
    "funnels.onboarding.step1-user-goal": {
        he: "איך נוכל לעזור",
        en: "How can we help",
    },
    "funnels.onboarding.step2-user-details": {
        he: "פרטים אישיים",
        en: "Personal details",
    },
    "funnels.onboarding.step3-loading": {
        he: "מתחילים",
        en: "Starting",
    },
    "onboarding.personas.teacher": { he: "מורה", en: "Teacher" },
    "onboarding.personas.lecturer": { he: "מרצה", en: "Lecturer" },
    "onboarding.personas.instructor": { he: "מדריך", en: "Instructor" },
    "onboarding.personas.coach": { he: "מאמן", en: "Coach" },
    "onboarding.personas.mentor": { he: "מנטור", en: "Mentor" },
    "onboarding.personas.expert": { he: "מומחה", en: "Expert" },
    "onboarding.personas.doctor": { he: "דוקטור", en: "Doctor" },
    "onboarding.personas.professor": { he: "פרופסור", en: "Professor" },
    "onboarding.personas.influencer": { he: "משפיען רשת", en: "Influencer" },
    "onboarding.personas.professional": { he: "בעל מקצוע", en: "Professional" },
    "onboarding.personas.other": { he: "אחר", en: "Other" },
    "funnels.presentation.step1": { he: "נושא", en: "Topic" },
    "funnels.presentation.step2": { he: "ייצור", en: "Generation" },
    "funnels.create-presentation.step-1.title": {
        he: "איך לקרוא למצגת?",
        en: "What should the presentation be called?",
    },
    "funnels.create-presentation.create-with-ai.link": {
        he: "יצירה עם AI",
        en: "Create with AI",
    },
    "funnels.syllabus.step1": { he: "נושא", en: "Topic" },
    "funnels.syllabus.step2": { he: "ייצור", en: "Generation" },
    "funnels.create-syllabus.step-1.title": {
        he: "מה שם הקורס שלך?",
        en: "What is your course name?",
    },
    "funnels.create-syllabus.create-with-ai.link": {
        he: "יצירה עם AI",
        en: "Create with AI",
    },
    "funnels.lomda.step1": { he: "מיתוג", en: "Branding" },
    "funnels.lomda.step2": { he: "אחסון", en: "Storage" },
    "funnels.lomda.step3": { he: "יצירה", en: "Creation" },
    "funnels.create-lomda.step-1.title": {
        he: "הגדרות מיתוג הלומדה",
        en: "Lomda branding settings",
    },
    "funnels.create-lomda.step-1.loading": { he: "טוען...", en: "Loading..." },
    "funnels.create-lomda.step-1.colors.primary-label": {
        he: "צבע ראשי",
        en: "Primary color",
    },
    "funnels.create-lomda.step-1.colors.secondary-label": {
        he: "צבע משני",
        en: "Secondary color",
    },
    "funnels.create-lomda.step-1.colors.description": {
        he: "נא לבחור צמד צבעים לשימוש בתבניות הלומדה. (טיפ: צבעי המיתוג שלכם)",
        en: "Choose a pair of colors to use in the lomda templates. (Tip: your brand colors)",
    },
    "funnels.create-lomda.step-1.logo.label": { he: "לוגו", en: "Logo" },
    "funnels.create-lomda.step-1.logo.description": {
        he: "אפשר להעלות לוגו כדי שיהיה מוצג בתבניות הלומדה.",
        en: "You can upload a logo to display in the lomda templates.",
    },
    "funnels.create-lomda.step-1.upload-logo.subtitle": {
        he: "קבצים נתמכים: PNG, SVG, JPG, WEBP",
        en: "Supported files: PNG, SVG, JPG, WEBP",
    },
    "funnels.create-lomda.step-1.template.label": {
        he: "תבנית סצנה",
        en: "Scene template",
    },
    "funnels.create-lomda.step-1.template.placeholder": {
        he: "בחרו תבנית",
        en: "Choose a template",
    },
    "funnels.create-lomda.step-1.template.description": {
        he: "תבנית המגדירה את מראה הסצנה",
        en: "Template that defines the scene layout",
    },
    "funnels.create-lomda.step-2.title": {
        he: "הגדרות אחסון",
        en: "Storage settings",
    },
    "funnels.create-lomda.step-2.loading": { he: "טוען...", en: "Loading..." },
    "funnels.create-lomda.step-2.name.label": {
        he: "שם הלומדה",
        en: "Lomda name",
    },
    "funnels.create-lomda.step-2.name.placeholder": {
        he: "שם פריט הלומדה",
        en: "Lomda item name",
    },
    "funnels.create-lomda.step-2.name.description": {
        he: "פריט הלומדה יופיע תחת השם הזה באזור האחסון.",
        en: "The lomda item will appear under this name in storage.",
    },
    "funnels.create-lomda.step-2.folder.label": { he: "תיקייה", en: "Folder" },
    "funnels.create-lomda.step-2.folder.description": {
        he: "תיקייה בה תוכלו למצוא את הלומדה החדשה באזור האחסון",
        en: "Folder where you'll find the new lomda in storage",
    },
    "funnels.create-lomda.step-2.folder.placeholder": {
        he: "לחצו כדי לבחור תיקייה",
        en: "Click to choose a folder",
    },
    "funnels.create-lomda.step-2.folder.modal.description": {
        he: "נווטו לתיקייה שבה תרצו לשמור ולחצו אישור.",
        en: "Navigate to the folder you want to save in and click Confirm.",
    },
    "funnels.create-lomda.step-2.folder.modal.confirm": {
        he: "אישור",
        en: "Confirm",
    },
    "funnels.create-lomda.step-3.title": {
        he: "הלומדה שלך כמעט מוכנה",
        en: "Your lomda is almost ready",
    },
    "funnels.create-lomda.step-3.generating": {
        he: "תהליך הייצור יכול לקחת כמה דקות, תלוי בגודל הקובץ ובכמות הסצנות בלומדה.",
        en: "Generation can take a few minutes, depending on file size and number of scenes.",
    },
    "funnels.create-lomda.error.retry": {
        he: "אנא נסו שנית לאחר בדיקת תקינות המידע שהזנתם.",
        en: "Please try again after checking the information you entered.",
    },
    "funnels.ai-lomda.step1": { he: "קובץ", en: "File" },
    "funnels.ai-lomda.step2": { he: "מיתוג", en: "Branding" },
    "funnels.ai-lomda.step3": { he: "תסריט", en: "Script" },
    "funnels.ai-lomda.step4": { he: "אחסון", en: "Storage" },
    "funnels.ai-lomda.step5": { he: "יצירה", en: "Creation" },
    "funnels.ai-create-lomda-v2.step1": { he: "נושא", en: "Topic" },
    "funnels.ai-create-lomda-v2.step2": { he: "חומרים", en: "Materials" },
    "funnels.ai-create-lomda-v2.step3": { he: "שיטה", en: "Method" },
    "funnels.ai-create-lomda-v2.step4": { he: "עוד פרטים", en: "Details" },
    "funnels.ai-create-lomda-v2.step5": { he: "עיצוב", en: "Style" },
    "funnels.ai-create-lomda-v2.step6": { he: "יצירה", en: "Generating" },
    "funnels.ai-create-lomda-v2.step7": {
        he: "אינטראקציות",
        en: "Interactions",
    },

    // ── V3 AI-creation funnel (HTML lomda) ──
    "funnels.ai-create-lomda-v3.brandkit": { he: "עיצוב", en: "Style" },
    "funnels.ai-create-lomda-v3.topic": { he: "נושא", en: "Topic" },
    "funnels.ai-create-lomda-v3.sources": { he: "חומרים", en: "Materials" },
    "funnels.ai-create-lomda-v3.method": { he: "שיטה", en: "Method" },
    "funnels.ai-create-lomda-v3.generating": { he: "יצירה", en: "Generating" },
    "funnels.ai-create-lomda-v3.brandkit.title": {
        he: "בחרו ערכת מותג",
        en: "Choose a brand kit",
    },
    "funnels.ai-create-lomda-v3.brandkit.description": {
        he: "הצבעים והפונטים שלה יעצבו את הלומדה שתיווצר",
        en: "Its colors and fonts style your generated lomda.",
    },
    "funnels.ai-create-lomda-v3.topic.title": {
        he: "על מה הלומדה?",
        en: "What's your lomda about?",
    },
    "funnels.ai-create-lomda-v3.topic.description": {
        he: "ספרו לנו על מה הלומדה ובאיזו שפה",
        en: "Tell us what the lomda is about and in which language.",
    },
    "funnels.ai-create-lomda-v3.topic.label": {
        he: "נושא הלומדה",
        en: "Training topic",
    },
    "funnels.ai-create-lomda-v3.topic.placeholder": {
        he: "למשל: פרוטוקול בטיחות בעבודה לעובדים חדשים",
        en: "e.g., Workplace safety protocol for new employees",
    },
    "funnels.ai-create-lomda-v3.topic.helper": {
        he: "נושא ברור עוזר ל-AI ליצור תוכן ממוקד ורלוונטי",
        en: "A clear topic helps the AI create focused, relevant content",
    },
    "funnels.ai-create-lomda-v3.topic.language": {
        he: "שפה",
        en: "Language",
    },
    "funnels.ai-create-lomda-v3.method.title": {
        he: "איזו שיטה תרצו?",
        en: "Which method would you like?",
    },
    "funnels.ai-create-lomda-v3.method.description": {
        he: "השיטה שתבחרו תשפיע על אורך הלומדה והעומק שלה",
        en: "The method you choose affects the lomda's length and depth.",
    },
    "funnels.ai-create-lomda-v3.generating.title": {
        he: "יוצרים את הלומדה שלכם…",
        en: "Crafting your lomda…",
    },
    "funnels.ai-create-lomda-v3.generating.description": {
        he: "ה-AI עובד בשבילכם — זה עשוי לקחת מספר דקות.",
        en: "AI at work — this may take a few minutes.",
    },
    "funnels.ai-create-lomda-v3.generating.failed-files": {
        he: "{file}: יצוא תוכן נכשל או שהתוכן לא רלוונטי",
        en: "{file}: content extraction failed or content is not relevant",
    },
    "funnels.ai-create-lomda-v2.stepAvatar": { he: "אווטאר", en: "Avatar" },
    "funnels.ai-create-lomda-v2.stepAvatar.title": {
        he: "הוספת אווטאר",
        en: "Add an avatar",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.description": {
        he: "בחרו אווטאר AI שיופיע בלומדה, או המשיכו ללא אווטאר.",
        en: "Pick an AI avatar to appear in your lomda, or continue without one.",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.selected": {
        he: "האווטאר הנבחר:",
        en: "Selected avatar:",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.no-looks": {
        he: "לאווטאר זה אין מראות זמינים",
        en: "This avatar has no available looks",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.remove": {
        he: "הסרה",
        en: "Remove",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.create": {
        he: "צרו אווטאר",
        en: "Create avatar",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.tabs.mine": {
        he: "שלי",
        en: "My avatars",
    },
    "funnels.ai-create-lomda-v2.stepAvatar.tabs.library": {
        he: "ספרייה",
        en: "Library",
    },
    "funnels.ai-create-lomda-v2.step1.title": {
        he: "על מה הלומדה?",
        en: "What's your lomda about?",
    },
    "funnels.ai-create-lomda-v2.step1.description": {
        he: "ספרו לנו על מה הלומדה ובאיזו שפה",
        en: "Tell us what the lomda is about and in which language.",
    },
    "funnels.ai-create-lomda-v2.step1.topic.label": {
        he: "נושא הלומדה",
        en: "Training topic",
    },
    "funnels.ai-create-lomda-v2.step1.topic.placeholder": {
        he: "למשל: פרוטוקול בטיחות בעבודה לעובדים חדשים",
        en: "e.g., Workplace safety protocol for new employees",
    },
    "funnels.ai-create-lomda-v2.step1.topic.helper": {
        he: "נושא ברור עוזר ל-AI ליצור תוכן ממוקד ורלוונטי",
        en: "A clear topic helps the AI create focused, relevant content",
    },
    "funnels.ai-create-lomda-v2.step2.materials.label": {
        he: "חומרים",
        en: "Materials",
    },
    "funnels.ai-create-lomda-v2.step2.tab.files": {
        he: "קבצים",
        en: "Files",
    },
    "funnels.ai-create-lomda-v2.step2.tab.videos": {
        he: "סרטונים",
        en: "Videos",
    },
    "funnels.ai-create-lomda-v2.step2.tab.prompt": {
        he: "טקסט חופשי",
        en: "Free text",
    },
    "funnels.ai-create-lomda-v2.step2.documents.label": {
        he: "העלאת חומרי עזר",
        en: "Upload reference materials",
    },
    "funnels.ai-create-lomda-v2.step2.documents.helper": {
        he: "העלו מסמכי הדרכה, מדריכים או מצגות. ה-AI ישתמש בהם כחומר מקור.",
        en: "Upload training documents, manuals, or slides. The AI will use them as source material.",
    },
    "funnels.ai-create-lomda-v2.step2.videos.label": {
        he: "בחירת סרטונים",
        en: "Select videos",
    },
    "funnels.ai-create-lomda-v2.step2.videos.helper": {
        he: "בחרו סרטונים עם כתוביות מוכנות. ה-AI ישתמש בתוכן הכתוביות.",
        en: "Select videos with completed captions. The AI will use the caption content.",
    },
    "funnels.ai-create-lomda-v2.step2.outline.expand": {
        he: "הוספת מתווה מפורט",
        en: "Add detailed outline",
    },
    "funnels.ai-create-lomda-v2.step2.outline.placeholder": {
        he: "הדביקו מבנה שיעור מפורט או נקודות מפתח לכיסוי...",
        en: "Paste a detailed lesson structure or key points to cover...",
    },
    "funnels.ai-create-lomda-v2.step2.outline.helper": {
        he: "אופציונלי: ספקו מבנה ספציפי אם יש לכם. אחרת ה-AI יבנה את מבנה השיעור מהנושא והחומרים.",
        en: "Optional: provide a specific structure if you have one. Otherwise the AI will structure the lesson from your topic and materials.",
    },
    "funnels.ai-create-lomda-v2.step2.title": {
        he: "על מה נתבסס?",
        en: "What should we work with?",
    },
    "funnels.ai-create-lomda-v2.step2.description": {
        he: "מסמכים, סרטונים, יוטיוב, כל מה שרלוונטי לנושא",
        en: "Documents, videos, YouTube, anything relevant to the topic.",
    },
    "funnels.ai-create-lomda-v2.step3.title": {
        he: "איזו שיטה תרצו?",
        en: "Which method would you like?",
    },
    "funnels.ai-create-lomda-v2.step3.description": {
        he: "השיטה שתבחרו תשפיע על אורך הלומדה והעומק שלה",
        en: "The method you choose affects the lomda's length and depth.",
    },
    "funnels.ai-create-lomda-v2.step4.title": {
        he: "בואו נדייק את הלומדה",
        en: "Let's fine-tune the lomda",
    },
    "funnels.ai-create-lomda-v2.step4.description": {
        he: "טון דיבור, קהל יעד ומטרות למידה — הכל אופציונלי.",
        en: "Tone, target audience, and learning objectives — all optional.",
    },
    "funnels.ai-create-lomda-v2.step4.audience.label": {
        he: "קהל יעד",
        en: "Target audience",
    },
    "funnels.ai-create-lomda-v2.step4.audience.placeholder": {
        he: "למשל: חברי צוות מכירות חדשים",
        en: "e.g., New sales team members",
    },
    "funnels.ai-create-lomda-v2.step4.objectives.label": {
        he: "מטרות למידה",
        en: "Learning objectives",
    },
    "funnels.ai-create-lomda-v2.step4.objectives.placeholder": {
        he: "מטרת למידה {index}",
        en: "Objective {index}",
    },
    "funnels.ai-create-lomda-v2.step4.objectives.add": {
        he: "הוספת מטרה",
        en: "Add objective",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.label": {
        he: "אופן יצירת הלומדה",
        en: "How to build the lomda",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.follow_source.title": {
        he: "צמוד למקור",
        en: "Stick to the source",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.follow_source.description": {
        he: "בחרו בשיטה זו אם אתם רוצים שהלומדה תלמד את התוכן בדיוק כפי שהוא מופיע בחומרים",
        en: "Choose this if you want the lomda to teach the content exactly as it appears in the materials, in the original order.",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.focus_objectives.title": {
        he: "חדור מטרה",
        en: "Mission focused",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.focus_objectives.description": {
        he: "בחרו בשיטה זו אם אתם רוצים לומדה ממוקדת בנושא שהגדרתם, בלי להיכנס לכל פרט בחומרים",
        en: "Choose this if you want a lomda focused on the topic you defined, without covering every detail in the sources.",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.summary.title": {
        he: "סקירה מהירה",
        en: "Quick overview",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.summary.description": {
        he: "בחרו בשיטה זו אם אתם רוצים לומדה שמתמקדת בעיקרי הדברים בלבד, בלי להיכנס לעומק",
        en: "Choose this if you want a lomda that focuses on the main points only, without going in depth.",
    },
    "funnels.ai-create-lomda-v2.step3.coverage.estimatedDuration": {
        he: "משך משוער: כ-{minutes} דקות",
        en: "Estimated duration: ~{minutes} minutes",
    },
    "funnels.ai-create-lomda-v2.step3.followSource.label": {
        he: "על איזה מקור לבסס את הלומדה?",
        en: "Which source should drive the lomda?",
    },
    "funnels.ai-create-lomda-v2.step3.followSource.placeholder": {
        he: "בחרו מקור",
        en: "Pick a source",
    },
    "funnels.ai-create-lomda-v2.step3.followSource.noSources": {
        he: "לא הועלו חומרים בשלב הקודם",
        en: "No sources were added in the previous step",
    },
    "funnels.ai-create-lomda-v2.step3.followSource.helper": {
        he: "המקור שנבחר יקבע את מספר הסצנות ואת סדר הלימוד. שאר החומרים ישמשו כחומרי עזר.",
        en: "The selected source will drive scene count and order. Other sources will only serve as reference.",
    },
    "funnels.ai-create-lomda-v2.step3.usePdfPagesAsBackground.label": {
        he: "השתמשו בעמודי ה-PDF כרקע לסצנות",
        en: "Use the PDF pages as scene backgrounds",
    },
    "funnels.ai-create-lomda-v2.step3.usePdfPagesAsBackground.helper": {
        he: "כל סצנה תוצג על רקע העמוד המקורי מתוך ה-PDF. מתאים ללימוד עם נאמנות גבוהה למקור.",
        en: "Each scene will be shown over its matching PDF page as the background. Best for high-fidelity teach-from-source lomdas.",
    },
    "funnels.ai-create-lomda-v2.step3.usePdfPagesAsBackground.aspectRatioNote":
        {
            he: "מומלץ רק עבור מסמכי PDF עד 40 עמודים וביחס 16:9 (מצגות סטנדרטיות).",
            en: "Recommended only for PDFs under 40 pages in 16:9 widescreen format (standard presentation slides).",
        },
    "funnels.ai-create-lomda-v2.step4.tone.label": {
        he: "טון דיבור",
        en: "Tone of voice",
    },
    "funnels.ai-create-lomda-v2.step4.tone.placeholder": {
        he: "בחרו טון",
        en: "Select tone",
    },
    "funnels.ai-create-lomda-v2.step4.avatar.label": {
        he: "אווטאר AI",
        en: "AI Avatar",
    },
    "funnels.ai-create-lomda-v2.step4.avatar.description": {
        he: "פרזנטור דיגיטלי שיציג את הלומדה",
        en: "Include a presenter avatar in the video — coming soon",
    },
    "funnels.ai-create-lomda-v2.step1.language.label": {
        he: "שפה",
        en: "Language",
    },
    "funnels.ai-create-lomda-v2.step1.narrator-gender.label": {
        he: "קול",
        en: "Voice",
    },
    "funnels.ai-create-lomda-v2.step1.narrator-gender.female": {
        he: "נשי",
        en: "Female",
    },
    "funnels.ai-create-lomda-v2.step1.narrator-gender.male": {
        he: "גברי",
        en: "Male",
    },
    "funnels.ai-create-lomda-v2.step5.title": {
        he: "איך זה ייראה?",
        en: "How should it look?",
    },
    "funnels.ai-create-lomda-v2.step5.description": {
        he: "בחרו רקעים וצבעים שמתאימים למותג שלכם. תמיד אפשר לשנות בהמשך באדיטור",
        en: "Pick backgrounds and colors that fit your brand — you can tweak them later in the editor.",
    },
    "funnels.ai-create-lomda-v2.step5.tab.templates": {
        he: "טמפלייטים",
        en: "Templates",
    },
    "funnels.ai-create-lomda-v2.step5.custom": {
        he: "מותאם אישית",
        en: "Custom",
    },
    "funnels.ai-create-lomda-v2.step5.background.label": {
        he: "רקע",
        en: "Background",
    },
    "funnels.ai-create-lomda-v2.step5.background.solid": {
        he: "צבע אחיד",
        en: "Solid color",
    },
    "funnels.ai-create-lomda-v2.step5.background.gradient": {
        he: "גרדיאנט",
        en: "Gradient",
    },
    "funnels.ai-create-lomda-v2.step5.background.image": {
        he: "תמונה",
        en: "Image",
    },
    "funnels.ai-create-lomda-v2.step5.background.color": {
        he: "צבע רקע",
        en: "Background color",
    },
    "funnels.ai-create-lomda-v2.step5.background.gradient-stops": {
        he: "צבעי המעבר",
        en: "Gradient colors",
    },
    "funnels.ai-create-lomda-v2.step5.background.from": {
        he: "מ",
        en: "From",
    },
    "funnels.ai-create-lomda-v2.step5.background.to": {
        he: "עד",
        en: "To",
    },
    "funnels.ai-create-lomda-v2.step5.background.image-section": {
        he: "תמונת רקע",
        en: "Background image",
    },
    "funnels.ai-create-lomda-v2.step5.background.image-upload-hint": {
        he: "העלאת תמונה",
        en: "Upload image",
    },
    "funnels.ai-create-lomda-v2.step5.background.image-upload-formats": {
        he: "PNG, JPG, WEBP ועוד",
        en: "PNG, JPG, WEBP, and more",
    },
    "funnels.ai-create-lomda-v2.step5.text-color": {
        he: "צבע טקסט",
        en: "Text color",
    },
    "funnels.ai-create-lomda-v2.step6.title": {
        he: "יוצרים את הלומדה שלכם…",
        en: "Crafting your lomda…",
    },
    "funnels.ai-create-lomda-v2.step6.description": {
        he: "ה-AI עובד בשבילכם — זה עשוי לקחת מספר דקות.",
        en: "AI at work — this may take a few minutes.",
    },
    "funnels.ai-create-lomda-v2.step6.phase.init.title": {
        he: "מתחילים…",
        en: "Getting started…",
    },
    "funnels.ai-create-lomda-v2.step6.phase.init.subtitle": {
        he: "מכינים את סביבת העבודה",
        en: "Warming up the studio",
    },
    "funnels.ai-create-lomda-v2.step6.phase.ingest.title": {
        he: "קוראים את החומרים שלכם",
        en: "Reading your materials",
    },
    "funnels.ai-create-lomda-v2.step6.phase.ingest.subtitle": {
        he: "סופגים כל פרט מהמקורות שהעלאתם",
        en: "Absorbing every detail from your sources",
    },
    "funnels.ai-create-lomda-v2.step6.phase.outline.title": {
        he: "מתכננים את השיעור",
        en: "Designing the lesson",
    },
    "funnels.ai-create-lomda-v2.step6.phase.outline.subtitle": {
        he: "בונים קשת פדגוגית שמובילה את הלומד שלב-שלב",
        en: "Shaping a pedagogical arc that carries the learner through",
    },
    "funnels.ai-create-lomda-v2.step6.phase.scenes.title": {
        he: "מביאים את זה לחיים",
        en: "Bringing it to life",
    },
    "funnels.ai-create-lomda-v2.step6.phase.scenes.subtitle": {
        he: "כותבים תסריטים ויוצרים חזותיים לכל סצנה",
        en: "Writing scripts and creating visuals for every scene",
    },
    "funnels.ai-create-lomda-v2.step6.phase.finalize.title": {
        he: "ליטוש אחרון",
        en: "Final polish",
    },
    "funnels.ai-create-lomda-v2.step6.phase.finalize.subtitle": {
        he: "מחברים את כל החלקים לחוויה חלקה",
        en: "Stitching everything into a seamless experience",
    },
    "funnels.ai-create-lomda-v2.step6.name.label": {
        he: "שם ההדרכה",
        en: "Training name",
    },
    "funnels.ai-create-lomda-v2.step6.folder.label": {
        he: "שמירה בתיקייה",
        en: "Save to folder",
    },
    "funnels.ai-create-lomda-v2.step6.folder.none": {
        he: "(ללא תיקייה)",
        en: "(No folder)",
    },
    "funnels.ai-create-lomda-v2.step6.folder.choose": {
        he: "בחרו",
        en: "Choose",
    },
    "funnels.ai-create-lomda-v2.step6.section.content": {
        he: "תוכן",
        en: "Content",
    },
    "funnels.ai-create-lomda-v2.step6.section.settings": {
        he: "הגדרות",
        en: "Settings",
    },
    "funnels.ai-create-lomda-v2.step6.section.style": {
        he: "סגנון",
        en: "Style",
    },
    "funnels.ai-create-lomda-v2.step6.generate": {
        he: "יצירה",
        en: "Generate",
    },
    "funnels.ai-create-lomda-v2.step6.docs-count": {
        he: "{count} מסמכים",
        en: "{count} document(s)",
    },
    "funnels.ai-create-lomda-v2.step6.videos-count": {
        he: "{count} סרטונים",
        en: "{count} video(s)",
    },
    "funnels.ai-create-lomda-v2.step6.outline-provided": {
        he: "מתווה מפורט: סופק",
        en: "Detailed outline: provided",
    },
    "funnels.ai-create-lomda-v2.step6.outline-not-provided": {
        he: "מתווה מפורט: לא סופק",
        en: "Detailed outline: not provided",
    },
    "funnels.ai-create-lomda-v2.step6.failed-files": {
        he: "{file}: יצוא תוכן נכשל או שהתוכן לא רלוונטי",
        en: "{file}: content extraction failed or content is not relevant",
    },
    "funnels.ai-create-lomda-v2.step6.live.init": {
        he: "מדליקים את הסטודיו ומכוונים את המנוע ✦",
        en: "Igniting the studio and tuning the engine ✦",
    },
    "funnels.ai-create-lomda-v2.step6.live.ingest": {
        he: "מקשיבים לכל מילה מהחומרים שלכם ✦",
        en: "Listening to every word in your sources ✦",
    },
    "funnels.ai-create-lomda-v2.step6.live.outline": {
        he: "בונים מסע למידה שבאמת תופס ✦",
        en: "Architecting a learning journey that lands ✦",
    },
    "funnels.ai-create-lomda-v2.step6.live.scenes": {
        he: "מציירים כל סצנה ביד ובלב ✦",
        en: "Painting every scene by hand ✦",
    },
    "funnels.ai-create-lomda-v2.step6.live.finalize": {
        he: "מוסיפים ליטוש סופי לכל פיקסל ✦",
        en: "Adding the final polish to every pixel ✦",
    },
    "funnels.ai-create-lomda-v2.step2.videos.select": {
        he: "בחרו סרטונים",
        en: "Select Videos",
    },
    "funnels.ai-create-lomda-v2.step2.videos.duration": {
        he: "משך",
        en: "Duration",
    },
    "funnels.ai-create-lomda-v2.step2.videos.remove": {
        he: "הסרת סרטון",
        en: "Remove video",
    },
    "funnels.ai-create-lomda-v2.step2.documents.upload-hint": {
        he: "העלאת קבצים",
        en: "Upload files",
    },
    "funnels.ai-create-lomda-v2.step2.documents.formats": {
        he: "PDF",
        en: "PDF",
    },
    "funnels.ai-create-lomda-v2.step2.documents.remove": {
        he: "הסר קובץ",
        en: "Remove file",
    },
    "funnels.ai-create-lomda-v2.step2.use-pdf-images": {
        he: "השתמש בתמונות מתוך ה-PDF",
        en: "Use images extracted from PDFs",
    },
    "funnels.ai-create-lomda-v2.step2.tab.youtube": {
        he: "יוטיוב",
        en: "YouTube",
    },
    "funnels.ai-create-lomda-v2.step2.videos.cta": {
        he: "בחרו סרטונים",
        en: "Select videos",
    },
    "funnels.ai-create-lomda-v2.step2.videos.cta-hint": {
        he: "ניתן לבחור רק סרטונים עם כתוביות",
        en: "Only videos with captions can be selected",
    },
    "funnels.ai-create-lomda-v2.step2.videos.check.loading": {
        he: "בודק כתוביות לסרטונים...",
        en: "Checking video captions...",
    },
    "funnels.ai-create-lomda-v2.step2.videos.check.success": {
        he: "כל הסרטונים מתומללים ומוכנים",
        en: "All videos have transcripts",
    },
    "funnels.ai-create-lomda-v2.step2.videos.check.failed": {
        he: "בדיקת הכתוביות נכשלה",
        en: "Caption check failed",
    },
    "funnels.ai-create-lomda-v2.step2.videos.check.removed": {
        he: "הוסרו סרטונים ללא כתוביות",
        en: "Removed videos without captions",
    },
    "funnels.ai-create-lomda-v2.step2.youtube.placeholder": {
        he: "https://www.youtube.com/watch?v=12345",
        en: "https://www.youtube.com/watch?v=12345",
    },
    "funnels.ai-create-lomda-v2.step2.youtube.add": {
        he: "הוסף",
        en: "Add",
    },
    "funnels.ai-create-lomda-v2.step2.youtube.already-added": {
        he: "הסרטון כבר נוסף",
        en: "Video already added",
    },
    "funnels.ai-create-lomda-v2.step2.youtube.invalid-url": {
        he: "קישור יוטיוב לא תקין",
        en: "Invalid YouTube URL",
    },
    "funnels.ai-create-lomda-v2.step2.youtube.no-captions": {
        he: "לסרטון אין כתוביות זמינות לתמלול",
        en: "This video has no transcript available",
    },
    "funnels.ai-create-lomda-v2.step2.instructions.label": {
        he: "הנחיות ל-AI",
        en: "Instructions for the AI",
    },
    "funnels.ai-create-lomda-v2.step2.instructions.hint": {
        he: "הקלידו @ כדי לתייג מקור מצורף ולהסביר ל-AI איך להשתמש בו",
        en: "Type @ to tag an attached source and tell the AI how to use it",
    },
    "funnels.ai-create-lomda-v2.step2.instructions.placeholder": {
        he: "לדוגמה: בנה לומדה שמתמקדת בפרקים 2-4 של @מסמך, ושלב דוגמאות מ-@וידאו.",
        en: "For example: build a lomda focused on chapters 2-4 of @document, and weave in examples from @video.",
    },
    "funnels.ai-create-lomda-v2.step2.instructions.no-sources": {
        he: "אין מקורות מצורפים",
        en: "No attached sources",
    },
    "funnels.ai-create-lomda.skip-ai.link": {
        he: "דילוג על AI",
        en: "Skip AI",
    },
    "funnels.ai-create-lomda-v2.skip-ai.link": {
        he: "דילוג על AI",
        en: "Skip AI",
    },
    "funnels.ai-create-lomda-v2.step7.title": {
        he: "אינטראקציות",
        en: "Interactions",
    },
    "funnels.ai-create-lomda-v2.step7.description": {
        he: "תרגול פעיל שומר על מעורבות הלומדים והופך צפייה ללמידה",
        en: "Active retrieval keeps learners engaged and turns viewing into learning. Choose how questions appear during your lomda.",
    },
    "funnels.ai-create-lomda-v2.step7.generate.label": {
        he: "יצירת אינטראקציות - מומלץ",
        en: "Generate interactions — recommended",
    },
    "funnels.ai-create-lomda-v2.step7.generate.description": {
        he: "ה-AI יציב שאלות במקומות המתאימים לאורך הלומדה כדי לחזק את ההבנה.",
        en: "The AI places questions at the right moments throughout the lomda to reinforce understanding.",
    },
    "funnels.ai-create-lomda-v2.step7.density.label": {
        he: "כמות אינטראקציות",
        en: "Number of interactions",
    },
    "funnels.ai-create-lomda-v2.step7.density.helper": {
        he: "כמה שאלות יופיעו לאורך הלומדה. ברירת המחדל היא הרבה — תרגול אקטיבי תכוף הוא מה שגורם לתוכן להיתפס.",
        en: "How many questions appear across the lomda. The default is many — frequent retrieval practice is what makes content stick.",
    },
    "funnels.ai-create-lomda-v2.step7.density.low": {
        he: "מעט",
        en: "Few",
    },
    "funnels.ai-create-lomda-v2.step7.density.balanced": {
        he: "מאוזן",
        en: "Balanced",
    },
    "funnels.ai-create-lomda-v2.step7.density.max": {
        he: "הרבה",
        en: "Many",
    },
    "funnels.ai-create-lomda-v2.step7.attemptLimit.label": {
        he: "מספר ניסיונות למענה באינטראקציה",
        en: "Number of attempts per interaction",
    },
    "funnels.ai-create-lomda-v2.step7.attemptLimit.helper": {
        he: "כמה פעמים הלומד יכול לנסות לפני שמוצגת התשובה הנכונה.",
        en: "How many times a learner can answer before the correct answer is revealed.",
    },
    "funnels.ai-create-lomda-v2.step7.allowSkipping.label": {
        he: "הצגת אפשרות דילוג על האינטראקציות",
        en: "Show option to skip interactions",
    },
    "funnels.ai-create-lomda-v2.step7.allowSkipping.description": {
        he: "הצגת כפתור דילוג בכל אינטראקציה.",
        en: "Show a skip button on every interaction.",
    },
    "funnels.ai-create-lomda-v2.step7.rewindOnWrong.label": {
        he: "החזרה לסצנה הרלוונטית לאחר מיצוי הניסיונות",
        en: "Rewind to the relevant scene after attempts run out",
    },
    "funnels.ai-create-lomda-v2.step7.rewindOnWrong.description": {
        he: "כשהלומד עונה לא נכון, הוא יוחזר לתחילת הסצנה שבה הוסבר הנושא — רלוונטי לשאלות אמריקאיות בלבד.",
        en: "On a wrong answer, send the learner back to the start of the scene that introduced the concept — close-questions only.",
    },
    "funnels.ai-create-lomda-v2.step7.instructions.label": {
        he: "הנחיות ל-AI",
        en: "Instructions for the AI",
    },
    "funnels.ai-create-lomda-v2.step7.instructions.placeholder": {
        he: "לדוגמה: התמקד בבדיקת ההבנה של שלבי התהליך ויישום הנהלים במצבים יומיומיים, ופחות בשינון מונחים והגדרות.",
        en: "For example: focus on verifying understanding of the process steps and applying the procedures in everyday situations, rather than memorizing terms and definitions.",
    },
    "funnels.ai-create-lomda-v2.step7.instructions.helper": {
        he: "הנחיות חופשיות ל-AI לגבי האופן שבו עליו לבנות את האינטראקציות. גוברות על ההגדרות של ברירת המחדל.",
        en: "Free-form guidance for the AI on how to design the interactions. Takes precedence over the defaults.",
    },
    "funnels.ai-create-lomda-v2.themes.professional-blue": {
        he: "כחול מקצועי",
        en: "Professional Blue",
    },
    "funnels.ai-create-lomda-v2.themes.warm-coral": {
        he: "אלמוג חם",
        en: "Warm Coral",
    },
    "funnels.ai-create-lomda-v2.themes.forest-green": {
        he: "ירוק יער",
        en: "Forest Green",
    },
    "funnels.ai-create-lomda-v2.themes.lavender": {
        he: "לבנדר",
        en: "Lavender",
    },
    "funnels.ai-create-lomda-v2.themes.sunset-pink": {
        he: "ורוד שקיעה",
        en: "Sunset Pink",
    },
    "funnels.ai-create-lomda-v2.themes.ocean-teal": {
        he: "כחול אוקיינוס",
        en: "Ocean Teal",
    },
    "funnels.ai-create-lomda-v2.themes.clean-white": {
        he: "לבן נקי",
        en: "Clean White",
    },
    "funnels.ai-create-lomda-v2.themes.dark-slate": {
        he: "אפור כהה",
        en: "Dark Slate",
    },
    "funnels.ai-create-lomda-v2.themes.soft-purple": {
        he: "סגול רך",
        en: "Soft Purple",
    },
    "funnels.ai-create-lomda-v2.themes.warm-beige": {
        he: "בז' חם",
        en: "Warm Beige",
    },
    "funnels.create-lomda-v2.create-with-ai.link": {
        he: "יצירה עם AI",
        en: "Create with AI",
    },
    "funnels.lomda-v3.step1": { he: "שם ותיקייה", en: "Name and folder" },
    "funnels.lomda-v3.step2": { he: "ייצור", en: "Creating" },
    "funnels.lomda-v3.step-1.title": {
        he: "יצירת לומדה",
        en: "Create a lomda",
    },
    "lomda-v3.editor.placeholder.description": {
        he: "הלומדה נוצרה. עורך הלומדות יהיה זמין כאן בקרוב.",
        en: "Your lomda was created. The editor will be available here soon.",
    },
    "lomda-v3.editor.placeholder.back-to-storage": {
        he: "חזרה לאחסון",
        en: "Back to storage",
    },
    "lomda-v3-editor.navbar.saving": { he: "שומר...", en: "Saving..." },
    "lomda-v3-editor.navbar.saved": { he: "נשמר", en: "Saved" },
    "lomda-v3-editor.navbar.save-error": {
        he: "השמירה נכשלה",
        en: "Save failed",
    },
    "lomda-v3-editor.navbar.edit": { he: "עריכה", en: "Edit" },
    "lomda-v3-editor.navbar.preview": {
        he: "תצוגה מקדימה",
        en: "Preview",
    },
    "lomda-v3-editor.navbar.publish": { he: "פרסום", en: "Publish" },
    "lomda-v3-editor.navbar.undo": { he: "ביטול", en: "Undo" },
    "lomda-v3-editor.navbar.redo": { he: "ביצוע חוזר", en: "Redo" },
    "lomda-v3-editor.navbar.lomda-name": { he: "שם הלומדה", en: "Lomda name" },
    "lomda-v3-editor.navbar.embed": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.sidebar.pages-desc": { he: "ניהול עמודי הלומדה", en: "Manage your lomda pages" },
    "lomda-v3-editor.sidebar.blocks-desc": { he: "הוספת תוכן לעמוד", en: "Add content to the page" },
    "lomda-v3-editor.sidebar.settings-desc": { he: "שפה, מיתוג ועוד", en: "Language, branding and more" },
    "lomda-v3-editor.pages.add": { he: "הוספת עמוד", en: "Add page" },
    "lomda-v3-editor.pages.new-page": { he: "עמוד חדש", en: "New page" },
    "lomda-v3-editor.pages.rename": { he: "שינוי שם", en: "Rename" },
    "lomda-v3-editor.pages.rename-placeholder": { he: "שם העמוד", en: "Page name" },
    "lomda-v3-editor.pages.page-label": {
        he: "עמוד {number}",
        en: "Page {number}",
    },
    "lomda-v3-editor.pages.delete": { he: "מחיקת עמוד", en: "Delete page" },

    "lomda-v3-editor.pages.background": { he: "רקע העמוד", en: "Page background" },
    "lomda-v3-editor.pages.background-default": { he: "ברירת המחדל של הלומדה", en: "Lomda default" },
    "lomda-v3-editor.pages.background-color": { he: "צבע", en: "Color" },
    "lomda-v3-editor.pages.background-opacity": { he: "אטימות", en: "Opacity" },
    "lomda-v3-editor.toolbar.add-block": {
        he: "הוספת בלוק",
        en: "Add block",
    },
    "lomda-v3-editor.sidebar.blocks": { he: "בלוקים", en: "Blocks" },
    "lomda-v3-editor.block-group.text": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.block-group.media": { he: "מדיה", en: "Media" },

    "lomda-v3-editor.block.text.label": { he: "בלוק טקסט", en: "Text Block" },
    "lomda-v3-editor.block.text.desc": {
        he: "הוספת תוכן טקסט ללומדה",
        en: "Add text content to your lesson",
    },
    "lomda-v3-editor.block.text.placeholder": {
        he: "כתבו את התוכן שלכם...",
        en: "Type your content...",
    },
    "lomda-v3-editor.block.text.link-prompt": {
        he: "הזינו כתובת קישור",
        en: "Enter a link URL",
    },
    "lomda-v3-editor.block.list.label": { he: "רשימה", en: "List" },
    "lomda-v3-editor.block.list.desc": {
        he: "הוספת רשימת פריטים",
        en: "Add a list of items",
    },
    "lomda-v3-editor.block.list.placeholder": {
        he: "פריט ברשימה",
        en: "List item",
    },
    "lomda-v3-editor.block.quote.label": { he: "ציטוט", en: "Quote" },
    "lomda-v3-editor.block.quote.desc": {
        he: "הוספת טקסט מצוטט",
        en: "Add a quoted text",
    },
    "lomda-v3-editor.block.quote.placeholder": {
        he: "הזינו ציטוט...",
        en: "Enter a quote...",
    },
    "lomda-v3-editor.block.image.label": { he: "תמונה", en: "Image" },
    "lomda-v3-editor.block.image.desc": { he: "הוספת תמונה", en: "Add an image" },
    "lomda-v3-editor.block.image.upload-title": {
        he: "העלאת תמונה",
        en: "Upload Image",
    },
    "lomda-v3-editor.block.image.upload-subtitle": {
        he: "גררו ושחררו או לחצו להעלאה",
        en: "Drag & drop or click to upload",
    },
    "lomda-v3-editor.block.image.row-width": { he: "רוחב", en: "Row width" },
    "lomda-v3-editor.block.image.fill-cover": { he: "מילוי", en: "Fill" },
    "lomda-v3-editor.block.image.fill-contain": { he: "התאמה", en: "Fit" },
    "lomda-v3-editor.block.image.replace": {
        he: "החלפת תמונה",
        en: "Replace image",
    },
    "lomda-v3-editor.block.video.label": { he: "וידאו", en: "Video" },
    "lomda-v3-editor.block.video.desc": { he: "הוספת וידאו", en: "Add a video" },
    "lomda-v3-editor.block.video.choose-title": {
        he: "בחירת וידאו מהאחסון",
        en: "Choose a video from storage",
    },
    "lomda-v3-editor.block.video.choose-subtitle": {
        he: "לחצו כדי לבחור מהספרייה שלכם",
        en: "Click to pick from your library",
    },
    "lomda-v3-editor.block.video.picker-description": {
        he: "בחרו וידאו מהאחסון להוספה ללומדה",
        en: "Pick a video from your storage to add to the lomda",
    },
    "lomda-v3-editor.block.video.picker-confirm": {
        he: "בחירת וידאו",
        en: "Choose video",
    },
    "lomda-v3-editor.block.video.replace": {
        he: "החלפת וידאו",
        en: "Replace video",
    },
    "lomda-v3-editor.block.video.unavailable": {
        he: "הווידאו אינו זמין",
        en: "Video unavailable",
    },
    "lomda-v3-editor.block.audio.label": { he: "אודיו", en: "Audio" },
    "lomda-v3-editor.block.audio.desc": { he: "הוספת אודיו", en: "Add audio" },
    "lomda-v3-editor.block.audio.upload-title": {
        he: "העלאת אודיו",
        en: "Upload Audio",
    },
    "lomda-v3-editor.block.audio.upload-subtitle": {
        he: "גררו ושחררו או לחצו להעלאה",
        en: "Drag & drop or click to upload",
    },
    "lomda-v3-editor.block.audio.replace": {
        he: "החלפת אודיו",
        en: "Replace audio",
    },
    "lomda-v3-editor.block.embed.label": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.block.embed.desc": {
        he: "הטמעת תוכן חיצוני",
        en: "Embed external content",
    },
    "lomda-v3-editor.block.embed.upload-title": {
        he: "הטמעת תוכן",
        en: "Embed content",
    },
    "lomda-v3-editor.block.embed.url-placeholder": {
        he: "הדביקו כתובת להטמעה",
        en: "Paste a URL to embed",
    },
    "lomda-v3-editor.block.embed.submit": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.block.embed.replace": {
        he: "החלפת הטמעה",
        en: "Replace embed",
    },
    "lomda-v3-editor.block.attachment.label": { he: "קובץ מצורף", en: "Attachment" },
    "lomda-v3-editor.block.attachment.desc": {
        he: "הוספת קובץ להורדה",
        en: "Add a downloadable file",
    },
    "lomda-v3-editor.block.attachment.upload-title": {
        he: "העלאת קובץ",
        en: "Upload File",
    },
    "lomda-v3-editor.block.attachment.upload-subtitle": {
        he: "גררו ושחררו או לחצו להעלאה",
        en: "Drag & drop or click to upload",
    },
    "lomda-v3-editor.block.attachment.replace": {
        he: "החלפת קובץ",
        en: "Replace file",
    },
    "lomda-v3-editor.block.attachment.name": {
        he: "שם הקובץ",
        en: "File name",
    },

    "lomda-v3-editor.block.drag": { he: "גרירת בלוק", en: "Drag block" },
    "lomda-v3-editor.block.options": { he: "אפשרויות בלוק", en: "Block options" },
    "lomda-v3-editor.block.settings": { he: "הגדרות בלוק", en: "Block settings" },
    "lomda-v3-editor.block.edit": { he: "עריכה", en: "Edit" },
    "lomda-v3-editor.block.empty-edit": { he: "הוספת תוכן: {block}", en: "Add {block} content" },
    "lomda-v3-editor.block.load-error": { he: "לא ניתן לטעון את הבלוק הזה. נסו למחוק ולהוסיף אותו מחדש.", en: "This block couldn't load. Try deleting and re-adding it." },
    "lomda-v3-editor.block.duplicate": { he: "שכפול", en: "Duplicate" },
    "lomda-v3-editor.block.delete": { he: "מחיקה", en: "Delete" },
    "lomda-v3-editor.block.copy-to-page": { he: "העתקה או העברה לעמוד", en: "Copy or move to page" },
    "lomda-v3-editor.block.copy-here": { he: "העתקה", en: "Copy" },
    "lomda-v3-editor.block.move-here": { he: "העברה", en: "Move" },
    "lomda-v3-editor.block.copy-to-named-page": { he: "העתקה ל{page}", en: "Copy to {page}" },
    "lomda-v3-editor.block.move-to-named-page": { he: "העברה ל{page}", en: "Move to {page}" },
    "lomda-v3-editor.block.current-page": { he: "העמוד הנוכחי", en: "Current page" },
    "lomda-v3-editor.block.page-with-title": { he: "{page} · {title}", en: "{page} · {title}" },
    "lomda-v3-editor.block.copy-to-page-empty": { he: "אין עמודים נוספים. הוסיפו עמוד כדי להעתיק או להעביר את המקטע לשם.", en: "There are no other pages. Add a page to copy or move this section there." },
    "lomda-v3-editor.block.copied-to": { he: "הועתק ל{page}", en: "Copied to {page}" },
    "lomda-v3-editor.block.moved-to": { he: "הועבר ל{page}", en: "Moved to {page}" },
    "lomda-v3-editor.block.go-to-page": { he: "מעבר לעמוד", en: "Go to page" },

    "lomda-v3-editor.format.bold": { he: "מודגש", en: "Bold" },
    "lomda-v3-editor.format.italic": { he: "נטוי", en: "Italic" },
    "lomda-v3-editor.format.underline": { he: "קו תחתון", en: "Underline" },
    "lomda-v3-editor.format.strike": { he: "קו חוצה", en: "Strikethrough" },
    "lomda-v3-editor.format.font": { he: "גופן", en: "Font" },
    "lomda-v3-editor.format.font-default": { he: "ברירת מחדל", en: "Default" },
    "lomda-v3-editor.format.weight": { he: "עובי", en: "Weight" },
    "lomda-v3-editor.format.weight-default": { he: "ברירת מחדל", en: "Default" },
    "lomda-v3-editor.format.weight-light": { he: "דק", en: "Light" },
    "lomda-v3-editor.format.weight-regular": { he: "רגיל", en: "Regular" },
    "lomda-v3-editor.format.weight-medium": { he: "בינוני", en: "Medium" },
    "lomda-v3-editor.format.weight-semibold": { he: "חצי מודגש", en: "Semibold" },
    "lomda-v3-editor.format.weight-bold": { he: "מודגש", en: "Bold" },
    "lomda-v3-editor.format.ltr": { he: "LTR", en: "LTR" },
    "lomda-v3-editor.format.rtl": { he: "RTL", en: "RTL" },
    "lomda-v3-editor.format.h1": { he: "כותרת 1", en: "Heading 1" },
    "lomda-v3-editor.format.h2": { he: "כותרת 2", en: "Heading 2" },
    "lomda-v3-editor.format.bullet-list": {
        he: "רשימת תבליטים",
        en: "Bulleted list",
    },
    "lomda-v3-editor.format.ordered-list": {
        he: "רשימה ממוספרת",
        en: "Numbered list",
    },
    "lomda-v3-editor.format.list": { he: "רשימה", en: "List" },
    "lomda-v3-editor.format.list-bulleted": { he: "תבליטים", en: "Bulleted" },
    "lomda-v3-editor.format.list-numbered": { he: "ממוספר", en: "Numbered" },
    "lomda-v3-editor.format.list-checkmark": { he: "סימני וי", en: "Checkmark" },
    "lomda-v3-editor.format.list-steps": { he: "שלבים", en: "Steps" },
    "lomda-v3-editor.format.quote": { he: "ציטוט", en: "Quote" },
    "lomda-v3-editor.format.align-start": {
        he: "יישור להתחלה",
        en: "Align start",
    },
    "lomda-v3-editor.format.align-center": { he: "מרכוז", en: "Align center" },
    "lomda-v3-editor.format.align-end": { he: "יישור לסוף", en: "Align end" },
    "lomda-v3-editor.format.link": { he: "קישור", en: "Link" },
    "lomda-v3-editor.format.h3": { he: "כותרת 3", en: "Heading 3" },
    "lomda-v3-editor.format.h4": { he: "כותרת 4", en: "Heading 4" },
    "lomda-v3-editor.format.h5": { he: "כותרת 5", en: "Heading 5" },
    "lomda-v3-editor.format.h6": { he: "כותרת 6", en: "Heading 6" },
    "lomda-v3-editor.format.label": { he: "תווית", en: "Label" },
    "lomda-v3-editor.format.paragraph": { he: "פסקה", en: "Paragraph" },
    "lomda-v3-editor.format.small": { he: "טקסט קטן", en: "Small text" },
    "lomda-v3-editor.format.theme-font": {
        he: "גופן ערכת הנושא ({font})",
        en: "Theme font ({font})",
    },
    "lomda-v3-editor.format.theme-font-mixed": {
        he: "גופן ערכת הנושא",
        en: "Theme font",
    },
    "lomda-v3-editor.format.normal": { he: "רגיל", en: "Normal" },
    "lomda-v3-editor.format.edit-with-ai": { he: "עריכה עם AI", en: "Edit with AI" },
    "lomda-v3-editor.format.color": { he: "צבע טקסט", en: "Text color" },
    "lomda-v3-editor.format.color-reset": { he: "איפוס צבע", en: "Reset color" },
    "lomda-v3-editor.format.more": { he: "עוד", en: "More" },
    "lomda-v3-editor.format.insert-table": { he: "הוספת טבלה", en: "Insert table" },
    "lomda-v3-editor.format.divider": { he: "קו מפריד", en: "Divider" },
    "lomda-v3-editor.format.table-add-row": { he: "הוספת שורה", en: "Add row" },
    "lomda-v3-editor.format.table-add-col": { he: "הוספת עמודה", en: "Add column" },
    "lomda-v3-editor.format.table-del-row": { he: "מחיקת שורה", en: "Delete row" },
    "lomda-v3-editor.format.table-del-col": { he: "מחיקת עמודה", en: "Delete column" },
    "lomda-v3-editor.format.table-delete": { he: "מחיקת טבלה", en: "Delete table" },
    "lomda-v3-editor.format.undo": { he: "ביטול", en: "Undo" },
    "lomda-v3-editor.format.redo": { he: "ביצוע חוזר", en: "Redo" },

    "lomda-v3-editor.sidebar.course-outline": { he: "מבנה הקורס", en: "Course outline" },
    "lomda-v3-editor.sidebar.content-blocks": { he: "בלוקי תוכן", en: "Content blocks" },
    "lomda-v3-editor.sidebar.collapse": { he: "כיווץ", en: "Collapse" },
    "lomda-v3-editor.sidebar.pages": { he: "עמודים", en: "Pages" },
    "lomda-v3-editor.sidebar.text": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.sidebar.media": { he: "מדיה", en: "Media" },
    "lomda-v3-editor.sidebar.content": { he: "תוכן", en: "Content" },
    "lomda-v3-editor.sidebar.assets": { he: "נכסי מדיה", en: "Media assets" },
    "lomda-v3-editor.sidebar.assets-desc": {
        he: "כל קובצי המדיה של הלומדה",
        en: "All the lomda's media assets",
    },
    "lomda-v3-editor.assets.empty": {
        he: "עדיין אין מדיה בלומדה הזו",
        en: "No media in this lomda yet",
    },
    "lomda-v3-editor.assets.upload-title": {
        he: "העלאת קבצים",
        en: "Upload files",
    },
    "lomda-v3-editor.assets.upload-subtitle": {
        he: "תמונות, וידאו, אודיו ומסמכים · אפשר כמה קבצים או תיקייה",
        en: "Images, video, audio and documents · several files or a folder",
    },
    "lomda-v3-editor.assets.duplicate": {
        he: "הקובץ הזה כבר נמצא בספריית המדיה",
        en: "This file is already in the media library",
    },
    "lomda-v3-editor.assets.images": { he: "תמונות", en: "Images" },
    "lomda-v3-editor.assets.videos": { he: "סרטונים", en: "Videos" },
    "lomda-v3-editor.assets.audio": { he: "אודיו", en: "Audio" },
    "lomda-v3-editor.assets.documents": { he: "מסמכים", en: "Documents" },
    "lomda-v3-editor.assets.icons": { he: "אייקונים", en: "Icons" },
    "lomda-v3-editor.assets.slot-too-large": {
        he: "הקובץ גדול מדי למקום הזה (עד {max}MB)",
        en: "Too large for this slot (max {max} MB)",
    },
    "lomda-v3-editor.assets.slot-wrong-type": {
        he: "המקום הזה לא מקבל קבצים מהסוג הזה",
        en: "This slot doesn't take this file type",
    },
    "lomda-v3-editor.assets.delete-named": {
        he: "מחיקת {name}",
        en: "Delete {name}",
    },
    "lomda-v3-editor.assets.delete-title": {
        he: "למחוק את הקובץ מהספרייה?",
        en: "Delete this file from the library?",
    },
    "lomda-v3-editor.assets.delete-used-image-one": {
        he: "התמונה הזו נמצאת בשימוש במקום אחד. למחוק אותה? הבלוק יישאר, בלעדיה.",
        en: "This image is used in 1 place. Delete it? The block stays, without it.",
    },
    "lomda-v3-editor.assets.delete-used-image": {
        he: "התמונה הזו נמצאת בשימוש ב-{count} מקומות. למחוק אותה מכל המקומות? הבלוקים יישארו, בלעדיה.",
        en: "This image is used in {count} places. Delete it everywhere? The blocks stay, without it.",
    },
    "lomda-v3-editor.assets.delete-used-video-one": {
        he: "הסרטון הזה נמצא בשימוש במקום אחד. למחוק אותו? הבלוק יישאר, בלעדיו.",
        en: "This video is used in 1 place. Delete it? The block stays, without it.",
    },
    "lomda-v3-editor.assets.delete-used-video": {
        he: "הסרטון הזה נמצא בשימוש ב-{count} מקומות. למחוק אותו מכל המקומות? הבלוקים יישארו, בלעדיו.",
        en: "This video is used in {count} places. Delete it everywhere? The blocks stay, without it.",
    },
    "lomda-v3-editor.assets.delete-used-audio-one": {
        he: "קובץ האודיו הזה נמצא בשימוש במקום אחד. למחוק אותו? הבלוק יישאר, בלעדיו.",
        en: "This audio file is used in 1 place. Delete it? The block stays, without it.",
    },
    "lomda-v3-editor.assets.delete-used-audio": {
        he: "קובץ האודיו הזה נמצא בשימוש ב-{count} מקומות. למחוק אותו מכל המקומות? הבלוקים יישארו, בלעדיו.",
        en: "This audio file is used in {count} places. Delete it everywhere? The blocks stay, without it.",
    },
    "lomda-v3-editor.assets.delete-used-file-one": {
        he: "המסמך הזה נמצא בשימוש במקום אחד. למחוק אותו? הבלוק יישאר, בלעדיו.",
        en: "This document is used in 1 place. Delete it? The block stays, without it.",
    },
    "lomda-v3-editor.assets.delete-used-file": {
        he: "המסמך הזה נמצא בשימוש ב-{count} מקומות. למחוק אותו מכל המקומות? הבלוקים יישארו, בלעדיו.",
        en: "This document is used in {count} places. Delete it everywhere? The blocks stay, without it.",
    },
    "lomda-v3-editor.assets.delete-used-icon-one": {
        he: "האייקון הזה נמצא בשימוש במקום אחד. למחוק אותו? הבלוק יישאר, בלעדיו.",
        en: "This icon is used in 1 place. Delete it? The block stays, without it.",
    },
    "lomda-v3-editor.assets.delete-used-icon": {
        he: "האייקון הזה נמצא בשימוש ב-{count} מקומות. למחוק אותו מכל המקומות? הבלוקים יישארו, בלעדיו.",
        en: "This icon is used in {count} places. Delete it everywhere? The blocks stay, without it.",
    },
    "lomda-v3-editor.assets.add": { he: "הוספה", en: "Add" },
    "lomda-v3-editor.assets.add-hint": {
        he: "הוספה אחרי הבלוק הנבחר, או לתוך העמודה, הלשונית או השלב שנבחרו",
        en: "Add after the selected block, or into the selected column, tab or step",
    },
    "lomda-v3-editor.assets.used-in": { he: "בשימוש ב…", en: "Used in…" },
    "lomda-v3-editor.assets.go-to": { he: "מעבר אל {target}", en: "Go to {target}" },
    "lomda-v3-editor.assets.used-in-item": {
        he: "עמוד {page} · {block}",
        en: "Page {page} · {block}",
    },
    "lomda-v3-editor.assets.no-images": {
        he: "עדיין אין תמונות בספריית המדיה",
        en: "No images in the media library yet",
    },
    "lomda-v3-editor.assets.no-videos": {
        he: "עדיין אין סרטונים בספריית המדיה",
        en: "No videos in the media library yet",
    },
    "lomda-v3-editor.assets.no-audio": {
        he: "עדיין אין קובצי שמע בספריית המדיה",
        en: "No audio in the media library yet",
    },
    "lomda-v3-editor.assets.no-files": {
        he: "עדיין אין קבצים בספריית המדיה",
        en: "No files in the media library yet",
    },
    "lomda-v3-editor.media.tab-upload": { he: "העלאה", en: "Upload" },
    "lomda-v3-editor.media.tab-library": { he: "שימוש חוזר במדיה", en: "Reuse media" },
    "lomda-v3-editor.sidebar.interactive": { he: "אינטראקטיבי", en: "Interactive" },
    "lomda-v3-editor.sidebar.settings": { he: "הגדרות", en: "Settings" },
    "lomda-v3-editor.appearance.reset": { he: "איפוס לברירת מחדל", en: "Reset to default" },
    "lomda-v3-editor.settings.language": { he: "שפה", en: "Language" },
    "lomda-v3-editor.settings.lang-he": { he: "עברית", en: "Hebrew" },
    "lomda-v3-editor.settings.lang-en": { he: "אנגלית", en: "English" },
    "lomda-v3-editor.settings.direction": { he: "כיוון", en: "Direction" },
    "lomda-v3-editor.settings.spacing": { he: "מרווחים ועיגול פינות", en: "Spacing & radius" },
    "lomda-v3-editor.settings.block-gap": {
        he: "מרווח בין מקטעים",
        en: "Sections gap",
    },
    "lomda-v3-editor.settings.grid-gap": {
        he: "מרווח רשת",
        en: "Grid gap",
    },
    "lomda-v3-editor.settings.inner-gap": {
        he: "מרווח פנימי",
        en: "Inner gap",
    },
    "lomda-v3-editor.settings.rtl": { he: "מימין לשמאל", en: "Right to left" },
    "lomda-v3-editor.settings.ltr": { he: "משמאל לימין", en: "Left to right" },
    "lomda-v3-editor.settings.learner-experience": {
        he: "חוויית הלומד",
        en: "Learner experience",
    },
    "lomda-v3-editor.settings.show-page-list": {
        he: "הצגת תפריט תוכן העניינים",
        en: "Show contents menu",
    },
    "lomda-v3-editor.settings.show-page-list-hint": {
        he: "התפריט שבו הלומד עובר בין העמודים",
        en: "The menu learners use to jump between pages",
    },
    "lomda-v3-editor.settings.nav-after-scroll": {
        he: "הצגת הכפתורים חזרה והמשך רק בסוף העמוד",
        en: "Show Back and Continue only at the end of the page",
    },
    "lomda-v3-editor.settings.nav-after-scroll-hint": {
        he: "הכפתורים מופיעים כשהלומד מגיע לסוף העמוד. אם תפריט תוכן העניינים מוצג, הלומד עדיין יכול לעבור ממנו לעמוד אחר",
        en: "The buttons appear once learners reach the end of the page. If the contents menu is shown, learners can still jump to another page from it",
    },
    "lomda-v3-editor.theme.colors": { he: "צבעים", en: "Colors" },
    "lomda-v3-editor.theme.use-theme": { he: "שימוש בערכת הנושא", en: "Use theme" },
    "lomda-v3-editor.theme.fonts": { he: "גופנים", en: "Fonts" },
    "lomda-v3-editor.theme.typography": { he: "טיפוגרפיה", en: "Typography" },
    "lomda-v3-editor.theme.preset-h1": { he: "H1", en: "H1" },
    "lomda-v3-editor.theme.preset-h2": { he: "H2", en: "H2" },
    "lomda-v3-editor.theme.preset-h3": { he: "H3", en: "H3" },
    "lomda-v3-editor.theme.preset-h4": { he: "H4", en: "H4" },
    "lomda-v3-editor.theme.preset-h5": { he: "H5", en: "H5" },
    "lomda-v3-editor.theme.preset-h6": { he: "H6", en: "H6" },
    "lomda-v3-editor.theme.preset-label": { he: "תווית", en: "Label" },
    "lomda-v3-editor.theme.preset-p": { he: "פסקה", en: "Paragraph" },
    "lomda-v3-editor.theme.preset-small": { he: "טקסט קטן", en: "Small text" },
    "lomda-v3-editor.theme.col-size": { he: "גודל", en: "Size" },
    "lomda-v3-editor.theme.col-weight": { he: "משקל", en: "Weight" },
    "lomda-v3-editor.theme.col-line": { he: "שורה", en: "Line" },
    "lomda-v3-editor.theme.reset": { he: "איפוס לברירת המחדל", en: "Reset to default" },
    "lomda-v3-editor.theme.background": { he: "רקע", en: "Background" },
    "lomda-v3-editor.theme.foreground": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.theme.primary": { he: "צבע ראשי", en: "Primary" },
    "lomda-v3-editor.theme.primary-fg": { he: "טקסט על ראשי", en: "Primary text" },
    "lomda-v3-editor.theme.card": { he: "רקע כרטיס", en: "Card" },
    "lomda-v3-editor.theme.card-fg": { he: "טקסט על כרטיסים", en: "Text on cards" },
    "lomda-v3-editor.theme.muted": { he: "מעומעם", en: "Muted" },
    "lomda-v3-editor.theme.muted-fg": { he: "טקסט מעומעם", en: "Muted text" },
    "lomda-v3-editor.theme.border": { he: "גבול", en: "Border" },
    "lomda-v3-editor.theme.border-dashed": { he: "גבול מקווקו", en: "Border dashed" },
    "lomda-v3-editor.theme.icons": { he: "אייקונים", en: "Icons" },
    "lomda-v3-editor.theme.secondary": { he: "משני", en: "Secondary" },
    "lomda-v3-editor.theme.border-active": { he: "גבול פעיל", en: "Border active" },
    "lomda-v3-editor.theme.hover": { he: "ריחוף", en: "Hover" },
    "lomda-v3-editor.theme.success": { he: "הצלחה", en: "Success" },
    "lomda-v3-editor.theme.destructive": { he: "הרסני", en: "Destructive" },
    "lomda-v3-editor.theme.font-heading": { he: "גופן כותרות", en: "Heading font" },
    "lomda-v3-editor.theme.font-heading-desc": {
        he: "גופן הכותרות משמש עבור H1, H2, H3, H4, H5, H6 ותווית.",
        en: "The heading font is used for H1, H2, H3, H4, H5, H6 and Label.",
    },
    "lomda-v3-editor.theme.font-body": { he: "גופן טקסט", en: "Body font" },
    "lomda-v3-editor.theme.font-body-desc": {
        he: "גופן הטקסט משמש עבור פסקה וטקסט קטן.",
        en: "The body font is used for Paragraph and Small text.",
    },
    "lomda-v3-editor.own-fonts.title": {
        he: "{count} טקסטים ב-{blocks} בלוקים משתמשים בגופן משלהם. להחליף גם אותם ל-{font}?",
        en: "{count} texts in {blocks} blocks use their own font. Switch them to {font} too?",
    },
    "lomda-v3-editor.own-fonts.title-one-block": {
        he: "{count} טקסטים בבלוק אחד משתמשים בגופן משלהם. להחליף גם אותם ל-{font}?",
        en: "{count} texts in 1 block use their own font. Switch them to {font} too?",
    },
    "lomda-v3-editor.own-fonts.title-one": {
        he: "טקסט אחד בבלוק אחד משתמש בגופן משלו. להחליף גם אותו ל-{font}?",
        en: "1 text in 1 block uses its own font. Switch it to {font} too?",
    },
    "lomda-v3-editor.own-fonts.font-pair": {
        he: "{titles} / {body}",
        en: "{titles} / {body}",
    },
    "lomda-v3-editor.own-fonts.description": {
        he: "טקסטים שיוחלפו יעקבו מעכשיו אחרי הגופן של הלומדה.",
        en: "Switched texts follow the lomda's font from now on.",
    },
    "lomda-v3-editor.own-page-backgrounds.title": {
        he: "{count} עמודים משתמשים ברקע משלהם. להחליף גם אותם לרקע של הלומדה?",
        en: "{count} pages use their own background. Switch them to the lomda background too?",
    },
    "lomda-v3-editor.own-page-backgrounds.title-one": {
        he: "עמוד אחד משתמש ברקע משלו. להחליף גם אותו לרקע של הלומדה?",
        en: "1 page uses its own background. Switch it to the lomda background too?",
    },
    "lomda-v3-editor.own-page-backgrounds.description": {
        he: "עמודים שיוחלפו יעקבו מעכשיו אחרי הרקע של הלומדה.",
        en: "Switched pages follow the lomda's background from now on.",
    },
    "lomda-v3-editor.own-values.place": {
        he: "עמוד {page} · {name}",
        en: "Page {page} · {name}",
    },
    "lomda-v3-editor.own-values.more": { he: "ועוד {count}", en: "and {count} more" },
    "lomda-v3-editor.own-values.switch": { he: "להחליף אותם", en: "Switch them" },
    "lomda-v3-editor.own-values.switch-one": { he: "להחליף אותו", en: "Switch it" },
    "lomda-v3-editor.own-values.keep": { he: "להשאיר את שלהם", en: "Keep theirs" },
    "lomda-v3-editor.own-values.keep-one": { he: "להשאיר את שלו", en: "Keep its own" },
    "lomda-v3-editor.theme.radius": { he: "עיגול פינות", en: "Radius" },
    "lomda-v3-editor.theme.radius-desc": {
        he: "עיגול פינות קובע כמה מעוגלות הפינות של כרטיסים, כפתורים, תמונות ושדות.",
        en: "Radius sets how rounded the corners of cards, buttons, images, and inputs are.",
    },
    "lomda-v3-editor.theme.size-h1": { he: "כותרת 1", en: "Heading 1" },
    "lomda-v3-editor.theme.size-h2": { he: "כותרת 2", en: "Heading 2" },
    "lomda-v3-editor.theme.size-h3": { he: "כותרת 3", en: "Heading 3" },
    "lomda-v3-editor.theme.size-h4": { he: "כותרת 4", en: "Heading 4" },
    "lomda-v3-editor.theme.size-h5": { he: "כותרת 5", en: "Heading 5" },
    "lomda-v3-editor.theme.size-h6": { he: "כותרת 6", en: "Heading 6" },
    "lomda-v3-editor.theme.size-label": { he: "תווית", en: "Label" },
    "lomda-v3-editor.theme.size-p": { he: "פסקה", en: "Paragraph" },
    "lomda-v3-editor.theme.size-small": { he: "טקסט קטן", en: "Small text" },
    "lomda-v3-editor.theme.weights": { he: "משקלי גופן", en: "Font weights" },
    "lomda-v3-editor.theme.weight-h1": { he: "כותרת 1", en: "Heading 1" },
    "lomda-v3-editor.theme.weight-h2": { he: "כותרת 2", en: "Heading 2" },
    "lomda-v3-editor.theme.weight-h3": { he: "כותרת 3", en: "Heading 3" },
    "lomda-v3-editor.theme.weight-h4": { he: "כותרת 4", en: "Heading 4" },
    "lomda-v3-editor.theme.weight-h5": { he: "כותרת 5", en: "Heading 5" },
    "lomda-v3-editor.theme.weight-h6": { he: "כותרת 6", en: "Heading 6" },
    "lomda-v3-editor.theme.weight-label": { he: "תווית", en: "Label" },
    "lomda-v3-editor.theme.weight-p": { he: "פסקה", en: "Paragraph" },
    "lomda-v3-editor.theme.weight-small": { he: "טקסט קטן", en: "Small text" },
    "lomda-v3-editor.theme.spacing": { he: "מרווחים ועיגול פינות", en: "Spacing & radius" },
    "lomda-v3-editor.theme.section-gap": {
        he: "מרווח בין מקטעים",
        en: "Sections gap",
    },
    "lomda-v3-editor.theme.grid-gap": { he: "מרווח רשת", en: "Grid gap" },
    "lomda-v3-editor.theme.inner-gap": { he: "מרווח פנימי", en: "Inner gap" },
    "lomda-v3-editor.theme.section-gap-desc": {
        he: "מרווח בין מקטעים הוא הרווח בין מקטע אחד למשנהו בעמוד.",
        en: "Section gap is the spacing between one section and the next on the page.",
    },
    "lomda-v3-editor.theme.grid-gap-desc": {
        he: "מרווח רשת הוא הרווח בין פריטים בתוך רשת, למשל בין כרטיסים במקטע כרטיסים.",
        en: "Grid gap is the spacing between items inside a grid, for example between cards in a cards section.",
    },
    "lomda-v3-editor.theme.inner-gap-desc": {
        he: "מרווח פנימי הוא הרווח בתוך מקטע/בלוק אחד, למשל בין כותרת, פסקה, כפתור, תמונה או אלמנטים פנימיים אחרים.",
        en: "Inner gap is the spacing inside one section/block, for example between a title, paragraph, button, image, or other inner elements.",
    },
    "lomda-v3-editor.settings.branding": { he: "מיתוג ופונטים", en: "Branding & fonts" },
    "lomda-v3-editor.settings.branding-soon": { he: "ערכות מיתוג ופונטים מותאמים — בקרוב", en: "Brand kits and custom fonts — coming soon" },
    "lomda-v3-editor.sidebar.expand": { he: "הרחבה", en: "Expand" },

    "lomda-v3-editor.outline.navigate-to": { he: "ניווט אל:", en: "Navigate to:" },
    "lomda-v3-editor.outline.add": { he: "הוספה", en: "Add" },
    "lomda-v3-editor.outline.section": { he: "מקטע 1", en: "Section 1" },

    "lomda-v3-editor.category.text": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.category.layout": { he: "פריסה", en: "Layout" },
    "lomda-v3-editor.layout.columns": { he: "עמודות מרובות", en: "Multiple columns" },
    "lomda-v3-editor.layout.align-top": { he: "יישור למעלה", en: "Align top" },
    "lomda-v3-editor.layout.align-center": { he: "יישור למרכז", en: "Align middle" },
    "lomda-v3-editor.layout.align-bottom": { he: "יישור למטה", en: "Align bottom" },
    "lomda-v3-editor.layout.column-width": { he: "רוחב עמודה {number}", en: "Column {number} width" },
    "lomda-v3-editor.columns.add-block": { he: "הוספת בלוק", en: "Add block" },
    "lomda-v3-editor.category.media": { he: "מדיה ומסמכים", en: "Media & Document" },
    "lomda-v3-editor.category.embed": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.category.interactive": { he: "אינטראקטיבי", en: "Interactive" },

    "lomda-v3-editor.text-variant.heading-text": { he: "כותרת וטקסט", en: "Heading and text" },
    "lomda-v3-editor.text-variant.text": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.text-variant.two-columns": { he: "שתי עמודות", en: "Two columns" },
    "lomda-v3-editor.text-variant.three-columns": { he: "שלוש עמודות", en: "Three columns" },
    "lomda-v3-editor.text-variant.multi-columns": { he: "עמודות", en: "Columns" },
    "lomda-v3-editor.text.columns": { he: "עמודות", en: "Columns" },
    "lomda-v3-editor.text.columns-n": { he: "{count} עמודות", en: "{count} columns" },
    "lomda-v3-editor.text.column-n": { he: "עמודה {number}", en: "Column {number}" },
    "lomda-v3-editor.text.manage-columns": { he: "ניהול עמודות", en: "Manage columns" },
    "lomda-v3-editor.text.add-column": { he: "הוספת עמודה", en: "Add column" },
    "lomda-v3-editor.text.rows": { he: "שורות", en: "Rows" },
    "lomda-v3-editor.text.width": { he: "רוחב", en: "Width" },
    "lomda-v3-editor.text.background": { he: "רקע", en: "Background" },
    "lomda-v3-editor.text.background-color": { he: "צבע רקע", en: "Background color" },
    "lomda-v3-editor.text.background-gradient": { he: "מעבר צבע", en: "Gradient" },
    "lomda-v3-editor.text.background-color-to": { he: "צבע שני", en: "Second color" },
    "lomda-v3-editor.text.background-gradient-direction": {
        he: "כיוון",
        en: "Direction",
    },
    "lomda-v3-editor.text.background-image": { he: "תמונת רקע", en: "Background image" },
    "lomda-v3-editor.text.background-upload": { he: "העלאת תמונה", en: "Upload image" },
    "lomda-v3-editor.text.background-remove": { he: "הסרה", en: "Remove" },
    "lomda-v3-editor.text.background-overlay": { he: "הכהיה", en: "Overlay" },
    "lomda-v3-editor.text.background-height": { he: "גובה", en: "Height" },
    "lomda-v3-editor.text.background-move": {
        he: "הזזת הטקסט",
        en: "Move the text",
    },
    "lomda-v3-editor.text.background-reset-position": {
        he: "איפוס מיקום",
        en: "Reset position",
    },
    "lomda-v3-editor.text.background-focal": {
        he: "מוקד התמונה",
        en: "Image focus",
    },
    "lomda-v3-editor.text.background-height-auto": { he: "אוטו", en: "Auto" },
    "lomda-v3-editor.text.background-height-fixed": { he: "קבוע", en: "Fixed" },
    "lomda-v3-editor.text.background-height-full": {
        he: "מסך מלא",
        en: "Full",
    },
    "lomda-v3-editor.text.background-full-width": {
        he: "רוחב מלא",
        en: "Full width",
    },
    "lomda-v3-editor.table.header": { he: "שורת כותרת", en: "Header row" },
    "lomda-v3-editor.table.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.table.appearance-card-bg": { he: "רקע הטבלה", en: "Card background" },
    "lomda-v3-editor.table.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.table.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.table.appearance-header-bg": { he: "רקע הכותרת", en: "Header background" },
    "lomda-v3-editor.text.table-hint": { he: "לחצו על הטבלה כדי לערוך שורות ועמודות", en: "Click the table to edit rows & columns" },
    "lomda-v3-editor.text-variant.link": { he: "קישור", en: "Link (curation)" },
    "lomda-v3-editor.text-variant.quote": { he: "ציטוט", en: "Quote" },
    "lomda-v3-editor.text-variant.table": { he: "טבלה", en: "Table" },
    "lomda-v3-editor.text-variant.heading-placeholder": { he: "כותרת", en: "Heading" },

    "lomda-v3-editor.media-variant.single-image": { he: "תמונה בודדת", en: "Single image" },
    "lomda-v3-editor.media-variant.media": { he: "מדיה", en: "Media" },
    "lomda-v3-editor.media-variant.image": { he: "תמונה", en: "Image" },
    "lomda-v3-editor.media-variant.video": { he: "וידאו", en: "Video" },
    "lomda-v3-editor.media-variant.media-top": { he: "מדיה עם כיתוב למעלה", en: "Media, caption on top" },
    "lomda-v3-editor.media-variant.media-bottom": { he: "מדיה עם כיתוב למטה", en: "Media, caption on bottom" },
    "lomda-v3-editor.media-variant.image-top": { he: "תמונה למעלה", en: "Image on top" },
    "lomda-v3-editor.media-variant.image-bottom": { he: "תמונה למטה", en: "Image on bottom" },
    "lomda-v3-editor.media-variant.two-images": { he: "שתי תמונות", en: "Two images" },
    "lomda-v3-editor.media-variant.three-images": { he: "שלוש תמונות", en: "Three images" },
    "lomda-v3-editor.media-variant.multiple-images": { he: "תמונות מרובות", en: "Multiple images" },
    "lomda-v3-editor.media-variant.single-video": { he: "וידאו בודד", en: "Single video" },
    "lomda-v3-editor.media-variant.video-top": { he: "וידאו למעלה", en: "Video on top" },
    "lomda-v3-editor.media-variant.video-bottom": { he: "וידאו למטה", en: "Video on bottom" },
    "lomda-v3-editor.media-variant.two-videos": { he: "שני סרטונים", en: "Two videos" },
    "lomda-v3-editor.media-variant.video-left": { he: "וידאו בצד", en: "Video on the left" },
    "lomda-v3-editor.media-variant.video-right": { he: "וידאו בצד שני", en: "Video on the right" },
    "lomda-v3-editor.media-variant.single-audio": { he: "אודיו", en: "Single audio" },
    "lomda-v3-editor.media-variant.document": { he: "מסמך", en: "Document" },

    "lomda-v3-editor.media.images": { he: "תמונות", en: "Images" },
    "lomda-v3-editor.media.image": { he: "תמונה", en: "Image" },
    "lomda-v3-editor.media.video": { he: "וידאו", en: "Video" },
    "lomda-v3-editor.media.width": { he: "רוחב", en: "Width" },
    "lomda-v3-editor.media.aspect": { he: "יחס", en: "Aspect" },
    "lomda-v3-editor.media.aspect-auto": { he: "אוטומטי", en: "Auto" },
    "lomda-v3-editor.media.layout": { he: "פריסה", en: "Layout" },
    "lomda-v3-editor.settings.hint": {
        he: "בחרו בלוק כדי לערוך את ההגדרות שלו",
        en: "Select a block to edit its settings",
    },
    "lomda-v3-editor.block-type.text": { he: "טקסט", en: "Text" },
    "lomda-v3-editor.block-card.text.title": { he: "בלוק טקסט", en: "Text Block" },
    "lomda-v3-editor.block-card.text.description": {
        he: "הוסף תוכן טקסט לשיעור",
        en: "Add text content to your lesson",
    },
    "lomda-v3-editor.block-card.image.title": { he: "תמונה", en: "Image" },
    "lomda-v3-editor.block-card.image.description": {
        he: "הוסף תמונה",
        en: "Add an image",
    },
    "lomda-v3-editor.block-card.video.title": { he: "וידאו", en: "Video" },
    "lomda-v3-editor.block-card.video.description": {
        he: "הוסף וידאו",
        en: "Add a video",
    },
    "lomda-v3-editor.block-card.quote.title": { he: "ציטוט", en: "Quote" },
    "lomda-v3-editor.block-card.quote.description": {
        he: "הוסף טקסט מצוטט",
        en: "Add a quoted text",
    },
    "lomda-v3-editor.block-card.knowledge-check.title": { he: "בדיקת ידע", en: "Knowledge check" },
    "lomda-v3-editor.block-card.knowledge-check.description": {
        he: "הוסף מקטע שאלות",
        en: "Add a question section",
    },
    "lomda-v3-editor.block-card.flip-cards.title": { he: "כרטיסים מתהפכים", en: "Flip Cards" },
    "lomda-v3-editor.block-card.flip-cards.description": {
        he: "הוסף כרטיסים מתהפכים אינטראקטיביים",
        en: "Add interactive flip cards",
    },
    "lomda-v3-editor.block-card.accordion.title": { he: "אקורדיון", en: "Accordion" },
    "lomda-v3-editor.block-card.accordion.description": {
        he: "הוסף פריטי תוכן הניתנים להרחבה",
        en: "Add expandable content items",
    },
    "lomda-v3-editor.block-card.hotspot.title": { he: "נקודות חמות", en: "Hotspots" },
    "lomda-v3-editor.block-card.hotspot.description": {
        he: "הוסף נקודות לחיצה לתמונה",
        en: "Add clickable points to an image",
    },
    "lomda-v3-editor.block-card.table.title": { he: "טבלה", en: "Table" },
    "lomda-v3-editor.block-card.table.description": {
        he: "צור תוכן מובנה עם שורות ועמודות",
        en: "Create structured content with rows and columns",
    },
    "lomda-v3-editor.block-card.multi-column.title": { he: "ריבוי עמודות", en: "Multi-column" },
    "lomda-v3-editor.block-card.multi-column.description": {
        he: "הצב בלוקים שונים זה לצד זה",
        en: "Place different content blocks side by side",
    },
    "lomda-v3-editor.layout.spacer": { he: "רווח", en: "Spacer" },
    "lomda-v3-editor.block-card.spacer.description": {
        he: "הוסף רווח ריק בין בלוקים",
        en: "Add empty space between blocks",
    },
    "lomda-v3-editor.spacer.height": { he: "גובה", en: "Height" },
    "lomda-v3-editor.spacer.px": { he: "{value} px", en: "{value} px" },
    "lomda-v3-editor.spacer.unit": { he: "px", en: "px" },
    "lomda-v3-editor.layout.divider": { he: "קו מפריד", en: "Divider" },
    "lomda-v3-editor.block-card.divider.description": {
        he: "הוסף קו אופקי בין בלוקים",
        en: "Add a horizontal line between blocks",
    },
    "lomda-v3-editor.divider.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.divider.thickness": { he: "עובי", en: "Thickness" },
    "lomda-v3-editor.divider.thickness.thin": { he: "דק", en: "Thin" },
    "lomda-v3-editor.divider.thickness.medium": { he: "בינוני", en: "Medium" },
    "lomda-v3-editor.divider.thickness.thick": { he: "עבה", en: "Thick" },
    "lomda-v3-editor.divider.color": { he: "צבע", en: "Color" },
    "lomda-v3-editor.divider.spacing": { he: "מרווח", en: "Spacing" },
    "lomda-v3-editor.divider.spacing.none": { he: "ללא", en: "None" },
    "lomda-v3-editor.divider.spacing.s": { he: "S", en: "S" },
    "lomda-v3-editor.divider.spacing.m": { he: "M", en: "M" },
    "lomda-v3-editor.divider.spacing.l": { he: "L", en: "L" },
    "lomda-v3-editor.divider.spacing.s-name": { he: "קטן", en: "Small" },
    "lomda-v3-editor.divider.spacing.m-name": { he: "בינוני", en: "Medium" },
    "lomda-v3-editor.divider.spacing.l-name": { he: "גדול", en: "Large" },
    "lomda-v3-editor.layout.button": { he: "כפתור", en: "Button" },
    "lomda-v3-editor.block-card.button.description": {
        he: "הוסף כפתור שפותח אתר או עובר לעמוד אחר",
        en: "Add a button that opens a website or another page",
    },
    "lomda-v3-editor.button.default-label": { he: "למידע נוסף", en: "Learn more" },
    "lomda-v3-editor.button.label": { he: "טקסט הכפתור", en: "Button text" },
    "lomda-v3-editor.button.opens-new-tab": { he: "(נפתח בכרטיסייה חדשה)", en: "(opens in a new tab)" },
    "lomda-v3-editor.button.action": { he: "פעולה", en: "Action" },
    "lomda-v3-editor.button.action.url": { he: "פתיחת אתר", en: "Open a website" },
    "lomda-v3-editor.button.action.page": { he: "מעבר לעמוד", en: "Go to a page" },
    "lomda-v3-editor.button.url": { he: "כתובת האתר", en: "Website address" },
    "lomda-v3-editor.button.url-invalid": {
        he: "יש להזין כתובת אתר תקינה (http או https)",
        en: "Enter a valid website address (http or https)",
    },
    "lomda-v3-editor.button.choose-page": { he: "העמוד שייפתח", en: "Page to open" },
    "lomda-v3-editor.button.hint.no-action": {
        he: "יש לבחור פעולה לכפתור. עד אז הלומדים לא יראו אותו.",
        en: "Choose an action for this button. Learners won't see it until then.",
    },
    "lomda-v3-editor.button.hint.page-missing": {
        he: "העמוד שהכפתור פתח נמחק. יש לבחור עמוד אחר. עד אז הלומדים לא יראו את הכפתור.",
        en: "The page this button opened was deleted. Choose another page. Learners won't see the button until then.",
    },
    "lomda-v3-editor.button.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.button.style": { he: "סגנון", en: "Style" },
    "lomda-v3-editor.button.style.filled": { he: "מלא", en: "Filled" },
    "lomda-v3-editor.button.style.outline": { he: "מתאר", en: "Outline" },
    "lomda-v3-editor.button.color": { he: "צבע הכפתור", en: "Button color" },
    "lomda-v3-editor.button.text-color": { he: "צבע הטקסט", en: "Text color" },
    "lomda-v3-editor.button.size": { he: "גודל", en: "Size" },
    "lomda-v3-editor.button.size.s": { he: "S", en: "S" },
    "lomda-v3-editor.button.size.m": { he: "M", en: "M" },
    "lomda-v3-editor.button.size.l": { he: "L", en: "L" },
    "lomda-v3-editor.button.size.s-name": { he: "קטן", en: "Small" },
    "lomda-v3-editor.button.size.m-name": { he: "בינוני", en: "Medium" },
    "lomda-v3-editor.button.size.l-name": { he: "גדול", en: "Large" },
    "lomda-v3-editor.button.align": { he: "יישור", en: "Alignment" },
    "lomda-v3-editor.block-card.tabs.title": { he: "טאבים", en: "Tabs" },
    "lomda-v3-editor.block-card.tabs.description": {
        he: "ארגן תוכן בטאבים",
        en: "Organize content into tabs",
    },
    "lomda-v3-editor.block-card.audio.title": { he: "אודיו", en: "Audio" },
    "lomda-v3-editor.block-card.audio.description": {
        he: "הוסף אודיו עם בקרות הפעלה",
        en: "Add audio with playback controls",
    },
    "lomda-v3-editor.block-card.document.title": { he: "מסמך", en: "Document" },
    "lomda-v3-editor.block-card.document.description": {
        he: "הוסף קבצים לצפייה או הורדה עבור הלומדים",
        en: "Add files for learners to view or download",
    },
    "lomda-v3-editor.media-variant.storage-video": { he: "וידאו מאחסון", en: "Storage video" },
    "lomda-v3-editor.block-card.storage-video.title": { he: "וידאו מאחסון", en: "Storage video" },
    "lomda-v3-editor.block-card.storage-video.description": {
        he: "הוסף וידאו מהאחסון שלך",
        en: "Add a video from your storage",
    },
    "lomda-v3-editor.block-card.walkthrough.description": {
        he: "הדרך לומדים שלב אחר שלב",
        en: "Guide learners step by step",
    },
    "lomda-v3-editor.block-card.scenario.description": {
        he: "תרחיש החלטות מסתעף",
        en: "A branching decision scenario",
    },
    "lomda-v3-editor.block-card.checklist.description": {
        he: "רשימת משימות לסימון",
        en: "A checkable task list",
    },
    "lomda-v3-editor.block-card.card-sorting.description": {
        he: "מיין כרטיסים לקטגוריות",
        en: "Sort cards into categories",
    },
    "lomda-v3-editor.block-card.how-to.description": {
        he: "מדריך שלב אחר שלב",
        en: "A step-by-step how-to guide",
    },
    "lomda-v3-editor.block-card.steps.description": {
        he: "תוכן בשלבים ממוספרים",
        en: "Numbered step-by-step content",
    },
    "lomda-v3-editor.block-type.media": { he: "מדיה", en: "Media" },
    "lomda-v3-editor.block-type.audio": { he: "אודיו", en: "Audio" },
    "lomda-v3-editor.block-type.embed": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.block-type.html": { he: "HTML", en: "HTML" },
    "lomda-v3-editor.block-type.attachment": { he: "קובץ מצורף", en: "Attachment" },
    "lomda-v3-editor.block-type.walkthrough": {
        he: "הדרכה אינטראקטיבית",
        en: "Walkthrough",
    },
    "lomda-v3-editor.media.upload-image": { he: "העלאת תמונה", en: "Upload Image" },
    "lomda-v3-editor.media.upload-video": { he: "העלאת סרטון", en: "Upload Video" },
    "lomda-v3-editor.media.drop-hint": { he: "גררו ושחררו או לחצו להעלאה", en: "Drag & drop or click to upload" },
    "lomda-v3-editor.media.fill": { he: "מילוי", en: "Fill" },
    "lomda-v3-editor.media.fit": { he: "התאמה", en: "Fit" },
    "lomda-v3-editor.media.crop": { he: "חיתוך", en: "Crop" },
    "lomda-v3-editor.media.replace-image": { he: "החלפת תמונה", en: "Replace image" },
    "lomda-v3-editor.media.replace-video": { he: "החלפת סרטון", en: "Replace video" },
    "lomda-v3-editor.media.replace-audio": { he: "החלפת אודיו", en: "Replace audio" },
    "lomda-v3-editor.media.replace-document": { he: "החלפת מסמך", en: "Replace document" },
    "lomda-v3-editor.media.video-thumbnail": { he: "תמונה ממוזערת", en: "Video Thumbnail" },
    "lomda-v3-editor.media.thumbnail-current-auto": { he: "נוכחי: תמונה ממוזערת אוטומטית", en: "Current: auto-generated thumbnail" },
    "lomda-v3-editor.media.thumbnail-upload-hint": { he: "גררו ושחררו תמונה כאן, או לחצו להעלאה", en: "Drag & drop an image here, or click to upload" },
    "lomda-v3-editor.media.thumbnail-replace": { he: "החלפה", en: "Replace" },
    "lomda-v3-editor.media.thumbnail-remove": { he: "הסרת תמונה ממוזערת מותאמת", en: "Remove custom thumbnail" },
    "lomda-v3-editor.media.choose-video": { he: "בחירת וידאו מהאחסון", en: "Choose video from storage" },
    "lomda-v3-editor.media.image-link": { he: "הדביקו קישור לתמונה", en: "Paste image link" },
    "lomda-v3-editor.media.video-link": { he: "הדביקו קישור (YouTube, Vimeo)", en: "Paste link (YouTube, Vimeo)" },
    "lomda-v3-editor.media.add": { he: "הוספה", en: "Add" },
    "lomda-v3-editor.media.replace": { he: "החלפה", en: "Replace" },
    "lomda-v3-editor.media.video-picker": { he: "בחרו וידאו מהאחסון", en: "Pick a video from your storage" },
    "lomda-v3-editor.media.video-pick": { he: "בחירת וידאו", en: "Choose video" },
    "lomda-v3-editor.media.back": { he: "חזרה", en: "Back" },
    "lomda-v3-editor.media.source-upload-title": { he: "העלאת קובץ", en: "Upload file" },
    "lomda-v3-editor.media.source-upload-desc": { he: "מהמחשב שלך (עד {max}MB)", en: "From your computer (max {max}MB)" },
    "lomda-v3-editor.media.source-link-title": { he: "הדבקת קישור", en: "Paste link" },
    "lomda-v3-editor.media.source-link-desc": { he: "YouTube, Vimeo ועוד", en: "YouTube, Vimeo, etc." },
    "lomda-v3-editor.media.source-storage-title": { he: "מהאחסון", en: "From storage" },
    "lomda-v3-editor.media.source-storage-desc": { he: "בחירה מהאחסון שלך", en: "Choose from your storage" },
    "lomda-v3-editor.media.video-unavailable": { he: "הווידאו אינו זמין", en: "Video unavailable" },
    "lomda-v3-editor.media.alt-text": { he: "טקסט חלופי", en: "Alt text" },
    "lomda-v3-editor.media.alt-placeholder": { he: "תיאור התמונה", en: "Describe the image" },
    "lomda-v3-editor.audio.title": { he: "כותרת האודיו", en: "Audio title" },

    "lomda-v3-editor.embed-variant.web-link": { he: "קישור אינטרנט", en: "Web link" },
    "lomda-v3-editor.embed-variant.html": { he: "HTML", en: "HTML" },
    "lomda-v3-editor.embed-variant.storage": { he: "וידאו מהאחסון", en: "Storage video" },
    "lomda-v3-editor.embed-variant.canva": { he: "Canva", en: "Canva" },
    "lomda-v3-editor.embed-variant.microsoft": { he: "Microsoft Services", en: "Microsoft Services" },
    "lomda-v3-editor.embed-variant.google": { he: "Google Services", en: "Google Services" },
    "lomda-v3-editor.embed-variant.surveymonkey": { he: "Survey Monkey", en: "Survey Monkey" },
    "lomda-v3-editor.embed-variant.vimeo": { he: "Vimeo", en: "Vimeo" },
    "lomda-v3-editor.embed-variant.slideshare": { he: "Slideshare", en: "Slideshare" },
    "lomda-v3-editor.embed-variant.prezi": { he: "Prezi", en: "Prezi" },
    "lomda-v3-editor.embed-variant.youtube": { he: "YouTube", en: "YouTube" },
    "lomda-v3-editor.embed-variant.powtoon": { he: "Powtoon", en: "Powtoon" },
    "lomda-v3-editor.embed-variant.h5p": { he: "H5P", en: "H5P" },

    "lomda-v3-editor.embed.hint": { he: "הטמעת קישור {provider} לקורס", en: "Embed {provider} link into your course" },
    "lomda-v3-editor.embed.paste": { he: "הדביקו קישור כאן", en: "Paste link here" },
    "lomda-v3-editor.embed.submit": { he: "הטמעה", en: "Embed" },
    "lomda-v3-editor.embed.replace": { he: "החלפה", en: "Replace" },
    "lomda-v3-editor.embed.replace-url": { he: "החלפת קישור", en: "Replace url" },
    "lomda-v3-editor.embed.blocked.text": { he: "האתר הזה לא מאפשר להציג אותו כאן. אפשר לפתוח אותו בכרטיסייה חדשה.", en: "This site doesn't allow being shown here. You can open it in a new tab." },
    "lomda-v3-editor.embed.blocked.open": { he: "פתיחה בכרטיסייה חדשה", en: "Open in new tab" },
    "lomda-v3-editor.embed.blocked.author-warning": { he: "האתר הזה לא מאפשר הטמעה. הלומדים יראו במקומו כרטיס עם כפתור לפתיחה בכרטיסייה חדשה.", en: "This site doesn't allow embedding. Learners will see a card with an \"Open in new tab\" button instead." },

    "lomda-v3-editor.html.code": { he: "קוד", en: "Code" },
    "lomda-v3-editor.html.preview": { he: "תצוגה", en: "Preview" },
    "lomda-v3-editor.html.placeholder": { he: "הדביקו או כתבו HTML...", en: "Paste or write HTML..." },

    "lomda-v3-editor.interactive.walkthrough": { he: "הדרכה", en: "Walkthrough" },
    "lomda-v3-editor.interactive.scenario": { he: "תרחיש", en: "Scenario" },
    "lomda-v3-editor.interactive.checklist": { he: "רשימת משימות", en: "Checklist" },
    "lomda-v3-editor.interactive.card-sorting": { he: "מיון כרטיסים", en: "Card sorting" },
    "lomda-v3-editor.interactive.how-to": { he: "מדריך", en: "How-to" },
    "lomda-v3-editor.interactive.flip-cards": { he: "כרטיסי היפוך", en: "Flip cards" },
    "lomda-v3-editor.interactive.accordion": { he: "אקורדיון", en: "Accordion" },
    "lomda-v3-editor.interactive.tabs": { he: "טאבים", en: "Tabs" },
    "lomda-v3-editor.interactive.hotspot": { he: "נקודות על תמונה", en: "Hotspot on image" },
    "lomda-v3-editor.interactive.knowledge-check": { he: "בדיקת ידע", en: "Knowledge check" },
    "lomda-v3-editor.interactive.rating": { he: "דירוג", en: "Rating" },
    "lomda-v3-editor.interactive.poll": { he: "סקר", en: "Poll" },
    "lomda-v3-editor.interactive.word-cloud": { he: "ענן מילים", en: "Word cloud" },
    "lomda-v3-editor.interactive.open-ended": { he: "שאלה פתוחה", en: "Open-ended question" },
    "lomda-v3-editor.interactive.complete-sentence": { he: "השלמת משפט", en: "Complete the sentence" },
    "lomda-v3-editor.block-card.rating.title": { he: "דירוג", en: "Rating" },
    "lomda-v3-editor.block-card.rating.description": { he: "בקשו מהלומד לדרג מ-1 עד 5 כוכבים", en: "Ask the learner to rate from 1 to 5 stars" },
    "lomda-v3-editor.interactive.chart": { he: "תרשים", en: "Chart" },
    "lomda-v3-editor.chart.variant-bar": { he: "תרשים עמודות", en: "Bar chart" },
    "lomda-v3-editor.chart.variant-line": { he: "תרשים קו", en: "Line chart" },
    "lomda-v3-editor.chart.variant-donut": { he: "תרשים טבעת", en: "Donut chart" },
    "lomda-v3-editor.chart.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.chart.manage-data": { he: "נתונים", en: "Data" },
    "lomda-v3-editor.chart.unit": { he: "יחידה", en: "Unit" },
    "lomda-v3-editor.chart.unit-placeholder": { he: "%", en: "%" },
    "lomda-v3-editor.chart.value": { he: "ערך", en: "Value" },
    "lomda-v3-editor.chart.slice-number": { he: "נתון {number}", en: "Value {number}" },
    "lomda-v3-editor.chart.title-placeholder": { he: "כותרת התרשים", en: "Chart title" },
    "lomda-v3-editor.chart.no-data": {
        he: "הוספת נתונים מסרגל ההגדרות",
        en: "Add values from the settings bar",
    },
    "lomda-v3-editor.chart.caption-placeholder": {
        he: "מה התרשים מראה ומאיפה הנתונים",
        en: "What the chart shows and where the data is from",
    },
    "lomda-v3-editor.chart.appearance-series-color": { he: "צבע הנתונים", en: "Data color" },
    "lomda-v3-editor.chart.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.chart.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.chart.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.chart.appearance-show-values": { he: "הצגת ערכים", en: "Show values" },
    "lomda-v3-editor.chart.appearance-horizontal": { he: "עמודות אופקיות", en: "Horizontal bars" },
    "lomda-v3-editor.block-card.chart.title": { he: "תרשים", en: "Chart" },
    "lomda-v3-editor.block-card.chart.description": {
        he: "עמודות, קו או טבעת — השוואה, מגמה או חלוקה",
        en: "Bar, line or donut — a comparison, a trend or a breakdown",
    },
    "lomda-v3-editor.chart.add": { he: "הוספת ערך", en: "Add value" },
    "lomda-v3-editor.block-card.poll.title": { he: "סקר", en: "Poll" },
    "lomda-v3-editor.block-card.poll.description": { he: "שאלת דעה — הלומד בוחר ורואה תוצאות", en: "An opinion question — the learner picks and sees results" },
    "lomda-v3-editor.block-card.word-cloud.title": { he: "ענן מילים", en: "Word cloud" },
    "lomda-v3-editor.block-card.word-cloud.description": { he: "הלומד כותב מילים ורואה ענן מילים", en: "The learner writes words and sees a word cloud" },
    "lomda-v3-editor.block-card.open-ended.title": { he: "שאלה פתוחה", en: "Open-ended question" },
    "lomda-v3-editor.block-card.open-ended.description": { he: "שאלה חופשית — בוט מספק משוב על התשובה", en: "A free-text question — a bot gives feedback on the answer" },
    "lomda-v3-editor.block-card.complete-sentence.title": { he: "השלמת משפט", en: "Complete the sentence" },
    "lomda-v3-editor.block-card.complete-sentence.description": { he: "מלאו את החסר עם תשובה נכונה", en: "Fill in the blank with a correct answer" },
    "lomda-v3-editor.rating.question-placeholder": { he: "כתבו את השאלה שלכם...", en: "Write your question..." },
    "lomda-v3-editor.rating.send": { he: "שליחה", en: "Send" },
    "lomda-v3-editor.rating.thanks": { he: "תודה על הדירוג!", en: "Thanks for your rating!" },
    "lomda-v3-editor.poll.question-placeholder": { he: "כתבו את שאלת הסקר...", en: "Write your poll question..." },
    "lomda-v3-editor.poll.option-placeholder": { he: "אפשרות תשובה", en: "Answer option" },
    "lomda-v3-editor.poll.manage-options": { he: "ניהול אפשרויות", en: "Manage options" },
    "lomda-v3-editor.poll.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.poll.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.poll.appearance-option-bg": { he: "רקע האפשרות", en: "Option background" },
    "lomda-v3-editor.poll.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.poll.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.poll.add": { he: "הוספת אפשרות", en: "Add option" },
    "lomda-v3-editor.poll.voted": { he: "תודה! אלו התוצאות עד כה.", en: "Thanks! Here are the results so far." },
    "lomda-v3-editor.word-cloud.question-placeholder": { he: "כתבו את השאלה שלכם...", en: "Write your question..." },
    "lomda-v3-editor.word-cloud.word-count": { he: "מספר מילים", en: "Number of words" },
    "lomda-v3-editor.word-cloud.word-placeholder": { he: "מילה {number}", en: "Word {number}" },
    "lomda-v3-editor.word-cloud.submit": { he: "הצגת הענן", en: "Show the cloud" },
    "lomda-v3-editor.word-cloud.generating": { he: "ענן המילים ייווצר כאן", en: "Your word cloud will appear here" },
    "lomda-v3-editor.open-ended.question-placeholder": { he: "כתבו את השאלה שלכם...", en: "Write your question..." },
    "lomda-v3-editor.open-ended.title-desc": { he: "השאלה שהלומד יראה מעל תיבת התשובה.", en: "The question the learner sees above the answer box." },
    "lomda-v3-editor.open-ended.answer-desc": { he: "הלומד כותב תשובה חופשית ומקבל משוב מ-AI.", en: "The learner writes a free answer and gets AI feedback." },
    "lomda-v3-editor.open-ended.guidance-label": { he: "הנחיה למשוב ה-AI", en: "AI feedback guidance" },
    "lomda-v3-editor.open-ended.guidance-placeholder": { he: "איך הבוט צריך להגיב לתשובה?", en: "How should the bot respond to the answer?" },
    "lomda-v3-editor.open-ended.guidance-desc": { he: "נותן ל-AI כיוון איך להגיב (למשל: עודד, הצג נקודות מבט נוספות).", en: "Steers how the AI responds (e.g. encourage, add perspectives)." },
    "lomda-v3-editor.open-ended.answer-placeholder": { he: "כתבו את תשובתכם כאן...", en: "Write your answer here..." },
    "lomda-v3-editor.open-ended.feedback-title": { he: "משוב", en: "Feedback" },
    "lomda-v3-editor.open-ended.thinking": { he: "חושב...", en: "Thinking..." },
    "lomda-v3-editor.open-ended.submit": { he: "שליחה למשוב", en: "Get feedback" },
    "lomda-v3-editor.complete-sentence.title": {
        he: "השלימו את המשפטים",
        en: "Complete the sentences",
    },
    "lomda-v3-editor.complete-sentence.sentence-label": { he: "משפט", en: "Sentence" },
    "lomda-v3-editor.complete-sentence.text-placeholder": { he: "כתבו את המשפט, ועטפו כל מילת-תשובה בקו תחתון, למשל: מים הם _מימן_ ו_חמצן_", en: "Write the sentence, wrapping each answer word in underscores, e.g. Water is _hydrogen_ and _oxygen_" },
    "lomda-v3-editor.complete-sentence.blank-hint": { he: "כל _מילה_ עטופה הופכת לתפריט נפתח; המילה שבתוך הקווים היא התשובה הנכונה.", en: "Each _word_ you wrap becomes a dropdown blank; the wrapped word is its correct answer." },
    "lomda-v3-editor.complete-sentence.distractors-label": { he: "אפשרויות", en: "Options" },
    "lomda-v3-editor.complete-sentence.distractors-placeholder": { he: "מילים שגויות להוספה למאגר, מופרדות בפסיק", en: "Wrong words to add to the bank, comma-separated" },
    "lomda-v3-editor.complete-sentence.distractors-hint": { he: "יתווספו למאגר המילים כדי להקשות על הבחירה.", en: "Added to the word bank to make the choice harder." },
    "lomda-v3-editor.complete-sentence.word-bank-label": { he: "מאגר מילים", en: "Word bank" },
    "lomda-v3-editor.complete-sentence.blank-choose": { he: "בחרו", en: "select" },
    "lomda-v3-editor.complete-sentence.answer-label": { he: "תשובה נכונה", en: "Correct answer" },
    "lomda-v3-editor.complete-sentence.answers-desc": { he: "התשובה שתיחשב נכונה עבור החסר.", en: "The answer accepted as correct for the blank." },
    "lomda-v3-editor.complete-sentence.answer-placeholder": { he: "תשובה נכונה", en: "Correct answer" },
    "lomda-v3-editor.complete-sentence.add-answer": { he: "הוספת תשובה", en: "Add answer" },
    "lomda-v3-editor.complete-sentence.explanation-placeholder": { he: "הסבר (מוצג אחרי תשובה נכונה)", en: "Explanation (shown after a correct answer)" },
    "lomda-v3-editor.complete-sentence.correct": { he: "נכון!", en: "Correct!" },
    "lomda-v3-editor.complete-sentence.try-again": { he: "לא בדיוק, נסו שוב.", en: "Not quite, try again." },
    "lomda-v3-editor.complete-sentence.check": { he: "בדיקה", en: "Check" },
    "lomda-v3-editor.interactive.steps": { he: "שלבים", en: "Steps" },
    "lomda-v3-editor.steps.step-label": { he: "שלב {number}", en: "Step {number}" },
    "lomda-v3-editor.steps.title-placeholder": { he: "כותרת השלב", en: "Step title" },
    "lomda-v3-editor.steps.subtitle-placeholder": { he: "תיאור קצר", en: "Short description" },
    "lomda-v3-editor.steps.heading-placeholder": { he: "כותרת השלב", en: "Step heading" },
    "lomda-v3-editor.steps.content-placeholder": { he: "תוכן השלב...", en: "Step content..." },
    "lomda-v3-editor.steps.add": { he: "הוספת שלב", en: "Add step" },
    "lomda-v3-editor.steps.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.steps.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.steps.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.steps.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.steps.appearance-number-color": { he: "צבע המספר", en: "Number color" },
    "lomda-v3-editor.steps.appearance-number-bg": { he: "רקע המספר", en: "Number background" },
    "lomda-v3-editor.steps.appearance-active-number-bg": { he: "רקע מספר פעיל", en: "Active number background" },
    "lomda-v3-editor.steps.manage-steps": { he: "ניהול שלבים", en: "Manage steps" },
    "lomda-v3-editor.steps.display": { he: "תצוגה", en: "Display" },
    "lomda-v3-editor.steps.display-panel": { he: "עם תוכן", en: "With content" },
    "lomda-v3-editor.steps.display-list": { he: "רשימה בלבד", en: "List only" },

    "lomda-v3-editor.quiz.title-placeholder": { he: "כותרת", en: "Title" },
    "lomda-v3-editor.quiz.description-placeholder": { he: "תיאור קצר", en: "Short description" },
    "lomda-v3-editor.quiz.explanation": { he: "הסבר", en: "Explanation" },
    "lomda-v3-editor.quiz.feedback": { he: "משוב", en: "Feedback" },
    "lomda-v3-editor.quiz.option-feedback": { he: "משוב לאפשרות {letter}", en: "Feedback for option {letter}" },
    "lomda-v3-editor.quiz.choose-one": { he: "בחרו תשובה אחת", en: "Choose one option" },
    "lomda-v3-editor.quiz.choose-multiple": { he: "בחרו תשובה אחת או יותר", en: "Choose one or more options" },
    "lomda-v3-editor.quiz.allow-multiple": { he: "אפשר מספר תשובות נכונות", en: "Allow multiple answers" },
    "lomda-v3-editor.quiz.question-n": { he: "שאלה {number}", en: "Question {number}" },
    "lomda-v3-editor.quiz.question-placeholder": { he: "כתבו שאלה...", en: "Type question..." },
    "lomda-v3-editor.quiz.option-placeholder": { he: "כתבו אפשרות...", en: "Type option..." },
    "lomda-v3-editor.quiz.instr-single": { he: "בחרו אפשרות אחת", en: "Choose one option" },
    "lomda-v3-editor.quiz.instr-multiple": { he: "בחרו אפשרות אחת או יותר", en: "Choose one or more options" },
    "lomda-v3-editor.quiz.instr-true-false": { he: "בחרו האם ההיגד נכון או לא נכון", en: "Choose whether the statement is true or false" },
    "lomda-v3-editor.quiz.instr-open": { he: "שאלה פתוחה", en: "Open question" },
    "lomda-v3-editor.quiz.instruction": { he: "הנחיה", en: "Instruction" },
    "lomda-v3-editor.quiz.show-instruction": { he: "הצגת הנחיה", en: "Show instruction" },
    "lomda-v3-editor.quiz.open-answer-placeholder": { he: "הלומד יכתוב את תשובתו כאן", en: "The learner will type their answer here" },
    "lomda-v3-editor.quiz.delete-question": { he: "מחיקת שאלה", en: "Delete question" },
    "lomda-v3-editor.quiz.add-new-question": { he: "הוספת שאלה חדשה", en: "Add new question" },
    "lomda-v3-editor.quiz.type-single": { he: "בחירה יחידה", en: "Single choice" },
    "lomda-v3-editor.quiz.type-multiple": { he: "בחירה מרובה", en: "Multiple choice" },
    "lomda-v3-editor.quiz.type-true-false": { he: "נכון / לא נכון", en: "True / False" },
    "lomda-v3-editor.quiz.type-open": { he: "שאלה פתוחה", en: "Open question" },
    "lomda-v3-editor.quiz.correct-answer": { he: "תשובה נכונה", en: "Correct answer" },
    "lomda-v3-editor.quiz.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.quiz.manage-options": { he: "ניהול אפשרויות", en: "Manage options" },
    "lomda-v3-editor.quiz.option-n": { he: "אפשרות {letter}", en: "Option {letter}" },
    "lomda-v3-editor.quiz.correct-badge": { he: "נכונה", en: "Correct" },
    "lomda-v3-editor.quiz.true": { he: "נכון", en: "True" },
    "lomda-v3-editor.quiz.false": { he: "לא נכון", en: "False" },
    "lomda-v3-editor.quiz.appearance-option-bg": { he: "רקע אפשרות", en: "Option background" },
    "lomda-v3-editor.quiz.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.quiz.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.quiz.appearance-selected": { he: "צבע מצב נבחר", en: "Selected state color" },
    "lomda-v3-editor.quiz.mark-correct": { he: "סימון כתשובה נכונה", en: "Mark as correct" },
    "lomda-v3-editor.quiz.add-option": { he: "הוספת אפשרות", en: "Add option" },
    "lomda-v3-editor.quiz.add-question": { he: "הוספת שאלה", en: "Add question" },
    "lomda-v3-editor.quiz.check": { he: "בדיקה", en: "Check answers" },
    "lomda-v3-editor.quiz.retry": { he: "ניסיון חוזר", en: "Try again" },
    "lomda-v3-editor.quiz.score": { he: "{correct}/{total} נכון", en: "{correct}/{total} correct" },

    "lomda-v3-editor.accordion.untitled": { he: "ללא כותרת", en: "Untitled" },
    "lomda-v3-editor.accordion.heading-placeholder": { he: "כותרת", en: "Title" },
    "lomda-v3-editor.accordion.description-placeholder": { he: "תיאור קצר", en: "Short description" },
    "lomda-v3-editor.accordion.title-placeholder": { he: "כותרת הסעיף", en: "Section title" },
    "lomda-v3-editor.accordion.body-placeholder": { he: "תוכן הסעיף...", en: "Section content..." },
    "lomda-v3-editor.accordion.add": { he: "הוספת סעיף", en: "Add section" },
    "lomda-v3-editor.accordion.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.accordion.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.accordion.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.accordion.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.accordion.appearance-number-color": { he: "צבע המספר", en: "Number color" },
    "lomda-v3-editor.accordion.appearance-number-bg": { he: "רקע המספר", en: "Number background" },
    "lomda-v3-editor.accordion.appearance-checkmark": { he: "הצגת סימן וי", en: "Show checkmark" },
    "lomda-v3-editor.accordion.manage-items": { he: "ניהול פריטים", en: "Manage items" },

    "lomda-v3-editor.tabs.tab-label": { he: "טאב {number}", en: "Tab {number}" },
    "lomda-v3-editor.tabs.label-placeholder": { he: "שם הטאב", en: "Tab name" },
    "lomda-v3-editor.tabs.body-placeholder": { he: "תוכן הטאב...", en: "Tab content..." },
    "lomda-v3-editor.tabs.content-placeholder": { he: "תוכן הטאב...", en: "Tab content..." },
    "lomda-v3-editor.tabs.icon": { he: "סמל", en: "Icon" },
    "lomda-v3-editor.tabs.add": { he: "הוספת טאב", en: "Add tab" },
    "lomda-v3-editor.tabs.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.tabs.tab-appearance": {
        he: "עיצוב הטאב",
        en: "Tab appearance",
    },
    "lomda-v3-editor.tabs.appearance-tab-bg": { he: "רקע הטאב", en: "Tab background" },
    "lomda-v3-editor.tabs.appearance-active-tab-bg": { he: "רקע טאב פעיל", en: "Active tab background" },
    "lomda-v3-editor.tabs.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.tabs.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.tabs.appearance-show-icons": { he: "הצגת אייקונים", en: "Show tab icons" },
    "lomda-v3-editor.tabs.appearance-icon-color": { he: "צבע האייקון", en: "Icon color" },
    "lomda-v3-editor.tabs.appearance-active-icon-color": { he: "צבע אייקון פעיל", en: "Active icon color" },
    "lomda-v3-editor.tabs.manage-tabs": { he: "ניהול טאבים", en: "Manage tabs" },
    "lomda-v3-editor.tabs.select-icon": { he: "בחירת אייקון", en: "Select icon" },

    "lomda-v3-editor.checklist.title-placeholder": { he: "כותרת הרשימה", en: "Checklist title" },
    "lomda-v3-editor.checklist.item-placeholder": { he: "משימה...", en: "Task..." },
    "lomda-v3-editor.checklist.add": { he: "הוספת משימה", en: "Add task" },
    "lomda-v3-editor.checklist.progress": { he: "{done}/{total} הושלמו", en: "{done}/{total} done" },
    "lomda-v3-editor.checklist.done": { he: "{done}/{total} הושלמו", en: "{done}/{total} done" },
    "lomda-v3-editor.checklist.untitled": { he: "רשימת משימות", en: "Checklist" },
    "lomda-v3-editor.checklist.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.checklist.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.checklist.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.checklist.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.checklist.appearance-number-color": { he: "צבע המספר", en: "Number color" },
    "lomda-v3-editor.checklist.appearance-number-bg": { he: "רקע המספר", en: "Number background" },
    "lomda-v3-editor.checklist.appearance-check-color": { he: "צבע הסימון", en: "Check color" },
    "lomda-v3-editor.checklist.manage-items": { he: "ניהול משימות", en: "Manage items" },

    "lomda-v3-editor.flip.front": { he: "צד קדמי", en: "Front" },
    "lomda-v3-editor.flip.back": { he: "צד אחורי", en: "Back" },
    "lomda-v3-editor.flip.tap": { he: "הקליקו להיפוך", en: "Tap to flip" },
    "lomda-v3-editor.flip.card-n": { he: "כרטיס {number}", en: "Card {number}" },
    "lomda-v3-editor.flip.front-placeholder": { he: "תוכן הצד הקדמי...", en: "Front content..." },
    "lomda-v3-editor.flip.back-placeholder": { he: "תוכן הצד האחורי...", en: "Back content..." },
    "lomda-v3-editor.flip.add": { he: "הוספת כרטיס", en: "Add card" },
    "lomda-v3-editor.flip.flip": { he: "היפוך", en: "Flip" },
    "lomda-v3-editor.flip.flip-back": { he: "חזרה", en: "Flip back" },
    "lomda-v3-editor.flip.title-placeholder": { he: "כותרת", en: "Title" },
    "lomda-v3-editor.flip.description-placeholder": { he: "תיאור קצר", en: "Short description" },
    "lomda-v3-editor.flip.front-title": { he: "כותרת הכרטיס", en: "Card title" },
    "lomda-v3-editor.flip.front-subtitle": { he: "כותרת משנה", en: "Subtitle" },
    "lomda-v3-editor.flip.back-title": { he: "כותרת הצד האחורי", en: "Back title" },
    "lomda-v3-editor.flip.columns": { he: "עמודות", en: "Columns" },
    "lomda-v3-editor.flip.columns-n": { he: "{count} עמודות", en: "{count} columns" },
    "lomda-v3-editor.flip.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.flip.appearance-card-bg": { he: "רקע הכרטיס", en: "Card background" },
    "lomda-v3-editor.flip.appearance-border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.flip.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.flip.appearance-icon-color": { he: "צבע האייקון", en: "Icon color" },
    "lomda-v3-editor.flip.appearance-icon-bg": { he: "רקע האייקון", en: "Icon background" },
    "lomda-v3-editor.flip.appearance-checkmark": { he: "הצגת סימן וי", en: "Show checkmark" },
    "lomda-v3-editor.flip.manage-cards": { he: "ניהול כרטיסים", en: "Manage cards" },
    "lomda-v3-editor.flip.card-title-ph": { he: "כותרת", en: "Title" },
    "lomda-v3-editor.flip.card-description-ph": { he: "תיאור", en: "Description" },
    "lomda-v3-editor.flip.card-back-ph": { he: "הקלידו תוכן אחורי…", en: "Type back content…" },
    "lomda-v3-editor.flip.delete-card": { he: "מחיקת כרטיס", en: "Delete card" },
    "lomda-v3-editor.flip.select-icon": { he: "בחירת אייקון", en: "Select icon" },

    "lomda-v3-editor.how-to.title-placeholder": { he: "כותרת השלב", en: "Step title" },
    "lomda-v3-editor.how-to.body-placeholder": { he: "תיאור השלב...", en: "Step description..." },
    "lomda-v3-editor.how-to.add": { he: "הוספת שלב", en: "Add step" },
    "lomda-v3-editor.how-to.add-image": { he: "הוספת תמונה", en: "Add image" },
    "lomda-v3-editor.how-to.replace-image": { he: "החלפת תמונה", en: "Replace image" },

    "lomda-v3-editor.scenario.scene-n": { he: "סצנה {number}", en: "Scene {number}" },
    "lomda-v3-editor.scenario.scene-name": { he: "שם הסצנה", en: "Scene name" },
    "lomda-v3-editor.scenario.dialogue-placeholder": { he: "כתבו דיאלוג שמבקש מהלומד לבחור...", en: "Add a dialogue that asks the learner to make a choice..." },
    "lomda-v3-editor.scenario.choice-placeholder": { he: "טקסט הבחירה", en: "Choice text" },
    "lomda-v3-editor.scenario.feedback-placeholder": { he: "משוב לבחירה זו (אופציונלי)", en: "Feedback for this choice (optional)" },
    "lomda-v3-editor.scenario.quality-good": { he: "בחירה טובה", en: "Good choice" },
    "lomda-v3-editor.scenario.quality-average": { he: "בחירה בינונית", en: "Average choice" },
    "lomda-v3-editor.scenario.quality-poor": { he: "בחירה גרועה", en: "Poor choice" },
    "lomda-v3-editor.scenario.goto": { he: "מעבר אל", en: "Go to" },
    "lomda-v3-editor.scenario.goto-next": { he: "הסצנה הבאה", en: "Next scene" },
    "lomda-v3-editor.scenario.goto-end": { he: "סיום התרחיש", en: "End scenario" },
    "lomda-v3-editor.scenario.add-choice": { he: "הוספת בחירה", en: "Add choice" },
    "lomda-v3-editor.scenario.add-scene": { he: "הוספת סצנה", en: "Add scene" },
    "lomda-v3-editor.scenario.continue": { he: "המשך", en: "Continue" },
    "lomda-v3-editor.scenario.done": { he: "התרחיש הושלם", en: "Scenario complete" },
    "lomda-v3-editor.scenario.restart": { he: "התחלה מחדש", en: "Start over" },

    "lomda-v3-editor.card-sorting.categories": { he: "קטגוריות", en: "Categories" },
    "lomda-v3-editor.card-sorting.cards": { he: "כרטיסים", en: "Cards" },
    "lomda-v3-editor.card-sorting.category-placeholder": { he: "שם הקטגוריה", en: "Category name" },
    "lomda-v3-editor.card-sorting.category-n": { he: "קטגוריה {number}", en: "Category {number}" },
    "lomda-v3-editor.card-sorting.card-placeholder": { he: "טקסט הכרטיס", en: "Card text" },
    "lomda-v3-editor.card-sorting.correct-category": { he: "הקטגוריה הנכונה", en: "Correct category" },
    "lomda-v3-editor.card-sorting.add-category": { he: "הוספת קטגוריה", en: "Add category" },
    "lomda-v3-editor.card-sorting.add-card": { he: "הוספת כרטיס", en: "Add card" },
    "lomda-v3-editor.card-sorting.choose": { he: "בחרו קטגוריה", en: "Choose category" },
    "lomda-v3-editor.card-sorting.check": { he: "בדיקה", en: "Check" },
    "lomda-v3-editor.card-sorting.retry": { he: "ניסיון חוזר", en: "Try again" },
    "lomda-v3-editor.card-sorting.score": { he: "{correct}/{total} נכון", en: "{correct}/{total} correct" },
    "lomda-v3-editor.card-sorting.submit": { he: "שליחה", en: "Submit" },
    "lomda-v3-editor.card-sorting.pool-empty": { he: "כל הכרטיסים מוינו", en: "All cards sorted" },
    "lomda-v3-editor.cards.gap": { he: "מרווח", en: "Gap" },
    "lomda-v3-editor.cards.border": { he: "מסגרת", en: "Border" },
    "lomda-v3-editor.cards.background": { he: "רקע", en: "Background" },
    "lomda-v3-editor.cards.transparent": { he: "שקוף", en: "Transparent" },
    "lomda-v3-editor.card-sorting.appearance": { he: "עיצוב", en: "Appearance" },
    "lomda-v3-editor.card-sorting.appearance-border-width": { he: "עובי מסגרת", en: "Border width" },
    "lomda-v3-editor.card-sorting.manage-categories": { he: "ניהול קטגוריות", en: "Manage categories" },
    "lomda-v3-editor.card-sorting.manage-cards": { he: "ניהול כרטיסים", en: "Manage cards" },
    "lomda-v3-editor.card-sorting.title-placeholder": { he: "כותרת התרגיל", en: "Activity title" },
    "lomda-v3-editor.card-sorting.description-placeholder": { he: "הוראות / תיאור", en: "Instructions / description" },

    "lomda-v3-editor.walkthrough.editor-title": { he: "עורך הדרכה", en: "Walkthrough editor" },
    "lomda-v3-editor.walkthrough.get-started": { he: "התחילו עם הדרכה", en: "Get started with walkthrough" },
    "lomda-v3-editor.walkthrough.get-started-desc": { he: "צרו מדריך אינטראקטיבי שלב-אחר-שלב שעוזר ללומדים להבין תהליכים", en: "Create a step-by-step, interactive guide that helps learners explore and understand processes" },
    "lomda-v3-editor.walkthrough.start-creating": { he: "התחלת יצירה", en: "Start creating" },
    "lomda-v3-editor.walkthrough.edit-walkthrough": { he: "עריכת ההדרכה", en: "Edit walkthrough" },
    "lomda-v3-editor.walkthrough.n-steps": { he: "{n} שלבים", en: "{n} steps" },
    "lomda-v3-editor.walkthrough.start-with": { he: "באיזה שלב תרצו להתחיל?", en: "Which step would you like to start with?" },
    "lomda-v3-editor.walkthrough.info-step": { he: "שלב מידע", en: "Info step" },
    "lomda-v3-editor.walkthrough.info-step-desc": { he: "הוספת טקסט והנחיות שמסבירים מה הלומד צריך לדעת", en: "Add text-based context or instructions to explain what learners need to know" },
    "lomda-v3-editor.walkthrough.image-step": { he: "שלב תמונה", en: "Image step" },
    "lomda-v3-editor.walkthrough.image-step-desc": { he: "העלאת תמונה והוספת נקודות לחיצה לחקירה ויזואלית", en: "Upload an image and add clickable hotspots to help learners explore things visually" },
    "lomda-v3-editor.walkthrough.image-info-step": { he: "שלב תמונה (מידע)", en: "Image (info only)" },
    "lomda-v3-editor.walkthrough.image-info-step-desc": { he: "תמונה עם אזורים מודגשים להצגה בלבד — ללא צורך בלחיצה, מתקדמים עם כפתור", en: "An image with highlighted areas to view only — no clicking, advance with a button" },
    "lomda-v3-editor.walkthrough.change-image": { he: "החלפת תמונה", en: "Change image" },
    "lomda-v3-editor.walkthrough.alt-text": { he: "טקסט חלופי", en: "Alt text" },
    "lomda-v3-editor.walkthrough.grid": { he: "רשת עזר", en: "Grid" },
    "lomda-v3-editor.walkthrough.new-step": { he: "שלב חדש", en: "New step" },
    "lomda-v3-editor.walkthrough.step-name": { he: "שם השלב", en: "Step name" },
    "lomda-v3-editor.walkthrough.step-name-placeholder": { he: "הזינו שם לשלב", en: "Enter step name" },
    "lomda-v3-editor.walkthrough.step-n": { he: "שלב {n}", en: "Step {n}" },
    "lomda-v3-editor.walkthrough.button-text": { he: "טקסט הכפתור", en: "Button text" },
    "lomda-v3-editor.walkthrough.add-button": { he: "הוספת כפתור", en: "Add button" },
    "lomda-v3-editor.walkthrough.next-step": { he: "השלב הבא", en: "Next step" },
    "lomda-v3-editor.walkthrough.no-navigate": { he: "ללא ניווט", en: "Don't navigate" },
    "lomda-v3-editor.walkthrough.hotspot-hint": { he: "לחצו על התמונה כדי להוסיף נקודת לחיצה", en: "Click on the image to add a hotspot" },
    "lomda-v3-editor.walkthrough.hotspot": { he: "נקודה", en: "Hotspot" },
    "lomda-v3-editor.walkthrough.hotspot-label": { he: "תווית הנקודה", en: "Hotspot label" },
    "lomda-v3-editor.walkthrough.hotspot-draw-hint": { he: "גררו על התמונה כדי לסמן אזור להדגשה", en: "Drag on the image to highlight an area" },
    "lomda-v3-editor.walkthrough.hotspot-draw-hint-info": { he: "גררו כדי לסמן אזורים. הלומד רק צופה בהם וממשיך בלחיצה על 'המשך'", en: "Drag to highlight areas. Learners just view them, then click Continue" },
    "lomda-v3-editor.walkthrough.mode-interactive": { he: "אינטראקטיבי", en: "Interactive" },
    "lomda-v3-editor.walkthrough.mode-info": { he: "מידע בלבד", en: "Info only" },
    "lomda-v3-editor.walkthrough.hotspot-text": { he: "טקסט ההסבר...", en: "Callout text..." },
    "lomda-v3-editor.walkthrough.hotspot-settings": { he: "הגדרות נקודה", en: "Hotspot settings" },
    "lomda-v3-editor.walkthrough.link-to": { he: "קישור אל", en: "Link to" },
    "lomda-v3-editor.walkthrough.text-position": { he: "מיקום תיבת הטקסט", en: "Text box position" },
    "lomda-v3-editor.walkthrough.highlight-color": { he: "צבע ההדגשה", en: "Highlight color" },
    "lomda-v3-editor.walkthrough.custom-color": { he: "צבע מותאם אישית", en: "Custom color" },
    "lomda-v3-editor.walkthrough.pos-auto": { he: "אוטומטי", en: "Auto" },
    "lomda-v3-editor.walkthrough.pos-top": { he: "למעלה", en: "Top" },
    "lomda-v3-editor.walkthrough.pos-bottom": { he: "למטה", en: "Bottom" },
    "lomda-v3-editor.walkthrough.pos-start": { he: "התחלה", en: "Start" },
    "lomda-v3-editor.walkthrough.pos-end": { he: "סוף", en: "End" },
    "lomda-v3-editor.walkthrough.pos-none": { he: "ללא תיבת טקסט", en: "No text box" },
    "lomda-v3-editor.walkthrough.previous": { he: "השלב הקודם", en: "Previous step" },
    "lomda-v3-editor.walkthrough.next": { he: "הבא", en: "Next" },
    "lomda-v3-editor.walkthrough.restart": { he: "התחלה מחדש", en: "Restart" },
    "lomda-v3-editor.walkthrough.step-counter": { he: "שלב {current} מתוך {total}", en: "Step {current} of {total}" },
    "lomda-v3-editor.walkthrough.empty-play": { he: "אין עדיין שלבים", en: "No steps yet" },
    "lomda-v3-editor.walkthrough.no-image": { he: "אין תמונה", en: "No image" },
    "lomda-v3-editor.walkthrough.edit": { he: "עריכה", en: "Edit" },
    "lomda-v3-editor.walkthrough.preview": { he: "תצוגה מקדימה", en: "Preview" },

    "lomda-v3-editor.coming-soon": { he: "בקרוב", en: "Soon" },

    "lomda-v3-editor.add.create-ai": { he: "יצירה עם AI", en: "Create with AI" },
    "lomda-v3-editor.add.more": { he: "עוד", en: "More" },

    "lomda-v3-editor.block.change-layout": { he: "שינוי פריסה", en: "Change layout" },

    "lomda-v3-editor.quote.placeholder": { he: "הזינו ציטוט...", en: "Enter a quote..." },
    "lomda-v3-editor.quote.author-photo": { he: "תמונת מחבר", en: "Author photo" },
    "lomda-v3-editor.quote.author-name": { he: "שם המחבר", en: "Author name" },
    "lomda-v3-editor.quote.author-title": { he: "תפקיד המחבר", en: "Author title" },
    "lomda-v3-editor.quote.replace-photo": { he: "החלפת תמונה", en: "Replace photo" },

    "lomda-v3-editor.link.title": { he: "כותרת", en: "Title" },
    "lomda-v3-editor.link.description": { he: "תיאור", en: "Description" },
    "lomda-v3-editor.link.url": { he: "כתובת URL", en: "URL" },
    "lomda-v3-editor.link.thumbnail": { he: "תמונה ממוזערת", en: "Thumbnail" },
    "lomda-v3-editor.link.replace-thumbnail": { he: "החלפת תמונה", en: "Replace thumbnail" },
    "lomda-v3-editor.link.visit": { he: "מעבר לאתר", en: "Visit website" },

    "lomda-v3-editor.canvas.page-title": { he: "כותרת העמוד", en: "Page title" },
    "lomda-v3-editor.canvas.untitled": { he: "עמוד ללא שם", en: "Untitled page" },
    "lomda-v3-editor.pages.drag": { he: "גרירת עמוד", en: "Drag page" },
    "lomda-v3-editor.empty": { he: "", en: "" },

    "lomda-v3-editor.canvas.no-pages": {
        he: "אין עדיין עמודים. הוסיפו עמוד כדי להתחיל.",
        en: "No pages yet. Add a page to get started.",
    },
    "lomda-v3-editor.canvas.empty-page": {
        he: "העמוד ריק. הוסיפו בלוק כדי להתחיל.",
        en: "This page is empty. Add a block to get started.",
    },
    "lomda-v3-editor.canvas.drop-here": {
        he: "גררו ושחררו בלוק כאן",
        en: "Drag & drop block here",
    },
    "lomda-v3-editor.preview.part": {
        he: "חלק {current} מתוך {total}",
        en: "Part {current} of {total}",
    },
    "lomda-v3-editor.preview.continue": { he: "המשך", en: "Continue" },
    "lomda-v3-editor.preview.finish": { he: "סיום", en: "Finish" },
    "lomda-v3-editor.preview.back": { he: "חזרה", en: "Back" },
    "lomda-v3-editor.preview.exit": {
        he: "חזרה לעריכה",
        en: "Back to editor",
    },
    "lomda-v3-editor.preview.fullscreen": { he: "מסך מלא", en: "Full screen" },
    "lomda-v3-editor.preview.exit-fullscreen": { he: "יציאה ממסך מלא", en: "Exit full screen" },
    "lomda-v3-editor.preview.contents": { he: "תוכן העניינים", en: "Contents" },
    "lomda-v3-editor.share.title": { he: "שיתוף", en: "Share" },
    "lomda-v3-editor.share.description": {
        he: "שתפו את הלומדה באמצעות קישור ציבורי",
        en: "Share this lomda with a public link",
    },
    "lomda-v3-editor.share.visibility-label": { he: "נראות", en: "Visibility" },
    "lomda-v3-editor.share.public": { he: "ציבורי", en: "Public" },
    "lomda-v3-editor.share.private": { he: "פרטי", en: "Private" },
    "lomda-v3-editor.share.link-label": {
        he: "קישור לשיתוף",
        en: "Share link",
    },
    "lomda-v3-editor.share.copy": { he: "העתקת קישור", en: "Copy link" },
    "lomda-v3-editor.share.copied": { he: "הקישור הועתק", en: "Link copied" },
    "lomda-v3-editor.share.private-warning": {
        he: "הפכו את הלומדה לציבורית כדי לשתף את הקישור",
        en: "Make the lomda public to share the link",
    },
    "lomda-v3-editor.share.embed-label": { he: "קוד הטמעה", en: "Embed code" },
    "lomda-v3-editor.share.copy-embed": {
        he: "העתקת קוד ההטמעה",
        en: "Copy embed code",
    },
    "lomda-v3-editor.share.embed-copied": {
        he: "קוד ההטמעה הועתק",
        en: "Embed code copied",
    },
    "lomda-v3-editor.share.private-embed-warning": {
        he: "הפכו את הלומדה לציבורית כדי להטמיע אותה",
        en: "Make the lomda public to embed it",
    },
    "lomda-v3-editor.share.embed-description": {
        he: "הדביקו את הקוד באתר כדי להציג בו את הלומדה",
        en: "Paste this code into a website to show the lomda there",
    },
    "lomda-v3-editor.share.public-label": {
        he: "לומדה ציבורית",
        en: "Public lomda",
    },
    "lomda-v3-editor.share.public-hint": {
        he: "כל מי שיש לו את הקישור יכול לצפות בה",
        en: "Anyone with the link can view it",
    },
    "lomda-v3-editor.preview.untitled-lomda": {
        he: "לומדה ללא שם",
        en: "Untitled lomda",
    },
    "lomda-v3-editor.preview.empty": {
        he: "אין עדיין תוכן להצגה.",
        en: "Nothing to preview yet.",
    },
    "lomda-v3-editor.preview.completed": {
        he: "הלומדה הושלמה 🎉",
        en: "Lesson Completed 🎉",
    },
    "lomda-v3-editor.preview.completed-desc": {
        he: "כל הכבוד! סיימתם לעבור על הלומדה.",
        en: "Great job — you've reached the end of this lesson.",
    },
    "lomda-v3-editor.preview.restart": { he: "התחלה מחדש", en: "Restart" },
    "funnels.ai-create-lomda.step-1.title": {
        he: "יש לבחור קובץ לומדה",
        en: "Choose a lomda file",
    },
    "funnels.ai-create-lomda.step-1.description": {
        he: "יש לבחור קובץ לומדה כדי שהAI יוכל ליצור לומדה על סמך תוכן העמודים שבו, בשפת תסריט שתבחרו בשלב הבא.",
        en: "Choose a lomda file so the AI can generate a lomda based on its pages, in the script language you select next.",
    },
    "funnels.ai-create-lomda.step-1.loading": {
        he: "טוען...",
        en: "Loading...",
    },
    "funnels.ai-create-lomda.step-1.upload-file.subtitle": {
        he: "קבצים נתמכים: PDF",
        en: "Supported files: PDF",
    },
    "funnels.ai-create-lomda.step-2.title": {
        he: "הגדרות מיתוג הלומדה",
        en: "Lomda branding settings",
    },
    "funnels.ai-create-lomda.step-2.loading": {
        he: "טוען...",
        en: "Loading...",
    },
    "funnels.ai-create-lomda.step-2.colors.primary-label": {
        he: "צבע ראשי",
        en: "Primary color",
    },
    "funnels.ai-create-lomda.step-2.colors.secondary-label": {
        he: "צבע משני",
        en: "Secondary color",
    },
    "funnels.ai-create-lomda.step-2.colors.description": {
        he: "נא לבחור צמד צבעים לשימוש בתבניות הלומדה. (טיפ: צבעי המיתוג שלכם)",
        en: "Choose a pair of colors to use in the lomda templates. (Tip: your brand colors)",
    },
    "funnels.ai-create-lomda.step-2.logo.label": { he: "לוגו", en: "Logo" },
    "funnels.ai-create-lomda.step-2.logo.description": {
        he: "אפשר להעלות לוגו כדי שיהיה מוצג בתבניות הלומדה.",
        en: "You can upload a logo to display in the lomda templates.",
    },
    "funnels.ai-create-lomda.step-3.upload-logo.subtitle": {
        he: "קבצים נתמכים: PNG, SVG, JPG, WEBP",
        en: "Supported files: PNG, SVG, JPG, WEBP",
    },
    "funnels.ai-create-lomda.step-2.template.label": {
        he: "תבנית סצנה",
        en: "Scene template",
    },
    "funnels.ai-create-lomda.step-2.template.placeholder": {
        he: "בחרו תבנית",
        en: "Choose a template",
    },
    "funnels.ai-create-lomda.step-2.template.description": {
        he: "תבנית המגדירה את מראה הסצנה",
        en: "Template that defines the scene layout",
    },
    "funnels.ai-create-lomda.step-3.title": {
        he: "הגדרות תסריט הלומדה",
        en: "Lomda script settings",
    },
    "funnels.ai-create-lomda.step-3.language.label": {
        he: "שפה",
        en: "Language",
    },
    "funnels.ai-create-lomda.step-3.language.placeholder": {
        he: "בחרו את השפה הרצויה",
        en: "Choose the desired language",
    },
    "funnels.ai-create-lomda.step-3.language.description": {
        he: "השפה שבה הAI יכתוב את התסריט ללומדה. על סמך תוכן הקובץ שבחרתם.",
        en: "The language the AI will use for the lomda script, based on the selected file.",
    },
    "funnels.ai-create-lomda.step-3.script-length.label": {
        he: "אורך התסריט",
        en: "Script length",
    },
    "funnels.ai-create-lomda.step-3.script-length.placeholder": {
        he: "בחרו את אורך התסריט",
        en: "Choose script length",
    },
    "funnels.ai-create-lomda.step-3.script-length.description": {
        he: "אורך התסריט שהAI יכתוב לכל סצנה בלומדה.",
        en: "How long the AI script should be for each scene.",
    },
    "funnels.ai-create-lomda.step-3.voice-tone.label": {
        he: "טון הדיבור",
        en: "Voice tone",
    },
    "funnels.ai-create-lomda.step-3.voice-tone.placeholder": {
        he: "בחרו את טון הדיבור",
        en: "Choose voice tone",
    },
    "funnels.ai-create-lomda.step-3.voice-tone.description": {
        he: "סגנון הדיבור שהAI יכתוב את תסריט הסצנות בלומדה.",
        en: "The speaking style for the AI-written script.",
    },
    "funnels.ai-create-lomda.step-4.title": {
        he: "הגדרות אחסון",
        en: "Storage settings",
    },
    "funnels.ai-create-lomda.step-4.loading": {
        he: "טוען...",
        en: "Loading...",
    },
    "funnels.ai-create-lomda.step-4.name.label": {
        he: "שם הלומדה",
        en: "Lomda name",
    },
    "funnels.ai-create-lomda.step-4.name.placeholder": {
        he: "שם פריט הלומדה",
        en: "Lomda item name",
    },
    "funnels.ai-create-lomda.step-4.name.description": {
        he: "פריט הלומדה יופיע תחת השם הזה באזור האחסון.",
        en: "The lomda item will appear under this name in storage.",
    },
    "funnels.ai-create-lomda.step-4.folder.label": {
        he: "תיקייה",
        en: "Folder",
    },
    "funnels.ai-create-lomda.step-4.folder.description": {
        he: "תיקייה בה תוכלו למצוא את הלומדה החדשה באזור האחסון",
        en: "Folder where you'll find the new lomda in storage",
    },
    "funnels.ai-create-lomda.step-5.title": {
        he: "הלומדה שלך כמעט מוכנה",
        en: "Your lomda is almost ready",
    },
    "funnels.ai-create-lomda.step-5.generating": {
        he: "תהליך הייצור יכול לקחת כמה דקות, תלוי בגודל הקובץ ובכמות הסצנות בלומדה.",
        en: "Generation can take a few minutes, depending on file size and number of scenes.",
    },
    "funnels.ai-presentation.step1": { he: "נושא", en: "Topic" },
    "funnels.ai-presentation.step2": { he: "אורך", en: "Length" },
    "funnels.ai-presentation.step3": { he: "מיתוג", en: "Branding" },
    "funnels.ai-presentation.step4": { he: "שפה", en: "Language" },
    "funnels.ai-presentation.step5": { he: "ייצור", en: "Generation" },
    "funnels.ai-presentation.step-2.slides-length.1-5": {
        he: "1-5",
        en: "1-5",
    },
    "funnels.ai-presentation.step-2.slides-length.5-10": {
        he: "5-10",
        en: "5-10",
    },
    "funnels.ai-presentation.step-2.slides-length.10-20": {
        he: "10-20",
        en: "10-20",
    },
    "funnels.ai-presentation.step-2.slides-length.20": { he: "20+", en: "20+" },
    "funnels.ai-create-presentation.step-1.title": {
        he: "איך תרצו ליצור את המצגת?",
        en: "How would you like to create the presentation?",
    },
    "funnels.ai-create-presentation.step-1.tab.prompt": {
        he: "יצירה מפרומפט",
        en: "Create from prompt",
    },
    "funnels.ai-create-presentation.step-1.tab.file": {
        he: "יצירה מקובץ",
        en: "Create from file",
    },
    "funnels.ai-create-presentation.step-1.prompt.placeholder": {
        he: "רשמו את נושא המצגת...",
        en: "Write the presentation topic...",
    },
    "funnels.ai-create-presentation.step-1.notes.label": {
        he: "הערות:",
        en: "Notes:",
    },
    "funnels.ai-create-presentation.step-1.prefill.course": {
        he: " בקורס {courseName}",
        en: " in course {courseName}",
    },
    "funnels.ai-create-presentation.step-2.title": {
        he: "כמה שקופיות יהיו במצגת?",
        en: "How many slides will the presentation have?",
    },
    "funnels.ai-create-presentation.step-2.description": {
        he: "בחרו את טווח מספר השקופיות שתרצו במצגת שלכם",
        en: "Choose the range of slides you want in your presentation",
    },
    "funnels.ai-create-presentation.step-3.title": {
        he: "באיזה צבעים לבנות את המצגת?",
        en: "What colors should the presentation use?",
    },
    "funnels.ai-create-presentation.step-3.description": {
        he: "בחרו את הצבע העיקרי עבור עיצוב המצגת שלכם",
        en: "Choose the primary color for your presentation design",
    },
    "funnels.ai-create-presentation.step-4.title": {
        he: "באיזה שפה לבנות את המצגת?",
        en: "In what language should the presentation be built?",
    },
    "funnels.ai-create-presentation.step-4.language.placeholder": {
        he: "בחרו את השפה בה מדברים בסרטונים",
        en: "Choose the language spoken in the videos",
    },
    "funnels.ai-create-presentation.step-5.title": {
        he: "המצגת שלך כמעט מוכנה",
        en: "Your presentation is almost ready",
    },
    "funnels.ai-create-presentation.step-5.description": {
        he: "תהליך הייצור יכול לקחת כמה דקות, תלוי בגודל הקובץ ובכמות השקופיות.",
        en: "Generation can take a few minutes, depending on file size and slide count.",
    },
    "funnels.ai-syllabus.step1": { he: "נושא", en: "Topic" },
    "funnels.ai-syllabus.step2": { he: "קהל יעד", en: "Target audience" },
    "funnels.ai-syllabus.step3": { he: "אורך", en: "Length" },
    "funnels.ai-syllabus.step4": { he: "שפה", en: "Language" },
    "funnels.ai-syllabus.step5": { he: "ייצור", en: "Generation" },
    "funnels.ai-syllabus.step-3.course-length.1": {
        he: "עד שעה",
        en: "Up to 1 hour",
    },
    "funnels.ai-syllabus.step-3.course-length.1-5": {
        he: "1-5 שעות",
        en: "1-5 hours",
    },
    "funnels.ai-syllabus.step-3.course-length.5-20": {
        he: "5-20 שעות",
        en: "5-20 hours",
    },
    "funnels.ai-syllabus.step-3.course-length.20": {
        he: "20+ שעות",
        en: "20+ hours",
    },
    "funnels.ai-create-syllabus.step-1.title": {
        he: "מהו נושא הקורס שלך?",
        en: "What is your course topic?",
    },
    "funnels.ai-create-syllabus.step-1.description": {
        he: 'תתארו את הקורס שלכם בכמה מילים, לדוגמא: "קורס עקרונות שפות תכנות"',
        en: 'Describe your course in a few words, e.g., "Principles of Programming Languages"',
    },
    "funnels.ai-create-syllabus.step-2.title": {
        he: "מי הקהל היעד של הקורס?",
        en: "Who is the target audience for the course?",
    },
    "funnels.ai-create-syllabus.step-2.description": {
        he: 'כאן תוסיפו למי מיועד הקורס, לדוגמא: "מתכנתים מתחילים בגיל צעיר ללא ניסיון עבודה בתחום"',
        en: 'Describe who the course is for, e.g., "Beginner developers with no industry experience"',
    },
    "funnels.ai-create-syllabus.step-3.title": {
        he: "מה אורך הקורס?",
        en: "What is the course length?",
    },
    "funnels.ai-create-syllabus.step-4.title": {
        he: "באיזה שפה לבנות את הסילאבוס?",
        en: "In what language should the syllabus be built?",
    },
    "funnels.ai-create-syllabus.step-4.language.placeholder": {
        he: "בחרו את השפה בה מדברים בסרטונים",
        en: "Choose the language spoken in the videos",
    },
    "funnels.ai-create-syllabus.step-5.title": {
        he: "הסילאבוס שלך כמעט מוכן",
        en: "Your syllabus is almost ready",
    },
    "funnels.ai-create-syllabus.step-5.description": {
        he: "תהליך הייצור לוקח בדרך כלל 5-10 שניות",
        en: "Generation usually takes 5-10 seconds",
    },
    "funnels.ai-video-upload.step1": {
        he: "בחירת סרטונים",
        en: "Select videos",
    },
    "funnels.ai-video-upload.step2": {
        he: "אפשרויות עיבוד",
        en: "Processing options",
    },
    "funnels.ai-video-upload.step4": { he: "אחסון", en: "Storage" },
    "funnels.ai-video-upload.step5": { he: "העלאה", en: "Upload" },
    "funnels.ai-upload-videos.step-1.title": {
        he: "בחרו סרטונים להעלאה",
        en: "Choose videos to upload",
    },
    "funnels.ai-upload-videos.step-1.description-not-eligible": {
        he: "נגמרה לכם היתרה. הוסיפו יתרה כדי להעלות קבצים נוספים.",
        en: "Your balance has run out. Add funds to upload more files.",
    },
    "funnels.ai-upload-videos.step-1.drop-here": {
        he: "שחררו את הקבצים",
        en: "Drop the files",
    },
    "funnels.ai-upload-videos.step-1.upgrade": {
        he: "הוספת יתרה",
        en: "Add funds",
    },
    "funnels.ai-upload-videos.step-1.upload-device": {
        he: "העלאת סרטונים מהמכשיר",
        en: "Upload videos from device",
    },
    "funnels.ai-upload-videos.step-1.upload-folder": {
        he: "העלאת תיקייה מהמכשיר",
        en: "Upload a folder from device",
    },
    "funnels.ai-upload-videos.step-1.upload-folder-description": {
        he: "מבנה התיקייה מהמכשיר יישמר כפי שהוא",
        en: "The folder structure from your device will be preserved",
    },
    "funnels.ai-upload-videos.step-1.import-gdrive": {
        he: "ייבוא מגוגל דרייב",
        en: "Import from Google Drive",
    },
    "funnels.ai-upload-videos.step-1.import-vimeo": {
        he: "ייבוא מוימאו",
        en: "Import from Vimeo",
    },
    "funnels.ai-upload-videos.coming-soon": { he: "בקרוב", en: "Coming soon" },
    "funnels.ai-upload-files.step-2.title": {
        he: "מה ברצונכם לעשות עם הסרטונים?",
        en: "What would you like to do with the videos?",
    },
    "funnels.ai-upload-files.step-2.description": {
        he: "בחרו מהאפשרויות שיש ל-AI שלנו להציע לשדרוג הסרטונים",
        en: "Choose from the AI options to enhance your videos",
    },
    "funnels.ai-upload-videos.step2.captions-option.title": {
        he: "יצירת כתוביות",
        en: "Generate captions",
    },
    "funnels.ai-upload-videos.step2.captions-option.description": {
        he: "ה-AI יצור כתוביות מדוייקות לסרטונים.",
        en: "AI will generate accurate captions for the videos.",
    },
    "funnels.ai-upload-videos.step2.interactions-option.title": {
        he: "יצירת שאלות אינטראקטיביות",
        en: "Generate interactive questions",
    },
    "funnels.ai-upload-videos.step2.interactions-option.description": {
        he: "ה-AI יצור שאלות אינטראקטיביות שיופיעו בתוך הנגן.",
        en: "AI will generate interactive questions that appear in the player.",
    },
    "funnels.ai-upload-videos.step-2.interactions.same-language-option": {
        he: "שפת הדיבור בסרטון",
        en: "Same as video's spoken language",
    },
    "funnels.ai-upload-videos.step-2.interactions.language.label": {
        he: "שפת השאלות",
        en: "Questions language",
    },
    "funnels.ai-upload-videos.step2.chapters-option.title": {
        he: "יצירת פרקים",
        en: "Generate chapters",
    },
    "funnels.ai-upload-videos.step2.chapters-option.description": {
        he: "ה-AI יצור פרקים לסרטונים שיופיעו בתוך הנגן.",
        en: "AI will generate chapters that appear in the player.",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.title": {
        he: "הוספה לידע של צ׳אטבוט",
        en: "Add to chatbot knowledge",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.description": {
        he: "הסרטון יתווסף אוטומטית לבסיס הידע של הצ׳אטבוטים שתבחרו.",
        en: "The video will be added automatically to the knowledge base of the chatbots you choose.",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.search-placeholder":
        {
            he: "חיפוש צ׳אטבוטים לפי שם...",
            en: "Search chatbots by name...",
        },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.loading": {
        he: "טוען...",
        en: "Loading...",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.empty": {
        he: "לא נמצאו צ׳אטבוטים",
        en: "No chatbots found",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.selected-count": {
        he: "{count} צ׳אטבוטים נבחרו",
        en: "{count} selected",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.select-hint": {
        he: "בחרו לפחות צ׳אטבוט אחד",
        en: "Select at least one chatbot",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.hide-reference": {
        he: "הסתרת מקור ידע",
        en: "Hide knowledge source",
    },
    "funnels.ai-upload-videos.step2.chatbot-knowledge-option.untitled-chatbot": {
        he: "צ׳אטבוט ללא שם",
        en: "Untitled chatbot",
    },
    "funnels.ai-upload-videos.step2.translations-option.title": {
        he: "יצירת תרגומים",
        en: "Generate translations",
    },
    "funnels.ai-upload-videos.step2.translations-option.description": {
        he: "ה-AI יצור תרגומים בשפות שונות לכתוביות.",
        en: "AI will generate translations for captions in multiple languages.",
    },
    "funnels.ai-upload-videos.step2.dubbings-option.title": {
        he: "יצירת דיבובים",
        en: "Generate dubbings",
    },
    "funnels.ai-upload-videos.step2.dubbings-option.description": {
        he: "ה-AI יצור דיבובים בשפות שונות לסרטון.",
        en: "AI will generate dubbings in multiple languages for the video.",
    },
    "funnels.ai-upload-videos.step-2.error.missing-transcription-language": {
        he: "כדי להמשיך יש לבחור את השפה בה מדברים בסרטון ביצירת כתוביות",
        en: "To continue, select the spoken language for caption generation.",
    },
    "funnels.ai-upload-videos.step-2.preset-applied": {
        he: 'התבנית "{name}" הוחלה מהגדרות התיקייה',
        en: "Preset “{name}” applied from the folder settings",
    },
    "funnels.ai-upload-videos.step-2.preset-clear": {
        he: "איפוס",
        en: "Reset",
    },
    "funnels.ai-upload-videos.step-2.error.hotwords-format": {
        he: 'אנא ודאו שכל המילים בשדה המילים החמות מופרדות בפסיק, למשל: "dog, cat, bird"',
        en: 'Make sure hotwords are comma-separated, e.g., "dog, cat, bird".',
    },
    "funnels.ai-upload-videos.step-2.error.captions-required-for-advanced": {
        he: "כדי לייצר שאלות/פרקים/תרגומים/דיבובים חובה לייצר כתוביות",
        en: "To generate questions/chapters/translations/dubbings, captions are required.",
    },
    "funnels.ai-upload-videos.step-2.error.captions-required-for-interactions":
        {
            he: "כדי לייצר שאלות חובה לייצר כתוביות",
            en: "Captions are required to generate questions.",
        },
    "funnels.ai-upload-videos.step-2.error.captions-required-for-chapters": {
        he: "כדי לייצר פרקים חובה לייצר כתוביות",
        en: "Captions are required to generate chapters.",
    },
    "funnels.ai-upload-videos.step-2.error.captions-required-for-chatbot-knowledge":
        {
            he: "כדי להוסיף את הסרטון לידע של צ׳אטבוט חובה לייצר כתוביות",
            en: "Captions are required to add the video to a chatbot's knowledge.",
        },
    "funnels.ai-upload-videos.step-2.error.captions-required-for-translations":
        {
            he: "כדי לייצר תרגומים חובה לייצר כתוביות",
            en: "Captions are required to generate translations.",
        },
    "funnels.ai-upload-videos.step-2.error.translations-required-for-dubbings":
        {
            he: "כדי לייצר דיבובים חובה לייצר תרגומים",
            en: "Translations are required to generate dubbings.",
        },
    "funnels.ai-upload-videos.step-2.error.captions-translations-required-for-dubbings":
        {
            he: "כדי לייצר דיבובים חובה לייצר כתוביות ותרגומים",
            en: "Captions and translations are required to generate dubbings.",
        },
    "funnels.ai-upload-videos.step-2.error.translation-required-for-language-dub":
        {
            he: "כדי לייצר דיבוב לשפה מסויימת חובה לייצר לה גם תרגום",
            en: "To dub a language, you must also generate its translation.",
        },
    "funnels.ai-upload-videos.step-2.transcription.language.label": {
        he: "השפה בה מדברים בסרטון:",
        en: "Spoken language:",
    },
    "funnels.ai-upload-videos.step-2.transcription.context.label": {
        he: "הקשר לתמלול:",
        en: "Transcription context:",
    },
    "funnels.ai-upload-videos.step-2.transcription.context.hint": {
        he: "כאן אפשר לתת למודל הקשר על הסרטונים (למשל תעשייה או תחום) וזה יעזור בזיהוי מונחים רלוונטיים.",
        en: "Provide context (industry/field) to help the model recognize relevant terms.",
    },
    "funnels.ai-upload-videos.step-2.transcription.context.placeholder": {
        he: "לדוגמה: קורס SolidCAM",
        en: "e.g., SolidCAM course",
    },
    "funnels.ai-upload-videos.step-2.transcription.hotwords.label": {
        he: "מילים חמות:",
        en: "Hotwords:",
    },
    "funnels.ai-upload-videos.step-2.transcription.hotwords.hint": {
        he: "רשימת מילים מופרדות בפסיק שהמודל ישים לב אליהן במיוחד, למשל שמות או מונחים טכניים.",
        en: "Comma-separated words the model should pay special attention to.",
    },
    "funnels.ai-upload-videos.step-2.transcription.hotwords.placeholder": {
        he: "לדוגמה: CNC, SolidCam, CAM",
        en: "e.g., CNC, SolidCam, CAM",
    },
    "funnels.ai-upload-videos.step-2.language-select.hint": {
        he: "לחצו על שפה כדי לבחור אותה",
        en: "Click a language to select it",
    },
    "funnels.ai-upload-videos.step-2.dubbings.model.label": {
        he: "מודל:",
        en: "Model:",
    },
    "funnels.ai-upload-videos.step-2.dubbings.voice.label": {
        he: "קול:",
        en: "Voice:",
    },
    "funnels.ai-upload-videos.step-2.dubbings.instructions.label": {
        he: "הנחיות דיבוב:",
        en: "Dubbing instructions:",
    },
    "funnels.ai-upload-videos.dubbings-playground.error.no-text": {
        he: "אנא הזינו טקסט.",
        en: "Please enter text.",
    },
    "funnels.ai-upload-videos.dubbings-playground.error.unexpected": {
        he: "קרתה שגיאה לא צפויה, נסו שוב מאוחר יותר.",
        en: "An unexpected error occurred. Please try again later.",
    },
    "funnels.ai-upload-videos.dubbings-playground.trigger": {
        he: "ניסוי הגדרות דיבוב",
        en: "Test dubbing settings",
    },
    "funnels.ai-upload-videos.dubbings-playground.title": {
        he: "ניסוי הגדרות דיבוב",
        en: "Test dubbing settings",
    },
    "funnels.ai-upload-videos.dubbings-playground.description": {
        he: 'כאן תוכלו לייצר דוגמאות דיבוב עם קולות שונים. כשמצאתם את ההגדרות שאתם אוהבים, לחצו "בחירת הגדרות".',
        en: 'Generate dubbing samples with different voices. When you find settings you like, click "Select settings".',
    },
    "funnels.ai-upload-videos.dubbings-playground.text.label": {
        he: "טקסט",
        en: "Text",
    },
    "funnels.ai-upload-videos.dubbings-playground.model.label": {
        he: "מודל:",
        en: "Model:",
    },
    "funnels.ai-upload-videos.dubbings-playground.voice.label": {
        he: "קול:",
        en: "Voice:",
    },
    "funnels.ai-upload-videos.dubbings-playground.instructions.label": {
        he: "הנחיות דיבוב:",
        en: "Dubbing instructions:",
    },
    "funnels.ai-upload-videos.dubbings-playground.instructions.placeholder": {
        he: "דוגמאות: תדבר בהתלהבות, תישמע מקצועי...",
        en: "Examples: speak enthusiastically, sound professional...",
    },
    "funnels.ai-upload-videos.dubbings-playground.generate": {
        he: "יצירת דיבוב",
        en: "Generate dubbing",
    },
    "funnels.ai-upload-videos.dubbings-playground.confirm": {
        he: "בחירת הגדרות",
        en: "Select settings",
    },
    "funnels.ai-file-upload.step-4.title": {
        he: "תרצו לשמור את הסרטונים בתיקייה מסוימת?",
        en: "Do you want to save the videos in a specific folder?",
    },
    "funnels.ai-file-upload.step-4.description": {
        he: "עכשיו זה הזמן לבחור לסרטונים את המקום הנכון",
        en: "Now is the time to choose the right place for the videos",
    },
    "funnels.ai-upload-videos.step-4.submit-btn": { he: "סיום", en: "Finish" },
    "funnels.ai-upload-videos.step-4.folder.placeholder": {
        he: "לחצו כדי לבחור תיקייה",
        en: "Click to choose a folder",
    },
    "funnels.ai-upload-videos.step-4.folder.modal.description": {
        he: "נווטו לתיקייה שבה תרצו לשמור את הסרטונים ולחצו אישור.",
        en: "Navigate to the folder where you want to save the videos and click Confirm.",
    },
    "funnels.ai-upload-videos.step-4.folder.modal.confirm": {
        he: "אישור",
        en: "Confirm",
    },
    "funnels.ai-file-upload.step-5.title": {
        he: "העלאה מתבצעת",
        en: "Uploading in progress",
    },
    "funnels.ai-upload-videos.step-5.submit-btn": {
        he: "מעבר לאחסון",
        en: "Go to storage",
    },
    "funnels.ai-upload-videos.step-5.error.unexpected": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "funnels.ai-upload-videos.step-5.error.upload-failed": {
        he: "ההעלאה נכשלה",
        en: "Upload failed",
    },
    "funnels.ai-upload-videos.step-5.error.folder-create-failed": {
        he: "יצירת התיקיות נכשלה",
        en: "Failed to create folders",
    },
    "video-workflow-error.captions.unexpected-error1": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור כתוביות",
        en: "An unexpected error occurred while generating captions",
    },
    "video-workflow-error.captions.unexpected-error2": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור כתוביות",
        en: "An unexpected error occurred while generating captions",
    },
    "video-workflow-error.captions.unexpected-error3": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור כתוביות",
        en: "An unexpected error occurred while generating captions",
    },
    "video-workflow-error.captions.unexpected-error4": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור כתוביות",
        en: "An unexpected error occurred while generating captions",
    },
    "video-workflow-error.captions.unexpected-error5": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור כתוביות",
        en: "An unexpected error occurred while generating captions",
    },
    "video-workflow-error.captions.not-eligible": {
        he: "אין לך מספיק טוקנים כדי להמשיך",
        en: "You don't have enough tokens to continue",
    },
    "video-workflow-error.translations.unexpected-error1": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור תרגומים",
        en: "An unexpected error occurred while generating translations",
    },
    "video-workflow-error.translations.unexpected-error2": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור תרגומים",
        en: "An unexpected error occurred while generating translations",
    },
    "video-workflow-error.translations.not-eligible": {
        he: "אין לך מספיק טוקנים כדי להמשיך",
        en: "You don't have enough tokens to continue",
    },
    "video-workflow-error.dubbings.unexpected-error1": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור דיבובים",
        en: "An unexpected error occurred while generating dubbings",
    },
    "video-workflow-error.dubbings.unexpected-error2": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור דיבובים",
        en: "An unexpected error occurred while generating dubbings",
    },
    "video-workflow-error.dubbings.unexpected-error2.5": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "video-workflow-error.dubbings.unexpected-error2.6": {
        he: "קרתה שגיאה - לחלק מהשפות כבר נוצרו דיבובים",
        en: "An error occurred — dubbings were already generated for some languages",
    },
    "video-workflow-error.dubbings.unexpected-error2.7": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור דיבובים",
        en: "An unexpected error occurred while generating dubbings",
    },
    "video-workflow-error.dubbings.unexpected-error2.8": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "video-workflow-error.dubbings.not-eligible": {
        he: "אין לך מספיק טוקנים כדי להמשיך",
        en: "You don't have enough tokens to continue",
    },
    "video-workflow-error.dubbings.video-too-long": {
        he: "אין אפשרות ליצור דיבובים לווידאו העולה על חצי שעה",
        en: "Dubbings can't be generated for videos longer than 30 minutes",
    },
    "video-workflow-error.chapters.unexpected-error": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור פרקים",
        en: "An unexpected error occurred while generating chapters",
    },
    "video-workflow-error.chapters.unexpected-error1": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור פרקים",
        en: "An unexpected error occurred while generating chapters",
    },
    "video-workflow-error.chapters.not-eligible": {
        he: "אין לך מספיק טוקנים כדי להמשיך",
        en: "You don't have enough tokens to continue",
    },
    "video-workflow-error.interactions.unexpected-error": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור שאלות",
        en: "An unexpected error occurred while generating questions",
    },
    "video-workflow-error.interactions.unexpected-error1": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור שאלות",
        en: "An unexpected error occurred while generating questions",
    },
    "video-workflow-error.interactions.unexpected-error2": {
        he: "אירעה שגיאה לא צפוייה בנסיון ייצור שאלות",
        en: "An unexpected error occurred while generating questions",
    },
    "video-workflow-error.interactions.not-eligible": {
        he: "אין לך מספיק טוקנים כדי להמשיך",
        en: "You don't have enough tokens to continue",
    },
    "funnels.ai-upload-videos.step-5.step-of": {
        he: "שלב {current} מתוך {total}",
        en: "Step {current} of {total}",
    },
    "funnels.ai-upload-videos.step-5.step.uploading": {
        he: "מתבצעת העלאה",
        en: "Uploading",
    },
    "funnels.ai-upload-videos.step-5.step.importing": {
        he: "מתבצע ייבוא",
        en: "Importing",
    },
    "funnels.ai-upload-videos.step-5.completed": {
        he: "העלאה ועיבוד הסתיימו בהצלחה!",
        en: "Upload and processing completed successfully!",
    },
    "funnels.ai-upload-videos.workflow-step.processing": {
        he: "עיבוד הסרטון",
        en: "Processing video",
    },
    "funnels.ai-upload-videos.workflow-step.captions": {
        he: "יצירת כתוביות",
        en: "Generating captions",
    },
    "funnels.ai-upload-videos.workflow-step.chapters": {
        he: "יצירת פרקים",
        en: "Generating chapters",
    },
    "funnels.ai-upload-videos.workflow-step.interactions": {
        he: "יצירת שאלות",
        en: "Generating questions",
    },
    "funnels.ai-upload-videos.workflow-step.translations": {
        he: "יצירת תרגומים",
        en: "Generating translations",
    },
    "funnels.ai-upload-videos.workflow-step.dubbings": {
        he: "יצירת דיבובים",
        en: "Generating dubbings",
    },
    "funnels.ai-upload-videos.workflow-step.chatbot-knowledge": {
        he: "הוספה לידע של צ׳אטבוט",
        en: "Adding to chatbot knowledge",
    },
    "funnels.vimeo-explorer.title.folders": {
        he: "בחירת תיקיות",
        en: "Select folders",
    },
    "funnels.vimeo-explorer.title.videos": {
        he: "בחירת סרטונים",
        en: "Select videos",
    },
    "funnels.vimeo-explorer.description.folders": {
        he: "לחצו על תיקייה פעם אחת כדי לבחור אותה ופעמיים כדי להיכנס אליה.",
        en: "Click a folder once to select it, twice to open it.",
    },
    "funnels.vimeo-explorer.description.videos": {
        he: "לחצו על סרטונים כדי לבחור אותם.",
        en: "Click videos to select them.",
    },
    "funnels.vimeo-explorer.badge.folder": {
        he: "תיקייה: {name}",
        en: "Folder: {name}",
    },
    "funnels.vimeo-explorer.search.placeholder": {
        he: "חיפוש...",
        en: "Search...",
    },
    "funnels.vimeo-explorer.folders.empty.title": {
        he: "לא נמצאו תיקיות",
        en: "No folders found",
    },
    "funnels.vimeo-explorer.folders.empty.description": {
        he: "נסו להקליד את שם התיקיה המלא וללחוץ אנטר",
        en: "Try typing the full folder name and press Enter",
    },
    "funnels.vimeo-explorer.videos.empty.title": {
        he: "לא נמצאו סרטונים",
        en: "No videos found",
    },
    "funnels.vimeo-explorer.videos.empty.description": {
        he: "נסו להקליד את שם הסרטון המלא וללחוץ אנטר",
        en: "Try typing the full video name and press Enter",
    },
    "funnels.vimeo-explorer.footer.select.folders-videos": {
        he: "בחירת {folders} תיקיות ו-{videos} סרטונים",
        en: "Select {folders} folders and {videos} videos",
    },
    "funnels.vimeo-explorer.footer.select.folders": {
        he: "בחירת {folders} תיקיות",
        en: "Select {folders} folders",
    },
    "funnels.vimeo-explorer.footer.select.videos": {
        he: "בחירת {videos} סרטונים",
        en: "Select {videos} videos",
    },
    "funnels.vimeo-explorer.footer.select.none": {
        he: "בחירת 0 קבצים",
        en: "Select 0 files",
    },
    "funnels.vimeo-explorer.footer.prev": { he: "הקודם", en: "Previous" },
    "funnels.vimeo-explorer.footer.next": { he: "הבא", en: "Next" },
    "funnels.vimeo-explorer.footer.page-of": {
        he: "עמוד {page} מתוך {total}",
        en: "Page {page} of {total}",
    },
    "funnels.vimeo-explorer.nav.folders": { he: "תיקיות", en: "Folders" },
    "funnels.vimeo-explorer.nav.videos": { he: "סרטונים", en: "Videos" },
    "funnels.vimeo-explorer.warning.title": { he: "שימו לב", en: "Notice" },
    "funnels.vimeo-explorer.warning.missing-downloads.line-1": {
        he: "לחלק מהסרטונים לא הצלחנו לייבא קישורי הורדה מוימאו ולכן לא ניתן לבחור אותם.",
        en: "Some videos don’t have downloadable links from Vimeo and can’t be selected.",
    },
    "funnels.vimeo-explorer.warning.missing-downloads.line-2": {
        he: "אתם צריכים לשנות את הגדרות החשבון שלכם בוימאו כדי שנוכל.",
        en: "You need to change your Vimeo account settings so we can access them.",
    },
    "funnels.vimeo-explorer.warning.too-many-videos": {
        he: "בחרתם בסך הכל {count} סרטונים, אך הכמות המקסימלית להעלאה בודדת היא 100.",
        en: "You selected {count} videos, but the maximum per upload is 100.",
    },
    "home.header.upgrade-plan-btn": {
        he: "הוספת יתרה",
        en: "Add funds",
    },
    "home.header.preview-btn": {
        he: "תצוגה מקדימה",
        en: "Preview",
    },
    "home.title": {
        he: "ברוכים הבאים{firstName}",
        en: "Welcome{firstName}",
    },
    "home.trial.progress": {
        he: "{elapsedDays} / {trialPeriod} ימים בתקופת הניסיון",
        en: "{elapsedDays} / {trialPeriod} days of trial",
    },
    "home.cta.syllabus": {
        he: "יצירת סילאבוס עם AI",
        en: "Create a syllabus with AI",
    },
    "home.cta.presentations": {
        he: "יצירה והקלטת מצגת עם AI",
        en: "Create and record a presentation with AI",
    },
    "home.cta.lomda": { he: "יצירת לומדת וידאו", en: "Create video lomda" },
    "home.cta.lomda-v3": {
        he: "יצירת לומדת HTML",
        en: "Create HTML lomda",
    },
    "home.cta.storage": { he: "אחסון", en: "Storage" },
    "home.cta.title": {
        he: "רוצים לשפר את חווית הלמידה?",
        en: "Want to improve the learning experience?",
    },
    "home.cta.subtitle": {
        he: "הנה ההמלצות שלנו עבורכם:",
        en: "Here are our recommendations for you:",
    },
    "home.cta.captions.title": {
        he: "צרו לסרטונים כתוביות",
        en: "Add captions to your videos",
    },
    "home.cta.captions.subtitle": {
        he: "ל-{captionsPercent}% מהסרטונים שלכם יש כתוביות",
        en: "{captionsPercent}% of your videos have captions",
    },
    "home.cta.translations.title": {
        he: "תרגמו את הסרטונים",
        en: "Translate your videos",
    },
    "home.cta.translations.subtitle": {
        he: "{multiLangPercent}% מהסרטונים שלכם מתורגמים לשפות נוספות",
        en: "{multiLangPercent}% of your videos are translated into other languages",
    },
    "home.cta.interactions.title": {
        he: "הפכו את הסרטונים לאינטראקטיביים",
        en: "Make your videos interactive",
    },
    "home.cta.interactions.subtitle": {
        he: "{interactionsPercent}% מהסרטונים שלכם אינטראקטיביים",
        en: "{interactionsPercent}% of your videos are interactive",
    },
    "home.cta.ai-agents.title": {
        he: "הפעילו סוכני AI",
        en: "Activate AI agents",
    },
    "home.cta.ai-agents.subtitle": {
        he: "היו לכם {totalMessagesLast7d} הודעות לסוכני AI בשבוע האחרון",
        en: "You had {totalMessagesLast7d} messages to AI agents in the last week",
    },
    "home.cta.illustration.alt": {
        he: "איור של אדם מול מחשב נייד",
        en: "Illustration of a person in front of a laptop",
    },
    "home.stats.header.title": { he: "ידעלאבס", en: "YedaLabs" },
    "home.stats.header.next-reset": {
        he: "תאריך סיום תקופת חיוב נוכחית:",
        en: "Current billing cycle end date:",
    },
    "home.stats.storage-balance": {
        he: "בנק אחסון חודשי",
        en: "Monthly storage bank",
    },
    "home.stats.storage-tooltip": {
        he: "דוגמה - אם יש לך בבנק 10TB, ובפועל יש לך 2TB של אחסון, אז היתרה בבנק תספיק לך ל-5 חודשים.",
        en: "Example - if you have 10TB in your bank and you actually use 2TB of storage, your bank balance will last for 5 months.",
    },
    "home.stats.bandwidth-balance": {
        he: "בנק טראפיק",
        en: "Bandwidth bank",
    },
    "home.stats.credits-balance": {
        he: "בנק קרדיטים",
        en: "Credits bank",
    },
    "home.stats.last-30-days": {
        he: "שימוש ב-30 ימים אחרונים",
        en: "Last 30 days usage",
    },
    "home.stats.this-month": {
        he: "שימוש מתחילת החודש",
        en: "Usage since start of month",
    },
    "home.stats.currently-stored": {
        he: "כרגע מאוחסנים בחשבונך",
        en: "Currently stored in your account",
    },
    "home.stats.storage-usage": { he: "שימוש אחסון", en: "Storage usage" },
    "home.stats.bandwidth-usage": { he: "שימוש טראפיק", en: "Bandwidth usage" },
    "home.stats.ai-usage": { he: "שימוש AI", en: "AI usage" },
    "home.stats.video-edit-usage": {
        he: "שימוש עריכת וידאו",
        en: "Video editing usage",
    },
    "unit.tokens": { he: "טוקנים", en: "tokens" },
    "unit.credits": { he: "קרדיטים", en: "credits" },
    "unit.minutes": { he: "דקות", en: "minutes" },
    "home.usage-transactions-list.title": {
        he: "פירוט שימוש במערכת",
        en: "Usage details",
    },
    "home.usage-transactions-list.subtitle": {
        he: "צפייה בעסקאות שימוש",
        en: "Usage history",
    },
    "home.usage-transactions.empty": {
        he: "אין עדיין תוצאות.",
        en: "No results yet.",
    },
    "home.usage-transactions.pagination.results-range": {
        he: "מציג תוצאות {range} מתוך {total}",
        en: "Showing results {range} of {total}",
    },
    "home.usage-transactions.pagination.prev": { he: "הקודם", en: "Previous" },
    "home.usage-transactions.pagination.next": { he: "הבא", en: "Next" },
    "home.usage-transactions.columns.number": { he: "#", en: "#" },
    "home.usage-transactions.columns.action": { he: "פעולה", en: "Action" },
    "home.usage-transactions.columns.amount": { he: "כמות", en: "Amount" },
    "home.usage-transactions.columns.date": { he: "תאריך", en: "Date" },
    "home.usage-transactions.action.generate-presentation": {
        he: "יצירת מצגת",
        en: "Generate presentation",
    },
    "home.usage-transactions.action.generate-syllabus": {
        he: "יצירת סילאבוס",
        en: "Generate syllabus",
    },
    "home.usage-transactions.action.generate-video-chapters": {
        he: "יצירת פרקים",
        en: "Generate chapters",
    },
    "home.usage-transactions.action.generate-video-interactions": {
        he: "יצירת אינטראקציות",
        en: "Generate interactions",
    },
    "home.usage-transactions.action.video-interactions": {
        he: "אינטראקציות בנגן",
        en: "Player interactions",
    },
    "home.usage-transactions.action.chatbot": { he: "צ'אטבוט", en: "Chatbot" },
    "home.usage-transactions.action.translate-captions": {
        he: "תרגומים",
        en: "Translations",
    },
    "home.usage-transactions.action.dubbing-captions": {
        he: "דיבובים",
        en: "Dubbings",
    },
    "home.usage-transactions.action.transcribe-video": {
        he: "יצירת כתוביות",
        en: "Create captions",
    },
    "home.usage-transactions.action.edit-video": {
        he: "עריכת וידאו",
        en: "Video editing",
    },
    "home.usage-transactions.type.storage": { he: "אחסון", en: "Storage" },
    "home.usage-transactions.type.bandwidth": {
        he: "טראפיק",
        en: "Bandwidth",
    },
    "home.usage-transactions.type.credits": {
        he: "קרדיטים",
        en: "Credits",
    },
    "home.usage-transactions.columns.details": {
        he: "פירוט",
        en: "Details",
    },
    "home.usage-transactions.columns.details.funding": {
        he: "קניית יתרה",
        en: "Funding",
    },
    "home.usage-transactions.columns.details.yearly-reset": {
        he: "איפוס שנתי",
        en: "Yearly reset",
    },
    "home.usage-transactions.columns.details.video_transcribe_hour": {
        he: "יצירת כתוביות",
        en: "Create captions",
    },
    "home.usage-transactions.columns.details.video_interactions_hour": {
        he: "אינטראקציות בנגן",
        en: "Player interactions",
    },
    "home.usage-transactions.columns.details.video_chapters_hour": {
        he: "יצירת פרקים",
        en: "Generate chapters",
    },
    "home.usage-transactions.columns.details.video_translation_hour": {
        he: "תרגומים",
        en: "Translations",
    },
    "home.usage-transactions.columns.details.video_dubbing_hour": {
        he: "דיבובים",
        en: "Dubbings",
    },
    "home.usage-transactions.columns.details.presentation": {
        he: "יצירת מצגת",
        en: "Generate presentation",
    },
    "home.usage-transactions.columns.details.syllabus": {
        he: "יצירת סילאבוס",
        en: "Generate syllabus",
    },
    "home.usage-transactions.columns.details.lomda": {
        he: "לומדה (גרסה ישנה)",
        en: "Lomda",
    },
    "home.usage-transactions.columns.details.generate_lomda_scene_audio": {
        he: "יצירת אודיו ללומדה (גרסה ישנה)",
        en: "Generate lomda scene audio",
    },
    "home.usage-transactions.columns.details.generate_lomda_video": {
        he: "יצירת וידאו ללומדה (גרסה ישנה)",
        en: "Generate lomda video",
    },
    "home.usage-transactions.columns.details.chatbot_message": {
        he: "צ'אטבוט",
        en: "Chatbot",
    },
    "home.usage-transactions.columns.details.edit_video": {
        he: "עריכת וידאו",
        en: "Video editing",
    },
    "home.usage-transactions.columns.details.lomda_v2_ai_creation": {
        he: "יצירת לומדה",
        en: "Lomda creation",
    },
    "home.usage-transactions.columns.details.lomda_v3_ai_creation": {
        he: "יצירת לומדת HTML",
        en: "HTML lomda creation",
    },
    "home.usage-transactions.columns.details.lomda_v2_rendering": {
        he: "רינדור לומדה",
        en: "Lomda rendering",
    },
    "home.usage-transactions.columns.details.lomda_v2_avatar_generation": {
        he: "יצירת אווטאר ללומדה",
        en: "Lomda avatar generation",
    },
    "home.usage-transactions.columns.details.lomda_v2_voice_over_generation": {
        he: "יצירת קריינות ללומדה",
        en: "Lomda voice-over generation",
    },
    "home.usage-transactions.amount.tokens": {
        he: "{amount} טוקנים",
        en: "{amount} tokens",
    },
    "home.usage-transactions.amount.credits": {
        he: "{amount} קרדיטים",
        en: "{amount} credits",
    },
    "home.usage-transactions.amount.minutes": {
        he: "{amount} דקות",
        en: "{amount} minutes",
    },
    "home.usage-transactions.export.button": { he: "ייצוא", en: "Export" },
    "home.usage-transactions.export.title": {
        he: "ייצוא טבלת שימוש טוקנים",
        en: "Export token usage table",
    },
    "home.usage-transactions.export.description": {
        he: "בחרו את תאריך התחלה ותאריך סיום ולחצו הורדה.",
        en: "Select a start and end date, then click Download.",
    },
    "home.usage-transactions.export.start-date.label": {
        he: "תאריך התחלה",
        en: "Start date",
    },
    "home.usage-transactions.export.end-date.label": {
        he: "תאריך סיום",
        en: "End date",
    },
    "home.usage-transactions.export.select-date.placeholder": {
        he: "בחרו תאריך",
        en: "Select a date",
    },
    "home.usage-transactions.export.download": { he: "הורדה", en: "Download" },
    "home.usage-transactions.export.sheet-name": { he: "שימוש", en: "Usage" },
    "home.usage-transactions.export.filename": {
        he: "yedalabs_usage_{startDate}__{endDate}",
        en: "yedalabs_usage_{startDate}__{endDate}",
    },
    "home.usage-transactions.export.errors.start-date-required": {
        he: "יש לבחור תאריך התחלה",
        en: "Please select a start date",
    },
    "home.usage-transactions.export.errors.end-date-required": {
        he: "יש לבחור תאריך סיום",
        en: "Please select an end date",
    },
    "home.usage-transactions.export.errors.start-date-before-end": {
        he: "תאריך התחלה חייב להיות לפני תאריך סיום",
        en: "Start date must be before end date",
    },
    "home.usage-transactions.export.errors.no-records": {
        he: "לא נמצאו רשומות לטווח תאריכים",
        en: "No records found for the selected date range",
    },
    "home.usage-transactions.export.errors.unexpected": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "interaction-modal.answers-answers.label": {
        he: "תשובות נכונות",
        en: "Correct answers",
    },
    "interaction-modal.answers.add-btn": { he: "הוספה", en: "Add" },
    "interaction-modal.audio.content.language.label": {
        he: "שפה",
        en: "Language",
    },
    "interaction-modal.audio.content.recording.label": {
        he: "הקלטה (לא חובה)",
        en: "Recording (optional)",
    },
    "interaction-modal.submit-button.continue": { he: "המשך", en: "Continue" },
    "interaction-modal.submit-button.finish": { he: "סיום", en: "Finish" },
    "interaction-modal.audio.content.sentence.label": {
        he: "משפט",
        en: "Sentence",
    },
    "interaction-modal.form-fields.add-btn": {
        he: "הוספת שדה",
        en: "Add field",
    },
    "interaction-modal.form-fields.label": { he: "שדות", en: "Fields" },
    "interaction-modal.form.field-input-placeholder": {
        he: "שם השדה",
        en: "Field name",
    },
    "interaction-modal.form.select-field-icon.label": {
        he: "בחרו אייקון",
        en: "Choose an icon",
    },
    "interaction-modal.form.select-field-icon.none-option": {
        he: "ללא אייקון",
        en: "No icon",
    },
    "interaction-modal.match.answers.label": {
        he: "זוגות קלפים",
        en: "Card pairs",
    },
    "interaction-modal.select-type.label": { he: "סוג", en: "Type" },
    "interaction-modal.select-type.placeholder": {
        he: "בחרו סוג",
        en: "Choose a type",
    },
    "interaction-modal.select-type.open": {
        he: "שאלה פתוחה",
        en: "Open question",
    },
    "interaction-modal.select-type.closed": {
        he: "שאלה סגורה",
        en: "Closed question",
    },
    "interaction-modal.select-type.form": { he: "טופס", en: "Form" },
    "interaction-modal.select-type.rating": { he: "דירוג", en: "Rating" },
    "interaction-modal.select-type.link": { he: "לינק", en: "Link" },
    "interaction-modal.select-type.audio": { he: "אודיו", en: "Audio" },
    "interaction-modal.select-type.ai-chat": {
        he: "שיחה עם AI",
        en: "Chat with AI",
    },
    "interaction-modal.select-type.cloze": {
        he: "השלמת משפטים",
        en: "Cloze",
    },
    "interaction-modal.select-type.match": {
        he: "התאמת קלפים",
        en: "Match cards",
    },
    "interaction-modal.time-and-type-step.header.title": {
        he: "זמן וסוג",
        en: "Time and type",
    },
    "interaction-modal.time-and-type-step.header.description": {
        he: "בחרו מתי האינטראקציה תופיע ואת הסוג שלה",
        en: "Choose when the interaction appears and its type",
    },
    "interaction-modal.settings-step.header.title": {
        he: "הגדרות",
        en: "Settings",
    },
    "interaction-modal.settings-step.header.description": {
        he: "בחרו את הגדרות האינטראקציה",
        en: "Choose the interaction settings",
    },
    "interaction-modal.sidebar.time-and-type": {
        he: "זמן וסוג",
        en: "Time and type",
    },
    "interaction-modal.sidebar.question": { he: "שאלה", en: "Question" },
    "interaction-modal.sidebar.rating": { he: "דירוג", en: "Rating" },
    "interaction-modal.sidebar.form": { he: "טופס", en: "Form" },
    "interaction-modal.sidebar.link": { he: "לינק", en: "Link" },
    "interaction-modal.sidebar.audio": { he: "אודיו", en: "Audio" },
    "interaction-modal.sidebar.cloze": { he: "כותרת", en: "Title" },
    "interaction-modal.sidebar.match": { he: "כותרת", en: "Title" },
    "interaction-modal.sidebar.answers": { he: "תשובות", en: "Answers" },
    "interaction-modal.sidebar.answers.audio": { he: "תוכן", en: "Content" },
    "interaction-modal.sidebar.answers.cloze": { he: "טקסט", en: "Text" },
    "interaction-modal.sidebar.answers.match": { he: "קלפים", en: "Cards" },
    "interaction-modal.sidebar.settings": { he: "הגדרות", en: "Settings" },
    "interaction-modal.sidebar.scene-and-type": {
        he: "מיקום וסוג",
        en: "Position and type",
    },
    "interaction-modal.scene-and-type-step.header.title": {
        he: "מיקום וסוג",
        en: "Position and type",
    },
    "interaction-modal.scene-and-type-step.header.description": {
        he: "בחרו אחרי איזו סצנה האינטראקציה תופיע ואת הסוג שלה",
        en: "Choose which scene the interaction follows and its type",
    },
    "interaction-modal.after-scene.label": {
        he: "אחרי",
        en: "After",
    },
    "interaction-modal.gap-order.label": {
        he: "סדר",
        en: "Order",
    },
    "interaction-modal.form.header.title": { he: "טופס", en: "Form" },
    "interaction-modal.form.header.description": {
        he: "הזינו את הכותרת ובחרו את השדות",
        en: "Enter a title and choose the fields",
    },
    "interaction-modal.form-question.label": { he: "כותרת", en: "Title" },
    "interaction-modal.rating.header.title": { he: "דירוג", en: "Rating" },
    "interaction-modal.rating.header.description": {
        he: "הזינו את הכותרת שלכם",
        en: "Enter your title",
    },
    "interaction-modal.rating.label": { he: "כותרת", en: "Title" },
    "interaction-modal.link.header.title": { he: "לינק", en: "Link" },
    "interaction-modal.link.header.description": {
        he: "בחרו כותרת והזינו את כתובת הלינק",
        en: "Choose a title and enter the link URL",
    },
    "interaction-modal.link.label": { he: "כותרת", en: "Title" },
    "interaction-modal.audio.header.title": { he: "אודיו", en: "Audio" },
    "interaction-modal.audio.header.description": {
        he: "הזינו את הכותרת שלכם",
        en: "Enter your title",
    },
    "interaction-modal.audio.label": { he: "כותרת", en: "Title" },
    "interaction-modal.cloze.header.title": {
        he: "השלמת משפטים",
        en: "Cloze",
    },
    "interaction-modal.cloze.header.description": {
        he: "הזינו את הכותרת שלכם",
        en: "Enter your title",
    },
    "interaction-modal.cloze.label": { he: "כותרת", en: "Title" },
    "interaction-modal.match.header.title": {
        he: "התאמת קלפים",
        en: "Match cards",
    },
    "interaction-modal.match.header.description": {
        he: "הזינו את הכותרת שלכם",
        en: "Enter your title",
    },
    "interaction-modal.match.label": { he: "כותרת", en: "Title" },
    "interaction-modal.ai-chat.header.title": {
        he: "שיחה עם AI",
        en: "Chat with AI",
    },
    "interaction-modal.ai-chat.header.description": {
        he: "הזינו את השאלה שעליה ה-AI ישוחח עם התלמיד וייבחן אותו",
        en: "Enter the question the AI will chat with the student about and evaluate them on",
    },
    "interaction-modal.ai-chat.question.label": {
        he: "שאלה",
        en: "Question",
    },
    "interaction-modal.question.header.title": { he: "שאלה", en: "Question" },
    "interaction-modal.question.header.description": {
        he: "הזינו את השאלה שלכם",
        en: "Enter your question",
    },
    "interaction-modal.question.label": { he: "שאלה", en: "Question" },
    "interaction-modal.answers-audio.header.title": {
        he: "תוכן",
        en: "Content",
    },
    "interaction-modal.answers-audio.header.description": {
        he: "הזינו את התוכן לאינטראקציה",
        en: "Enter the interaction content",
    },
    "interaction-modal.answers-cloze.header.title": { he: "טקסט", en: "Text" },
    "interaction-modal.answers-cloze.header.description": {
        he: "הזינו את הטקסט, מילה שתרצו שהצופים ישלימו סמנו כך: _כלב_ .",
        en: "Enter the text; mark a word for viewers to complete like this: _dog_.",
    },
    "interaction-modal.answers-match.header.title": {
        he: "קלפים",
        en: "Cards",
    },
    "interaction-modal.answers-match.header.description": {
        he: "הוסיפו את זוגות הקלפים",
        en: "Add the card pairs",
    },
    "interaction-modal.answers.header.title": { he: "תשובות", en: "Answers" },
    "interaction-modal.answers.header.description": {
        he: "הזינו את התשובות לשאלה שלכם",
        en: "Enter the answers to your question",
    },
    "interaction-modal.close-question.answers.label": {
        he: "אפשרויות תשובה",
        en: "Answer options",
    },
    "interaction-modal.open-question.answers.label": {
        he: "תשובות נכונות",
        en: "Correct answers",
    },
    "interaction-modal.answers.validation.select-correct": {
        he: "לא נבחרו תשובות נכונות. בחרו לפחות תשובה אחת.",
        en: "No correct answers selected. Choose at least one.",
    },
    "interaction-modal.audio.content.language.placeholder": {
        he: "בחרו שפה",
        en: "Select a language",
    },
    "interaction-modal.audio.content.recording.upload": {
        he: "העלאת קובץ אודיו",
        en: "Upload audio file",
    },
    "interaction-modal.audio.content.recording.record": {
        he: "הקלטה",
        en: "Record",
    },
    "interaction-modal.settings.attempts-limit.1": { he: "1", en: "1" },
    "interaction-modal.settings.attempts-limit.2": { he: "2", en: "2" },
    "interaction-modal.settings.attempts-limit.3": { he: "3", en: "3" },
    "interaction-modal.settings.attempts-limit.4": { he: "4", en: "4" },
    "interaction-modal.settings.attempts-limit.5": { he: "5", en: "5" },
    "interaction-modal.settings.attempts-limit.10": { he: "10", en: "10" },
    "interaction-modal.settings.allowSkipping": {
        he: "הצגת אפשרות דילוג",
        en: "Show skip option",
    },
    "interaction-modal.settings.hideCorrectAnswer": {
        he: "הסתרת תשובה נכונה",
        en: "Hide correct answer",
    },
    "interaction-modal.settings.repeatUntilCorrect": {
        he: "החזרת הצופה אחורה לאחר כשלון",
        en: "Send viewer back after failure",
    },
    "interaction-modal.settings.select-attempt-limit.label": {
        he: "מספר נסיונות מענה",
        en: "Number of attempts",
    },
    "interaction-modal.settings.header.title": {
        he: "הגדרות",
        en: "Settings",
    },
    "interaction-modal.settings.header.description": {
        he: "בחרו את הגדרות האינטראקציה",
        en: "Choose the interaction settings",
    },
    "interaction-modal.timestamp.label": { he: "זמן הופעה", en: "Appear time" },
    "auth-card.errors.email.required": {
        he: "שדה המייל חובה",
        en: "Email is required",
    },
    "auth-card.errors.email.invalid": {
        he: "כתובת מייל לא חוקית",
        en: "Invalid email address",
    },
    "auth-card.errors.password.required": {
        he: "שדה הסיסמה חובה",
        en: "Password is required",
    },
    "auth-card.errors.password.invalid": {
        he: "הסיסמה חייבת להיות לפחות 6 תווים",
        en: "Password must be at least 6 characters",
    },
    "auth.errors.sign-in.failed": {
        he: "הפרטים שהוזנו אינם תקינים",
        en: "The details you entered are invalid",
    },
    "auth.error.email-not-found": {
        he: "המייל שהוזן לא נמצא",
        en: "Email not found",
    },
    "auth.error.invalid-credentials": {
        he: "המייל או הסיסמה שהוזנו שגויים",
        en: "Incorrect email or password",
    },
    "auth.error.password-login-not-available": {
        he: "לא קיימת סיסמה למייל שהוזן",
        en: "No password exists for this email",
    },
    "auth.error.default": {
        he: "אירעה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "auth.error-message.reset-passord.user-not-found": {
        he: "לא נמצא משתמש עם מייל זה",
        en: "No user found with this email",
    },
    "auth.layout.tos-prefix": {
        he: "התחברות מהווה הסכמה",
        en: "By signing in, you agree",
    },
    "auth.layout.tos-link": {
        he: "לתקנון ולתנאי השימוש",
        en: "to the Terms of Service",
    },
    "login.title": { he: "התחברות", en: "Log in" },
    "login.description": {
        he: "המשיכו עם גוגל או מלאו את הפרטים",
        en: "Continue with Google or fill in your details",
    },
    "login.no-account": { he: "אין לך עדיין חשבון?", en: "No account yet?" },
    "login.signup-link": { he: "הרשמה", en: "Sign up" },
    "login.email": { he: "מייל", en: "Email" },
    "login.forgot-password": { he: "שכחת סיסמה?", en: "Forgot password?" },
    "login.login-btn": { he: "כניסה", en: "Login" },
    "login.password": { he: "סיסמה", en: "Password" },
    "admin.content.title": {
        he: "עריכת תכנים בפלטפורמה",
        en: "Edit platform content",
    },
    "admin.content.pages.pricing-page": { he: "דף מחירון", en: "Pricing page" },
    "admin.content.pages.pricing-plans": { he: "מנויים", en: "Plans" },
    "admin.content.pages.pricing-trigger": {
        he: "טריגרים",
        en: "Triggers",
    },
    "admin.content.actions.title": { he: "עורך JSON", en: "JSON Editor" },
    "admin.content.actions.save": { he: "שמירה", en: "Save" },
    "admin.content.actions.env-label": {
        he: "באיזה סביבה:",
        en: "Environment:",
    },
    "admin.content.actions.save-success": {
        he: "השינויים נשמרו בהצלחה בסביבת {env}!",
        en: "Changes saved successfully in {env} environment!",
    },
    "admin.content.actions.error-unexpected": {
        he: "אירעה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "admin.manage.title": { he: "ניהול אדמינים", en: "Admin management" },
    "admin.manage.actions.add-admin": { he: "הוספת אדמין", en: "Add admin" },
    "admin.manage.actions.edit": { he: "עריכה", en: "Edit" },
    "admin.manage.actions.delete": { he: "מחיקה", en: "Delete" },
    "admin.manage.table.columns.name": { he: "שם", en: "Name" },
    "admin.manage.table.columns.email": { he: "מייל", en: "Email" },
    "admin.manage.table.columns.permissions": {
        he: "הרשאות",
        en: "Permissions",
    },
    "admin.manage.table.columns.actions": { he: "פעולות", en: "Actions" },
    "admin.manage.create.title": { he: "יצירת אדמין", en: "Create admin" },
    "admin.manage.create.description": {
        he: "ודאו שהמשתמש אותו תרצו להפוך לאדמין כבר נרשם לאקסייטר והזינו את המייל איתו הוא נרשם.",
        en: "Make sure the user you want to promote is already registered and enter the email they used.",
    },
    "admin.manage.create.email": { he: "מייל", en: "Email" },
    "admin.manage.create.success": {
        he: "האדמין נוצר בהצלחה!",
        en: "Admin created successfully!",
    },
    "admin.manage.edit.title": { he: "עדכון הרשאות", en: "Update permissions" },
    "admin.manage.edit.description": {
        he: "בחרו את ההרשאות של האדמין:",
        en: "Choose the admin permissions:",
    },
    "admin.manage.edit.description-suffix": {
        he: "ולחצו שמירה.",
        en: "and click Save.",
    },
    "admin.manage.edit.permissions": { he: "הרשאות", en: "Permissions" },
    "admin.manage.edit.permissions.empty": {
        he: "לא נמצאו תוצאות",
        en: "No results found",
    },
    "admin.manage.edit.success": {
        he: "האדמין עודכן בהצלחה!",
        en: "Admin updated successfully!",
    },
    "admin.manage.delete.title": { he: "מחיקת אדמין", en: "Delete admin" },
    "admin.manage.delete.description": {
        he: "אתה בטוח שתרצה למחוק את האדמין:",
        en: "Are you sure you want to delete this admin:",
    },
    "admin.manage.delete.success": {
        he: "האדמין נמחק בהצלחה!",
        en: "Admin deleted successfully!",
    },
    "admin.users.title": { he: "ניהול משתמשים", en: "User management" },
    "admin.users.table.columns.name": { he: "שם", en: "Name" },
    "admin.users.table.columns.email": { he: "מייל", en: "Email" },
    "admin.users.table.columns.phone": { he: "טלפון", en: "Phone" },
    "admin.users.table.columns.plan": { he: "מנוי", en: "Plan" },
    "admin.users.table.columns.created-at": {
        he: "נרשם בתאריך",
        en: "Created at",
    },
    "admin.users.table.columns.allow-download-videos": {
        he: "אפשר הורדת וידאו",
        en: "Allow video download",
    },
    "admin.users.search.placeholder": {
        he: "הזינו מייל",
        en: "Enter email",
    },
    "admin.users.search.button": { he: "חיפוש", en: "Search" },
    "admin.users.pagination.results": {
        he: "מציג תוצאות {resultsRange} מתוך {total} תוצאות",
        en: "Showing {resultsRange} of {total} results",
    },
    "admin.users.pagination.per-page": {
        he: "תוצאות בעמוד",
        en: "Results per page",
    },
    "admin.pagination.prev": { he: "הקודם", en: "Previous" },
    "admin.pagination.next": { he: "הבא", en: "Next" },
    "admin.users.sign-in-as-user.button": {
        he: "כניסה למשתמש",
        en: "Sign in as user",
    },
    "admin.users.sign-in-as-user.error": {
        he: "קרתה שגיאה",
        en: "Something went wrong",
    },
    "admin.users.sign-in-as-user.link-hint": {
        he: "לחצו על הלינק כדי להעתיק אותו ופתחו אותו בחלון גלישה בסתר",
        en: "Click the link to copy it and open it in an incognito window",
    },
    "admin.users.sign-in-as-user.link-copied": {
        he: "הלינק הועתק בהצלחה, פתחו אותו בחלון גלישה בסתר",
        en: "Link copied successfully. Open it in an incognito window",
    },
    "admin.users.tabs.general": { he: "כללי", en: "General" },
    "admin.users.tabs.sign-in": { he: "כניסה למשתמש", en: "Sign in as user" },
    "admin.users.tabs.discounts": { he: "הנחות", en: "Discounts" },
    "admin.users.tabs.billing": { he: "בנקים", en: "Banks" },
    "admin.users.plan.errors.no-user": {
        he: "לא נבחר משתמש",
        en: "No user selected",
    },
    "admin.users.plan.errors.save-failed": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred",
    },
    "admin.users.plan.limits.tokens": { he: "מגבלת טוקנים", en: "Token limit" },
    "admin.users.plan.limits.storage": {
        he: "מגבלת אחסון (GB)",
        en: "Storage limit (GB)",
    },
    "admin.users.plan.limits.traffic": {
        he: "מגבלת טראפיק (GB)",
        en: "Traffic limit (GB)",
    },
    "admin.users.plan.limits.video-editor": {
        he: "מגבלת עורך וידאו (דקות)",
        en: "Video editor limit (minutes)",
    },
    "admin.users.plan.limits.download": {
        he: "הורדת סרטונים",
        en: "Video downloads",
    },
    "admin.users.plan.limits.recording": {
        he: "הקלטה ברקורדר",
        en: "Recording",
    },
    "admin.users.plan.limits.chatbot-messages": {
        he: "שאלות פר טוקן בצאטבוטים",
        en: "Chatbot questions per token",
    },
    "admin.users.plan.fields.start-date": {
        he: "תאריך תחילת מנוי",
        en: "Plan start date",
    },
    "admin.users.plan.fields.start-date.placeholder": {
        he: "בחר תאריך בעבר",
        en: "Select a past date",
    },
    "admin.users.plan.fields.reset-frequency": {
        he: "תדירות איפוס קרדיטים",
        en: "Credits reset frequency",
    },
    "admin.users.plan.fields.plan-name": {
        he: "שם המנוי (מתחיל/מתקדם...)",
        en: "Plan name (starter/pro...)",
    },
    "admin.users.plan.fields.hide-pricing": {
        he: "הסתרת מחירון",
        en: "Hide pricing",
    },
    "admin.users.plan.fields.exceeded-usage": {
        he: "חריגה ממגבלות שימוש",
        en: "Exceeded usage limits",
    },
    "admin.users.flags.has-negative-balance": {
        he: "מאזן שלילי פעיל",
        en: "Has negative balance",
    },
    "admin.users.flags.negative-balance-countdown": {
        he: "ימים עד חסימת החשבון אם המאזן השלילי לא יוסדר",
        en: "Days until account blocked if negative balance not resolved",
    },
    "admin.users.flags.negative-balance-start": {
        he: "תאריך התחלת מאזן שלילי",
        en: "Negative balance start date",
    },
    "admin.users.flags.negative-balance-start.placeholder": {
        he: "בחר תאריך בעבר",
        en: "Select a past date",
    },
    "admin.users.billing.balances": { he: "יתרות", en: "Balances" },
    "admin.users.billing.add-funds.title": {
        he: "הוספת יתרה",
        en: "Add Funds",
    },
    "admin.users.billing.add-funds.button": {
        he: "הוסף יתרה",
        en: "Add Funds",
    },
    "admin.users.billing.add-funds.empty": {
        he: "יש להזין סכום אחד לפחות",
        en: "Enter at least one amount",
    },
    "admin.users.billing.add-funds.success": {
        he: "היתרה נוספה בהצלחה",
        en: "Funds added successfully",
    },
    "admin.users.billing.add-funds.error": {
        he: "שגיאה בהוספת יתרה",
        en: "Failed to add funds",
    },
    "admin.users.billing.settings": {
        he: "הגדרות חיוב",
        en: "Billing Settings",
    },
    "admin.users.billing.cycle-start-date": {
        he: "תאריך תחילת מחזור החיוב השנתי",
        en: "Yearly billing cycle start date",
    },
    "admin.users.billing.pick-date": {
        he: "בחר תאריך",
        en: "Pick a date",
    },
    "admin.users.billing.reset-on-cycle-end": {
        he: "איפוס יתרה בסוף המחזור השנתי",
        en: "Reset balance on yearly cycle end",
    },
    "admin.users.billing.negative-balance": {
        he: "יתרה שלילית",
        en: "Negative Balance",
    },
    "admin.users.tabs.renewals": {
        he: "חידושים",
        en: "Renewals",
    },
    "admin.users.billing.renewals.title": {
        he: "חידושים מתוזמנים",
        en: "Scheduled Renewals",
    },
    "admin.users.billing.renewals.schedule": {
        he: "תזמן חידוש",
        en: "Schedule Renewal",
    },
    "admin.users.billing.renewals.select-cycle": {
        he: "בחר מחזור חיוב",
        en: "Select billing cycle",
    },
    "admin.users.billing.renewals.no-cycle-date": {
        he: "לא ניתן לתזמן חידוש ללא תאריך מחזור חיוב",
        en: "Cannot schedule renewals without a billing cycle start date",
    },
    "admin.users.billing.renewals.empty": {
        he: "יש להזין סכום אחד לפחות",
        en: "Enter at least one amount",
    },
    "admin.users.billing.renewals.success": {
        he: "החידוש תוזמן בהצלחה",
        en: "Renewal scheduled successfully",
    },
    "admin.users.billing.renewals.error": {
        he: "שגיאה בתזמון חידוש",
        en: "Failed to schedule renewal",
    },
    "admin.users.billing.renewals.delete-error": {
        he: "שגיאה במחיקת חידוש",
        en: "Failed to delete renewal",
    },
    "admin.users.billing.renewals.delete-success": {
        he: "החידוש נמחק בהצלחה",
        en: "Renewal deleted successfully",
    },
    "admin.users.billing.renewals.none": {
        he: "אין חידושים מתוזמנים",
        en: "No scheduled renewals",
    },
    "admin.users.billing.renewals.pending": {
        he: "ממתין",
        en: "Pending",
    },
    "admin.users.billing.renewals.applied": {
        he: "הופעל",
        en: "Applied",
    },
    "admin.common.enabled": { he: "פתוח", en: "Enabled" },
    "admin.common.disabled": { he: "חסום", en: "Disabled" },
    "lomda-sheet.aside.card.language.title": { he: "שפה", en: "Language" },
    "lomda-sheet.aside.card.lomda-aspect-ratio.title": {
        he: "יחס גובה-רוחב",
        en: "Aspect ratio",
    },
    "lomda-sheet.aside.card.lomda-created-at.title": {
        he: "נוצר בתאריך",
        en: "Created on",
    },
    "lomda-sheet.aside.card.lomda-updated-at.title": {
        he: "עודכן לאחרונה",
        en: "Last updated",
    },
    "lomda-sheet.aside.card.num-of-slides.title": {
        he: "מספר סצנות",
        en: "Number of scenes",
    },
    "lomda-sheet.folders.label": { he: "תיקיות", en: "Folders" },
    "lomda-sheet.general-tab.lomda-name.label": {
        he: "שם הלומדה",
        en: "Learning module name",
    },
    "lomda-sheet.general-tab.save-btn": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "lomda-sheet.header.delete-btn": { he: "מחיקה", en: "Delete" },
    "lomda-sheet.header.duplicate-btn": { he: "העתקה", en: "Duplicate" },
    "lomda-sheet.header.edit-btn": { he: "עריכה", en: "Edit" },
    "lomda-sheet.header.folders": {
        he: "תיקיות: {folderNames}",
        en: "Folders: {folderNames}",
    },
    "lomda-sheet.header.title": {
        he: "לומדה: {lomdaName}",
        en: "Learning module: {lomdaName}",
    },
    "lomda-sheet.header.title.placeholder": {
        he: "לומדה ללא שם",
        en: "Untitled learning module",
    },
    "lomda-sheet.duplicate.default-name": {
        he: "{name} - עותק",
        en: "{name} - copy",
    },
    "lomda.render.steps.progress": {
        he: "שלב {currentStep} מתוך {totalSteps}",
        en: "Step {currentStep} of {totalSteps}",
    },
    "lomda.render.steps.rendering": {
        he: "מתבצע רינדור",
        en: "Rendering in progress",
    },
    "lomda.render.steps.completed": {
        he: "העלאה ועיבוד הסתיימו בהצלחה!",
        en: "Upload and processing completed successfully!",
    },
    "navbar.camera-menu.name": { he: "בחירת מצלמה", en: "Choose camera" },
    "navbar.file-menu.export-file.title": {
        he: "הורדת קובץ מצגת",
        en: "Download presentation file",
    },
    "navbar.file-menu.export.description": {
        he: "ייצוא הקובץ של המצגת הזאת",
        en: "Export this presentation file",
    },
    "navbar.file-menu.import-file.title": {
        he: "פתיחת קובץ מצגת",
        en: "Open presentation file",
    },
    "navbar.file-menu.import-pdf.title": { he: "ייבוא PDF", en: "Import PDF" },
    "navbar.file-menu.import.description": {
        he: "ייבוא קובץ של מצגת אחרת",
        en: "Import a file from another presentation",
    },
    "navbar.file-menu.name": { he: "קובץ", en: "File" },
    "navbar.file-menu.pdf.description": {
        he: "הורדה של המצגת כ-PDF",
        en: "Download the presentation as PDF",
    },
    "navbar.file-menu.pdf.title": { he: "הורדה כ-PDF", en: "Download as PDF" },
    "navbar.mic-menu.name": { he: "בחירת מיקרופון", en: "Choose microphone" },
    "navbar.hint.sidebar": { he: "פתיחת תפריט צד", en: "Toggle sidebar" },
    "navbar.hint.redo": { he: "בצע שוב", en: "Redo" },
    "navbar.hint.undo": { he: "בטל", en: "Undo" },
    "navbar.hint.choose-camera": {
        he: "בחירת מצלמה",
        en: "Choose camera",
    },
    "navbar.hint.microphone": {
        he: "בחירת מיקרופון",
        en: "Choose microphone",
    },
    "navbar.hint.sharescreen": {
        he: "שיתוף מסך",
        en: "Screen sharing",
    },
    "navbar.hint.floating-nav": {
        he: "הצג או הסתר שלט סצנות",
        en: "Show or hide scene navigator",
    },
    "navbar.hint.generate-video": { he: "צור וידאו", en: "Generate video" },
    non_existant: { he: "Non existant", en: "Non-existent" },
    "paused-indicator.title": { he: "Paused", en: "Paused" },
    "payments.cancel-plan-alert-modal.cancel-plan-button": {
        he: "ביטול המנוי",
        en: "Cancel subscription",
    },
    "payments.cancel-plan-alert-modal.description": {
        he: "ביטול המנוי יביא למחיקת כל התכנים שלכם בתוך 14 יום ממועד סיום המנוי, אתם בטוחים שתרצו להמשיך?",
        en: "Canceling the subscription will delete all your content within 14 days after the subscription ends. Are you sure you want to continue?",
    },
    "payments.cancel-plan-alert-modal.ok-button": { he: "חזרה", en: "Back" },
    "payments.cancel-plan-alert-modal.title": {
        he: "ביטול מנוי",
        en: "Cancel subscription",
    },
    "payments.change-plan-alert-modal.description": {
        he: "כדי לבצע שינויים במנוי יש ליצור קשר עם מחלקת המכירות שלנו ונעזור לכם בהקדם.",
        en: "To make subscription changes, contact our sales team and we'll help you soon.",
    },
    "payments.change-plan-alert-modal.ok-button": {
        he: "אישור",
        en: "Confirm",
    },
    "payments.change-plan-alert-modal.title": {
        he: "שינויים במנוי",
        en: "Subscription changes",
    },
    "payments.errors.no-plan-selected": {
        he: "לא נבחר מנוי.",
        en: "No plan selected.",
    },
    "payments.errors.unexpected-contact-support": {
        he: "קרתה שגיאה לא צפויה. צרו קשר עם שירות הלקוחות שלנו.",
        en: "An unexpected error occurred. Please contact our support team.",
    },
    "payments.header.title": { he: "הצטרפות", en: "Join" },
    "payments.header.title.payment": { he: "תשלום", en: "Pay" },
    "payments.header.discount-badge": {
        he: "{percent}% הנחה",
        en: "{percent}% off",
    },
    "platform.sidebar.logo.alt": { he: "לוגו", en: "Logo" },
    "platform.sidebar.home": { he: "בית", en: "Home" },
    "platform.sidebar.storage": { he: "אחסון", en: "Storage" },
    "platform.sidebar.courses": { he: "קורסים", en: "Courses" },
    "platform.sidebar.chat-bots": { he: "סוכני AI", en: "AI agents" },
    "platform.sidebar.analytics": { he: "אנליטיקות", en: "Analytics" },
    "platform.sidebar.tools": { he: "כלים", en: "Tools" },
    "platform.sidebar.section.platform": { he: "פלטפורמה", en: "Platform" },
    "platform.sidebar.section.tools": { he: "כלים", en: "Tools" },
    "platform.sidebar.section.account": { he: "חשבון", en: "Account" },
    "platform.sidebar.section.admin": { he: "אדמין", en: "Admin" },
    "platform.sidebar.tools.html-lomda": {
        he: "יצירת לומדת HTML",
        en: "Create HTML lomda",
    },
    "platform.sidebar.tools.video-lomda": {
        he: "יצירת לומדת וידאו",
        en: "Create video lomda",
    },
    "platform.sidebar.tools.record-presentation": {
        he: "הקלטת מצגת",
        en: "Record a presentation",
    },
    "platform.sidebar.tools.syllabus": {
        he: "יצירת סילאבוס",
        en: "Create syllabus",
    },
    "platform.sidebar.tools.presentation": {
        he: "הקלטת מצגת",
        en: "Create and record presentation",
    },
    "platform.sidebar.tools.lomda": {
        he: "יצירת לומדת וידאו",
        en: "Create Video Lomda",
    },
    "platform.sidebar.tools.lomda-v3": {
        he: "יצירת לומדת HTML",
        en: "Create HTML Lomda",
    },
    "platform.sidebar.tools.lomda-v3-ai": {
        he: "יצירת לומדת HTML עם AI",
        en: "Create HTML Lomda with AI",
    },
    "platform.sidebar.tools.upload-videos": {
        he: "העלאת סרטונים",
        en: "Upload videos",
    },
    "platform.sidebar.pricing": { he: "מחירון", en: "Pricing" },
    "platform.sidebar.admin": { he: "אדמין", en: "Admin" },
    "platform.sidebar.admin.users": { he: "משתמשים", en: "Users" },
    "platform.sidebar.admin.content": { he: "תכנים", en: "Content" },
    "platform.sidebar.admin.admins": { he: "אדמינים", en: "Admins" },
    "platform.sidebar.admin.design": { he: "עיצוב", en: "Design" },
    "platform.sidebar.coming-soon-badge": { he: "בקרוב", en: "Coming soon" },
    "platform.sidebar.negative-balance.title": {
        he: "חשבונך ייחסם בקרוב",
        en: "Your account will be blocked soon",
    },
    "platform.sidebar.negative-balance.description": {
        he: "יש לכם יתרה שלילית. החשבון ייחסם בעוד {days} ימים. אנא הוסיפו יתרה או הסדירו את התשלום.",
        en: "You have a negative balance. Your account will be blocked in {days} days. Please add funds to settle your balance.",
    },
    "platform.sidebar.negative-balance.cta": {
        he: "הוספת יתרה",
        en: "Add funds",
    },
    "platform.sidebar.billing-cycle-ending.title": {
        he: "חידוש מנוי נדרש",
        en: "Renew your plan",
    },
    "platform.sidebar.billing-cycle-ending.description": {
        he: "המנוי שלכם מסתיים בעוד {days} ימים. צרו איתנו קשר לחידוש המנוי ולמניעת הפסקת שירות.",
        en: "Your plan ends in {days} days. Contact us to renew and avoid service disruption.",
    },
    "platform.sidebar.contact-us.trigger": {
        he: "צרו קשר",
        en: "Contact Us",
    },
    "platform.sidebar.contact-us.title": {
        he: "צרו קשר",
        en: "Contact Us",
    },
    "platform.sidebar.contact-us.description": {
        he: "מלאו את הפרטים ונחזור אליכם בהקדם",
        en: "Fill in your details and we'll get back to you shortly",
    },
    "platform.sidebar.contact-us.name": {
        he: "שם",
        en: "Name",
    },
    "platform.sidebar.contact-us.email": {
        he: "אימייל",
        en: "Email",
    },
    "platform.sidebar.contact-us.phone": {
        he: "טלפון",
        en: "Phone",
    },
    "platform.sidebar.contact-us.message": {
        he: "הודעה",
        en: "Message",
    },
    "platform.sidebar.contact-us.submit": {
        he: "שליחה",
        en: "Send",
    },
    "platform.sidebar.contact-us.success": {
        he: "ההודעה נשלחה בהצלחה!",
        en: "Message sent successfully!",
    },
    "platform.sidebar.billing-reminder.title": {
        he: "תזכורת תשלום",
        en: "Payment reminder",
    },
    "platform.sidebar.billing-reminder.description": {
        he: "תקופת החיוב שלך מתחדשת בעוד {days} ימים. הוסיפו כספים כדי למנוע הפסקת שירות.",
        en: "Your billing cycle renews in {days} days. Add funds to avoid service interruption.",
    },
    "platform.sidebar.billing-reminder.cta": {
        he: "הוספת כספים",
        en: "Add funds",
    },
    "presentation-sheet.aside.card.num-of-slides.title": {
        he: "מספר שקופיות",
        en: "Number of slides",
    },
    "presentation-sheet.aside.card.presentation-aspect-ratio.title": {
        he: "גודל המצגת",
        en: "Presentation size",
    },
    "presentation-sheet.aside.card.presentation-created-at.title": {
        he: "נוצר בתאריך",
        en: "Created on",
    },
    "presentation-sheet.aside.card.presentation-updated-at.title": {
        he: "עודכן לאחרונה",
        en: "Last updated",
    },
    "presentation-sheet.folders.label": { he: "תיקיות", en: "Folders" },
    "presentation-sheet.general-tab.presentation-name.label": {
        he: "שם המצגת",
        en: "Presentation name",
    },
    "presentation-sheet.general-tab.save-btn": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "presentation-sheet.header.delete-btn": { he: "מחיקה", en: "Delete" },
    "presentation-sheet.header.duplicate-btn": { he: "שכפול", en: "Duplicate" },
    "presentation-sheet.header.edit-btn": { he: "עריכה", en: "Edit" },
    "presentation-sheet.header.folders": {
        he: "תיקיות: {folderNames}",
        en: "Folders: {folderNames}",
    },
    "presentation-sheet.header.title": {
        he: "מצגת: {presentationName}",
        en: "Presentation: {presentationName}",
    },
    "presentation-sheet.header.title.placeholder": {
        he: "מצגת ללא שם",
        en: "Untitled presentation",
    },
    "presentation.action-modals.duplicate.description": {
        he: "בחרו שם למצגת החדשה",
        en: "Choose a name for the new presentation",
    },
    "presentation.action-modals.duplicate.name-input.label": {
        he: "שם",
        en: "Name",
    },
    "presentation.action-modals.duplicate.submit-btn": {
        he: "שכפול",
        en: "Duplicate",
    },
    "presentation.action-modals.duplicate.title": {
        he: "שכפול מצגת",
        en: "Duplicate presentation",
    },
    "presentation.action-modals.duplicate.default-name": {
        he: "{name} (עותק)",
        en: "{name} (copy)",
    },
    "recorder.floating-footer-menu.stop-screenshare-btn": {
        he: "סיום שיתוף מסך",
        en: "Stop screen sharing",
    },
    "recorder.footer.finish-recording": {
        he: "סיום הקלטה",
        en: "Finish recording",
    },
    "recorder.footer.start-btn": { he: "הקלטה", en: "Record" },
    "recorder.footer.pause-btn": { he: "השהייה", en: "Pause" },
    "recorder.footer.resume-btn": { he: "המשך", en: "Resume" },
    "recorder.image-sidebar.stock-images.actions-menu.for-green-screen": {
        he: "למסך הירוק",
        en: "Green screen",
    },
    "recorder.image-sidebar.stock-images.actions-menu.for-presentation": {
        he: "למצגת",
        en: "To presentation",
    },
    "recorder.image-sidebar.stock-images.actions-menu.for-slide": {
        he: "לשקופית",
        en: "To slide",
    },
    "recorder.image-sidebar.stock-images.actions-menu.label": {
        he: "בחירת התמונה כרקע",
        en: "Set image as background",
    },
    "recorder.sidebar.animation.duration-input.label": {
        he: "משך האנימציה (בשניות)",
        en: "Animation duration (seconds)",
    },
    "recorder.sidebar.animation.order-input.label": {
        he: "מספר האנימציה (ביחס לאנימציות בשקופית)",
        en: "Animation number (relative to slide animations)",
    },
    "recorder.sidebar.animation.remove-btn": { he: "מחיקה", en: "Delete" },
    "recorder.sidebar.animation.save-btn": { he: "שמירה", en: "Save" },
    "recorder.sidebar.draw.thickness": { he: "עובי", en: "Thickness" },
    "recorder.sidebar.icons.icon-stock.load-failed": {
        he: "ייבוא אייקונים נכשל. נסו שוב מאוחר יותר.",
        en: "Icon import failed. Please try again later.",
    },
    "recorder.sidebar.icons.icon-stock.load-more-btn": {
        he: "טען עוד...",
        en: "Load more...",
    },
    "recorder.sidebar.icons.icon-stock.no-results": {
        he: "לא נמצאו אייקונים. נסו חיפוש אחר.",
        en: "No icons found. Try another search.",
    },
    "recorder.sidebar.icons.icons-stock": {
        he: "מאגר אייקונים",
        en: "Icon library",
    },
    "recorder.sidebar.icons.header.title": { he: "אייקונים", en: "Icons" },
    "recorder.sidebar.icons.header.description": {
        he: "הוספת אייקונים למצגת",
        en: "Add icons to the presentation",
    },
    "recorder.sidebar.images.image-stock": {
        he: "מאגר תמונות",
        en: "Image library",
    },
    "recorder.sidebar.images.image-stock.load-failed": {
        he: "ייבוא תמונות נכשל. נסו שוב מאוחר יותר.",
        en: "Image import failed. Please try again later.",
    },
    "recorder.sidebar.images.image-stock.load-more-btn": {
        he: "טען עוד...",
        en: "Load more...",
    },
    "recorder.sidebar.images.image-stock.no-results": {
        he: "לא נמצאו תמונות. נסו חיפוש אחר.",
        en: "No images found. Try another search.",
    },
    "recorder.sidebar.settings.aspect-ratio.title": {
        he: "יחס גובה רוחב",
        en: "Aspect ratio",
    },
    "recorder.sidebar.settings.aspect-ratio.16:9": { he: "16:9", en: "16:9" },
    "recorder.sidebar.settings.aspect-ratio.9:16": { he: "9:16", en: "9:16" },
    "recorder.sidebar.settings.aspect-ratio.4:5": { he: "4:5", en: "4:5" },
    "recorder.sidebar.settings.aspect-ratio.1:1": { he: "1:1", en: "1:1" },
    "recorder.sidebar.settings.aspect-ratio.comment.landscape": {
        he: "לרוחב",
        en: "Landscape",
    },
    "recorder.sidebar.settings.aspect-ratio.comment.portrait": {
        he: "לאורך",
        en: "Portrait",
    },
    "recorder.sidebar.settings.aspect-ratio.comment.square": {
        he: "מרובע",
        en: "Square",
    },
    "recorder.sidebar.settings.bg-color.title": {
        he: "צבע רקע",
        en: "Background color",
    },
    "recorder.sidebar.settings.bg.images.title": {
        he: "תמונת רקע",
        en: "Background image",
    },
    "recorder.sidebar.settings.palette.title": {
        he: "פלטת צבעים",
        en: "Color palette",
    },
    "recorder.sidebar.settings.unsupported-browser": {
        he: "חלק מהאפשרויות לרקע המצלמה לא נתמכות בדפדפן שלך. יש להשתמש בכרום או בספארי.",
        en: "Some camera background options are not supported in your browser. Please use Chrome or Safari.",
    },
    "recorder.sidebar.settings.webcam-mode.title": {
        he: "רקע מצלמה",
        en: "Camera background",
    },
    "recorder.sidebar.settings.webcam-mode.normal": {
        he: "רגיל",
        en: "Normal",
    },
    "recorder.sidebar.settings.webcam-mode.green-screen": {
        he: "מסך ירוק",
        en: "Green screen",
    },
    "recorder.sidebar.settings.webcam-mode.blurred": {
        he: "טשטוש",
        en: "Blurred",
    },
    "recorder.sidebar.settings.webcam-mode.transparent": {
        he: "שקוף",
        en: "Transparent",
    },
    "recorder.sidebar.settings.webcam-resolution.title": {
        he: "רזולוציית מצלמה",
        en: "Camera resolution",
    },
    "recorder.sidebar.settings.webcam-shape.title": {
        he: "צורת מצלמה",
        en: "Camera shape",
    },
    "recorder.sidebar.settings.webcam-shape.rectangle": {
        he: "מלבן",
        en: "Rectangle",
    },
    "recorder.sidebar.settings.webcam-shape.circle": {
        he: "עיגול",
        en: "Circle",
    },
    "recorder.sidebar.slides.actions.delete": {
        he: "מחיקת השקופית",
        en: "Delete slide",
    },
    "recorder.sidebar.slides.actions.duplicate": {
        he: "שכפול השקופית",
        en: "Duplicate slide",
    },
    "recorder.sidebar.slides.actions.modal.close-btn": {
        he: "סיום",
        en: "Finish",
    },
    "recorder.sidebar.slides.actions.template": {
        he: "טמפלייטים",
        en: "Templates",
    },
    "recorder.sidebar.slides.settings-modal.bg-image.label": {
        he: "תמונת רקע",
        en: "Background image",
    },
    "recorder.sidebar.slides.settings-modal.description": {
        he: "עריכת הגדרות השקופית",
        en: "Edit slide settings",
    },
    "recorder.sidebar.slides.settings-modal.title": {
        he: "שקופית מספר {slideIndex}",
        en: "Slide #{slideIndex}",
    },
    "recorder.sidebar.slides.settings-modal.webcam-position.label": {
        he: "מיקום מצלמה",
        en: "Camera position",
    },
    "recorder.sidebar.storage": { he: "אחסון", en: "Storage" },
    "recorder.sidebar.slides": { he: "מצגת", en: "Slides" },
    "recorder.sidebar.texts": { he: "טקסט", en: "Text" },
    "recorder.sidebar.images": { he: "תמונות", en: "Images" },
    "recorder.sidebar.icons": { he: "אייקונים", en: "Icons" },
    "recorder.sidebar.shapes": { he: "צורות", en: "Shapes" },
    "recorder.sidebar.draw": { he: "ציור", en: "Draw" },
    "recorder.sidebar.settings": { he: "הגדרות", en: "Settings" },
    "recorder.sidebar.stroke.color": { he: "צבע", en: "Color" },
    "recorder.sidebar.stroke.thickness": { he: "עובי", en: "Thickness" },
    "recorder.sidebar.stroke.type": { he: "סוג", en: "Type" },
    "recorder.sidebar.texts.add-paragraph": {
        he: "הוספת פסקה",
        en: "Add paragraph",
    },
    "recorder.sidebar.texts.add-subtitle": {
        he: "הוספת תת כותרת",
        en: "Add subtitle",
    },
    "recorder.sidebar.texts.add-textbox": {
        he: "הוספת קופסת טקסט",
        en: "Add text box",
    },
    "recorder.sidebar.texts.add-title": {
        he: "הוספת כותרת",
        en: "Add title",
    },
    "recorder.slide-settings-modal.bg-color.input.label": {
        he: "צבע רקע",
        en: "Background color",
    },
    "recorder.templates.choose-template-btn": { he: "בחירה", en: "Select" },
    "recorder.templates.empty-slide.title": {
        he: "שקופית ריקה",
        en: "Blank slide",
    },
    "recorder.templates.generate-with-ai-input.btn": {
        he: "צור עם AI",
        en: "Create with AI",
    },
    "recorder.templates.generate-without-ai.btn": { he: "בחירה", en: "Select" },
    "recorder.upload-video-modal.storage-form.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "recorder.floating-footer-menu.slide-nav.next": {
        he: "לשקופית הבאה",
        en: "Next slide",
    },
    "recorder.floating-footer-menu.slide-nav.prev": {
        he: "לשקופית הקודמת",
        en: "Previous slide",
    },
    "recorder.floating-footer-menu.readd-webcam": {
        he: "הוספת מצלמה",
        en: "Add webcam",
    },
    "recorder.navbar.sharescreen.start": {
        he: "שיתוף מסך",
        en: "Start screen share",
    },
    "recorder.navbar.sharescreen.stop": {
        he: "הפסקת שיתוף מסך",
        en: "Stop screen share",
    },
    "recorder.project.save-failed": {
        he: "שמירת המצגת נכשלה",
        en: "Failed to save presentation",
    },
    "recorder.sidebar.fill-color.header.title": { he: "צבע", en: "Color" },
    "recorder.sidebar.fill-color.header.description": {
        he: "שנו צבע לאלמנט הנבחר",
        en: "Change the selected element color",
    },
    "recorder.sidebar.stroke.header.title": {
        he: "אפשרויות קו מתאר",
        en: "Stroke options",
    },
    "recorder.sidebar.stroke.header.description": {
        he: "שנו את קו המתאר של האלמנט הנבחר",
        en: "Change the selected element outline",
    },
    "recorder.sidebar.images.header.title": { he: "תמונות", en: "Images" },
    "recorder.sidebar.images.header.description": {
        he: "הוספת תמונות למצגת",
        en: "Add images to the presentation",
    },
    "recorder.sidebar.replace-image.header.title": {
        he: "החלפת תמונה",
        en: "Replace image",
    },
    "recorder.sidebar.replace-image.header.description": {
        he: "החליפו את התמונה הנבחרת",
        en: "Replace the selected image",
    },
    "recorder.sidebar.images.upload-image.title": {
        he: "העלאת תמונה",
        en: "Upload image",
    },
    "recorder.sidebar.images.upload-image.subtitle": {
        he: "גררו לכאן תמונה, או לחצו כדי לבחור תמונה",
        en: "Drag an image here, or click to choose an image",
    },
    "recorder.sidebar.opacity.header.title": { he: "אטימות", en: "Opacity" },
    "recorder.sidebar.opacity.header.description": {
        he: "שנו את רמת האטימות של האלמנט הנבחר",
        en: "Change the opacity of the selected element",
    },
    "recorder.sidebar.shapes.header.title": { he: "צורות", en: "Shapes" },
    "recorder.sidebar.shapes.header.description": {
        he: "הוסיפו צורות למצגת",
        en: "Add shapes to the presentation",
    },
    "recorder.sidebar.texts.header.title": { he: "טקסט", en: "Text" },
    "recorder.sidebar.texts.header.description": {
        he: "הוסיפו טקסטים למצגת",
        en: "Add text to the presentation",
    },
    "recorder.sidebar.texts.defaults.textbox": { he: "טקסט", en: "Text" },
    "recorder.sidebar.texts.defaults.title": { he: "כותרת", en: "Title" },
    "recorder.sidebar.texts.defaults.subtitle": {
        he: "תת כותרת",
        en: "Subtitle",
    },
    "recorder.sidebar.texts.defaults.paragraph": {
        he: "פסקה",
        en: "Paragraph",
    },
    "recorder.sidebar.settings.bg.presentation.tab": {
        he: "למצגת",
        en: "Presentation",
    },
    "recorder.sidebar.settings.bg.green-screen.tab": {
        he: "למסך ירוק",
        en: "Green screen",
    },
    "recorder.sidebar.settings.upload-bg-image.title": {
        he: "העלאת תמונה",
        en: "Upload image",
    },
    "recorder.sidebar.settings.upload-bg-image.subtitle": {
        he: "גררו לכאן תמונה, או לחצו כדי לבחור תמונה",
        en: "Drag an image here, or click to choose an image",
    },
    "recorder.sidebar.fonts.header.title": { he: "פונט", en: "Font" },
    "recorder.sidebar.fonts.header.description": {
        he: "שינוי פונט הטקסט שנבחר",
        en: "Change the selected text font",
    },
    "recorder.sidebar.animations.header.title": {
        he: "אנימציות",
        en: "Animations",
    },
    "recorder.sidebar.animations.header.description": {
        he: "הוסיפו אנימציות לאלמנטים הנבחרים",
        en: "Add animations to the selected elements",
    },
    "recorder.animations.fade-in.name": { he: "עמעום", en: "Fade in" },
    "recorder.animations.slide-from-left.name": {
        he: "כניסה משמאל",
        en: "Slide in from left",
    },
    "recorder.animations.slide-from-right.name": {
        he: "כניסה מימין",
        en: "Slide in from right",
    },
    "recorder.sidebar.animation.duration-input.placeholder": {
        he: "למשל: 1.2",
        en: "e.g. 1.2",
    },
    "recorder.sidebar.filters.header.title": { he: "פילטרים", en: "Filters" },
    "recorder.sidebar.filters.header.description": {
        he: "החילו פילטרים על התמונה",
        en: "Apply filters to the image",
    },
    "recorder.sidebar.draw.header.title": { he: "ציור", en: "Draw" },
    "recorder.sidebar.draw.header.description": {
        he: "שינוי צבע ועובי העט",
        en: "Change pen color and thickness",
    },
    "recorder.sidebar.slides.header.title": { he: "מצגת", en: "Slides" },
    "recorder.sidebar.slides.header.description": {
        he: "יצירה ועריכה של שקופיות",
        en: "Create and edit slides",
    },
    "recorder.sidebar.slides.actions.webcam-position.hint": {
        he: "מיקום מצלמה",
        en: "Webcam position",
    },
    "recorder.sidebar.slides.actions.sharescreen.hint": {
        he: "שקופית שיתוף מסך",
        en: "Screen share slide",
    },
    "recorder.sidebar.slides.actions.modal.webcam-position.placeholder": {
        he: "בחרו מיקום מצלמה",
        en: "Select webcam position",
    },
    "recorder.templates-modal.user-mode.title": {
        he: "תבניות",
        en: "Templates",
    },
    "recorder.templates-modal.user-mode.subtitle": {
        he: "בחרו תבנית מעוצבת לשקופית שלכם",
        en: "Choose a designed template for your slide",
    },
    "recorder.templates.categories.small-webcam": {
        he: "מצלמה קטנה",
        en: "Small webcam",
    },
    "recorder.templates.categories.full-screen-webcam": {
        he: "מצלמה במסך מלא",
        en: "Fullscreen webcam",
    },
    "recorder.templates.categories.title": { he: "כותרת", en: "Title" },
    "recorder.templates.categories.half-screen": {
        he: "חצי מסך",
        en: "Half screen",
    },
    "recorder.templates.modal.admin-title": {
        he: "טמפלייטים - אדמין",
        en: "Templates - Admin",
    },
    "recorder.templates.modal.title": {
        he: "לחצו על טמפלייט כדי לערוך אותו.",
        en: "Click a template to edit it.",
    },
    "recorder.pdf.page-label": { he: "עמוד {index}", en: "Page {index}" },
    "recorder.recording.default-name": {
        he: "הקלטה",
        en: "Recording",
    },
    "recorder.animations.hints.text": {
        he: "אנימציה על טקסט",
        en: "Text animation",
    },
    "recorder.animations.hints.image": {
        he: "אנימציה על תמונה",
        en: "Image animation",
    },
    "recorder.animations.hints.shape": {
        he: "אנימציה על צורה",
        en: "Shape animation",
    },
    "recorder.animations.hints.multiple": {
        he: "אנימציה על כמה אובייקטים",
        en: "Multiple objects animation",
    },
    "recorder.animations.hints.default": {
        he: "אנימציה",
        en: "Animation",
    },
    "recorder.easing.easeInQuad": {
        he: "התחלה איטית, סיום מהיר",
        en: "Slow start, fast end",
    },
    "recorder.easing.easeOutQuad": {
        he: "התחלה מהירה, סיום איטי",
        en: "Fast start, slow end",
    },
    "recorder.easing.easeInOutQuad": {
        he: "התחלה וסיום חלקים",
        en: "Smooth start and end",
    },
    "recorder.easing.easeInCubic": {
        he: "התגברות הדרגתית",
        en: "Gradual acceleration",
    },
    "recorder.easing.easeOutCubic": {
        he: "האטה הדרגתית",
        en: "Gradual deceleration",
    },
    "recorder.easing.easeInOutCubic": {
        he: "התגברות והאטה חלקים",
        en: "Smooth acceleration and deceleration",
    },
    "recorder.easing.easeInQuart": {
        he: "התחלה איטית מאוד, סיום מהיר",
        en: "Very slow start, fast end",
    },
    "recorder.easing.easeOutQuart": {
        he: "התחלה מהירה, סיום איטי מאוד",
        en: "Very fast start, slow end",
    },
    "recorder.easing.easeInOutQuart": {
        he: "התחלה וסיום מאוזנים ואיטיים",
        en: "Balanced, slow start and end",
    },
    "recorder.easing.easeInQuint": {
        he: "התחלה איטית מאוד, סיום מהיר מאוד",
        en: "Very slow start, very fast end",
    },
    "recorder.easing.easeOutQuint": {
        he: "התחלה מהירה מאוד, סיום איטי מאוד",
        en: "Very fast start, very slow end",
    },
    "recorder.easing.easeInOutQuint": {
        he: "התחלה וסיום איטיים מאוד",
        en: "Very slow start and end",
    },
    "recorder.easing.easeInSine": {
        he: "התגברות עדינה והדרגתית",
        en: "Gentle, gradual acceleration",
    },
    "recorder.easing.easeOutSine": {
        he: "האטה עדינה והדרגתית",
        en: "Gentle, gradual deceleration",
    },
    "recorder.easing.easeInOutSine": {
        he: "תנועה עדינה וזורמת",
        en: "Gentle, flowing motion",
    },
    "recorder.easing.easeInExpo": {
        he: "התחלה איטית, סיום מתפרץ",
        en: "Slow start, explosive end",
    },
    "recorder.easing.easeOutExpo": {
        he: "התחלה מתפרצת, סיום רגוע",
        en: "Explosive start, calm end",
    },
    "recorder.easing.easeInOutExpo": {
        he: "התחלה וסיום מתפרצים",
        en: "Explosive start and end",
    },
    "recorder.easing.easeInCirc": {
        he: "התחלה בתנועה מעגלית",
        en: "Circular start",
    },
    "recorder.easing.easeOutCirc": {
        he: "סיום בתנועה מעגלית",
        en: "Circular end",
    },
    "recorder.easing.easeInOutCirc": {
        he: "תנועה מעגלית לכל אורך הדרך",
        en: "Circular motion throughout",
    },
    "recorder.easing.easeInElastic": {
        he: "התחלת קפיצה אלסטית",
        en: "Elastic bounce at start",
    },
    "recorder.easing.easeOutElastic": {
        he: "סיום בקפיצה אלסטית",
        en: "Elastic bounce at end",
    },
    "recorder.easing.easeInOutElastic": {
        he: "קפיצה אלסטית בתחילה ובסיום",
        en: "Elastic bounce at start and end",
    },
    "recorder.easing.easeInBack": {
        he: "התחלה ברתיעה ודחיפה קדימה",
        en: "Pull back then push forward",
    },
    "recorder.easing.easeOutBack": {
        he: "סיום בחריגה וחזרה",
        en: "Overshoot then return",
    },
    "recorder.easing.easeInOutBack": {
        he: "חריגה בתחילה ובסיום",
        en: "Overshoot at start and end",
    },
    "recorder.easing.easeInBounce": {
        he: "קפיצה פנימה",
        en: "Bounce in",
    },
    "recorder.easing.easeOutBounce": {
        he: "קפיצה החוצה",
        en: "Bounce out",
    },
    "recorder.easing.easeInOutBounce": {
        he: "קפיצה פנימה והחוצה",
        en: "Bounce in and out",
    },
    "recorder.filters.none": { he: "ללא", en: "None" },
    "recorder.filters.polaroid": { he: "פולארויד", en: "Polaroid" },
    "recorder.filters.sepia": { he: "ספיה", en: "Sepia" },
    "recorder.filters.kodachrome": { he: "קודכרום", en: "Kodachrome" },
    "recorder.filters.contrast": { he: "ניגודיות", en: "Contrast" },
    "recorder.filters.brightness": { he: "בהירות", en: "Brightness" },
    "recorder.filters.greyscale": { he: "גווני אפור", en: "Grayscale" },
    "recorder.filters.brownie": { he: "בראוני", en: "Brownie" },
    "recorder.filters.vintage": { he: "וינטג׳", en: "Vintage" },
    "recorder.filters.technicolor": { he: "טכניקולור", en: "Technicolor" },
    "recorder.filters.pixelate": { he: "פיקסלציה", en: "Pixelate" },
    "recorder.filters.invert": { he: "היפוך צבעים", en: "Invert" },
    "recorder.filters.blur": { he: "טשטוש", en: "Blur" },
    "recorder.filters.sharpen": { he: "חידוד", en: "Sharpen" },
    "recorder.filters.emboss": { he: "תבליט", en: "Emboss" },
    "recorder.filters.removecolor": { he: "הסרת צבע", en: "Remove color" },
    "recorder.filters.blacknwhite": {
        he: "שחור לבן",
        en: "Black & white",
    },
    "recorder.filters.vibrance": { he: "חיוניות", en: "Vibrance" },
    "recorder.filters.blendcolor": { he: "מיזוג צבע", en: "Blend color" },
    "recorder.filters.huerotate": { he: "סיבוב גוון", en: "Hue rotate" },
    "recorder.filters.resize": { he: "שינוי גודל", en: "Resize" },
    "recorder.filters.saturation": { he: "רוויה", en: "Saturation" },
    "recorder.filters.gamma": { he: "גאמה", en: "Gamma" },
    "recorder.devices.camera": { he: "מצלמה", en: "camera" },
    "recorder.devices.microphone": { he: "מיקרופון", en: "microphone" },
    "recorder.devices.switch-failed": {
        he: "שינוי ה-{device} נכשל",
        en: "Failed to switch {device}",
    },
    "recorder.devices.resolution-changed": {
        he: "הרזולוציה שונתה ל-{resolution}",
        en: "Resolution changed to {resolution}",
    },
    "reset-password.email": { he: "מייל לאימות", en: "Verification email" },
    "reset-password.title": { he: "שחזור סיסמה", en: "Reset password" },
    "reset-password.description": {
        he: "המשיכו עם גוגל או הזינו מייל לאימות",
        en: "Continue with Google or enter your email to verify",
    },
    "reset-password.login-link": { he: "כניסה", en: "Log in" },
    "reset-password.submit-btn": {
        he: "שליחת מייל אימות",
        en: "Send verification email",
    },
    "settings.general.name.label": { he: "שם מלא", en: "Full name" },
    "settings.general.save-btn": { he: "שמירה", en: "Save" },
    "settings.header.description": {
        he: "מכאן תוכלו לערוך את הגדרות החשבון שלכם.",
        en: "Here you can edit your account settings.",
    },
    "settings.header.title": { he: "הגדרות", en: "Settings" },
    "settings.sidenav.general": { he: "כללי", en: "General" },
    "settings.sidenav.change-password": {
        he: "שינוי סיסמה",
        en: "Change password",
    },
    "settings.sidenav.billing": { he: "חיובים", en: "Billing" },
    "settings.sidenav.yedalabs-v2": { he: "YedaLabs V2", en: "YedaLabs V2" },
    "settings.yedalabs-v2.title": {
        he: "מזהה החשבון שלכם",
        en: "Your account ID",
    },
    "settings.yedalabs-v2.description": {
        he: "הדביקו את המזהה הזה ב-YedaLabs V2 כדי לקשר את החשבון. כל לומדה שתיווצר שם תישמר בספרייה שלכם ותחויב בקרדיטים שלכם.",
        en: "Paste this ID in YedaLabs V2 to link this account. Every lomda generated there is saved in your library and charged to your credits.",
    },
    "settings.yedalabs-v2.copy": { he: "העתקת המזהה", en: "Copy ID" },
    "settings.yedalabs-v2.copied": { he: "המזהה הועתק", en: "ID copied" },
    "settings.yedalabs-v2.load-failed": {
        he: "לא ניתן היה לטעון את בקשות הקישור. רעננו את הדף ונסו שוב.",
        en: "The link requests could not be loaded. Refresh the page and try again.",
    },
    "settings.yedalabs-v2.links": {
        he: "בקשות קישור מ-YedaLabs V2",
        en: "Link requests from YedaLabs V2",
    },
    "settings.yedalabs-v2.no-links": {
        he: "אף אחד עדיין לא ביקש לקשר חשבון.",
        en: "No one has asked to link yet.",
    },
    "settings.yedalabs-v2.status.pending": {
        he: "ממתין לאישור",
        en: "Waiting for approval",
    },
    "settings.yedalabs-v2.status.approved": { he: "מקושר", en: "Linked" },
    "settings.yedalabs-v2.approve": { he: "אישור", en: "Approve" },
    "settings.yedalabs-v2.reject": { he: "דחייה", en: "Reject" },
    "settings.yedalabs-v2.remove": { he: "הסרה", en: "Remove" },
    "settings.yedalms.title": {
        he: "חיבור ל-YedaLMS",
        en: "YedaLMS connection",
    },
    "settings.yedalms.description": {
        he: "הוסיפו את מזהי הקולג׳ים ב-YedaLMS שאליהם תרצו ליצור קורסים.",
        en: "Add the YedaLMS college IDs where you want to create courses.",
    },
    "settings.yedalms.college-id.label": {
        he: "מזהה קולג׳",
        en: "College ID",
    },
    "settings.yedalms.college-id.placeholder": {
        he: "לדוגמה: 202",
        en: "For example: 202",
    },
    "settings.yedalms.college-id.remove": {
        he: "הסרת מזהה קולג׳",
        en: "Remove college ID",
    },
    "settings.yedalms.empty": {
        he: "עדיין לא הוגדרו קולג׳ים של YedaLMS.",
        en: "No YedaLMS colleges have been configured yet.",
    },
    "settings.yedalms.save-btn": {
        he: "שמירת קולג׳ים",
        en: "Save colleges",
    },
    "settings.billing.add-funds.title": {
        he: "הוספת יתרה",
        en: "Add Funds",
    },
    "settings.billing.add-funds.description": {
        he: "בחרו כמה אחסון, טראפיק וקרדיטים להוסיף וראו את העלות הכוללת לפני התשלום.",
        en: "Select how much storage, bandwidth, and credits to add and see the total cost before payment.",
    },
    "settings.billing.add-funds.storage-tb": {
        he: "{amount}TB אחסון חודשי",
        en: "{amount}TB monthly storage",
    },
    "settings.billing.add-funds.bandwidth-tb": {
        he: "{amount}TB תעבורה",
        en: "{amount}TB Bandwidth",
    },
    "settings.billing.add-funds.credits": {
        he: "{amount} קרדיטים",
        en: "{amount} Credits",
    },
    "settings.billing.add-funds.total": {
        he: 'סה"כ',
        en: "Total",
    },
    "settings.billing.add-funds.make-payment": {
        he: "תשלום",
        en: "Make payment",
    },
    "settings.billing.history.title": {
        he: "היסטוריית חיובים",
        en: "Billing History",
    },
    "settings.billing.header.date": {
        he: "תאריך",
        en: "Date",
    },
    "settings.billing.header.amount": {
        he: "סכום",
        en: "Amount",
    },
    "settings.billing.header.invoice": {
        he: "חשבונית",
        en: "Invoice",
    },
    "settings.billing.history.empty": {
        he: "אין חשבוניות להצגה",
        en: "No invoices to display",
    },
    "settings.billing.action.download-invoice": {
        he: "הורדת חשבונית",
        en: "Download invoice",
    },
    "settings.billing.add-funds.minimum-payment": {
        he: "סכום מינימלי לתשלום הוא {amount} ₪",
        en: "Minimum payment amount is {amount} ₪",
    },
    "settings.password.password-repeat.label": {
        he: "אימות סיסמה חדשה",
        en: "Verify new password",
    },
    "settings.password.password.label": {
        he: "סיסמה חדשה",
        en: "New password",
    },
    "signup.title": { he: "ברוכים הבאים", en: "Welcome" },
    "signup.description": {
        he: "המשיכו עם גוגל או מלאו את הפרטים",
        en: "Continue with Google or fill in your details",
    },
    "signup.have-account": {
        he: "יש לך כבר חשבון?",
        en: "Already have an account?",
    },
    "signup.login-link": { he: "כניסה", en: "Log in" },
    "signup.errors.email-already-exists": {
        he: "כתובת המייל כבר נמצאת בשימוש",
        en: "Email is already in use",
    },
    "signup.errors.failed-to-create-user": {
        he: "אירעה שגיאה לא צפויה",
        en: "Failed to create user",
    },
    "signup.email": { he: "מייל", en: "Email" },
    "signup.password": { he: "סיסמה", en: "Password" },
    "signup.signup-btn": { he: "הרשמה", en: "Sign up" },
    "storage.analytics-tab.filter-by-dropdown.label": {
        he: "מינימום כדי להיחשב כצפייה:",
        en: "Minimum to count as view:",
    },
    "storage.analytics.date-range-picker.placeholder": {
        he: "בחרו תאריכים",
        en: "Select dates",
    },
    "storage.chatbots-grid.item.options.delete": { he: "מחיקה", en: "Delete" },
    "storage.chatbots-grid.item.options.duplicate": { he: "שכפול", en: "Duplicate" },
    "storage.chatbots-grid.item.options.duplicate-name": {
        he: "{name} - עותק",
        en: "{name} - copy",
    },
    "duplicate-chatbot-modal.header.title": {
        he: "שכפול סוכן",
        en: "Duplicate agent",
    },
    "duplicate-chatbot-modal.header.description": {
        he: "העותק יכלול את ההגדרות ובסיס הידע, אך לא את השיחות. אם לסוכן יש הרבה ידע, השכפול עשוי להימשך דקה או שתיים.",
        en: "The copy includes the settings and the knowledge base, but not the conversations. If the agent has a lot of knowledge, this can take a minute or two.",
    },
    "duplicate-chatbot-modal.name-input.label": {
        he: "שם הסוכן החדש",
        en: "New agent name",
    },
    "duplicate-chatbot-modal.in-progress": {
        he: "משכפל... אל תסגור את החלון",
        en: "Duplicating... please keep this window open",
    },
    "duplicate-chatbot-modal.error": {
        he: "אירעה שגיאה בנסיון שכפול הסוכן. אפשר לנסות שוב.",
        en: "Failed to duplicate the agent. You can try again.",
    },
    "toast.success.duplicate-chatbot-queued": {
        he: "הסוכן שוכפל. הידע שלו מועתק ברקע.",
        en: "Agent duplicated. Its knowledge is being copied in the background.",
    },
    "toast.success.duplicate-chatbot": {
        he: "הסוכן שוכפל בהצלחה!",
        en: "Agent duplicated successfully!",
    },
    "duplicate-chatbot-modal.cancel-btn": { he: "ביטול", en: "Cancel" },
    "duplicate-chatbot-modal.duplicate-btn": { he: "שכפול", en: "Duplicate" },
    "storage.create-folder-modal.folder-name-input.label": {
        he: "שם התיקייה",
        en: "Folder name",
    },
    "storage.create-folder-modal.header.description": {
        he: "בחרו שם לתיקייה ולחצו יצירה.",
        en: "Choose a folder name and click Create.",
    },
    "storage.create-folder-modal.header.title": {
        he: "תיקייה חדשה",
        en: "New folder",
    },
    "storage.create-folder-modal.submit-btn": { he: "יצירה", en: "Create" },
    "storage.delete-chatbot-modal.header.title": {
        he: "מחיקת צאטבוט",
        en: "Delete chatbot",
    },
    "storage.delete-folder-modal.cancel-btn": { he: "ביטול", en: "Cancel" },
    "storage.delete-folder-modal.header.title": {
        he: "מחיקת תיקייה",
        en: "Delete folder",
    },
    "storage.delete-lomda-modal.cancel-btn": { he: "ביטול", en: "Cancel" },
    "storage.delete-lomda-modal.delete-btn": { he: "מחיקה", en: "Delete" },
    "storage.delete-lomda-modal.header.description": {
        he: "אתם בטוחים שתרצו למחוק את הלומדה: {lomdaTitle}? פעולה זו לא ניתנת לשחזור.",
        en: "Are you sure you want to delete the learning module: {lomdaTitle}? This action cannot be undone.",
    },
    "storage.delete-lomda-modal.header.title": {
        he: "מחיקת לומדה",
        en: "Delete learning module",
    },
    "storage.delete-presentation-modal.cancel-btn": {
        he: "ביטול",
        en: "Cancel",
    },
    "storage.delete-presentation-modal.delete-btn": {
        he: "מחיקה",
        en: "Delete",
    },
    "storage.delete-presentation-modal.header.description": {
        he: "אתם בטוחים שתרצו למחוק את המצגת: {presentationName}? פעולה זו לא ניתנת לשחזור.",
        en: "Are you sure you want to delete the presentation: {presentationName}? This action cannot be undone.",
    },
    "storage.delete-presentation-modal.header.title": {
        he: "מחיקת מצגת",
        en: "Delete presentation",
    },
    "storage.delete-video-modal.cancel-btn": { he: "ביטול", en: "Cancel" },
    "storage.delete-video-modal.delete-btn": { he: "מחיקה", en: "Delete" },
    "storage.delete-video-modal.header.description": {
        he: "אתם בטוחים שתרצו למחוק את הסרטון: {videoTitle}? פעולה זו לא ניתנת לשחזור.",
        en: "Are you sure you want to delete the video: {videoTitle}? This action cannot be undone.",
    },
    "storage.delete-video-modal.header.title": {
        he: "מחיקת סרטון",
        en: "Delete video",
    },
    "storage.breadcrumbs.root": { he: "אחסון", en: "Storage" },
    "storage.files-grid.filter-by-type-btn": { he: "סוג", en: "Type" },
    "storage.files-grid.order-btn": { he: "סדר", en: "Order" },
    "storage.files-grid.pagination.page-out-of-pages": {
        he: "עמוד {currentPage} מתוך {totalPages}",
        en: "Page {currentPage} of {totalPages}",
    },
    "storage.files-grid.pagination.next": { he: "הבא", en: "Next" },
    "storage.files-grid.pagination.prev": { he: "הקודם", en: "Previous" },
    "storage.files-grid.pagination.results-per-page": {
        he: "תוצאות בעמוד:",
        en: "Results per page:",
    },
    "storage.files-grid.select-all-btn": { he: "בחירת הכל", en: "Select all" },
    "storage.files-grid.sort-btn": { he: "מיון לפי", en: "Sort by" },
    "storage.files-grid.title": { he: "קבצים", en: "Files" },
    "storage.files-grid.empty-filtered": {
        he: "אין בתיקייה קבצים מהסוג הזה",
        en: "No files of this type in this folder",
    },
    "storage.folder.delete-folder-modal.delete-btn": {
        he: "מחיקה",
        en: "Delete",
    },
    "storage.folder.settings-modal.header.description": {
        he: "כאן תוכלו לערוך את הגדרות התיקייה",
        en: "Here you can edit the folder settings",
    },
    "storage.folder.settings-modal.header.title": {
        he: "הגדרות התיקייה",
        en: "Folder settings",
    },
    "storage.folder.settings-modal.name-input.label": {
        he: "שם התיקייה",
        en: "Folder name",
    },
    "storage.folder.settings-modal.save-btn": { he: "שמירה", en: "Save" },
    "storage.folder.settings-modal.preset.label": {
        he: "תבנית העלאה",
        en: "Upload preset",
    },
    "storage.folder.settings-modal.preset.placeholder": {
        he: "בחירת תבנית",
        en: "Select a preset",
    },
    "storage.folder.settings-modal.preset.search-placeholder": {
        he: "חיפוש לפי שם...",
        en: "Search by name...",
    },
    "storage.folder.settings-modal.preset.empty": {
        he: "לא נמצאו תבניות",
        en: "No presets found",
    },
    "storage.folder.settings-modal.preset.none": {
        he: "ללא תבנית",
        en: "No preset",
    },
    "storage.folder.settings-modal.preset.hint": {
        he: "העלאת סרטונים מהתיקייה תתחיל עם אפשרויות העיבוד של התבנית.",
        en: "Uploading videos from this folder will start with the preset's processing options.",
    },
    "storage.folder.settings-modal.preset.manage-link": {
        he: "ניהול תבניות",
        en: "Manage presets",
    },
    "storage.folder-card.empty": { he: "ריקה", en: "Empty" },
    "storage.folder-card.files-count": {
        he: "{count} קבצים",
        en: "{count} files",
    },
    "storage.folder-card.folders-count": {
        he: "{count} תיקיות",
        en: "{count} folders",
    },
    "storage.folder-card.folders-files-count": {
        he: "{folders} תיקיות · {files} קבצים",
        en: "{folders} folders · {files} files",
    },
    "storage.folders-grid.item.options.delete": { he: "מחיקה", en: "Delete" },
    "storage.folders-grid.item.options.edit": { he: "הגדרות", en: "Settings" },
    "storage.folders-grid.item.options.export": {
        he: "ייצוא לינקים להטמעה",
        en: "Export embed links",
    },
    "storage.folders-grid.item.options.download": {
        he: "ייצוא לינקים להורדה",
        en: "Export download links",
    },
    "storage.folders-grid.item.options.label": {
        he: "אפשרויות תיקייה",
        en: "Folder options",
    },
    "storage.folders-grid.item.options.export-captions": {
        he: "ייצוא כתוביות",
        en: "Export captions",
    },
    "storage.folders-grid.item.options.upload-videos": {
        he: "העלאת סרטונים",
        en: "Upload videos",
    },
    "storage.folders-grid.item.options.create-yedalms-course": {
        he: "יצירת קורס ב-YedaLMS",
        en: "Create YedaLMS course",
    },
    "storage.create-yedalms-course-modal.title": {
        he: "יצירת קורס ב-YedaLMS",
        en: "Create a YedaLMS course",
    },
    "storage.create-yedalms-course-modal.description": {
        he: "ניצור קורס מהתיקייה {folder}. כל סרטון יהפוך לשיעור, לפי סדר שמות הקבצים.",
        en: "Create a course from {folder}. Each video becomes a lesson, ordered by file name.",
    },
    "storage.create-yedalms-course-modal.hint": {
        he: "שם הקורס, התיאור ושמות השיעורים יווצרו בעזרת AI על בסיס שם התיקייה, שמות הסרטונים ודגימה קצרה מהכתוביות.",
        en: "The course name, description and lesson titles will be generated with AI from the folder name, video titles and short caption samples.",
    },
    "storage.create-yedalms-course-modal.create-btn": {
        he: "יצירת קורס",
        en: "Create course",
    },
    "storage.create-yedalms-course-modal.close-btn": {
        he: "סגירה",
        en: "Close",
    },
    "storage.create-yedalms-course-modal.created": {
        he: "הקורס נוצר בהצלחה. מזהה קורס: {id}",
        en: "Course created successfully. Course ID: {id}",
    },
    "storage.create-yedalms-course-modal.error.no-videos": {
        he: "בתיקייה הזו אין סרטונים. יש לבחור תיקייה שמכילה לפחות סרטון אחד.",
        en: "This folder does not contain any videos. Choose a folder with at least one video.",
    },
    "storage.create-yedalms-course-modal.error.missing-config": {
        he: "הגדרות החיבור ל-YedaLMS חסרות. יש לבדוק את משתני הסביבה.",
        en: "YedaLMS connection settings are missing. Check the environment variables.",
    },
    "storage.create-yedalms-course-modal.error.college-not-configured": {
        he: "הקולג׳ שנבחר לא מוגדר בחשבון שלכם.",
        en: "The selected college is not configured for your account.",
    },
    "storage.create-yedalms-course-modal.error.invalid-college": {
        he: "מזהה הקולג׳ שנבחר לא תקין ב-YedaLMS. בדקו את מזהה הקולג׳ בהגדרות האחסון.",
        en: "The selected college ID is invalid in YedaLMS. Check the college ID in Storage Settings.",
    },
    "storage.create-yedalms-course-modal.error.not-admin": {
        he: "חשבון ה-YedaLMS שמוגדר אינו אדמין בקולג׳ שנבחר.",
        en: "The configured YedaLMS account is not an admin for the selected college.",
    },
    "storage.create-yedalms-course-modal.error.lms-login-failed": {
        he: "לא ניתן להתחבר ל-YedaLMS עם פרטי האדמין שהוגדרו.",
        en: "Could not log in to YedaLMS with the configured admin credentials.",
    },
    "storage.create-yedalms-course-modal.error.lms-create-failed": {
        he: "יצירת הקורס ב-YedaLMS נכשלה. בדקו את הגדרות הקולג׳ ונסו שוב.",
        en: "YedaLMS course creation failed. Check the college settings and try again.",
    },
    "storage.create-yedalms-course-modal.loading-colleges": {
        he: "טוען קולג׳ים...",
        en: "Loading colleges...",
    },
    "storage.create-yedalms-course-modal.college.label": {
        he: "קולג׳ ב-YedaLMS",
        en: "YedaLMS college",
    },
    "storage.create-yedalms-course-modal.college.placeholder": {
        he: "בחרו קולג׳",
        en: "Select a college",
    },
    "storage.create-yedalms-course-modal.language.label": {
        he: "שפת הקורס",
        en: "Course language",
    },
    "storage.create-yedalms-course-modal.no-colleges": {
        he: "לא הוגדרו קולג׳ים של YedaLMS. הוסיפו מזהי קולג׳ים בהגדרות החשבון.",
        en: "No YedaLMS colleges are configured. Add college IDs in account settings.",
    },
    "storage.folders-grid.order-btn": { he: "סדר", en: "Order" },
    "storage.folders-grid.pagination.next": { he: "הבא", en: "Next" },
    "storage.folders-grid.pagination.page-out-of-pages": {
        he: "עמוד {currentPage} מתוך {totalPages}",
        en: "Page {currentPage} of {totalPages}",
    },
    "storage.folders-grid.pagination.prev": { he: "הקודם", en: "Previous" },
    "storage.folders-grid.pagination.results-per-page": {
        he: "תוצאות בעמוד:",
        en: "Results per page:",
    },
    "storage.folders-grid.select-all-btn": {
        he: "בחירת הכל",
        en: "Select all",
    },
    "storage.folders-grid.sort-btn": { he: "מיון לפי", en: "Sort by" },
    "storage.folders-grid.title": { he: "תיקיות", en: "Folders" },
    "storage.lomda": { he: "לומדה", en: "Learning module" },
    "storage.lomda-v2": { he: "לומדת וידאו", en: "Video Lomda" },
    "storage.lomda-v3": { he: "לומדת HTML", en: "HTML Lomda" },
    "storage.lomda-card.num-of-scenes-badge.single": {
        he: "סצנה אחת",
        en: "One scene",
    },
    "storage.lomda-v3-card.num-of-pages-badge.single": {
        he: "עמוד אחד",
        en: "One page",
    },
    "storage.lomda-v3-card.num-of-pages-badge.multiple": {
        he: "עמודים",
        en: "pages",
    },
    "storage.no-name": { he: "ללא שם", en: "Untitled" },
    "storage.no-thumbnail": { he: "אין תמונה", en: "No image" },
    "storage.order-options.desc": { he: "יורד", en: "Descending" },
    "storage.order-options.asc": { he: "עולה", en: "Ascending" },
    "storage.sort-options.created-at": { he: "נוצר בתאריך", en: "Created at" },
    "storage.sort-options.name": { he: "שם", en: "Name" },
    "storage.filter-by-type-options.all": { he: "הכל", en: "All" },
    "storage.filter-by-type-options.videos": { he: "סרטונים", en: "Videos" },
    "storage.filter-by-type-options.presentations": {
        he: "מצגות",
        en: "Presentations",
    },
    "storage.filter-by-type-options.lomdas": { he: "לומדות", en: "Lomdas" },
    "storage.filter-by-type-options.lomdas-v2": {
        he: "לומדות וידאו",
        en: "Video Lomdas",
    },
    "storage.filter-by-type-options.lomdas-v3": {
        he: "לומדות HTML",
        en: "HTML Lomdas",
    },
    "storage.presentation": { he: "מצגת", en: "Presentation" },
    "storage.presentation-card.num-of-slides-badge.single": {
        he: "שקופית אחת",
        en: "One slide",
    },
    "storage.settings.netfree.add-external-course-id": {
        he: "הוספת ID של קורס במערכת הלמידה",
        en: "Add a course ID in the LMS",
    },
    "storage.settings.netfree.add-folder": {
        he: "הוספת תיקייה מהאחסון",
        en: "Add folder from storage",
    },
    "storage.settings.netfree.description": {
        he: "בחרו את הסרטונים הרלוונטים והמערכת תיצור לכם קובץ CSV אותו יש לשלוח לנטפרי בכתובת: info@netfree.link.",
        en: "Select the relevant videos and the system will create a CSV file to send to Netfree at: info@netfree.link.",
    },
    "storage.settings.netfree.description.line1": {
        he: "בחרו את הסרטונים הרלוונטיים והמערכת תיצור לכם קובץ CSV.",
        en: "Select the relevant videos and the system will create a CSV file.",
    },
    "storage.settings.netfree.description.line2": {
        he: "את הקובץ יש לשלוח לנטפרי בכתובת: info@netfree.link.",
        en: "Send the file to Netfree at: info@netfree.link.",
    },
    "storage.settings.netfree.explorer.description": {
        he: "נווטו אל התיקייה אותה תרצו לבחור ולחצו אישור.",
        en: "Navigate to the folder you want to select and click Confirm.",
    },
    "storage.settings.netfree.explorer.select-btn": {
        he: "בחירה",
        en: "Select",
    },
    "storage.settings.netfree.csv.header.direct-play-url": {
        he: "לינק לצפייה ישירה (MP4)",
        en: "Direct play link (MP4)",
    },
    "storage.settings.netfree.csv.filename": {
        he: "netfree.csv",
        en: "netfree.csv",
    },
    "storage.settings.netfree.title": {
        he: "שליחת סרטונים לפתיחה בנטפרי",
        en: "Send videos to Netfree for opening",
    },
    "storage.settings.netfree.generate-btn": {
        he: "יצירת CSV",
        en: "Create CSV",
    },
    "storage.settings.title": { he: "הגדרות", en: "Settings" },
    "storage.settings.save-btn": { he: "שמירת שינויים", en: "Save changes" },
    "storage.settings.enabled-resolutions.title": {
        he: "רזולוציות",
        en: "Resolutions",
    },
    "storage.settings.controls.title": {
        he: "אפשרויות בנגן",
        en: "Player controls",
    },
    "storage.settings.other-options.title": {
        he: "אפשרויות נוספות",
        en: "Other options",
    },
    "storage.settings.other-options.remember-player-position.title": {
        he: "שמירת מיקום נגן",
        en: "Remember player position",
    },
    "storage.settings.other-options.remember-player-position.description": {
        he: "הנגן יזכור איפה המשתמש עצר ויתחיל את הסרטון מאותו מקום.",
        en: "The player will remember where the viewer stopped and resume from there.",
    },
    "storage.settings.other-options.heatmap.title": {
        he: "הצגת מפת חום",
        en: "Show heatmap",
    },
    "storage.settings.other-options.heatmap.description": {
        he: "הנגן יציג מפת חום (Heatmap) של נתוני הצפייה בסרטון",
        en: "The player will show a heatmap of viewing data.",
    },
    "storage.settings.player-control-option.play": {
        he: "כפתור הפעלה/עצירה",
        en: "Play/pause button",
    },
    "storage.settings.player-control-option.play-large": {
        he: "כפתור הפעלה גדול",
        en: "Large play button",
    },
    "storage.settings.player-control-option.download": {
        he: "הורדה",
        en: "Download",
    },
    "storage.settings.player-control-option.volume": {
        he: "שליטה בעוצמת הקול",
        en: "Volume control",
    },
    "storage.settings.player-control-option.settings": {
        he: "הגדרות",
        en: "Settings",
    },
    "storage.settings.player-control-option.captions": {
        he: "כתוביות",
        en: "Captions",
    },
    "storage.settings.player-control-option.duration": {
        he: "משך הוידאו",
        en: "Video duration",
    },
    "storage.settings.player-control-option.current-time": {
        he: "זמן נוכחי",
        en: "Current time",
    },
    "storage.settings.player-control-option.rewind": {
        he: "הרצה לאחור",
        en: "Rewind",
    },
    "storage.settings.player-control-option.restart": {
        he: "התחל מחדש",
        en: "Restart",
    },
    "storage.settings.player-control-option.fast-forward": {
        he: "הרצה קדימה",
        en: "Fast forward",
    },
    "storage.settings.player-control-option.progress": {
        he: "התקדמות",
        en: "Progress",
    },
    "storage.settings.player-control-option.fullscreen": {
        he: "מסך מלא",
        en: "Fullscreen",
    },
    "storage.settings.resolution-options.240p": { he: "240p", en: "240p" },
    "storage.settings.resolution-options.360p": { he: "360p", en: "360p" },
    "storage.settings.resolution-options.720p": { he: "720p", en: "720p" },
    "storage.settings.resolution-options.1080p": { he: "1080p", en: "1080p" },
    "storage.settings.resolution-options.1440p": { he: "1440p", en: "1440p" },
    "storage.settings.resolution-options.2160p": { he: "2160p", en: "2160p" },
    "storage.settings.sidebar.security": { he: "אבטחה", en: "Security" },
    "storage.settings.sidebar.presets": { he: "תבניות", en: "Presets" },
    "storage.settings.sidebar.netfree": { he: "נטפרי", en: "Netfree" },
    "storage.settings.sidebar.yedalms": { he: "YedaLMS", en: "YedaLMS" },
    "storage.settings.sidebar.player": { he: "נגן", en: "Player" },
    "storage.settings.presets.title": {
        he: "תבניות להעלאת סרטונים",
        en: "Upload presets",
    },
    "storage.settings.presets.description": {
        he: "שמרו סט אפשרויות עיבוד וחברו אותו לתיקיות — העלאות מהתיקייה יתחילו איתו אוטומטית.",
        en: "Save a set of processing options and attach it to folders — uploads from the folder will start with it automatically.",
    },
    "storage.settings.presets.new-btn": {
        he: "תבנית חדשה",
        en: "New preset",
    },
    "storage.settings.presets.empty.title": {
        he: "אין תבניות עדיין",
        en: "No presets yet",
    },
    "storage.settings.presets.empty.description": {
        he: "צרו תבנית ראשונה כדי לקבוע מראש כתוביות, אינטראקציות, תרגומים ועוד עבור סרטונים חדשים.",
        en: "Create your first preset to predefine captions, interactions, translations and more for new videos.",
    },
    "storage.settings.presets.card.edit": {
        he: "עריכת תבנית",
        en: "Edit preset",
    },
    "storage.settings.presets.card.delete": {
        he: "מחיקת תבנית",
        en: "Delete preset",
    },
    "storage.settings.presets.card.no-folders": {
        he: "לא מחובר לאף תיקייה",
        en: "Not attached to any folder",
    },
    "storage.settings.presets.card.zoom-badge": {
        he: "זום",
        en: "Zoom",
    },
    "storage.settings.presets.zoom.title": {
        he: "תבנית להקלטות זום",
        en: "Zoom recordings preset",
    },
    "storage.settings.presets.zoom.description": {
        he: "הקלטות שמגיעות מזום דרך ה-LMS יעובדו אוטומטית לפי התבנית הזו.",
        en: "Recordings arriving from Zoom through the LMS are processed with this preset automatically.",
    },
    "storage.settings.presets.summary.captions": {
        he: "כתוביות",
        en: "Captions",
    },
    "storage.settings.presets.summary.interactions": {
        he: "שאלות אינטראקטיביות",
        en: "Interactions",
    },
    "storage.settings.presets.summary.chapters": {
        he: "פרקים",
        en: "Chapters",
    },
    "storage.settings.presets.summary.translations": {
        he: "תרגומים",
        en: "Translations",
    },
    "storage.settings.presets.summary.dubbings": {
        he: "דיבובים",
        en: "Dubbings",
    },
    "storage.settings.presets.summary.chatbots": {
        he: "ידע לצ׳אטבוטים",
        en: "Chatbot knowledge",
    },
    "storage.settings.presets.summary.nothing-enabled": {
        he: "לא נבחרו אפשרויות עיבוד",
        en: "No processing options selected",
    },
    "storage.settings.presets.editor.create-title": {
        he: "תבנית חדשה",
        en: "New preset",
    },
    "storage.settings.presets.editor.edit-title": {
        he: "עריכת תבנית",
        en: "Edit preset",
    },
    "storage.settings.presets.editor.description": {
        he: "בחרו שם ואפשרויות עיבוד. חיבור התבנית לתיקייה נעשה מהגדרות התיקייה.",
        en: "Choose a name and processing options. Attach the preset to a folder from the folder's settings.",
    },
    "storage.settings.presets.editor.name-label": {
        he: "שם התבנית",
        en: "Preset name",
    },
    "storage.settings.presets.editor.name-placeholder": {
        he: "למשל: קורס חשבי שכר",
        en: "e.g. Intro to computer sciene course",
    },
    "storage.settings.presets.editor.save-btn": {
        he: "שמירת תבנית",
        en: "Save preset",
    },
    "storage.settings.presets.editor.error.name-required": {
        he: "יש להזין שם לתבנית",
        en: "Please enter a preset name.",
    },
    "storage.settings.presets.editor.error.translations-empty": {
        he: "יש לבחור לפחות שפה אחת לתרגום, או לכבות את אפשרות התרגומים",
        en: "Select at least one translation language, or turn translations off.",
    },
    "storage.settings.presets.editor.error.dubbings-empty": {
        he: "יש לבחור לפחות שפה אחת לדיבוב, או לכבות את אפשרות הדיבובים",
        en: "Select at least one dubbing language, or turn dubbings off.",
    },
    "storage.settings.presets.delete.title": {
        he: "מחיקת תבנית",
        en: "Delete preset",
    },
    "storage.settings.presets.delete.description": {
        he: 'האם למחוק את התבנית "{name}"? פעולה זו אינה הפיכה.',
        en: "Delete the preset “{name}”? This cannot be undone.",
    },
    "storage.settings.presets.delete.detach-note": {
        he: "התבנית תנותק מ-{count} תיקיות.",
        en: "It will be detached from {count} folders.",
    },
    "storage.settings.presets.delete.detach-note-one": {
        he: "התבנית תנותק מהתיקייה המחוברת אליה.",
        en: "It will be detached from its attached folder.",
    },
    "storage.settings.presets.delete.confirm-btn": {
        he: "מחיקה",
        en: "Delete",
    },
    "storage.settings.presets.delete.cancel-btn": {
        he: "ביטול",
        en: "Cancel",
    },
    "storage.settings.presets.card.detach-folder": {
        he: "ניתוק התבנית מהתיקייה",
        en: "Detach preset from folder",
    },
    "storage.settings.presets.detach.title": {
        he: "ניתוק תבנית מתיקייה",
        en: "Detach preset from folder",
    },
    "storage.settings.presets.detach.description": {
        he: 'האם לנתק את התבנית "{preset}" מהתיקייה "{folder}"? העלאות חדשות מהתיקייה לא יתחילו עם אפשרויות התבנית.',
        en: 'Detach the preset "{preset}" from the folder "{folder}"? New uploads from this folder will no longer start with the preset\'s options.',
    },
    "storage.settings.presets.detach.confirm-btn": {
        he: "ניתוק",
        en: "Detach",
    },
    "storage.settings.presets.detach.cancel-btn": {
        he: "ביטול",
        en: "Cancel",
    },
    "storage.settings.security.add-hostname": {
        he: "הוספת דומיין",
        en: "Add domain",
    },
    "storage.settings.security.allowed-referrers.description": {
        he: "בחרו את הדומיינים המורשים להטמעת הסרטונים שלכם. בקשות מדומיינים אחרים יחסמו.",
        en: "Choose the allowed domains to embed your videos. Requests from other domains will be blocked.",
    },
    "storage.settings.security.allowed-referrers.title": {
        he: "דומיינים מורשים להטמעה",
        en: "Allowed embed domains",
    },
    "storage.settings.security.hostname-placeholder": {
        he: "www.example.com",
        en: "www.example.com",
    },
    "storage.settings.security.invalid-hostnames": {
        he: "דומיינים לא חוקיים:\n{hostnames}",
        en: "Invalid domains:\n{hostnames}",
    },
    "storage.settings.security.save-btn": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "storage.settings.security.title": {
        he: "שליחת סרטונים לפתיחה בנטפרי",
        en: "Send videos to Netfree for opening",
    },
    "storage.video-privacy-settings.public": { he: "פתוח", en: "Public" },
    "storage.video-privacy-settings.private": { he: "פרטי", en: "Private" },
    "storage.video": { he: "סרטון", en: "Video" },
    "syllabus.chapter.num-of-lessons": {
        he: "{numOfLessons} שיעורים",
        en: "{numOfLessons} lessons",
    },
    "syllabus.lesson.video": { he: "מצגת", en: "Presentation" },
    "syllabus.no-course-name": { he: "ללא שם", en: "Untitled" },
    "syllabus.subheader.add-chapter-btn": { he: "פרק חדש", en: "New chapter" },
    "syllabus.subheader.add-lesson-btn": { he: "שיעור חדש", en: "New lesson" },
    "syllabus.subtitle": { he: "פרקי הקורס", en: "Course chapters" },
    "syllabus.title": { he: "עריכת סילאבוס", en: "Edit syllabus" },
    "toolbar.replace-image": { he: "החלפת תמונה", en: "Replace image" },
    "toolbar.exit-draw-mode": {
        he: "יציאה ממצב ציור",
        en: "Exit draw mode",
    },
    "toolbar.color": { he: "צבע", en: "Color" },
    "toolbar.stroke": { he: "קו מתאר", en: "Stroke" },
    "toolbar.font": { he: "פונט", en: "Font" },
    "toolbar.bold": { he: "בולד", en: "Bold" },
    "toolbar.italic": { he: "איטלקי", en: "Italic" },
    "toolbar.underline": { he: "קו מתחת", en: "Underline" },
    "toolbar.linethrough": { he: "קו מעל", en: "Strikethrough" },
    "toolbar.alight-right": { he: "יישור ימין", en: "Align right" },
    "toolbar.alight-center": { he: "יישור מרכז", en: "Align center" },
    "toolbar.alight-left": { he: "יישור שמאל", en: "Align left" },
    "toolbar.dir-rtl": { he: "טקסט מימין לשמאל", en: "Right-to-left text" },
    "toolbar.dir-ltr": { he: "טקסט משמאל לימין", en: "Left-to-right text" },
    "toolbar.ol": { he: "רשימת מספרים", en: "Numbered list" },
    "toolbar.ul": { he: "רשימת נקודות", en: "Bulleted list" },
    "toolbar.filters": { he: "פילטרים", en: "Filters" },
    "toolbar.group": { he: "קיבוץ", en: "Group" },
    "toolbar.ungroup": { he: "ביטול קיבוץ", en: "Ungroup" },
    "toolbar.bring-forward": { he: "הבא קדימה", en: "Bring forward" },
    "toolbar.bring-backward": { he: "שלח אחורה", en: "Send backward" },
    "toolbar.animations": { he: "אנימציות", en: "Animations" },
    "toolbar.opacity": { he: "אטימות", en: "Opacity" },
    "toolbar.duplicate": { he: "שכפול", en: "Duplicate" },
    "toolbar.delete": { he: "מחיקה", en: "Delete" },
    "upload-file.max-file-size-exceeded-error.text": {
        he: "הגודל המקסימלי הוא {limit}MB",
        en: "Maximum size is {limit}MB",
    },
    "user-button.dropdown.logout": { he: "התנתקות", en: "Log out" },
    "user-button.dropdown.settings": { he: "הגדרות", en: "Settings" },
    "user-button.dropdown.upgrade-plan": {
        he: "שדרוג המנוי",
        en: "Upgrade subscription",
    },
    "verify-request.section.description": {
        he: "שלחנו לינק כניסה למערכת לכתובת המייל שהזנתם.",
        en: "We've sent a login link to the email address you entered.",
    },
    "verify-request.section.title": {
        he: "בדקו את תיבת המייל",
        en: "Check your email inbox",
    },
    "verify-request.title": { he: "אימות מייל", en: "Email verification" },
    "verify-request.description": {
        he: "המשיכו עם גוגל או אמתו את המייל שלכם",
        en: "Continue with Google or verify your email",
    },
    "verify-request.login-link": { he: "כניסה", en: "Log in" },
    "video-editor.export-modal.render-finished.description": {
        he: "הייצוא הסתיים בהצלחה והסרטון הערוך נשמר באחסון שלכם.",
        en: "Export completed successfully and the edited video was saved in your storage.",
    },
    "video-editor.export-modal.render-finished.title": {
        he: "הייצוא הסתיים!",
        en: "Export completed!",
    },
    "video-editor.export-modal.render-in-progress.title": {
        he: "מתבצע ייצוא",
        en: "Export in progress",
    },
    "video-editor.export-modal.error.unexpected": {
        he: "קרתה שגיאה לא צפויה.",
        en: "An unexpected error occurred.",
    },
    "video-editor.export-modal.upload-progress.finished-btn": {
        he: "מעבר לאחסון",
        en: "Go to storage",
    },
    "video-editor.navbar.title": { he: "עורך וידאו", en: "Video editor" },
    "video-editor.navbar.subtitle": {
        he: "שימו לב - ניתן לייצא סרטונים באורך של עד 60 דקות.",
        en: "Note: you can export videos up to 60 minutes long.",
    },
    "video-editor.navbar.export-btn": { he: "ייצוא", en: "Export" },
    "video-editor.sidebar.media.choose-from-video-stock": {
        he: "מאגר סרטונים",
        en: "Video library",
    },
    "video-editor.sidebar.media.choose-video-from-storage-btn": {
        he: "בחירה מהאחסון שלי",
        en: "Choose from my storage",
    },
    "video-editor.sidebar.media.video-stock.load-failed": {
        he: "ייבוא סרטונים נכשל. נסו שוב מאוחר יותר.",
        en: "Video import failed. Please try again later.",
    },
    "video-editor.sidebar.media.video-stock.load-more-btn": {
        he: "טען עוד...",
        en: "Load more...",
    },
    "video-editor.sidebar.media.video-stock.no-results": {
        he: "לא נמצאו סרטונים. נסו חיפוש אחר.",
        en: "No videos found. Try another search.",
    },
    "video-editor.sidebar.storage": { he: "אחסון", en: "Storage" },
    "video-editor.sidebar.text-settings.alignment.label": {
        he: "יישור טקסט",
        en: "Text alignment",
    },
    "video-editor.sidebar.text-settings.bg-color.label": {
        he: "צבע רקע",
        en: "Background color",
    },
    "video-editor.sidebar.text-settings.content.label": {
        he: "טקסט",
        en: "Text",
    },
    "video-editor.sidebar.text-settings.decorations.label": {
        he: "עיצוב",
        en: "Design",
    },
    "video-editor.text-settings.toggle.bold": { he: "בולד", en: "Bold" },
    "video-editor.text-settings.toggle.italic": { he: "איטלקי", en: "Italic" },
    "video-editor.text-settings.toggle.underline": {
        he: "קו מתחת",
        en: "Underline",
    },
    "video-editor.text-settings.align.right": {
        he: "יישור ימין",
        en: "Align right",
    },
    "video-editor.text-settings.align.center": {
        he: "יישור מרכז",
        en: "Align center",
    },
    "video-editor.text-settings.align.left": {
        he: "יישור שמאל",
        en: "Align left",
    },
    "video-editor.sidebar.text-settings.text-color.label": {
        he: "צבע טקסט",
        en: "Text color",
    },
    "video-editor.sidebar.texts.add-paragraph": {
        he: "הוספת פסקה",
        en: "Add paragraph",
    },
    "video-editor.sidebar.texts.add-subtitle": {
        he: "הוספת תת כותרת",
        en: "Add subtitle",
    },
    "video-editor.sidebar.texts.add-textbox": {
        he: "הוספת קופסת טקסט",
        en: "Add text box",
    },
    "video-editor.sidebar.texts.add-title": {
        he: "הוספת כותרת",
        en: "Add title",
    },
    "video-editor.storage-form.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "video-editor.storage-form.choose-folder.description": {
        he: "נווטו אל התיקייה שבה תרצו לשמור את הסרטון ולחצו אישור.",
        en: "Navigate to the folder where you want to save the video and click Confirm.",
    },
    "video-editor.timeline-item.context-menu.delete": {
        he: "מחיקה",
        en: "Delete",
    },
    "video-editor.timeline-item.context-menu.shift-overlay-to-start": {
        he: "הזזה לתחילת הסרטון",
        en: "Move to start of video",
    },
    "video-editor.timeline-item.context-menu.split": {
        he: "פיצול",
        en: "Split",
    },
    "video-editor.video-modal.storage-form.description": {
        he: "בחרו איפה לשמור את הסרטון באחסון שלכם.",
        en: "Choose where to save the video in your storage.",
    },
    "video-editor.video-modal.storage-form.folder.label": {
        he: "בחירת תיקייה",
        en: "Choose folder",
    },
    "video-editor.video-modal.storage-form.name.label": {
        he: "שם הסרטון החדש",
        en: "New video name",
    },
    "video-editor.video-modal.storage-form.save-btn": {
        he: "ייצוא",
        en: "Export",
    },
    "video-editor.video-modal.storage-form.title": {
        he: "ייצוא סרטון",
        en: "Export video",
    },
    "video-interaction.ai-chat-type.badge": {
        he: "שיחה עם AI",
        en: "Chat with AI",
    },
    "video-interaction.audio-type.badge": { he: "אודיו", en: "Audio" },
    "video-interaction.close-type.badge": {
        he: "שאלה סגורה",
        en: "Closed question",
    },
    "video-interaction.cloze-type.badge": {
        he: "השלמת משפטים",
        en: "Fill in the blanks",
    },
    "video-interaction.form-type.badge": { he: "טופס", en: "Form" },
    "video-interaction.link-type.badge": { he: "לינק", en: "Link" },
    "video-interaction.match-type.badge": {
        he: "התאמת קלפים",
        en: "Match cards",
    },
    "video-interaction.open-type.badge": {
        he: "שאלה פתוחה",
        en: "Open question",
    },
    "video-interaction.rating-type.badge": { he: "דירוג", en: "Rating" },
    "video-modal.alert-dialog.cancel": { he: "ביטול", en: "Cancel" },
    "video-modal.alert-dialog.confirm": { he: "מחיקה", en: "Delete" },
    "video-modal.alert-dialog.description": {
        he: "חזרה לשלב הקודם תוביל למחיקת ההקלטה ופעולה זו לא ניתנת לשחזור. אתם בטוחים שתרצו למחוק את ההקלטה?",
        en: "Going back will delete the recording and this action cannot be undone. Are you sure you want to delete the recording?",
    },
    "video-modal.alert-dialog.title": {
        he: "פעולה זו תמחק את ההקלטה",
        en: "This action will delete the recording",
    },
    "video-modal.preview-video.discard-btn": {
        he: "מחיקה והקלטה מחדש",
        en: "Delete and record again",
    },
    "video-modal.preview-video.save-btn": { he: "שמירה", en: "Save" },
    "video-modal.preview-video.title": {
        he: "תצוגה מקדימה",
        en: "Preview",
    },
    "video-modal.storage-form.description": {
        he: "בחרו איפה לשמור את ההקלטה באחסון שלכם ולחצו שמירה.",
        en: "Choose where to save the recording in your storage and click Save.",
    },
    "video-modal.storage-form.folder.label": {
        he: "בחירת תיקייה",
        en: "Choose folder",
    },
    "video-modal.storage-form.folder.description": {
        he: "נווטו אל התיקייה שבה תרצו לשמור את ההקלטה ולחצו אישור.",
        en: "Navigate to the folder where you want to save the recording and click Confirm.",
    },
    "video-modal.storage-form.name.label": {
        he: "שם הסרטון",
        en: "Video name",
    },
    "video-modal.storage-form.save-btn": { he: "שמירה", en: "Save" },
    "video-modal.storage-form.title": {
        he: "שמירת ההקלטה",
        en: "Save recording",
    },
    "video-modal.upload-progress.description.finished": {
        he: "ההעלאה הסתיימה בהצלחה והסרטון נשמר באחסון שלכם.",
        en: "Upload completed successfully and the video was saved in your storage.",
    },
    "video-modal.upload-progress.description.progress": {
        he: "נא לא לסגור את החלון, נשארו עוד:",
        en: "Please do not close the window, remaining:",
    },
    "video-modal.upload-progress.description.calculating": {
        he: "מחשב...",
        en: "Calculating...",
    },
    "video-modal.upload-progress.finished-btn": {
        he: "פתיחת ההקלטה באחסון",
        en: "Open recording in storage",
    },
    "video-modal.upload-progress.title.finished": {
        he: "ההעלאה הסתיימה!",
        en: "Upload completed!",
    },
    "video-modal.upload-progress.title.progress": {
        he: "העלאה מתבצעת",
        en: "Uploading",
    },
    "video-sheet.aside.card.player-mode.title": {
        he: "מצב נגן",
        en: "Player mode",
    },
    "video-sheet.aside.card.player-mode.video": {
        he: "סרטון",
        en: "Video",
    },
    "video-sheet.aside.card.player-mode.lomda": {
        he: "לומדה",
        en: "Lomda",
    },
    "video-sheet.aside.card.player-mode.lomda.disabled": {
        he: "יש להוסיף פרקים לפני שניתן להפעיל את מצב הלומדה.",
        en: "Add chapters first to enable lomda mode.",
    },
    "video-sheet.aside.card.direct-play-link.title": {
        he: "לינק לצפייה ישירה",
        en: "Direct watch link",
    },
    "video-sheet.aside.card.direct-play-link.warning": {
        he: 'כדי שהלינק יפעל יש לשנות את הגדרות הפרטיות ל-"פתוח".',
        en: 'To make the link work, change the privacy setting to "Public".',
    },
    "video-sheet.aside.card.embed.title": { he: "קוד הטמעה", en: "Embed code" },
    "video-sheet.aside.card.select-video-privacy.title": {
        he: "הגדרות פרטיות",
        en: "Privacy settings",
    },
    "video-sheet.embed-options.autoPlay.title": {
        he: "ניגון אוטומטי",
        en: "Autoplay",
    },
    "video-sheet.embed-options.muted.title": { he: "מושתק", en: "Muted" },
    "video-sheet.embed-options.captions.title": {
        he: "הפעלת כתוביות",
        en: "Enable captions",
    },
    "video-sheet.embed-options.disableSeeking.title": {
        he: "חסימת ניווט",
        en: "Disable seeking",
    },
    "video-sheet.embed-options.english-player.title": {
        he: "נגן באנגלית",
        en: "English player",
    },
    "video-sheet.aside.card.video-created-at.title": {
        he: "תאריך העלאה",
        en: "Upload date",
    },
    "video-sheet.aside.share-card.title": { he: "שיתוף", en: "Share" },
    "video-sheet.aside.share-card.description": {
        he: "שתפו את {item} לצפייה ישירה.",
        en: "Share the {item} for direct viewing.",
    },
    "video-sheet.aside.embed-card.title": { he: "הטמעה", en: "Embed" },
    "video-sheet.aside.embed-card.description": {
        he: "הטמיעו את {item} באתר חיצוני.",
        en: "Embed the {item} on an external site.",
    },
    "video-sheet.aside.item.video": { he: "הסרטון", en: "video" },
    "video-sheet.aside.item.lomda": { he: "הלומדה", en: "lomda" },
    "video-sheet.aside.player-options.title": {
        he: "אפשרויות נגן",
        en: "Player options",
    },
    "video-sheet.aside.copy": { he: "העתקה", en: "Copy" },
    "video-sheet.aside.copied": { he: "הועתק", en: "Copied" },
    "video-sheet.captions-tab.description": {
        he: "הוסיפו כתוביות לסרטון שלכם.",
        en: "Add captions to your video.",
    },
    "video-sheet.captions-tab.generate.select-language.menu-label": {
        he: "בחרו את השפה בסרטון",
        en: "Choose the video language",
    },
    "video-sheet.captions-tab.save-changes": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "video-sheet.captions-tab.title": { he: "כתוביות", en: "Captions" },
    "video-sheet.captions-tab.add-language": {
        he: "הוספה",
        en: "Add",
    },
    "video-sheet.captions-tab.published-checkbox": {
        he: "גלוי לצופים",
        en: "Published",
    },
    "video-sheet.captions-tab.editor.word-popover.confidence": {
        he: "רמת ביטחון: {percent}%",
        en: "Confidence: {percent}%",
    },
    "video-sheet.captions-tab.editor.word-popover.mark-correct": {
        he: "סימון כנכון",
        en: "Mark as correct",
    },
    "video-sheet.captions-tab.editor.word-popover.seek": {
        he: "מעבר לזמן זה",
        en: "Play from here",
    },
    "video-sheet.captions-tab.editor.find-replace": {
        he: "חיפוש והחלפה",
        en: "Find & Replace",
    },
    "video-sheet.captions-tab.editor.shortcuts": {
        he: "קיצורי מקלדת",
        en: "Shortcuts",
    },
    "video-sheet.captions-tab.editor.shortcuts-title": {
        he: "קיצורי מקלדת (Cmd+/)",
        en: "Shortcuts (Cmd+/)",
    },
    "video-sheet.captions-tab.editor.find.placeholder": {
        he: "חיפוש",
        en: "Find",
    },
    "video-sheet.captions-tab.editor.replace.placeholder": {
        he: "החלפה ל-",
        en: "Replace with",
    },
    "video-sheet.captions-tab.editor.match-case": {
        he: "התאמת רישיות",
        en: "Match case",
    },
    "video-sheet.captions-tab.editor.whole-word": {
        he: "מילה שלמה",
        en: "Whole word",
    },
    "video-sheet.captions-tab.editor.previous-match": {
        he: "התאמה קודמת",
        en: "Previous match",
    },
    "video-sheet.captions-tab.editor.next-match": {
        he: "התאמה הבאה",
        en: "Next match",
    },
    "video-sheet.captions-tab.editor.replace": {
        he: "החלפה",
        en: "Replace",
    },
    "video-sheet.captions-tab.editor.replace-all": {
        he: "החלפת הכול",
        en: "Replace all",
    },
    "video-sheet.captions-tab.editor.close": {
        he: "סגירה",
        en: "Close",
    },
    "video-sheet.captions-tab.editor.dismiss": {
        he: "סגירה",
        en: "Dismiss",
    },
    "video-sheet.captions-tab.editor.selection-bubble.replace-placeholder": {
        he: "החלפה ל-...",
        en: "Replace with...",
    },
    "video-sheet.captions-tab.editor.stats.no-confidence-data": {
        he: "אין נתוני רמת ביטחון ברמת המילה לכתוביות אלה.",
        en: "No word-level confidence data for this track.",
    },
    "video-sheet.captions-tab.editor.stats.all-reviewed": {
        he: "כל המילים המסומנות נבדקו",
        en: "All flagged words reviewed",
    },
    "video-sheet.captions-tab.editor.stats.low-confidence": {
        he: "ברמת ביטחון נמוכה",
        en: "low confidence",
    },
    "video-sheet.captions-tab.editor.stats.suspicious": {
        he: "חשודות",
        en: "suspicious",
    },
    "video-sheet.captions-tab.editor.stats.total-words": {
        he: "{count} מילים",
        en: "{count} words",
    },
    "video-sheet.captions-tab.editor.help.title": {
        he: "עורך הכתוביות — קיצורי מקלדת",
        en: "Captions editor — shortcuts",
    },
    "video-sheet.captions-tab.editor.help.description": {
        he: "טיפים לתיקון כתוביות מהר יותר. סמנו מילה כדי להחליף את כל המופעים שלה בלחיצה אחת.",
        en: "Tips for fixing captions faster. Select any word to replace every occurrence in one click.",
    },
    "video-sheet.captions-tab.editor.help.section.navigation": {
        he: "ניווט",
        en: "Navigation",
    },
    "video-sheet.captions-tab.editor.help.section.editing": {
        he: "עריכה",
        en: "Editing",
    },
    "video-sheet.captions-tab.editor.help.section.tools": {
        he: "כלים",
        en: "Tools",
    },
    "video-sheet.captions-tab.editor.help.got-it": {
        he: "הבנתי",
        en: "Got it",
    },
    "video-sheet.captions-tab.editor.help.shortcut.undo": {
        he: "ביטול",
        en: "Undo",
    },
    "video-sheet.captions-tab.editor.help.shortcut.redo": {
        he: "שחזור",
        en: "Redo",
    },
    "video-sheet.captions-tab.editor.help.shortcut.split": {
        he: "פיצול בלוק הכתוביות הנוכחי במיקום הסמן",
        en: "Split the current caption block at cursor",
    },
    "video-sheet.captions-tab.editor.help.shortcut.merge": {
        he: "בתחילת בלוק, מיזוג עם הבלוק הקודם",
        en: "At block start, merge with the previous block",
    },
    "video-sheet.captions-tab.editor.help.shortcut.jump-next": {
        he: "קפיצה למילה הבאה ברמת ביטחון נמוכה",
        en: "Jump to the next low-confidence word",
    },
    "video-sheet.captions-tab.editor.help.shortcut.jump-prev": {
        he: "קפיצה למילה הקודמת ברמת ביטחון נמוכה",
        en: "Jump to the previous low-confidence word",
    },
    "video-sheet.captions-tab.editor.help.shortcut.open-find": {
        he: "מיקוד חיפוש (אם מילה מסומנת, היא תועבר לשדה החיפוש)",
        en: "Focus find (if a word is selected, it goes into the find field)",
    },
    "video-sheet.captions-tab.editor.help.shortcut.save": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "video-sheet.captions-tab.editor.help.shortcut.show-help": {
        he: "הצגת מסך עזרה זה",
        en: "Show this help",
    },
    "video-sheet.captions-tab.editor.help.shortcut.escape": {
        he: "סגירת חיפוש והחלפה / סגירת בועת הסימון",
        en: "Close find & replace / dismiss selection bubble",
    },
    "video-sheet.captions-tab.editor.help.legend.red": {
        he: "אדום",
        en: "Red",
    },
    "video-sheet.captions-tab.editor.help.legend.amber": {
        he: "כתום",
        en: "Amber",
    },
    "video-sheet.captions-tab.editor.help.legend.before-red": {
        he: "",
        en: "",
    },
    "video-sheet.captions-tab.editor.help.legend.text": {
        he: "קווים תחתונים ב{red} מסמנים את המילים שהמודל היה הכי פחות בטוח לגביהן. קווים תחתונים ב{amber} הם הדרגה הבאה של חוסר ודאות. התחילו מהאדומות.",
        en: "{red} underlines mark the words the transcription model was least sure about. {amber} underlines are the next tier of uncertainty. Start with the red ones.",
    },
    "video-sheet.create-captions-with-ai.title": {
        he: "יצירת כתוביות עם AI",
        en: "Generate captions with AI",
    },
    "video-sheet.create-captions-with-ai.description": {
        he: "צרו כתוביות מדוייקות ברמה הגבוהה ביותר לסרטון שלכם",
        en: "Generate high-quality captions for your video.",
    },
    "video-sheet.generate-captions-with-ai-btn": {
        he: "יצירה",
        en: "Generate",
    },
    "video-sheet.chapters-tab.chapter.to": { he: "עד", en: "To" },
    "video-sheet.chapters-tab.chapter.placeholder": {
        he: "שם הפרק",
        en: "Chapter name",
    },
    "video-sheet.chapters-tab.description": {
        he: "מופיעים בתוך הנגן ומאפשרים לצופה לנווט בקלות בסרטון.",
        en: "Appear in the player and allow viewers to navigate the video easily.",
    },
    "video-sheet.chapters-tab.save-btn": { he: "שמירה", en: "Save" },
    "video-sheet.chapters-tab.add-btn": { he: "הוספת פרק", en: "Add chapter" },
    "video-sheet.chapters-tab.title": { he: "פרקים", en: "Chapters" },
    "video-sheet.create-chapters-with-ai.title": {
        he: "יצירת פרקים עם AI",
        en: "Generate chapters with AI",
    },
    "video-sheet.create-chapters-with-ai.description": {
        he: "לחצו כאן ובתוך כמה שניות יהיו מוכנים פרקים מדוייקים לסרטון",
        en: "Click here and within seconds your chapters will be ready.",
    },
    "video-sheet.generate-chapters-with-ai-btn": {
        he: "יצירה",
        en: "Generate",
    },
    "video-sheet.folders.label": { he: "תיקיות", en: "Folders" },
    "video-sheet.general-tab.end-screen-checkbox.label": {
        he: "הצגת מסך סיום",
        en: "Show end screen",
    },
    "video-sheet.general-tab.save-btn": {
        he: "שמירת שינויים",
        en: "Save changes",
    },
    "video-sheet.general-tab.thumbnail.label": {
        he: "תמונה ממוזערת (Thumbnail)",
        en: "Thumbnail",
    },
    "video-sheet.general-tab.video-name.label": {
        he: "שם הסרטון",
        en: "Video name",
    },
    "video-sheet.generate.select-voice.menu-label": {
        he: "בחרו קול לקריין",
        en: "Choose a narrator voice",
    },
    "video-sheet.generate.dubbings.model.label": { he: "מודל:", en: "Model:" },
    "video-sheet.generate.dubbings.model.placeholder": {
        he: "בחרו מודל",
        en: "Select a model",
    },
    "video-sheet.generate.dubbings.voice.label": { he: "קול:", en: "Voice:" },
    "video-sheet.generate.dubbings.instructions.label": {
        he: "הנחיות דיבוב",
        en: "Dubbing instructions",
    },
    "video-sheet.generate.interactions.same-language-option": {
        he: "שפת הדיבור בסרטון",
        en: "Same as video's spoken language",
    },
    "video-sheet.generate.interactions.language.label": {
        he: "שפת השאלות:",
        en: "Questions language:",
    },
    "video-sheet.generate.interactions.language.menu-label": {
        he: "בחרו שפה",
        en: "Select a language",
    },
    "video-sheet.generate.captions.spoken-language.label": {
        he: "השפה בה מדברים בסרטון:",
        en: "Spoken language in the video:",
    },
    "video-sheet.generate.captions.context.hint": {
        he: "כאן אפשר לתת למודל context על הסרטונים (למשל תעשייה או תחום) וזה יעזור בזיהוי מונחים רלוונטיים.",
        en: "Provide context about the videos (e.g., industry or domain) to help the model recognize relevant terms.",
    },
    "video-sheet.generate.captions.context.label": {
        he: "context לתמלול:",
        en: "Transcription context:",
    },
    "video-sheet.generate.captions.context.placeholder": {
        he: "לדוגמה: קורס SolidCAM",
        en: "Example: SolidCAM course",
    },
    "video-sheet.generate.captions.hotwords.hint": {
        he: "רשימת מילים מופרדות בפסיק שהמודל ישים לב אליהן במיוחד, למשל שמות או מונחים טכניים.",
        en: "Comma-separated words the model should pay special attention to, such as names or technical terms.",
    },
    "video-sheet.generate.captions.hotwords.label": {
        he: "מילים חמות:",
        en: "Hotwords:",
    },
    "video-sheet.generate.captions.hotwords.placeholder": {
        he: "לדוגמה: CNC, SolidCam, CAM",
        en: "Example: CNC, SolidCam, CAM",
    },
    "video-sheet.header.delete-btn": { he: "מחיקה", en: "Delete" },
    "video-sheet.header.download-btn": { he: "הורדה", en: "Download" },
    "video-sheet.download-menu.label": { he: "בחרו קובץ", en: "Choose file" },
    "video-sheet.download-menu.original-video": {
        he: "סרטון מקורי",
        en: "Original video",
    },
    "video-sheet.download-menu.audio": {
        he: "אודיו {language}",
        en: "Audio {language}",
    },
    "video-sheet.download-menu.captions": {
        he: "כתוביות {language}",
        en: "Captions {language}",
    },
    "video-sheet.header.edit-btn": { he: "עריכה", en: "Edit" },
    "video-sheet.header.folders": {
        he: "תיקיות: {folderNames}",
        en: "Folders: {folderNames}",
    },
    "video-sheet.header.title": {
        he: "סרטון: {videoTitle}",
        en: "Video: {videoTitle}",
    },
    "video-sheet.header.title.placeholder": {
        he: "סרטון ללא שם",
        en: "Untitled video",
    },
    "video-sheet.interactions-list.options.delete": {
        he: "מחיקה",
        en: "Delete",
    },
    "video-sheet.interactions-list.options.edit": { he: "עריכה", en: "Edit" },
    "video-sheet.interactions-tab.description": {
        he: "הפכו את הסרטון שלכם לאינטראקטיבי עם שאלות ואינטראקציות בתוך הנגן.",
        en: "Make your video interactive with questions and interactions inside the player.",
    },
    "video-sheet.interactions-tab.save-btn": { he: "שמירה", en: "Save" },
    "video-sheet.interactions-tab.add-btn": {
        he: "הוספת אינטראקציה",
        en: "Add interaction",
    },
    "video-sheet.interactions-tab.title": {
        he: "אינטראקציות",
        en: "Interactions",
    },
    "interactions.translate-with-captions.label": {
        he: "תרגמו גם את האינטראקציות לשפות אלו",
        en: "Also translate the interactions to these languages",
    },
    "video-sheet.interactions-tab.language-label": {
        he: "שפה",
        en: "Language",
    },
    "video-sheet.interactions-tab.translate.title": {
        he: "תרגום אינטראקציות",
        en: "Translate interactions",
    },
    "video-sheet.interactions-tab.translate.description": {
        he: "צרו את האינטראקציות בשפות נוספות שיש להן כתוביות.",
        en: "Create the interactions in additional languages that have captions.",
    },
    "video-sheet.interactions-tab.translate.existing-label": {
        he: "קיים בשפות",
        en: "Exists in",
    },
    "video-sheet.interactions-tab.translate.create-more-label": {
        he: "תרגום לשפות",
        en: "Translate to",
    },
    "video-sheet.interactions-tab.translate.no-captions": {
        he: "תרגמו תחילה את הכתוביות לשפות נוספות.",
        en: "Translate the captions to more languages first.",
    },
    "video-sheet.create-interactions-with-ai.title": {
        he: "יצירת שאלות אינטראקטיביות עם AI",
        en: "Generate interactive questions with AI",
    },
    "video-sheet.create-interactions-with-ai.description": {
        he: "לחצו כאן ובתוך כמה שניות ניצור לכם שאלות אינטראקטיביות על הסרטון",
        en: "Click here and within seconds we'll generate interactive questions for your video.",
    },
    "video-sheet.generate-interactions-with-ai-btn": {
        he: "יצירה",
        en: "Generate",
    },
    "video-sheet.translations-tab.description": {
        he: "תרגמו את הכתוביות לשפות נוספות.",
        en: "Translate captions to additional languages.",
    },
    "video-sheet.translations-tab.title": { he: "תרגומים", en: "Translations" },
    "video-sheet.create-translations-with-ai.title": {
        he: "יצירת תרגומים עם AI",
        en: "Generate translations with AI",
    },
    "video-sheet.create-translations-with-ai.description": {
        he: "בחרו את השפות אליהן תרצו לתרגם את הכתוביות",
        en: "Choose the languages you want to translate the captions into.",
    },
    "video-sheet.generate-translations-with-ai-btn": {
        he: "תרגום",
        en: "Translate",
    },
    "video-sheet.translations-tab.existing.label": {
        he: "תרגומים קיימים:",
        en: "Existing translations:",
    },
    "video-sheet.translations-tab.create-more.label": {
        he: "יצירת תרגומים נוספים:",
        en: "Create more translations:",
    },
    "video-sheet.translations-tab.delete-modal.title": {
        he: "מחיקת תרגום",
        en: "Delete translation",
    },
    "video-sheet.translations-tab.delete-modal.description": {
        he: "אתם בטוחים שתרצו למחוק את התרגום לשפה: {language}? פעולה זו לא ניתנת לשחזור.",
        en: "Are you sure you want to delete the translation for {language}? This action cannot be undone.",
    },
    "video-sheet.dubbings-tab.title": { he: "דיבובים", en: "Dubbings" },
    "video-sheet.dubbings-tab.description": {
        he: "אפשרו לצופים להחליף את שפת השמע בסרטון.",
        en: "Let viewers switch the audio language of the video.",
    },
    "video-sheet.dubbings-tab.existing.label": {
        he: "דיבובים קיימים:",
        en: "Existing dubbings:",
    },
    "video-sheet.dubbings-tab.create-more.label": {
        he: "יצירת דיבובים נוספים:",
        en: "Create more dubbings:",
    },
    "video-sheet.dubbings-tab.model.label": { he: "מודל:", en: "Model:" },
    "video-sheet.dubbings-tab.voice.label": { he: "קול:", en: "Voice:" },
    "video-sheet.dubbings-tab.instructions.label": {
        he: "הנחיות דיבוב:",
        en: "Dubbing instructions:",
    },
    "video-sheet.dubbings-tab.delete-modal.title": {
        he: "מחיקת דיבוב",
        en: "Delete dubbing",
    },
    "video-sheet.dubbings-tab.delete-modal.description": {
        he: "אתם בטוחים שתרצו למחוק את הדיבוב לשפה: {language}? פעולה זו לא ניתנת לשחזור.",
        en: "Are you sure you want to delete the dubbing for {language}? This action cannot be undone.",
    },
    "video-sheet.create-dubbings-with-ai.title": {
        he: "יצירת דיבובים עם AI",
        en: "Generate dubbings with AI",
    },
    "video-sheet.create-dubbings-with-ai.description": {
        he: "בחרו את השפות אליהן תרצו לדבב את השמע בסרטון",
        en: "Choose the languages you want to dub the video audio into.",
    },
    "video-sheet.generate-dubbings-with-ai-btn": {
        he: "דיבוב",
        en: "Dub",
    },
    "video-sheet.tabs.general": { he: "כללי", en: "General" },
    "video-sheet.tabs.captions": { he: "כתוביות", en: "Captions" },
    "video-sheet.tabs.chapters": { he: "פרקים", en: "Chapters" },
    "video-sheet.tabs.interactions": { he: "אינטראקציות", en: "Interactions" },
    "video-sheet.tabs.translations": { he: "תרגומים", en: "Translations" },
    "video-sheet.tabs.dubbings": { he: "דיבובים", en: "Dubbings" },
    "video-sheet.tabs.branding": { he: "מיתוג", en: "Branding" },
    "video-sheet.branding.title": { he: "מיתוג", en: "Branding" },
    "video-sheet.branding.subtitle": {
        he: "כרגע ההגדרות הללו משפיעות רק על סרטונים במצב לומדה ולא על סרטונים רגילים.",
        en: "These settings currently affect only videos in lomda mode, not regular videos.",
    },
    "video-sheet.branding.background-color.label": {
        he: "צבע רקע",
        en: "Background color",
    },
    "video-sheet.branding.text-color.label": {
        he: "צבע טקסט",
        en: "Text color",
    },
    "video-sheet.branding.primary-button.label": {
        he: "כפתור ראשי",
        en: "Primary button",
    },
    "video-sheet.branding.secondary-button.label": {
        he: "כפתור משני",
        en: "Secondary button",
    },
    "video-sheet.branding.button.bg": { he: "רקע", en: "Background" },
    "video-sheet.branding.button.text": { he: "טקסט", en: "Text" },
    "video-sheet.branding.button.border": { he: "מסגרת", en: "Border" },
    "video-sheet.branding.button.transparent": {
        he: "שקוף",
        en: "Transparent",
    },
    "video-sheet.branding.button.no-border": {
        he: "ללא מסגרת",
        en: "No border",
    },
    "video-sheet.branding.button.reset-defaults": {
        he: "איפוס",
        en: "Reset",
    },
    "video-sheet.branding.title-font.label": {
        he: "פונט כותרת",
        en: "Title font",
    },
    "video-sheet.branding.body-font.label": {
        he: "פונט גוף",
        en: "Body font",
    },
    "video-sheet.branding.image.label": {
        he: "תמונת רקע",
        en: "Background image",
    },
    "video-sheet.branding.title.label": { he: "כותרת", en: "Title" },
    "video-sheet.branding.subtitle.label": {
        he: "כותרת משנה",
        en: "Subtitle",
    },
    "video-sheet.branding.button-text.label": {
        he: "טקסט כפתור",
        en: "Button text",
    },
    "video-sheet.branding.stats.title": {
        he: "סטטיסטיקות מוצגות",
        en: "Displayed stats",
    },
    "video-sheet.branding.stats.value": { he: "ערך", en: "Value" },
    "video-sheet.branding.stats.value-label": {
        he: "תווית",
        en: "Label",
    },
    "video-sheet.branding.save": { he: "שמירה", en: "Save" },
    "video-sheet.video.title": { he: "וידאו", en: "Video" },
    "video-sheet.video-not-ready.subtitle": {
        he: "תהליך העיבוד נמשך בדרך כלל כמה דקות אבל יכול להגיע גם לשעה",
        en: "Processing usually takes a few minutes but can take up to an hour",
    },
    "video-sheet.video-not-ready.title": {
        he: "הסרטון נמצא בתהליך עיבוד",
        en: "The video is being processed",
    },
    "video-workflow-step.error-state.ctas.help": { he: "עזרה", en: "Help" },
    "video-workflow-step.error-state.ctas.retry": {
        he: "ניסיון חוזר",
        en: "Try again",
    },
    "video-workflow-step.error-state.ctas.upgrade-plan": {
        he: "שדרוג המנוי",
        en: "Upgrade subscription",
    },
    "video-workflow-step.error-state.subtitle": {
        he: "ניסיון {stepName} נכשל. בחרו איך תרצו להמשיך.",
        en: "{stepName} attempt failed. Choose how to continue.",
    },
    "video-workflow-step.progress": {
        he: "שלב {currentStep} מתוך {totalSteps} - {stepName}",
        en: "Step {currentStep} of {totalSteps} - {stepName}",
    },
    "video-workflow.step-name.processing": { he: "עיבוד", en: "Processing" },
    "video-workflow.step-name.captions": { he: "כתוביות", en: "Captions" },
    "video-workflow.step-name.translations": {
        he: "תרגומים",
        en: "Translations",
    },
    "video-workflow.step-name.dubbings": { he: "דיבובים", en: "Dubbings" },
    "video-workflow.step-name.chapters": { he: "פרקים", en: "Chapters" },
    "video-workflow.step-name.interactions": {
        he: "אינטראקציות",
        en: "Interactions",
    },
    "charts.no-data": {
        he: "אין נתונים עבור התאריכים שנבחרו",
        en: "No data for the selected dates",
    },
    "charts.payment-methods.title": {
        he: "אמצעי תשלום",
        en: "Payment methods",
    },
    "charts.sales.label": { he: "מכירות:", en: "Sales:" },
    "charts.sales.title": { he: "מכירות", en: "Sales" },
    "charts.transactions.label": { he: "עסקאות", en: "Transactions" },
    "charts.type.area": { he: "גרף שטח", en: "Area chart" },
    "charts.type.bar": { he: "גרף עמודות", en: "Bar chart" },
    "charts.type.line": { he: "גרף קווי", en: "Line chart" },
    "charts.type.pie": { he: "פאי", en: "Pie" },
    "charts.type.placeholder": { he: "סוג גרף", en: "Chart type" },
    "chatbot.demo.anonymous-name": { he: "אנונימי", en: "Anonymous" },
    "chatbot.demo.subtitle": {
        he: "לחצו על הבועה בצד שמאל למטה",
        en: "Click the bubble at the bottom-left",
    },
    "chatbot.demo.title": { he: "דמו סוכן AI", en: "AI agent demo" },
    "chatbots.overview-tab.empty-state.subtitle": {
        he: "לחצו על הכפתור למעלה ליצירת העוזר האישי הראשון שלכם.",
        en: "Click the button above to create your first personal assistant.",
    },
    "chatbots.overview-tab.empty-state.title": {
        he: "אין לכם עדיין סוכני AI",
        en: "You don't have any AI agents yet",
    },
    "common.cancel": { he: "ביטול", en: "Cancel" },
    "common.close": { he: "סגירה", en: "Close" },
    "common.confirm": { he: "אישור", en: "Confirm" },
    "common.delete": { he: "מחיקה", en: "Delete" },
    "common.add": { he: "הוספה", en: "Add" },
    "common.edit": { he: "עריכה", en: "Edit" },
    "common.menu": { he: "תפריט", en: "Menu" },
    "common.select": { he: "בחירה", en: "Select" },
    "common.yes": { he: "כן", en: "Yes" },
    "common.no": { he: "לא", en: "No" },
    "common.image.alt": { he: "תמונה", en: "Image" },
    "common.icon.alt": { he: "אייקון", en: "Icon" },
    "common.minutes": { he: "דקות", en: "minutes" },
    "common.seconds": { he: "שניות", en: "seconds" },
    "common.monthly": { he: "חודשי", en: "Monthly" },
    "common.yearly": { he: "שנתי", en: "Yearly" },
    "common.prev": { he: "הקודם", en: "Previous" },
    "common.next": { he: "הבא", en: "Next" },
    "common.all": { he: "כולם", en: "All" },
    "common.no-results": { he: "לא נמצאו תוצאות.", en: "No results found." },
    "common.save": { he: "שמירה", en: "Save" },
    "common.or": { he: "או", en: "Or" },
    "common.publish": { he: "פרסום", en: "Publish" },
    "common.coming-soon": { he: "בקרוב", en: "Coming soon" },
    "common.anonymous": { he: "אנונימי", en: "Anonymous" },
    "common.in-development": { he: "בפיתוח...", en: "In development..." },
    "common.save-changes": { he: "שמירת שינויים", en: "Save changes" },
    "courses.courses-tab.empty-state.subtitle": {
        he: "לחצו על הכפתור למעלה ליצירת הקורס הראשון שלכם.",
        en: "Click the button above to create your first course.",
    },
    "courses.courses-tab.empty-state.title": {
        he: "אין לכם עדיין קורסים",
        en: "You don't have any courses yet",
    },
    "error.page.description": {
        he: "מתנצלים, אירעה שגיאה. אפשר לנסות שוב או לעבור למסך הראשי.",
        en: "Sorry, something went wrong. You can try again or go back to the home screen.",
    },
    "error.page.heading": { he: "משהו השתבש", en: "Something went wrong" },
    "error.page.home": { he: "לעמוד הראשי", en: "Go to homepage" },
    "error.page.retry": { he: "נסו שוב", en: "Try again" },
    "error.page.title": { he: "שגיאה", en: "Error" },
    "funnels.ai-create-lomda.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "funnels.create-lomda.choose-folder.choose-btn": {
        he: "בחירה",
        en: "Select",
    },
    "lomda-editor.sidebar.avatar.header.description": {
        he: "יצירת אווטר מדובב בעזרת AI",
        en: "Create a dubbed avatar with AI",
    },
    "lomda-editor.navbar.floating-nav.hide": {
        he: "הסתר שלט סצנות",
        en: "Hide scene navigator",
    },
    "lomda-editor.navbar.floating-nav.show": {
        he: "הצג שלט סצנות",
        en: "Show scene navigator",
    },
    "lomda-editor.canvas.snap-hint": {
        he: "החזיקו Alt לביטול הצמדה",
        en: "Hold Alt to disable snapping",
    },
    "lomda-editor.navbar.render": {
        he: "רינדור לומדה",
        en: "Render lomda",
    },
    "lomda-editor.navbar.render.error.no-scenes": {
        he: "יש להוסיף לפחות סצנה אחת לפני רינדור",
        en: "You must add at least one scene before rendering",
    },
    "lomda-editor.navbar.render.error.too-many-scenes": {
        he: "לא ניתן לרנדר יותר מ-50 סצנות",
        en: "Cannot render more than 50 scenes",
    },
    "lomda-editor.navbar.sidebar.close": {
        he: "סגירת תפריט צד",
        en: "Close sidebar",
    },
    "lomda-editor.navbar.sidebar.open": {
        he: "פתיחת תפריט צד",
        en: "Open sidebar",
    },
    "lomda-editor.sidebar.avatar": { he: "אווטאר", en: "Avatar" },
    "lomda-editor.sidebar.avatar.header.title": { he: "אווטאר", en: "Avatar" },
    "lomda-editor.sidebar.avatars": { he: "אווטארים", en: "Avatars" },
    "lomda-editor.sidebar.avatars.header.title": {
        he: "אווטארים",
        en: "Avatars",
    },
    "lomda-editor.sidebar.avatars.header.description": {
        he: "מציגי AI לסצנות הלומדה שלך.",
        en: "AI presenters for your lomda scenes.",
    },
    "lomda-editor.sidebar.avatars.tabs.mine": { he: "שלי", en: "Mine" },
    "lomda-editor.sidebar.avatars.tabs.library": {
        he: "ספרייה",
        en: "Library",
    },
    "lomda-editor.sidebar.avatars.tabs.voices": { he: "קולות", en: "Voices" },
    "lomda-editor.sidebar.avatars.count": {
        he: "{count} אווטארים",
        en: "{count} avatars",
    },
    "lomda-editor.sidebar.avatars.count.one": {
        he: "אווטאר אחד",
        en: "1 avatar",
    },
    "lomda-editor.sidebar.avatars.new": { he: "חדש", en: "New" },
    "lomda-editor.sidebar.avatars.empty": {
        he: "צרו את האווטאר שלכם",
        en: "Create your own avatar",
    },
    "lomda-editor.sidebar.avatars.empty.description": {
        he: "הפכו את עצמכם או כל אדם אחר לאווטאר AI ושלבו אותו בסצנות שלכם.",
        en: "Turn yourself — or anyone else — into an AI avatar you can drop into your scenes.",
    },
    "lomda-editor.sidebar.avatars.create": {
        he: "אווטאר חדש",
        en: "New avatar",
    },
    "lomda-editor.sidebar.avatars.add-look": {
        he: "הוספת מראה חדש",
        en: "Add new look",
    },
    "lomda-editor.sidebar.avatars.voices.description": {
        he: "עיין, עצב או שכפל קולות לשימוש בסצנות עם אווטארים.",
        en: "Browse, design, or clone voices to use on avatar scenes.",
    },
    "lomda-editor.sidebar.avatars.voices.open-browser": {
        he: "פתיחת דפדפן הקולות",
        en: "Open voice browser",
    },
    "avatars.card.status.ready": { he: "מוכן", en: "Ready" },
    "avatars.card.status.pending-consent": {
        he: "ממתין להסכמה",
        en: "Pending consent",
    },
    "avatars.card.status.processing": { he: "באימון", en: "Training" },
    "avatars.card.status.failed": { he: "נכשל", en: "Failed" },
    "avatars.card.no-preview": { he: "אין תצוגה מקדימה", en: "No preview" },
    "avatars.card.refresh-status": {
        he: "רענון סטטוס",
        en: "Refresh status",
    },
    "avatars.card.sign-consent": {
        he: "פתיחת עמוד ההסכמה",
        en: "Open consent page",
    },
    "avatars.toast.consent.error": {
        he: "לא הצלחנו ליצור קישור הסכמה. נסו שוב.",
        en: "Couldn't generate a consent link. Please try again.",
    },
    "avatars.toast.avatar-not-ready": {
        he: "האווטאר עדיין לא מוכן. יש להמתין עד שיסיים לפני שימוש במראות שלו",
        en: "Avatar isn't ready yet. Wait for it to finish before using its looks",
    },
    "avatars.card.delete": { he: "מחיקה", en: "Delete" },
    "avatars.card.looks.count": {
        he: "{count} מראות",
        en: "{count} looks",
    },
    "avatars.card.looks.count.one": { he: "מראה אחד", en: "1 look" },
    "avatars.look.training": { he: "באימון", en: "Training" },
    "avatars.look.no-preview": { he: "אין תצוגה מקדימה", en: "No preview" },
    "avatars.look.applied": { he: "בשימוש", en: "Applied" },
    "avatars.look.unavailable": {
        he: "המראה יהיה זמין לבחירה כשיסיים אימון",
        en: "Available once training completes",
    },
    "avatars.engine.no-v-badge.tooltip": {
        he: "האיכות הגבוהה ביותר עדיין לא זמינה. היא תיפתח אוטומטית אחרי ההפקה הראשונה עם אווטאר זה.",
        en: "Highest quality isn't available yet. It unlocks automatically after this avatar's first video.",
    },
    "avatars.modal.create.title": {
        he: "יצירת אווטאר חדש",
        en: "Create a new avatar",
    },
    "avatars.modal.create.description": {
        he: "בנו זהות AI חדשה. ניתן להוסיף לה מראות נוספים מאוחר יותר.",
        en: "Build a new AI identity. You can add more looks to it later.",
    },
    "avatars.gallery.title": { he: "{name} — מראות", en: "{name} — looks" },
    "avatars.gallery.add-look": { he: "הוספת מראה", en: "Add look" },
    "avatars.add-look.title": {
        he: "הוספת מראה ל{name}",
        en: "Add a look to {name}",
    },
    "avatars.add-look.description": {
        he: "מראות שומרים את אותה הזהות אך משנים תלבושת, סגנון או רקע.",
        en: "Looks keep the same identity but change outfit, style, or setting.",
    },
    "avatars.add-look.tab.photo": { he: "מתמונה", en: "From photo" },
    "avatars.add-look.tab.prompt": { he: "מטקסט", en: "From prompt" },
    "avatars.add-look.name.label": {
        he: "שם המראה",
        en: "Look name",
    },
    "avatars.add-look.name.placeholder": {
        he: "לדוגמה: חולצה כחולה ברקע משרד",
        en: "e.g. Blue shirt, office background",
    },
    "avatars.add-look.photo.hint": {
        he: "מומלץ: תמונת ראש וכתפיים של אותה הדמות, אך עם תלבושת/רקע אחרים.",
        en: "Tip: a head-and-shoulders photo of the same person, but with a different outfit or background.",
    },
    "avatars.add-look.prompt.placeholder": {
        he: "תארו את המראה החדש: לבוש, רקע, סגנון, תאורה.",
        en: "Describe the new look: clothing, background, style, lighting.",
    },
    "avatars.add-look.prompt.hint": {
        he: "התמקדו במה שהשתנה — לא בכל הדמות.",
        en: "Focus on what's different — not on the whole person.",
    },
    "avatars.add-look.submit": { he: "הוספת מראה", en: "Add look" },
    "avatars.public.loading": {
        he: "טוען את הספרייה הציבורית…",
        en: "Loading public library…",
    },
    "avatars.public.no-looks": {
        he: "אין מראות זמינים",
        en: "No looks available",
    },
    "avatars.sidebar.looks.back": {
        he: "חזרה לאווטארים",
        en: "Back to avatars",
    },
    "avatars.sidebar.looks.subtitle": {
        he: "בחרו מראה מבין המראות השונים של האווטאר",
        en: "Choose a look from the avatar's different looks",
    },
    "avatars.wizard.back": { he: "חזרה", en: "Back" },
    "avatars.wizard.next": { he: "הבא", en: "Next" },
    "avatars.wizard.create": { he: "צור אווטאר", en: "Create avatar" },
    "avatars.wizard.submit-video": {
        he: "שליחה לאימון",
        en: "Submit for training",
    },
    "avatars.wizard.step1.title": {
        he: "כיצד תרצו ליצור את האווטאר?",
        en: "How would you like to create your avatar?",
    },
    "avatars.wizard.step1.subtitle": {
        he: "בחרו את השיטה שמתאימה לכם ביותר. ניתן ליצור כמה אווטארים שתרצו.",
        en: "Pick the approach that fits best. You can create as many avatars as you like.",
    },
    "avatars.wizard.step1.badge.fastest": { he: "הכי מהיר", en: "Fastest" },
    "avatars.wizard.step1.badge.most-realistic": {
        he: "הכי ריאליסטי",
        en: "Most realistic",
    },
    "avatars.wizard.step1.photo.title": {
        he: "מתמונה",
        en: "From a photo",
    },
    "avatars.wizard.step1.photo.description": {
        he: "העלאת תמונת פורטרייט בודדת. מוכן תוך שניות, ללא הסכמה.",
        en: "Upload a single portrait. Ready in seconds, no consent needed.",
    },
    "avatars.wizard.step1.video.title": {
        he: "מסרטון",
        en: "From a video",
    },
    "avatars.wizard.step1.video.description": {
        he: "30–180 שניות של דיבור מול המצלמה. נדרשת הסכמה ואימון של מספר דקות.",
        en: "30–180 seconds of talking to camera. Requires consent and a few minutes of training.",
    },
    "avatars.wizard.step1.prompt.title": {
        he: "מטקסט חופשי",
        en: "From a prompt",
    },
    "avatars.wizard.step1.prompt.description": {
        he: "תארו דמות חדשה לחלוטין במילים. ניתן לצרף עד 3 תמונות רפרנס.",
        en: "Describe a brand-new character in words. Add up to 3 reference images.",
    },
    "avatars.wizard.step2.title.photo": {
        he: "העלאת תמונת פורטרייט",
        en: "Upload a portrait photo",
    },
    "avatars.wizard.step2.subtitle.photo": {
        he: "תמונת ראש וכתפיים, מרכזית, ברורה ומוארת היטב.",
        en: "A clear, well-lit head-and-shoulders shot, facing the camera.",
    },
    "avatars.wizard.step2.title.video": {
        he: "העלאת סרטון אימון",
        en: "Upload a training video",
    },
    "avatars.wizard.step2.subtitle.video": {
        he: "סרטון פנים-אל-מצלמה של דובר יחיד. ככל שהאיכות גבוהה יותר — האווטאר יהיה מציאותי יותר.",
        en: "A front-facing, single-speaker clip. The better the quality, the more realistic the avatar.",
    },
    "avatars.wizard.step2.title.prompt": {
        he: "תיאור האווטאר",
        en: "Describe the avatar",
    },
    "avatars.wizard.step2.subtitle.prompt": {
        he: "ככל שהתיאור עשיר יותר (גיל, מגדר, הבעה, לבוש, רקע) — התוצאה תהיה מדויקת יותר.",
        en: "Richer descriptions (age, gender, expression, clothing, setting) give more accurate results.",
    },
    "avatars.wizard.step2.photo.upload": {
        he: "העלאת תמונת פורטרייט",
        en: "Upload portrait image",
    },
    "avatars.wizard.step2.photo.hint": {
        he: "מומלץ: JPG/PNG ברזולוציה של 1024px ומעלה, פנים גלויות, רקע נקי.",
        en: "Tip: JPG/PNG at 1024px or more, face clearly visible, clean background.",
    },
    "avatars.wizard.step2.photo.unsupported": {
        he: "פורמט תמונה לא נתמך: {type}",
        en: "Unsupported image format: {type}",
    },
    "avatars.wizard.step2.photo.too-large": {
        he: "התמונה חורגת מ-32MB. דחוס ונסה שוב.",
        en: "Image exceeds 32MB. Compress and retry.",
    },
    "avatars.wizard.step2.video.upload": {
        he: "העלאת סרטון אימון",
        en: "Upload training video",
    },
    "avatars.wizard.step2.video.duration": {
        he: "משך: {seconds} שניות",
        en: "Duration: {seconds}s",
    },
    "avatars.wizard.step2.video.read-error": {
        he: "לא ניתן לקרוא את הסרטון",
        en: "Could not read video",
    },
    "avatars.wizard.step2.video.upload-failed": {
        he: "ההעלאה נכשלה",
        en: "Upload failed",
    },
    "avatars.wizard.step2.video.hint": {
        he: "מומלץ {min}–{max} שניות של צילום פנים-אל-מצלמה, דובר יחיד, תאורה טובה ורקע נייטרלי.",
        en: "Aim for {min}–{max}s of front-facing, single-speaker footage with good lighting and a neutral background.",
    },
    "avatars.wizard.step2.video.warning.short": {
        he: "סרטונים קצרים מ-{min} שניות עשויים להפיק אווטאר באיכות פחות טובה.",
        en: "Videos shorter than {min}s may produce a lower-quality avatar.",
    },
    "avatars.wizard.step2.video.error.format": {
        he: "פורמט וידאו לא נתמך: {type}. יש להשתמש ב-MP4, MOV או WebM.",
        en: "Unsupported video format: {type}. Use MP4, MOV, or WebM.",
    },
    "avatars.wizard.step2.video.error.too-short": {
        he: "הסרטון קצר מדי ({seconds} שניות). המינימום הוא {min} שניות.",
        en: "Video is too short ({seconds}s). Minimum is {min}s.",
    },
    "avatars.wizard.step2.video.error.too-long": {
        he: "הסרטון ארוך מדי ({seconds} שניות). המקסימום הוא {max} שניות.",
        en: "Video is too long ({seconds}s). Maximum is {max}s.",
    },
    "avatars.wizard.step2.video.error.too-large": {
        he: "הסרטון חורג מ-32MB. דחסו ונסו שוב.",
        en: "Video exceeds 32MB. Compress and try again.",
    },
    "avatars.wizard.step2.prompt.describe": {
        he: "תיאור האווטאר",
        en: "Describe the avatar",
    },
    "avatars.wizard.step2.prompt.placeholder": {
        he: "לדוגמה: אישה ידידותית בשנות ה-30 לחייה עם שיער חום מתולתל, חולצת בלייזר כחולה, במשרד מואר היטב.",
        en: "e.g. A friendly woman in her 30s with brown curly hair, wearing a navy blazer, in a brightly lit office.",
    },
    "avatars.wizard.step2.prompt.references": {
        he: "תמונות רפרנס (אופציונלי, עד {max})",
        en: "Reference images (optional, up to {max})",
    },
    "avatars.wizard.step2.prompt.refs.add": {
        he: "הוספת תמונת רפרנס",
        en: "Add reference image",
    },
    "avatars.wizard.step2.prompt.refs.subtitle": {
        he: "תמונות שיעזרו לכוון את הסגנון",
        en: "Images that help guide the style",
    },
    "avatars.wizard.step2.prompt.refs.remove": {
        he: "הסרת תמונה",
        en: "Remove image",
    },
    "avatars.wizard.step3.title": {
        he: "כיצד תקראו לאווטאר?",
        en: "Name your avatar",
    },
    "avatars.wizard.step3.subtitle": {
        he: "בחרו שם שיעזור לכם לזהות את האווטאר במהירות.",
        en: "This name appears throughout the editor — pick something you'll recognize at a glance.",
    },
    "avatars.wizard.step3.name.label": {
        he: "שם האווטאר",
        en: "Avatar name",
    },
    "avatars.wizard.step3.name.placeholder": {
        he: "לדוגמה: תום ממכירות",
        en: "e.g. Tom from sales",
    },
    "avatars.wizard.step3.name.hint": {
        he: "ניתן לשנות מאוחר יותר. ניתן גם להוסיף לאווטאר מראות נוספים בהמשך מתפריט הצד.",
        en: "You can rename it later. You can also add more looks to this identity from the sidebar.",
    },
    "avatars.wizard.step4.title": {
        he: "סיכום ויצירה",
        en: "Review and create",
    },
    "avatars.wizard.step4.subtitle": {
        he: "בדקו את הפרטים ושלחו ליצירה.",
        en: "Take a final look and submit.",
    },
    "avatars.wizard.step4.mode": { he: "סוג", en: "Type" },
    "avatars.wizard.step4.name": { he: "שם", en: "Name" },
    "avatars.wizard.step4.prompt": { he: "תיאור", en: "Prompt" },
    "avatars.wizard.step4.media": { he: "מקור", en: "Source" },
    "avatars.wizard.step4.references": {
        he: "תמונות רפרנס",
        en: "Reference images",
    },
    "avatars.wizard.step4.note.video": {
        he: "מיד לאחר השליחה תקבלו קישור הסכמה ל-HeyGen, ולאחר מכן יתחיל אימון של מספר דקות.",
        en: "Right after submission you'll get a HeyGen consent link, then a few minutes of training will begin.",
    },
    "avatars.wizard.step4.mode.photo": { he: "תמונה", en: "Photo" },
    "avatars.wizard.step4.mode.video": { he: "וידאו", en: "Video" },
    "avatars.wizard.step4.mode.prompt": { he: "טקסט", en: "Prompt" },
    "avatars.toast.create-photo.success": {
        he: "האווטאר נוצר",
        en: "Avatar created",
    },
    "avatars.toast.create-video.success": {
        he: "האווטאר נשלח — בהמשך נדרשת הסכמה ואימון",
        en: "Avatar submitted — consent + training next",
    },
    "avatars.toast.create-prompt.success": {
        he: "יצירת האווטאר נכנסה לתור",
        en: "Avatar generation queued",
    },
    "avatars.toast.delete-avatar.success": {
        he: "האווטאר נמחק",
        en: "Avatar deleted",
    },
    "avatars.toast.delete-look.success": {
        he: "המראה נמחק",
        en: "Look deleted",
    },
    "avatars.toast.add-look.success": {
        he: "המראה נוסף",
        en: "Look added",
    },
    "avatars.toast.add-look-prompt.success": {
        he: "יצירת המראה נכנסה לתור",
        en: "Look generation queued",
    },
    "avatars.toast.rename-avatar.success": {
        he: "האווטאר שונה",
        en: "Avatar renamed",
    },
    "avatars.toast.refresh.loading": {
        he: "בודק סטטוס אווטאר...",
        en: "Checking avatar status...",
    },
    "avatars.toast.refresh.ready": {
        he: "האווטאר מוכן",
        en: "Avatar is ready",
    },
    "avatars.toast.refresh.still-training": {
        he: "האווטאר עדיין באימון",
        en: "Avatar is still training",
    },
    "avatars.toast.refresh.error": {
        he: "שגיאה ברענון האווטאר",
        en: "Failed to refresh avatar",
    },
    "avatars.toast.create-photo.error": {
        he: "יצירת האווטאר נכשלה. נסו שוב.",
        en: "Couldn't create the avatar. Please try again.",
    },
    "avatars.toast.create-video.error": {
        he: "יצירת אווטאר הווידאו נכשלה. ודאו שהסרטון תקין ונסו שוב.",
        en: "Couldn't create the video avatar. Check your clip and try again.",
    },
    "avatars.toast.create-prompt.error": {
        he: "יצירת האווטאר נכשלה. נסחו את התיאור מחדש ונסו שוב.",
        en: "Couldn't generate the avatar. Rephrase the prompt and try again.",
    },
    "avatars.toast.add-look.error": {
        he: "הוספת המראה נכשלה. נסו שוב.",
        en: "Couldn't add the look. Please try again.",
    },
    "avatars.toast.add-look-prompt.error": {
        he: "יצירת המראה נכשלה. נסחו מחדש ונסו שוב.",
        en: "Couldn't generate the look. Rephrase and try again.",
    },
    "avatars.toast.delete-avatar.error": {
        he: "מחיקת האווטאר נכשלה. נסו שוב.",
        en: "Couldn't delete the avatar. Please try again.",
    },
    "avatars.toast.delete-look.error": {
        he: "מחיקת המראה נכשלה. נסו שוב.",
        en: "Couldn't delete the look. Please try again.",
    },
    "avatars.toast.rename-avatar.error": {
        he: "שינוי שם האווטאר נכשל. נסו שוב.",
        en: "Couldn't rename the avatar. Please try again.",
    },
    "avatars.delete-avatar.confirm.title": {
        he: "למחוק את {name}?",
        en: "Delete {name}?",
    },
    "avatars.delete-avatar.confirm.description": {
        he: "האווטאר וכל המראות שלו יימחקו לצמיתות. לא ניתן לשחזר פעולה זו.",
        en: "The avatar and all of its looks will be permanently deleted. This cannot be undone.",
    },
    "avatars.delete-look.confirm.title": {
        he: "למחוק את המראה {name}?",
        en: "Delete look {name}?",
    },
    "avatars.delete-look.confirm.description": {
        he: "המראה יימחק לצמיתות. לא ניתן לשחזר פעולה זו.",
        en: "The look will be permanently deleted. This cannot be undone.",
    },
    "avatars.delete-confirm.cancel": { he: "ביטול", en: "Cancel" },
    "avatars.delete-confirm.confirm": { he: "מחיקה", en: "Delete" },
    "lomda-editor.sidebar.recording": {
        he: "הקלטה",
        en: "Recording",
    },
    "lomda-editor.sidebar.recording.header.description": {
        he: "הקלטת וידאו ממצלמת מחשב ודיבוב",
        en: "Record webcam video and voiceover",
    },
    "lomda-editor.sidebar.recording.header.title": {
        he: "הקלטה",
        en: "Recording",
    },
    "lomda-editor.sidebar.settings.header.description": {
        he: "שנו הגדרות בסיסיות בלומדה",
        en: "Adjust basic lomda settings",
    },
    "lomda-editor.sidebar.settings.header.title": {
        he: "הגדרות",
        en: "Settings",
    },
    "lomda-editor.sidebar.scenes": { he: "סצנות", en: "Scenes" },
    "lomda-editor.sidebar.scenes.description": {
        he: "צפו בכל הסצנות שלכם",
        en: "View all your scenes",
    },
    "lomda-editor.sidebar.settings": { he: "הגדרות", en: "Settings" },
    "lomda.editor.loading": {
        he: "טוען עורך לומדה...",
        en: "Loading lomda editor...",
    },
    "lomda-editor.sidebar.layout": { he: "פריסה", en: "Layout" },
    "lomda-editor.sidebar.layout.description": {
        he: "בחרו תבנית פריסה לסצנה",
        en: "Choose a layout template for the scene",
    },
    "lomda-editor.sidebar.layout.apply-to-all": {
        he: "החיל על כל הסצנות",
        en: "Apply to all scenes",
    },
    "lomda-editor.sidebar.style": { he: "עיצוב", en: "Style" },
    "lomda-editor.sidebar.style.description": {
        he: "התאמת עיצוב הלומדה והסצנות",
        en: "Customize lomda and scene styles",
    },
    "lomda-editor.sidebar.style.defaults": {
        he: "סצנה ברירת מחדל",
        en: "Lomda Defaults",
    },
    "lomda-editor.sidebar.style.default-background": {
        he: "רקע ברירת מחדל",
        en: "Default Background",
    },
    "lomda-editor.sidebar.style.default-text-color": {
        he: "צבע טקסט ברירת מחדל",
        en: "Default Text Color",
    },
    "lomda-editor.sidebar.style.scene-style": {
        he: "עיצוב סצנה",
        en: "Scene Style",
    },
    "lomda-editor.sidebar.style.select-scene": {
        he: "בחרו סצנה כדי לערוך את העיצוב שלה.",
        en: "Select a scene to edit its style.",
    },
    "lomda-editor.sidebar.style.text-color": {
        he: "צבע טקסט",
        en: "Text Color",
    },
    "lomda-editor.sidebar.style.reset-to-default": {
        he: "איפוס לברירת מחדל",
        en: "Reset to default",
    },
    "lomda-editor.sidebar.style.scene-background": {
        he: "רקע סצנה",
        en: "Scene Background",
    },
    "lomda-editor.sidebar.style.set-as-default": {
        he: "הגדר סצנה נוכחית כברירת מחדל",
        en: "Set current scene as default",
    },
    "lomda-editor.sidebar.style.reset-scene-to-defaults": {
        he: "אפס סצנה נוכחית לברירת מחדל",
        en: "Reset scene to defaults",
    },
    "lomda-editor.sidebar.style.apply-defaults-all-scenes": {
        he: "החל ברירת מחדל על כל הסצנות",
        en: "Apply defaults to all scenes",
    },
    "lomda-editor.sidebar.style.custom-gradient": {
        he: "גרדיאנט מותאם אישית",
        en: "Custom Gradient",
    },
    "lomda-editor.sidebar.style.gradient-from": {
        he: "מ",
        en: "From",
    },
    "lomda-editor.sidebar.style.gradient-to": {
        he: "אל",
        en: "To",
    },
    "lomda-editor.sidebar.style.tab.gradient": {
        he: "גרדיאנט",
        en: "Gradient",
    },
    "lomda-editor.sidebar.style.current-scene": {
        he: "סצנה נוכחית",
        en: "Current scene",
    },
    "lomda-editor.sidebar.background": { he: "רקע", en: "Background" },
    "lomda-editor.sidebar.background.description": {
        he: "התאמת רקע הסצנה",
        en: "Customize scene background",
    },
    "lomda-editor.sidebar.background.select-scene": {
        he: "בחרו סצנה כדי לערוך את הרקע שלה.",
        en: "Select a scene to edit its background.",
    },
    "lomda-editor.sidebar.background.tab.color": { he: "צבע", en: "Color" },
    "lomda-editor.sidebar.background.tab.media": { he: "מדיה", en: "Media" },
    "lomda-editor.sidebar.background.preset-gradients": {
        he: "גרדיאנטים מוכנים",
        en: "Preset Gradients",
    },
    "lomda-editor.sidebar.background.solid-colors": {
        he: "צבעים אחידים",
        en: "Solid Colors",
    },
    "lomda-editor.sidebar.background.custom-color": {
        he: "צבע מותאם אישית",
        en: "Custom Color",
    },
    "lomda-editor.sidebar.background.open-color-picker": {
        he: "פתח בוחר צבעים",
        en: "Open color picker",
    },
    "lomda-editor.sidebar.background.select-gradient": {
        he: "בחר גרדיאנט",
        en: "Select gradient",
    },
    "lomda-editor.sidebar.background.select-color": {
        he: "בחר צבע",
        en: "Select color",
    },
    "lomda-editor.sidebar.background.image-placeholder": {
        he: "העלאת תמונת רקע תתווסף בהמשך.",
        en: "Background image upload coming soon.",
    },
    "lomda-editor.sidebar.background.image-section": {
        he: "רקע",
        en: "Background",
    },
    "lomda-editor.sidebar.background.select-background-image": {
        he: "בחר תמונת רקע",
        en: "Select background image",
    },
    "lomda-editor.sidebar.background.upload-background": {
        he: "העלה רקע",
        en: "Upload background",
    },
    "lomda-editor.sidebar.background.upload-accept": {
        he: "PNG, JPG, MP4 או WebM",
        en: "PNG, JPG, MP4 or WebM",
    },
    "lomda-editor.sidebar.media": { he: "מדיה", en: "Media" },
    "lomda-editor.sidebar.media.description": {
        he: "הוסף נכסים לספרייה שלך",
        en: "Add assets to your library",
    },
    "lomda-editor.sidebar.media.search-placeholder": {
        he: "חיפוש...",
        en: "Search ...",
    },
    "lomda-editor.sidebar.media.tab.assets": { he: "קבצים", en: "Assets" },
    "lomda-editor.sidebar.media.tab.library": { he: "ספרייה", en: "Library" },
    "lomda-editor.sidebar.media.tab.stock": { he: "סטוק", en: "Stock" },
    "lomda-editor.sidebar.media.tab.ai": { he: "AI", en: "AI" },
    "lomda-editor.sidebar.media.tab.upload": { he: "העלאה", en: "Upload" },
    "lomda-editor.sidebar.media.assets.empty-title": {
        he: "אין נכסים עדיין",
        en: "No assets yet",
    },
    "lomda-editor.sidebar.media.assets.empty-description": {
        he: "העלה קבצים, צור עם AI או הקלט מדיה לשימוש בסצנות שלך",
        en: "Upload files, generate with AI, or record media to use in your scenes",
    },
    "lomda-editor.sidebar.media.assets.generate": { he: "צור", en: "Generate" },
    "lomda-editor.sidebar.media.assets.upload": { he: "העלה", en: "Upload" },
    "lomda-editor.sidebar.media.upload.title": {
        he: "העלאת מדיה",
        en: "Upload Media",
    },
    "lomda-editor.sidebar.media.upload.subtitle": {
        he: "תמונות, וידאו או אודיו",
        en: "Images, video or audio",
    },
    "lomda-editor.sidebar.media.upload.added-to-assets": {
        he: "קבצים שהועלו יופיעו בלשונית קבצים",
        en: "Uploaded files will appear in the Files tab",
    },
    "lomda-editor.sidebar.media.upload.replaced-and-added-to-assets": {
        he: "המדיה הוחלפה בסצנה ונוספה לקבצים",
        en: "Media replaced in scene and added to files",
    },
    "lomda-editor.sidebar.media.assets.source.uploaded": {
        he: "הועלה",
        en: "Uploaded",
    },
    "lomda-editor.sidebar.media.assets.source.generated": {
        he: "נוצר",
        en: "Generated",
    },
    "lomda-editor.sidebar.media.assets.filter.type-prefix": {
        he: "סוג:",
        en: "Type:",
    },
    "lomda-editor.sidebar.media.assets.filter.type-all": {
        he: "הכל",
        en: "All",
    },
    "lomda-editor.sidebar.media.assets.filter.type-image": {
        he: "תמונה",
        en: "Image",
    },
    "lomda-editor.sidebar.media.assets.filter.type-video": {
        he: "וידאו",
        en: "Video",
    },
    "lomda-editor.sidebar.media.assets.filter.type-audio": {
        he: "אודיו",
        en: "Audio",
    },
    "lomda-editor.sidebar.media.assets.filter.sort-prefix": {
        he: "מיון:",
        en: "Sort:",
    },
    "lomda-editor.sidebar.media.assets.filter.sort-newest": {
        he: "החדש ביותר",
        en: "Newest",
    },
    "lomda-editor.sidebar.media.assets.filter.sort-oldest": {
        he: "הישן ביותר",
        en: "Oldest",
    },
    "lomda-editor.sidebar.media.preview.title": {
        he: "קובץ",
        en: "File",
    },
    "lomda-editor.sidebar.media.preview.created-at": {
        he: "נוצר ב",
        en: "Created at",
    },
    "lomda-editor.sidebar.media.preview.type": {
        he: "סוג",
        en: "Type",
    },
    "lomda-editor.sidebar.media.preview.type-image": {
        he: "תמונה",
        en: "Image",
    },
    "lomda-editor.sidebar.media.preview.type-video": {
        he: "וידאו",
        en: "Video",
    },
    "lomda-editor.sidebar.media.preview.type-audio": {
        he: "אודיו",
        en: "Audio",
    },
    "lomda-editor.sidebar.media.preview.size": {
        he: "גודל",
        en: "Size",
    },
    "lomda-editor.sidebar.media.preview.delete": {
        he: "מחק",
        en: "Delete",
    },
    "lomda-editor.sidebar.media.preview.replace-in-scene": {
        he: "החלף בסצנה",
        en: "Replace in scene",
    },
    "lomda-editor.sidebar.media.preview.set-as-background": {
        he: "הגדר כרקע",
        en: "Set as background",
    },
    "lomda-editor.sidebar.media.preview.set-as-background-current-scene": {
        he: "סצנה נוכחית",
        en: "Current scene",
    },
    "lomda-editor.sidebar.media.preview.set-as-background-default": {
        he: "ברירת מחדל",
        en: "Default",
    },
    "lomda-editor.sidebar.media.preview.set-as-background-all-scenes": {
        he: "כל הסצנות",
        en: "All scenes",
    },
    "lomda-editor.sidebar.media.preview.change-media": {
        he: "החלף מדיה",
        en: "Change media",
    },
    "lomda-editor.sidebar.media.toast.no-media-selected": {
        he: "לא נבחר פריט מדיה בסצנה",
        en: "No media item selected in the scene",
    },
    "lomda-editor.sidebar.media.toast.no-scene-selected": {
        he: "בחר סצנה תחילה",
        en: "Select a scene first",
    },
    "lomda-editor.sidebar.media.toast.background-set-current": {
        he: "הרקע הוגדר לסצנה הנוכחית",
        en: "Background set for current scene",
    },
    "lomda-editor.sidebar.media.toast.background-set-default": {
        he: "הרקע הוגדר כברירת מחדל",
        en: "Background set as default",
    },
    "lomda-editor.sidebar.media.toast.background-set-all": {
        he: "הרקע הוגדר לכל הסצנות",
        en: "Background set for all scenes",
    },
    "lomda-editor.sidebar.media.toast.added-to-assets": {
        he: "נוסף לקבצי הלומדה",
        en: "Added to lomda assets",
    },
    "lomda-editor.sidebar.media.toast.replaced-in-scene": {
        he: "המדיה הוחלפה בסצנה",
        en: "Media replaced in scene",
    },
    "lomda-editor.sidebar.media.toast.image-only": {
        he: "ניתן לבחור תמונות בלבד עבור גריד התמונות",
        en: "Only images can be used in the image grid",
    },
    "lomda-editor.sidebar.media.toast.replaced-avatar": {
        he: "האווטאר הוחלף",
        en: "Avatar replaced",
    },
    "lomda-editor.sidebar.media.toast.stock-applied-scene": {
        he: "התמונה הוחלפה בסצנה",
        en: "Image applied to scene",
    },
    "lomda-editor.sidebar.media.toast.stock-applied-avatar": {
        he: "התמונה הוחלפה באווטאר",
        en: "Image applied to avatar",
    },
    "lomda-editor.sidebar.media.assets.stock": { he: "מלאי", en: "Stock" },
    "lomda-editor.sidebar.media.stock.search-placeholder": {
        he: "חיפוש...",
        en: "Search...",
    },
    "lomda-editor.sidebar.media.stock.load-more": {
        he: "טען עוד",
        en: "Load more",
    },
    "lomda-editor.sidebar.media.stock.no-results": {
        he: "לא נמצאו תוצאות",
        en: "No results found",
    },
    "lomda-editor.sidebar.media.stock.load-failed": {
        he: "טעינת התמונות נכשלה",
        en: "Failed to load images",
    },
    "lomda-editor.sidebar.media.stock.empty-prompt": {
        he: "חפשו תמונות סטוק",
        en: "Search stock images",
    },
    "lomda-editor.sidebar.media.tab.stock-images": {
        he: "סטוק תמונות",
        en: "Stock images",
    },
    "lomda-editor.sidebar.media.tab.stock-icons": {
        he: "סטוק אייקונים",
        en: "Stock icons",
    },
    "lomda-editor.sidebar.media.stock.icons.search-placeholder": {
        he: "חיפוש אייקונים...",
        en: "Search icons...",
    },
    "lomda-editor.sidebar.media.stock.icons.empty-prompt": {
        he: "חפשו אייקונים בסטוק",
        en: "Search stock icons",
    },
    "lomda-editor.sidebar.media.stock.icons.no-results": {
        he: "לא נמצאו אייקונים",
        en: "No icons found",
    },
    "lomda-editor.sidebar.media.stock.icons.load-failed": {
        he: "טעינת האייקונים נכשלה",
        en: "Failed to load icons",
    },
    "lomda-editor.sidebar.media.stock.icons.add-failed": {
        he: "הוספת האייקון נכשלה",
        en: "Failed to add icon",
    },
    "lomda-editor.sidebar.media.stock.hint-no-selection": {
        he: "בחרו תמונה בסצנה שאותה תרצו להחליף בתמונת סטוק",
        en: "Select a media item in a scene to replace with a stock image",
    },
    "lomda-editor.sidebar.media.stock.hint-with-selection": {
        he: "לחץ על תמונה כדי להחליף את המדיה הנבחרת",
        en: "Click an image to replace the selected media",
    },
    "lomda-editor.sidebar.media.upload.hint-no-selection": {
        he: "הקבצים שתעלו יתווספו לקבצי הלומדה",
        en: "Files you upload will be added to the lomda's assets",
    },
    "lomda-editor.sidebar.media.upload.hint-with-selection": {
        he: "הקובץ יחליף את המדיה הנבחרת ויתווסף לקבצי הלומדה",
        en: "The file will replace the selected media and be added to the lomda's assets",
    },
    "lomda-editor.sidebar.script": { he: "תסריט", en: "Script" },
    "lomda-editor.sidebar.script.description": {
        he: "ערכו והזינו תסריט לכל שקף.",
        en: "Edit and script for each slide.",
    },
    "lomda.audio-button.download": { he: "הורד דיבוב", en: "Download dubbing" },
    "lomda.audio-button.generate": { he: "צור דיבוב", en: "Generate dubbing" },
    "lomda.audio-button.generating": {
        he: "יוצר דיבוב...",
        en: "Generating dubbing...",
    },
    "lomda.audio-button.regenerate": {
        he: "רענן דיבוב",
        en: "Refresh dubbing",
    },
    "lomda.audio-button.regenerating": {
        he: "מרענן דיבוב...",
        en: "Refreshing dubbing...",
    },
    "lomda.audio-player.pause": { he: "השהיה", en: "Pause" },
    "lomda.audio-player.play": { he: "ניגון", en: "Play" },
    "lomda.render.error.creation": {
        he: "שגיאה ביצירת הוידאו",
        en: "Failed to generate video",
    },
    "lomda.render.error.no-scenes": {
        he: "אין סצנות ליצירת וידאו",
        en: "No scenes to generate a video",
    },
    "lomda.render.error.timeout": {
        he: "שגיאה בעיבוד הוידאו. נסו שוב בעוד מספר דקות.",
        en: "Video processing failed. Please try again in a few minutes.",
    },
    "lomda.render.progress-item.name": {
        he: "עיבוד לומדה",
        en: "Lomda processing",
    },
    "lomda.render.progress-step.generate-audio": {
        he: "יצירת דיבובים",
        en: "Generating dubbings",
    },
    "lomda.render.progress-step.render-scenes": {
        he: "רינדור סצנות",
        en: "Rendering scenes",
    },
    "lomda.render.progress-step.render-video": {
        he: "רינדור וידאו",
        en: "Rendering video",
    },
    "lomda.render.progress-step.upload-video": {
        he: "העלאת וידאו",
        en: "Uploading video",
    },
    "lomda.scene-floating-nav.next": { he: "סצנה הבאה", en: "Next scene" },
    "lomda.scene-floating-nav.prev": {
        he: "סצנה קודמת",
        en: "Previous scene",
    },
    "lomda.scene-label": { he: "סצנה {n}", en: "Scene {n}" },
    "lomda.scene-interactions.add": {
        he: "הוספת אינטראקציה",
        en: "Add interaction",
    },
    "lomda.scene-interactions.empty-question-placeholder": {
        he: "אינטראקציה ריקה",
        en: "Empty interaction",
    },
    "lomda.scene-interactions.delete.title": {
        he: "מחיקת האינטראקציה",
        en: "Delete interaction",
    },
    "lomda.scene-interactions.delete.description": {
        he: "האם אתם בטוחים? לא ניתן לבטל פעולה זו.",
        en: "Are you sure? This action cannot be undone.",
    },
    "editor.viewport-floater.scene-nav.next": {
        he: "סצנה הבאה",
        en: "Next scene",
    },
    "editor.viewport-floater.scene-nav.prev": {
        he: "סצנה קודמת",
        en: "Previous scene",
    },
    "lomda.scene-block.settings.aria": {
        he: "הגדרות בלוק",
        en: "Block settings",
    },
    "lomda.scene-block.insert": { he: "בלוקים", en: "Blocks" },
    "lomda.scene-block.add-card": { he: "הוספת קלף", en: "Add card" },
    "lomda.scene-block.add-item": { he: "הוסף פריט", en: "Add item" },
    "lomda.scene-block.add-step": { he: "הוסף שלב", en: "Add step" },
    "lomda.scene-block.delete": { he: "מחיקה", en: "Delete" },
    "lomda.scene-block.columns": { he: "עמודות", en: "Columns" },
    "lomda.scene-block.padding": { he: "ריווח פנימי", en: "Inner padding" },
    "lomda.scene-block.border-radius": {
        he: "עיגול פינות",
        en: "Border radius",
    },
    "lomda.scene-block.image-height": {
        he: "גובה תמונה",
        en: "Image height",
    },
    "lomda.scene-block.border-color": { he: "צבע מסגרת", en: "Border color" },
    "lomda.scene-block.background-color": {
        he: "צבע רקע",
        en: "Background color",
    },
    "lomda.scene-block.transparent": { he: "שקוף", en: "Transparent" },
    "lomda.scene-block.remove": { he: "הסר", en: "Remove" },
    "lomda.scene-block.reset": { he: "איפוס", en: "Reset" },
    "lomda.scene-block.block-type": { he: "סוג הבלוק", en: "Block type" },
    "lomda.scene-block.type.numbered-steps": {
        he: "שלבים",
        en: "Steps",
    },
    "lomda.scene-block.type.outline-cards": { he: "כרטיסים", en: "Cards" },
    "lomda.scene-block.type.image-grid": { he: "תמונות", en: "Images" },
    "lomda.scene-block.reorder": { he: "סדר", en: "Order" },
    "lomda.scene-block.drag-handle": {
        he: "גרירה לשינוי הסדר",
        en: "Drag to reorder",
    },
    "lomda.scene-block.untitled": { he: "ללא כותרת", en: "Untitled" },
    "lomda.scene-color.brand.label": { he: "צבעי מיתוג", en: "Brand colors" },
    "lomda.scene-color.primary.label": { he: "צבע ראשי", en: "Primary color" },
    "lomda.scene-color.primary.title": { he: "ראשי", en: "Primary" },
    "lomda.scene-color.secondary.label": {
        he: "צבע משני",
        en: "Secondary color",
    },
    "lomda.scene-color.secondary.title": { he: "משני", en: "Secondary" },
    "lomda.scene-edit.content.label": { he: "תוכן הסצנה", en: "Scene content" },
    "lomda.scene-edit.content.placeholder": {
        he: "הזינו תוכן לסצנה",
        en: "Enter scene content",
    },
    "lomda.scene-edit.duration.label": {
        he: "משך זמן (בשניות)",
        en: "Duration (seconds)",
    },
    "lomda.scene-edit.script.label": { he: "תסריט", en: "Script" },
    "lomda.scene-edit.script.placeholder": {
        he: "הזן תסריט קריינות",
        en: "Enter voiceover script",
    },
    "lomda.scene-edit.script.chars": { he: "תווים", en: "chars" },
    "lomda.scene-edit.script.duration": {
        he: "~{{value}}",
        en: "~{{value}}",
    },
    "lomda.scene-edit.tts.title": {
        he: "יצירת קריינות לסצנה",
        en: "Generate scene narration",
    },
    "lomda.scene-edit.tts.subtitle": {
        he: "הוסף הנחיות לטון, לקצב ולסגנון של הקריין.",
        en: "Add hints for tone, pacing and style for the AI narrator.",
    },
    "lomda.scene-edit.tts.instructions": {
        he: "הנחיות לקריין",
        en: "Narrator instructions",
    },
    "lomda.scene-edit.tts.instructions-placeholder": {
        he: "לדוגמה: לדבר ברוגע ובאמינות, להאט במונחים מקצועיים…",
        en: "e.g. Speak with calm authority, slow down on technical terms…",
    },
    "lomda.scene-edit.tts.apply-all": {
        he: "החל על כל הסצנות",
        en: "Apply to all scenes",
    },
    "lomda.scene-edit.tts.apply-all-applied": {
        he: "ההנחיות יחולו על כל הסצנות בלומדה",
        en: "Instructions will apply to all scenes in this lomda",
    },
    "lomda.scene-edit.tts.optional-hint": {
        he: "לא חובה ליצור עכשיו — אם תדלגו, הקריינות תופק אוטומטית בעת רינדור הלומדה.",
        en: "Optional — if you skip, narration will be generated automatically during render.",
    },
    "lomda.scene-edit.tts.nikud-hint": {
        he: "טיפ: אם המודל לא מבטא נכון מילה בעברית, הוספת ניקוד למילה הזו בתסריט תפתור את זה.",
        en: "Tip: if the model mispronounces a Hebrew word, adding ניקוד (vowel points) to that word in the script will fix it.",
    },
    "lomda.scene-edit.tts.generate": {
        he: "יצירת אודיו",
        en: "Generate audio",
    },
    "lomda.scene-edit.tts.regenerate": { he: "יצירה מחדש", en: "Regenerate" },
    "lomda.scene-edit.tts.generating": { he: "מייצר…", en: "Generating…" },
    "lomda.scene-edit.tts.generated": {
        he: "האודיו נוצר",
        en: "Audio generated",
    },
    "lomda.scene-edit.tts.failed": {
        he: "יצירת האודיו נכשלה",
        en: "Failed to generate audio",
    },
    "lomda.scene-edit.tts.not-eligible": {
        he: "אין מספיק קרדיטים ליצירת אודיו",
        en: "Not enough credits to generate audio",
    },
    "lomda.scene-edit.tts.locked-during-render": {
        he: "רינדור בעיצומו — המתינו לסיום לפני יצירת אודיו לסצנה",
        en: "Render in progress — wait for it to finish before generating scene audio",
    },
    "lomda.scene-edit.tts.status.missing": { he: "חסר", en: "Missing" },
    "lomda.scene-edit.tts.status.generating": {
        he: "מייצר…",
        en: "Generating…",
    },
    "lomda.scene-edit.tts.status.outdated": {
        he: "לא מעודכן",
        en: "Outdated",
    },
    "lomda.scene-edit.tts.status.ready": { he: "מוכן", en: "Ready" },
    "lomda.scene-edit.tts.status.failed": { he: "נכשל", en: "Failed" },
    "lomda.scene-edit.tts.status.outdated.tooltip": {
        he: "התסריט או ההנחיות שונו מאז יצירת האודיו האחרונה.",
        en: "Script or instructions changed since last generation.",
    },
    "lomda.scene-edit.title": { he: "עריכת סצנה", en: "Edit scene" },
    "lomda.scene-image.description": {
        he: "תמונת שקופית לסצנה",
        en: "Slide image for the scene",
    },
    "lomda.scene-image.label": { he: "תמונת רקע", en: "Background image" },
    "lomda.scene-image.upload.subtitle": {
        he: "או לחצו לבחירה (JPG, PNG, WEBP)",
        en: "Or click to select (JPG, PNG, WEBP)",
    },
    "lomda.scene-image.upload.title": {
        he: "גררו תמונה לכאן",
        en: "Drag an image here",
    },
    "scene-image.subtitle": {
        he: "או לחצו לבחירה (JPG, PNG, WEBP)",
        en: "Or click to select (JPG, PNG, WEBP)",
    },
    "scene-image.title": { he: "גררו תמונה לכאן", en: "Drag an image here" },
    "lomda.scene-manager.ai-actions": { he: "פעולות AI", en: "AI actions" },
    "lomda.scene-manager.audio.refreshing": {
        he: "מרענן דיבוב...",
        en: "Refreshing dubbing...",
    },
    "lomda.scene-manager.script.improve": {
        he: "שפר תסריט",
        en: "Improve script",
    },
    "lomda.scene-manager.script.improving": {
        he: "משפר תסריט...",
        en: "Improving script...",
    },
    "lomda.scene-manager.tab.script": { he: "תסריט", en: "Script" },
    "lomda.scene-manager.tab.settings": { he: "הגדרות", en: "Settings" },
    "lomda.scene-not-selected.subtitle": {
        he: "יש לבחור סצנה כדי לערוך את התסריט",
        en: "Select a scene to edit the script",
    },
    "lomda.scene-not-selected.title": {
        he: "לא נבחרה סצנה",
        en: "No scene selected",
    },
    "lomda.scene-template.label": { he: "תבנית סצנה", en: "Scene template" },
    "lomda.scene-template.placeholder": {
        he: "בחרו תבנית",
        en: "Choose a template",
    },
    "lomda.scene-template.description": {
        he: "תבנית המגדירה את מראה הסצנה",
        en: "Template that defines the scene look",
    },
    "lomda.scene-template.A": {
        he: "טקסט ממורכז בלבד",
        en: "Centered text only",
    },
    "lomda.scene-template.B": {
        he: "טקסט ממורכז + אווטאר ימין למטה",
        en: "Centered text + avatar bottom-right",
    },
    "lomda.scene-template.C": {
        he: "טקסט משמאל + מדיה מימין",
        en: "Text left + media right",
    },
    "lomda.scene-template.D": {
        he: "טקסט מיושר לשמאל בלבד",
        en: "Left-aligned text only",
    },
    "lomda.scene-template.E": {
        he: "טקסט משמאל + אווטאר ימין למטה",
        en: "Left-aligned text + avatar bottom-right",
    },
    "lomda.media-toolbar.aria": {
        he: "עיצוב תמונה",
        en: "Image formatting",
    },
    "lomda.media-toolbar.replace": {
        he: "החלף מדיה",
        en: "Replace media",
    },
    "lomda.media-toolbar.fit.contain": {
        he: "הצגת התמונה במלואה ללא חיתוך",
        en: "Show whole image, no cropping",
    },
    "lomda.media-toolbar.fit.cover": {
        he: "מילוי המסגרת (יתכן חיתוך)",
        en: "Fill slot (may crop)",
    },
    "lomda.media-toolbar.radius.label": {
        he: "פינות מעוגלות",
        en: "Corner radius",
    },
    "lomda.media-toolbar.radius.none": { he: "ללא", en: "None" },
    "lomda.media-toolbar.radius.sm": { he: "קטן", en: "Small" },
    "lomda.media-toolbar.radius.md": { he: "בינוני", en: "Medium" },
    "lomda.media-toolbar.radius.lg": { he: "גדול", en: "Large" },
    "lomda.media-toolbar.radius.xl": { he: "ענק", en: "Extra large" },
    "lomda.media-toolbar.column-ratio.label": {
        he: "רוחב",
        en: "Width",
    },
    "lomda.media-toolbar.column-ratio.reset": {
        he: "איפוס ל-{percent}%",
        en: "Reset to {percent}%",
    },
    "lomda.media-toolbar.fit.label": {
        he: "יחס רוחב/גובה",
        en: "Aspect ratio",
    },
    "lomda.media-toolbar.fit.contain-label": {
        he: "מקורי",
        en: "Original",
    },
    "lomda.media-toolbar.fit.cover-label": {
        he: "מילוי המסגרת",
        en: "Fill the frame",
    },
    "lomda.media-toolbar.shadow.label": { he: "צל", en: "Shadow" },
    "lomda.media-toolbar.shadow.none": { he: "ללא", en: "None" },
    "lomda.media-toolbar.shadow.sm": { he: "קל", en: "Small" },
    "lomda.media-toolbar.shadow.md": { he: "בינוני", en: "Medium" },
    "lomda.media-toolbar.shadow.lg": { he: "חזק", en: "Large" },
    "lomda.media-toolbar.svg-color.label": { he: "צבע", en: "Color" },
    "lomda.media-toolbar.svg-color.load-failed": {
        he: "טעינת ה-SVG נכשלה",
        en: "Failed to load the SVG",
    },
    "lomda.element-toolbar.media-aria": {
        he: "עיצוב מדיה",
        en: "Media formatting",
    },
    "lomda.element-toolbar.text-aria": {
        he: "עיצוב טקסט",
        en: "Text formatting",
    },
    "lomda.element-toolbar.avatar-aria": {
        he: "עיצוב אווטאר",
        en: "Avatar formatting",
    },
    "lomda.element-toolbar.layer": { he: "שכבה", en: "Layer" },
    "lomda.element-toolbar.bring-to-front": {
        he: "הבא קדימה",
        en: "Bring forward",
    },
    "lomda.element-toolbar.bring-forward": {
        he: "העבר קדימה",
        en: "Bring forward",
    },
    "lomda.element-toolbar.send-backward": {
        he: "העבר אחורה",
        en: "Send backward",
    },
    "lomda.element-toolbar.send-to-back": {
        he: "שלח אחורה",
        en: "Send backward",
    },
    "lomda.element-toolbar.delete": { he: "מחק", en: "Delete" },
    "lomda.element-toolbar.add-text": {
        he: "הוספת בלוק טקסט",
        en: "Add text block",
    },
    "lomda.text-block.default-label": { he: "תווית", en: "Label" },
    "lomda.text-block.default-heading": { he: "כותרת", en: "Heading" },
    "lomda.text-block.default-body": {
        he: "טקסט גוף לדוגמה. לחצו כדי לערוך.",
        en: "Body text goes here. Click to edit.",
    },
    "lomda.element-toolbar.edit-text": { he: "ערוך טקסט", en: "Edit text" },
    "lomda.element-toolbar.valign-top": {
        he: "יישור למעלה",
        en: "Align top",
    },
    "lomda.element-toolbar.valign-center": {
        he: "יישור למרכז",
        en: "Align middle",
    },
    "lomda.element-toolbar.valign-bottom": {
        he: "יישור למטה",
        en: "Align bottom",
    },
    "lomda.element-toolbar.replace-media": {
        he: "החלף מדיה",
        en: "Replace media",
    },
    "lomda.element-toolbar.fit": { he: "התאמה", en: "Fit" },
    "lomda.element-toolbar.radius": { he: "פינות", en: "Corners" },
    "lomda.element-toolbar.opacity": { he: "שקיפות", en: "Opacity" },
    "lomda.element-toolbar.frame": { he: "מסגרת", en: "Frame" },
    "lomda.element-toolbar.apply-to-all": {
        he: "החל על כל הסצנות",
        en: "Apply to all scenes",
    },
    "lomda.layout.text-center": { he: "טקסט במרכז", en: "Centered text" },
    "lomda.layout.text-top": { he: "טקסט למעלה", en: "Text at top" },
    "lomda.layout.text-media": { he: "טקסט ומדיה", en: "Text & media" },
    "lomda.layout.title": { he: "כותרת", en: "Title" },
    "lomda.layout.full-text": { he: "טקסט מלא", en: "Full-width text" },
    "lomda.layout.media-top": {
        he: "מדיה למעלה, טקסט למטה",
        en: "Media top, text below",
    },
    "lomda.layout.split-left": {
        he: "טקסט ומדיה",
        en: "Text & media",
    },
    "lomda.layout.split-right": {
        he: "מדיה וטקסט",
        en: "Media & text",
    },
    "lomda.layout.media-full": {
        he: "מדיה מלאה",
        en: "Full media",
    },
    "lomda.layout.text-top-media-bottom": {
        he: "טקסט למעלה, מדיה למטה",
        en: "Text top, media below",
    },
    "lomda.layout.avatar-right": {
        he: "אווטאר וטקסט",
        en: "Avatar & text",
    },
    "lomda.navbar.undo": { he: "בטל", en: "Undo" },
    "lomda.navbar.redo": { he: "בצע שוב", en: "Redo" },
    "lomda.canvas.add-text": { he: "טקסט", en: "Text" },
    "lomda.canvas.add-media": { he: "מדיה", en: "Media" },
    "lomda.canvas.add-avatar": { he: "אווטאר", en: "Avatar" },
    "lomda.avatar-toolbar.aria": {
        he: "עיצוב אווטאר",
        en: "Avatar formatting",
    },
    "lomda.avatar-toolbar.size": { he: "גודל", en: "Size" },
    "lomda.avatar-toolbar.size.label": {
        he: "רוחב",
        en: "Width",
    },
    "lomda.avatar-toolbar.radius": { he: "פינות", en: "Radius" },
    "lomda.avatar-toolbar.radius.label": {
        he: "רדיוס פינות",
        en: "Corner radius",
    },
    "lomda.avatar-toolbar.full-screen": { he: "מסך מלא", en: "Full screen" },
    "lomda.avatar-toolbar.exit-full-screen": {
        he: "ביטול מסך מלא",
        en: "Exit full screen",
    },
    "lomda.avatar-toolbar.frame": { he: "מסגרת", en: "Frame" },
    "lomda.avatar-toolbar.frame.ring-width": {
        he: "עובי מסגרת",
        en: "Ring width",
    },
    "lomda.avatar-toolbar.frame.ring-color": {
        he: "צבע מסגרת",
        en: "Ring color",
    },
    "lomda.avatar-toolbar.align": { he: "מיקום", en: "Align" },
    "lomda.avatar-toolbar.align.top-left": {
        he: "שמאל למעלה",
        en: "Top left",
    },
    "lomda.avatar-toolbar.align.top-center": {
        he: "מרכז למעלה",
        en: "Top center",
    },
    "lomda.avatar-toolbar.align.top-right": {
        he: "ימין למעלה",
        en: "Top right",
    },
    "lomda.avatar-toolbar.align.middle-left": {
        he: "שמאל באמצע",
        en: "Middle left",
    },
    "lomda.avatar-toolbar.align.middle-center": {
        he: "מרכז",
        en: "Middle center",
    },
    "lomda.avatar-toolbar.align.middle-right": {
        he: "ימין באמצע",
        en: "Middle right",
    },
    "lomda.avatar-toolbar.align.bottom-left": {
        he: "שמאל למטה",
        en: "Bottom left",
    },
    "lomda.avatar-toolbar.align.bottom-center": {
        he: "מרכז למטה",
        en: "Bottom center",
    },
    "lomda.avatar-toolbar.align.bottom-right": {
        he: "ימין למטה",
        en: "Bottom right",
    },
    "lomda.avatar-toolbar.apply-to-all": {
        he: "החל על כל הסצנות",
        en: "Apply to all scenes",
    },
    "lomda.avatar-toolbar.apply-to-all.tooltip": {
        he: "החל את הגדרות האווטאר על כל שאר הסצנות",
        en: "Apply this avatar config to all other scenes",
    },
    "lomda.avatar-toolbar.remove": { he: "הסר", en: "Remove" },
    "lomda.scenes.duplicate": { he: "שכפול", en: "Duplicate" },
    "lomda.chapter.placeholder": { he: "פרק {number}", en: "Chapter {number}" },
    "lomda.chapter.title-placeholder": {
        he: "כותרת פרק",
        en: "Chapter title",
    },
    "lomda.chapter.rename": { he: "שינוי שם", en: "Rename chapter" },
    "lomda.chapter.merge-with-previous": {
        he: "מיזוג עם הפרק הקודם",
        en: "Merge with previous chapter",
    },
    "lomda.chapter.split-here": {
        he: "פיצול לפרק חדש",
        en: "Start a new chapter here",
    },
    "lomda.chapter-step.scene-placeholder": {
        he: "סצנה {number}",
        en: "Scene {number}",
    },
    "lomda.chapter-step.split-here": {
        he: "פתיחת פרק חדש מסצנה זו",
        en: "Start a new chapter from this scene",
    },
    "lomda.chapter-step.scene-count": {
        he: "{count} סצנות",
        en: "{count} scenes",
    },
    "lomda.chapter-step.empty-state": {
        he: "אין סצנות בלומדה עדיין.",
        en: "No scenes in this lomda yet.",
    },
    "lomda.chapter-step.action.move-up": {
        he: "מעבר לסצנה שמעל",
        en: "Move to chapter above",
    },
    "lomda.chapter-step.action.move-down": {
        he: "מעבר לסצנה שמתחת",
        en: "Move to chapter below",
    },
    "lomda.chapter-step.action.isolate": {
        he: "פתיחת פרק חדש לסצנה זו",
        en: "Move into its own chapter",
    },
    "lomda.scenes.add-interaction": {
        he: "הוספת אינטראקציה",
        en: "Add interaction",
    },
    "lomda.scene-voice.description": {
        he: "הקול שיקריא את תסריט הסצנה",
        en: "The voice that reads the scene script",
    },
    "lomda.scene-voice.label": { he: "קול סצנה", en: "Scene voice" },
    "lomda.scene-voice.placeholder": {
        he: "בחרו קול לסצנה",
        en: "Choose a scene voice",
    },
    "lomda.scene.add": { he: "הוספת סצנה", en: "Add scene" },
    "lomda.scene.label": { he: "סצנה {number}", en: "Scene {number}" },
    "lomda.scenes.delete.description": {
        he: "האם אתה בטוח שברצונך למחוק את הסצנה הזו? הפעולה הזו אינה ניתנת לביטול.",
        en: "Are you sure you want to delete this scene? This action cannot be undone.",
    },
    "lomda.scenes.delete.title": { he: "מחק סצנה", en: "Delete scene" },
    "lomda.scenes.empty.subtitle": {
        he: "הוספת שקופית כדי להתחיל",
        en: "Add a slide to get started",
    },
    "lomda.scenes.empty.title": {
        he: "אין שקופיות...",
        en: "No slides yet...",
    },
    "lomda.sidebar.settings.aspect-ratio.label": {
        he: "יחס גובה-רוחב",
        en: "Aspect ratio",
    },
    "lomda.sidebar.settings.aspect-ratio.placeholder": {
        he: "בחר יחס גובה-רוחב",
        en: "Select aspect ratio",
    },
    "lomda.sidebar.settings.colors.label": { he: "צבעים", en: "Colors" },
    "lomda.sidebar.settings.colors.primary": { he: "ראשי", en: "Primary" },
    "lomda.sidebar.settings.colors.secondary": { he: "משני", en: "Secondary" },
    "lomda.sidebar.settings.language.label": { he: "שפה", en: "Language" },
    "lomda.sidebar.settings.language.placeholder": {
        he: "בחר שפה",
        en: "Select language",
    },
    "lomda.sidebar.settings.logo.label": { he: "לוגו", en: "Logo" },
    "lomda.sidebar.settings.script-length.label": {
        he: "אורך תסריט",
        en: "Script length",
    },
    "lomda.sidebar.settings.script-length.placeholder": {
        he: "בחר אורך תסריט",
        en: "Select script length",
    },
    "lomda.sidebar.settings.voice-tone.label": {
        he: "טון דיבור",
        en: "Voice tone",
    },
    "lomda.sidebar.settings.voice-tone.placeholder": {
        he: "בחר טון דיבור",
        en: "Select voice tone",
    },
    "lomda.aspect-ratio.16:9": { he: "16:9 (רחב)", en: "16:9 (Widescreen)" },
    "lomda.aspect-ratio.4:3": { he: "4:3 (מסורתי)", en: "4:3 (Standard)" },
    "lomda.aspect-ratio.1:1": { he: "1:1 (ריבוע)", en: "1:1 (Square)" },
    "lomda.aspect-ratio.1:1.414": { he: "1:1.414 (A4)", en: "1:1.414 (A4)" },
    "lomda.avatar": { he: "אווטאר", en: "Avatar" },
    "lomda.scene": { he: "סצנה", en: "Scene" },
    "lomda.script-length.asis": { he: "ללא שינוי", en: "No change" },
    "lomda.script-length.indepth": { he: "עמוק", en: "In depth" },
    "lomda.script-length.summary": { he: "סיכום", en: "Summary" },
    "lomda.voice-tone.educational": {
        he: "חינוכי ומעודד למידה",
        en: "Educational and encouraging",
    },
    "lomda.voice-tone.informative": {
        he: "הסברתי ומקצועי",
        en: "Informative and professional",
    },
    "lomda.voice-tone.conversational": {
        he: "שיחתי וחברי",
        en: "Conversational and friendly",
    },
    "lomda.voice-tone.authoritative": {
        he: "סמכותי ומקצועי",
        en: "Authoritative and expert",
    },
    "lomda.voice-tone.motivational": {
        he: "מוטיבציוני ומעורר השראה",
        en: "Motivational and inspiring",
    },
    "lomda.voice-tone.narrative": {
        he: "סיפורי ומושך",
        en: "Narrative and engaging",
    },
    "lomda.voice-tone.promotional": {
        he: "שיווקי ומעורר עניין",
        en: "Promotional and engaging",
    },
    "payments.card-fields.company-id": { he: 'ח"פ', en: "Company ID" },
    "payments.card-fields.cvv": { he: "3 ספרות", en: "CVV" },
    "payments.card-fields.expiry": { he: "תוקף", en: "Expiry" },
    "payments.card-fields.invoice-name": {
        he: "שם על החשבונית",
        en: "Invoice name",
    },
    "payments.card-fields.number": { he: "מספר כרטיס", en: "Card number" },
    "payments.change-plan.description": {
        he: "המנוי הישן יבוטל והמנוי החדש יכנס לתוקף מייד.",
        en: "Your old plan will be canceled and the new plan will take effect immediately.",
    },
    "payments.change-plan.title": { he: "שינוי מנוי", en: "Change plan" },
    "payments.footer.privacy.link": {
        he: "תנאי השימוש ומדיניות הפרטיות בפלטפורמה",
        en: "Terms of use and privacy policy",
    },
    "payments.footer.privacy.middle": {
        he: "את/ה מאשר/ת את",
        en: "You agree to the",
    },
    "payments.footer.privacy.prefix": {
        he: 'אנחנו מכבדים את פרטיותך. בלחיצה על "תשלום"',
        en: 'We respect your privacy. By clicking "Make payment"',
    },
    "payments.footer.privacy.suffix": { he: ".", en: "." },
    "payments.payment-form.needs-invoice": {
        he: "צריך חשבונית?",
        en: "Need an invoice?",
    },
    "payments.payment-form.installments": {
        he: "תשלומים",
        en: "Installments",
    },
    "payments.payment-form.split-installments": {
        he: "לפרוס לתשלומים?",
        en: "Split into installments?",
    },
    "payments.payment-form.single-payment": {
        he: "תשלום אחד",
        en: "Single payment",
    },
    "payments.payment-form.installments-option": {
        he: "{count} תשלומים",
        en: "{count} payments",
    },
    "payments.payment-form.submit": {
        he: "להצטרפות - {price}₪ {period}",
        en: "Join - ₪{price} {period}",
    },
    "payments.payment-wall.cancel-note": {
        he: "אפשר לבטל את המנוי בכל זמן",
        en: "You can cancel your subscription at any time",
    },
    "payments.payment-wall.cta": { he: "להוספת יתרה", en: "Add funds" },
    "payments.payment-wall.description": {
        he: "נגמרה לכם היתרה. הוסיפו יתרה לחשבון כדי להמשיך להשתמש בכל הכלים של ידע:",
        en: "Your balance has run out. Add funds to your account to keep using all of Yeda's tools:",
    },
    "payments.payment-wall.title": {
        he: "נגמרה היתרה",
        en: "Top up your account",
    },
    "payments.payment-wall.features.ai.title": {
        he: "יצירת תוכן עם AI",
        en: "AI content creation",
    },
    "payments.payment-wall.features.ai.description": {
        he: "מצגות, סילבוסים, לומדות ועוד. נוצרים לכם אוטומטית",
        en: "Presentations, syllabi, learning modules & more — generated automatically",
    },
    "payments.payment-wall.features.storage.title": {
        he: "אחסון והפצה",
        en: "Storage & delivery",
    },
    "payments.payment-wall.features.storage.description": {
        he: "כל הסרטונים שלכם במקום אחד. העלאה, ניהול ושיתוף בקליק",
        en: "Upload, manage, and share all your video content from one place",
    },
    "payments.payment-wall.features.interactions.title": {
        he: "אינטראקציה בתוך הסרטון",
        en: "Video interactions",
    },
    "payments.payment-wall.features.interactions.description": {
        he: "שאלות, צ׳אטבוט, חלוקה לפרקים ותרגום. הכל בנוי בפנים",
        en: "Questions, chatbot, chapters & translation — all built into the video",
    },
    "payments.security-note": {
        he: "100% מהעסקאות מאובטחות ומוצפנות בתקן PCI",
        en: "100% of transactions are secured and encrypted to PCI standards",
    },
    "payments.summary.discount": {
        he: "{percentage}% הנחה",
        en: "{percentage}% discount",
    },
    "payments.summary.plan": {
        he: "מנוי {planName} {frequency}",
        en: "Plan {planName} {frequency}",
    },
    "payments.summary.subtotal": { he: "סכום ביניים", en: "Subtotal" },
    "payments.summary.total": { he: 'סה"כ לתשלום', en: "Total due" },
    "payments.summary.vat": {
        he: '{percentage}% מע"מ',
        en: "{percentage}% VAT",
    },
    "payments.summary.frequency.yearly": { he: "שנתי", en: "Yearly" },
    "payments.summary.frequency.monthly": { he: "חודשי", en: "Monthly" },
    "recorder.footer.resolution-hint": {
        he: "רזולוציית הסרטון תהיה לפחות {resolution}.\n לשיפור הרזולוציה עשו zoom out בדפדפן.",
        en: "The video resolution will be at least {resolution}.\nTo improve the resolution, zoom out in your browser.",
    },
    "recorder.sidebar.animations.picker.label": {
        he: "אנימציה",
        en: "Animation",
    },
    "recorder.sidebar.settings.aspect-ratio.placeholder": {
        he: "בחרו יחס רוחב/גובה",
        en: "Select aspect ratio",
    },
    "recorder.sidebar.settings.bg.images.remove": {
        he: "הסרת תמונת רקע {name}",
        en: "Remove background image {name}",
    },
    "recorder.sidebar.settings.header.description": {
        he: "שנו הגדרות בסיסיות במצגת",
        en: "Adjust basic presentation settings",
    },
    "recorder.sidebar.settings.header.title": { he: "הגדרות", en: "Settings" },
    "recorder.sidebar.settings.webcam-resolution.placeholder": {
        he: "בחרו רזולוציית מצלמה",
        en: "Select webcam resolution",
    },
    "recorder.webcam.resolution.label": {
        he: "רזולוציית מצלמה",
        en: "Camera resolution",
    },
    "recorder.webcam.resolution.placeholder": {
        he: "בחרו רזולוציית מצלמה",
        en: "Select camera resolution",
    },
    "recorder.sidebar.slides.actions.modal.sharescreen.label": {
        he: "שקופית שיתוף מסך",
        en: "Screen share slide",
    },
    "recorder.template-settings.edit-title": {
        he: "עריכת טמפלייט #{id}",
        en: "Edit template #{id}",
    },
    "recorder.template-settings.new-title": {
        he: "יצירת טמפלייט חדש",
        en: "Create new template",
    },
    "recorder.template-settings.description": {
        he: "עדכנו את פרטי הטמפלייט",
        en: "Update the template details",
    },
    "recorder.template-settings.thumbnail.error": {
        he: "יצירת התמונה הממוזערת נכשלה",
        en: "Failed to generate thumbnail",
    },
    "recorder.template-settings.error.missing-fields": {
        he: "חסרים שדות חובה",
        en: "Required fields are missing",
    },
    "recorder.template-settings.error.missing-slide-id": {
        he: "לא נבחרה שקופית",
        en: "No slide selected",
    },
    "recorder.template-settings.category": { he: "קטגוריה", en: "Category" },
    "recorder.template-settings.thumbnail": {
        he: "תמונה ממוזערת",
        en: "Thumbnail",
    },
    "recorder.template-settings.webcam-position.hint": {
        he: "שינוי דרך הגדרות השקופית",
        en: "Change from slide settings",
    },
    "recorder.template-settings.webcam-position.label": {
        he: "מיקום מצלמה",
        en: "Webcam position",
    },
    "recorder.template-settings.webcam-position.not-set": {
        he: "לא הוגדר",
        en: "Not set",
    },
    "common.search.placeholder": { he: "חיפוש...", en: "Search..." },
    "common.create": { he: "יצירה", en: "Create" },
    "common.no-more-options": {
        he: "אין עוד אפשרויות.",
        en: "No more options.",
    },
    "analytics.tabs.storage": { he: "אחסון", en: "Storage" },
    "analytics.tabs.chatbots": { he: "סוכני AI", en: "AI agents" },
    "analytics.tabs.insights": { he: "תובנות", en: "Insights" },
    "analytics.chatbots.title": {
        he: "אנליטיקות סוכני AI",
        en: "AI agent analytics",
    },
    "analytics.chatbots.activity.title": { he: "פעילות", en: "Activity" },
    "analytics.chatbots.activity.description": {
        he: "צפו בפעילות העוזרים האישיים בתקופה שנבחרה",
        en: "See assistant activity in the selected period",
    },
    "analytics.chatbots.activity.users": { he: "משתמשים", en: "Users" },
    "analytics.chatbots.activity.messages": { he: "הודעות", en: "Messages" },
    "analytics.chatbots.activity.chats": { he: "שיחות", en: "Chats" },
    "analytics.chatbots.activity.engagement": {
        he: "אחוז מעורבות",
        en: "Engagement rate",
    },
    "analytics.chatbots.activity.total-users": {
        he: 'סה"כ משתמשים',
        en: "Total users",
    },
    "analytics.chatbots.activity.total-messages": {
        he: 'סה"כ הודעות',
        en: "Total messages",
    },
    "analytics.chatbots.stats.hours-saved": {
        he: "שעות נחסכו",
        en: "Hours saved",
    },
    "analytics.chatbots.stats.hours-saved.description": {
        he: "מחושב לפי כמות הבעיות שנפתרו בתקופה שנבחרה, כפול 5 דקות לבעיה",
        en: "Calculated from resolved issues in the period, times 5 minutes per issue",
    },
    "analytics.chatbots.stats.engagement-rate": {
        he: "אחוז מעורבות",
        en: "Engagement rate",
    },
    "analytics.chatbots.stats.engagement-rate.description": {
        he: "אחוז המשתמשים שדיברו עם העוזר האישי בתקופה שנבחרה",
        en: "Percentage of users who spoke with the assistant in the selected period",
    },
    "analytics.chatbots.stats.daily-messages": {
        he: "ממוצע הודעות יומי",
        en: "Avg daily messages",
    },
    "analytics.chatbots.stats.daily-messages.description": {
        he: "מחושב לפי הודעות משתמשים ללא הודעות של סוכן AI",
        en: "Calculated from user messages excluding AI messages",
    },
    "analytics.chatbots.stats.reference-clicks": {
        he: "הקלקות על רפרנסים",
        en: "Reference clicks",
    },
    "analytics.chatbots.stats.reference-clicks.description": {
        he: "הקלקות על רפרנסים בתשובות שנותן העוזר האישי",
        en: "Clicks on references in assistant answers",
    },
    "analytics.chatbots.languages.title": { he: "שפות", en: "Languages" },
    "analytics.chatbots.languages.description": {
        he: "גלו באיזה שפות מדברים עם הסוכני AI",
        en: "See which languages people use with AI agents",
    },
    "analytics.chatbots.languages.other": { he: "אחר", en: "Other" },
    "analytics.chatbots.flow.title": {
        he: "מסלול שיפור הבנה",
        en: "Understanding improvement flow",
    },
    "analytics.chatbots.flow.description": {
        he: "בקרוב: צפייה בסטטיסטיקות מסלול שיפור ההבנה",
        en: "Coming soon: understanding flow statistics",
    },
    "analytics.chatbots.flow.resolved-issues": {
        he: "בעיות נפתרו",
        en: "Issues resolved",
    },
    "analytics.chatbots.flow.avg-understanding-time": {
        he: "ממוצע זמן הבנה",
        en: "Avg understanding time",
    },
    "analytics.chatbots.flow.step.verify-understanding": {
        he: "מתבצע וידוא הבנה",
        en: "Understanding verified",
    },
    "analytics.chatbots.flow.step.view-references": {
        he: "צופה ברפרנסים",
        en: "Views references",
    },
    "analytics.chatbots.flow.step.receive-ai-answer": {
        he: "מקבל תשובה מ-AI",
        en: "Receives AI answer",
    },
    "analytics.chatbots.flow.step.user-asks": {
        he: "משתמש שואל שאלה",
        en: "User asks a question",
    },
    "analytics.chatbots.questions.title": { he: "סוכני AI", en: "AI agents" },
    "analytics.chatbots.questions.description": {
        he: "השוואה של כמות השאלות בלי תשובה לכמות השאלות עם תשובה",
        en: "Comparison of unanswered vs answered questions",
    },
    "analytics.chatbots.questions.with-answer": {
        he: "שאלות עם תשובה",
        en: "Answered questions",
    },
    "analytics.chatbots.questions.no-answer": {
        he: "שאלות ללא תשובה",
        en: "Unanswered questions",
    },
    "analytics.chatbots.filters.chatbot.label": {
        he: "סוכן AI: {name}",
        en: "AI agent: {name}",
    },
    "analytics.chatbots.filters.chatbot.search-placeholder": {
        he: "חיפוש סוכן AI",
        en: "Search AI agent",
    },
    "analytics.chatbots.filters.user.label": {
        he: "משתמש: {email}",
        en: "User: {email}",
    },
    "analytics.chatbots.filters.user.search-placeholder": {
        he: "חיפוש מייל",
        en: "Search email",
    },
    "analytics.chatbots.faqs.title": { he: "שאלות נפוצות", en: "FAQs" },
    "analytics.chatbots.faqs.description": {
        he: "גלו מה המשתמשים שואלים",
        en: "See what users ask",
    },
    "analytics.chatbots.faqs.empty.title": {
        he: "בחרו סוכן AI",
        en: "Choose an AI agent",
    },
    "analytics.chatbots.faqs.empty.description": {
        he: "כדי להציג שאלות נפוצות יש לבחור סוכן AI",
        en: "Select an AI agent to view FAQs",
    },
    "analytics.chatbots.faqs.columns.question": { he: "שאלה", en: "Question" },
    "analytics.chatbots.faqs.columns.chats": { he: "שיחות", en: "Chats" },
    "analytics.chatbots.faqs.columns.chatbot": {
        he: "סוכן AI",
        en: "AI agent",
    },
    "analytics.chatbots.faqs.panel.chat-count": {
        he: "שיחה {current} מתוך {total}:",
        en: "Chat {current} of {total}:",
    },
    "analytics.chatbots.faqs.panel.user": { he: "משתמש:", en: "User:" },
    "analytics.chatbots.faqs.panel.chat": { he: "שיחה:", en: "Chat:" },
    "analytics.chatbots.faqs.panel.date": { he: "תאריך:", en: "Date:" },
    "analytics.chatbots.faqs.panel.prev": {
        he: "שיחה קודמת",
        en: "Previous chat",
    },
    "analytics.chatbots.faqs.panel.next": { he: "שיחה הבאה", en: "Next chat" },
    "analytics.insights.title": {
        he: "תובנות - בקרוב",
        en: "Insights - coming soon",
    },
    "analytics.insights.quick.title": {
        he: "תובנות לשיפור",
        en: "Improvement insights",
    },
    "analytics.insights.quick.ai-badge": {
        he: "נוצר בעזרת AI",
        en: "Generated with AI",
    },
    "analytics.insights.quick.unanswered": {
        he: "העוזרים האישיים נתקלו ב-{count} שאלות ללא תשובה.",
        en: "Assistants encountered {count} unanswered questions.",
    },
    "analytics.insights.quick.low-rating": {
        he: "ל-{count} סרטונים יש דירוג נמוך מ-{threshold} כוכבים.",
        en: "{count} videos have a rating below {threshold} stars.",
    },
    "analytics.insights.quick.low-completion": {
        he: "ל-{count} סרטונים יש אחוז סיום נמוך מ-{threshold}%.",
        en: "{count} videos have a completion rate below {threshold}%.",
    },
    "analytics.insights.quick.low-success": {
        he: "ל-{count} אינטראקציות יש אחוז הצלחה נמוך מ-{threshold}%.",
        en: "{count} interactions have a success rate below {threshold}%.",
    },
    "analytics.insights.q-without-a.example-question": {
        he: "איך מתקינים תוכנת פריוריטי?",
        en: "How do I install Priority software?",
    },
    "analytics.insights.q-without-a.title": {
        he: "שאלה ללא תשובה",
        en: "Unanswered question",
    },
    "analytics.insights.q-without-a.chatbot": {
        he: "סוכן AI: {name}",
        en: "AI agent: {name}",
    },
    "analytics.insights.q-without-a.user": {
        he: "משתמש: {email}",
        en: "User: {email}",
    },
    "analytics.insights.q-without-a.date": {
        he: "תאריך: {date}",
        en: "Date: {date}",
    },
    "analytics.insights.q-without-a.add": {
        he: "הוספת תשובה לידע",
        en: "Add answer to knowledge",
    },
    "analytics.insights.storage.title": {
        he: "תיקיות וסרטונים",
        en: "Folders and videos",
    },
    "analytics.insights.storage.pagination.results": {
        he: "מציג תוצאות {from}-{to} מתוך {total}",
        en: "Showing results {from}-{to} of {total}",
    },
    "analytics.insights.storage.columns.type": { he: "סוג", en: "Type" },
    "analytics.insights.storage.columns.name": { he: "שם", en: "Name" },
    "analytics.insights.storage.columns.rating": { he: "דירוג", en: "Rating" },
    "analytics.insights.storage.columns.completion": {
        he: "אחוז סיום",
        en: "Completion rate",
    },
    "analytics.insights.storage.columns.success": {
        he: "אחוז הצלחה",
        en: "Success rate",
    },
    "analytics.insights.storage.types.videos": { he: "סרטונים", en: "Videos" },
    "analytics.insights.storage.types.folders": { he: "תיקיות", en: "Folders" },
    "analytics.insights.storage.types.video": { he: "סרטון", en: "Video" },
    "analytics.insights.storage.types.folder": { he: "תיקייה", en: "Folder" },
    "analytics.insights.storage.sample.video": {
        he: "סרטון {index}",
        en: "Video {index}",
    },
    "analytics.storage.title": {
        he: "אנליטיקות אחסון",
        en: "Storage analytics",
    },
    "analytics.storage.stats.avg-rating": {
        he: "דירוג ממוצע",
        en: "Average rating",
    },
    "analytics.storage.stats.completion-rate": {
        he: "אחוז סיום",
        en: "Completion rate",
    },
    "analytics.storage.stats.completion-rate.description": {
        he: "האחוז הממוצע מאורך הסרטון שמשתמשים צופים",
        en: "Average percentage of the video watched",
    },
    "analytics.storage.stats.total-videos": {
        he: "כמות סרטונים",
        en: "Total videos",
    },
    "analytics.storage.stats.total-hours": {
        he: "כמות שעות",
        en: "Total hours",
    },
    "analytics.storage.videos.title": { he: "סרטונים", en: "Videos" },
    "analytics.storage.videos.description": {
        he: "כאן תוכלו לראות ביצועי הסרטונים",
        en: "Here you can see video performance",
    },
    "analytics.storage.videos.pagination.results": {
        he: "מציג תוצאות {from}-{to} מתוך {total}",
        en: "Showing results {from}-{to} of {total}",
    },
    "analytics.storage.videos.columns.name": { he: "שם", en: "Name" },
    "analytics.storage.videos.columns.rating": { he: "דירוג", en: "Rating" },
    "analytics.storage.videos.columns.completion": {
        he: "אחוז סיום",
        en: "Completion rate",
    },
    "analytics.storage.videos.columns.success": {
        he: "אחוז הצלחה",
        en: "Success rate",
    },
    "analytics.storage.interactions.title": {
        he: "אינטראקציות",
        en: "Interactions",
    },
    "analytics.storage.interactions.description": {
        he: "גלו איך הצופים השתתפו בסרטונים",
        en: "See how viewers interacted with the videos",
    },
    "analytics.storage.interactions.pagination.results": {
        he: "מציג תוצאות {from}-{to} מתוך {total}",
        en: "Showing results {from}-{to} of {total}",
    },
    "analytics.storage.interactions.columns.type": { he: "סוג", en: "Type" },
    "analytics.storage.interactions.columns.question": {
        he: "שאלה",
        en: "Question",
    },
    "analytics.storage.interactions.columns.results": {
        he: "תוצאות",
        en: "Results",
    },
    "analytics.storage.interactions.columns.participants": {
        he: "משתתפים",
        en: "Participants",
    },
    "analytics.storage.interactions.video-label": {
        he: "סרטון:",
        en: "Video:",
    },
    "analytics.storage.views.title": { he: "צפיות", en: "Views" },
    "analytics.storage.views.description": {
        he: "סך כל הצפיות לפי סוגי מכשירים",
        en: "Total views by device type",
    },
    "analytics.storage.views.total-views": {
        he: "סך כל הצפיות",
        en: "Total views",
    },
    "analytics.storage.views.unique-views": {
        he: "צפיות ייחודיות",
        en: "Unique views",
    },
    "analytics.storage.views.device.mobile": { he: "טלפון", en: "Mobile" },
    "analytics.storage.views.device.desktop": { he: "מחשב", en: "Desktop" },
    "analytics.storage.views.churn": { he: "נטישה", en: "Churn" },
    "analytics.storage.views.finished": { he: "סיום", en: "Finished" },
    "analytics.storage.views.rewatched": { he: "צפייה חוזרת", en: "Rewatched" },
    "analytics.storage.views-pie.title": {
        he: "נתוני צפייה",
        en: "Viewing metrics",
    },
    "analytics.storage.views-pie.description": {
        he: "אספנו עבורכם את התובנות הכי מעניינות",
        en: "We gathered the most interesting insights for you",
    },
    "analytics.storage.views-pie.avg-speed": {
        he: "מהירות צפייה ממוצעת",
        en: "Avg watch speed",
    },
    "analytics.storage.views-pie.avg-rewatch": {
        he: "ממוצע צפייה חוזרת",
        en: "Avg rewatch count",
    },
    "analytics.storage.filter-by.text.all": {
        he: "נתונים עבור: כל האחסון",
        en: "Data for: all storage",
    },
    "analytics.storage.filter-by.text.folder": {
        he: "נתונים עבור תיקייה: {name}",
        en: "Data for folder: {name}",
    },
    "analytics.storage.filter-by.text.video": {
        he: "נתונים עבור סרטון: {name}",
        en: "Data for video: {name}",
    },
    "analytics.storage.filter-by.title": { he: "סינון לפי", en: "Filter by" },
    "analytics.storage.filter-by.items.all": {
        he: "כל האחסון",
        en: "All storage",
    },
    "analytics.storage.filter-by.items.folder": { he: "תיקייה", en: "Folder" },
    "analytics.storage.filter-by.items.video": { he: "סרטון", en: "Video" },
    "analytics.storage.filter-by.folder.description": {
        he: "נווטו לתיקייה שתרצו לראות את האנליטיקות שלה ולחצו אישור.",
        en: "Navigate to the folder to view its analytics and click confirm.",
    },
    "analytics.storage.filter-by.video.description": {
        he: "בחרו סרטון שתרצו לראות את האנליטיקות שלו ולחצו אישור.",
        en: "Choose a video to view its analytics and click confirm.",
    },
    "analytics.storage.minimum-view.title": {
        he: "מינימום לצפייה",
        en: "Minimum view",
    },
    "analytics.storage.minimum-view.label": {
        he: "מינימום לצפייה: {value}%",
        en: "Minimum view: {value}%",
    },
    "analytics.storage.minimum-view.20": {
        he: "20% מהסרטון",
        en: "20% of the video",
    },
    "analytics.storage.minimum-view.40": {
        he: "40% מהסרטון",
        en: "40% of the video",
    },
    "analytics.storage.minimum-view.60": {
        he: "60% מהסרטון",
        en: "60% of the video",
    },
    "analytics.storage.minimum-view.80": {
        he: "80% מהסרטון",
        en: "80% of the video",
    },
    "analytics.storage.minimum-view.100": {
        he: "100% מהסרטון",
        en: "100% of the video",
    },
    "analytics.date-range.last-1": { he: "חודש אחרון", en: "Last month" },
    "analytics.date-range.last-3": {
        he: "3 חודשים אחרונים",
        en: "Last 3 months",
    },
    "analytics.date-range.last-6": {
        he: "6 חודשים אחרונים",
        en: "Last 6 months",
    },
    "analytics.date-range.last-12": { he: "שנה אחרונה", en: "Last year" },
    "toast.error.default": { he: "אירעה שגיאה", en: "Something went wrong." },
    "toast.error.copy-failed": {
        he: "ההעתקה נכשלה. נסו שוב או העתיקו ידנית",
        en: "Couldn't copy. Try again or copy it by hand",
    },
    "toast.loading.default": { he: "טוען...", en: "Loading..." },
    "toast.success.default": {
        he: "הפעולה בוצעה בהצלחה!",
        en: "Action completed successfully!",
    },
    "toast.success.update": {
        he: "השינויים עודכנו בהצלחה",
        en: "Changes updated successfully",
    },
    "toast.success.recorder.icon-added": {
        he: "האייקון נוסף בהצלחה!",
        en: "Icon added successfully!",
    },
    "toast.success.recorder.slide.duplicate": {
        he: "השקופית שוכפלה בהצלחה!",
        en: "Slide duplicated successfully!",
    },
    "toast.success.recorder.slide.delete": {
        he: "השקופית נמחקה בהצלחה!",
        en: "Slide deleted successfully!",
    },
    "toast.recorder.green-screen.no-image": {
        he: "יש לבחור תמונת רקע למסך הירוק בהגדרות",
        en: "Select a background image for green screen in settings",
    },
    "toast.error.recorder.sidebar.animations.no-animation-selected": {
        he: "יש לבחור אנימציה",
        en: "Please select an animation",
    },
    "toast.error.recorder.sidebar.animations.missing-values": {
        he: "חסרים ערכים",
        en: "Missing values",
    },
    "toast.error.recorder.no-audio-or-video-permission": {
        he: "לא אושרה גישה למיקרופון ולמצלמה",
        en: "Microphone and camera access was not granted",
    },
    "toast.error.recorder.no-audio-or-video-devices-found": {
        he: "לא נמצאו מיקרופון או מצלמה. ודאו שיש לכם מכשירים זמינים",
        en: "No microphone or camera found. Make sure you have devices available",
    },
    "toast.error.recorder.audio-or-video-unexpected-permission-error": {
        he: "אירעה שגיאה לא צפויה בניסיון בקשת גישה למצלמה והמיקרופון",
        en: "An unexpected error occurred while requesting camera and microphone access",
    },
    "toast.error.recorder.no-connected-devices": {
        he: "לא נמצאו מכשירים מחוברים",
        en: "No connected devices found",
    },
    "toast.error.recorder.no-camera-found": {
        he: "לא נמצאה מצלמה",
        en: "No camera found",
    },
    "toast.error.recorder.change-resolution-failed": {
        he: "שינוי הרזולוציה נכשל",
        en: "Failed to change resolution",
    },
    "toast.error.recorder.replace-image": {
        he: "בחרו תמונה במצגת שתרצו להחליף",
        en: "Select an image in the presentation to replace",
    },
    "toast.error.recorder.background-image-update-failed": {
        he: "אירעה שגיאה בניסיון עדכון תמונת רקע.",
        en: "Failed to update background image.",
    },
    "toast.error.recorder.animations.no-elements": {
        he: "לא נבחרו אלמנטים",
        en: "No elements selected",
    },
    "toast.success.create-chatbot": {
        he: "העוזר האישי נוצר בהצלחה!",
        en: "Chatbot created successfully!",
    },
    "toast.error.delete-chatbot": {
        he: "אירעה שגיאה בנסיון מחיקת צאטבוט",
        en: "Failed to delete chatbot.",
    },
    "toast.success.delete-chatbot": {
        he: "הצאטבוט נמחקה בהצלחה!",
        en: "Chatbot deleted successfully!",
    },
    "toast.error.unsaved-chatbot-changes": {
        he: "יש שינויים שלא נשמרו",
        en: "You have unsaved changes.",
    },
    "toast.success.create-chatbot-group": {
        he: "הקבוצה נוצרה בהצלחה!",
        en: "Group created successfully!",
    },
    "toast.error.create-chatbot-group": {
        he: "אירעה שגיאה בנסיון יצירת הקבוצה",
        en: "Failed to create group.",
    },
    "toast.success.save-chatbot-knowledge-changes": {
        he: "השינויים נשמרו בהצלחה!",
        en: "Changes saved successfully!",
    },
    "toast.error.invalid-url": {
        he: "ה-URL שהזנת לא תקין",
        en: "The URL you entered is invalid.",
    },
    "toast.error.chatbot-answer.missing-question": {
        he: "יש להזין שאלה",
        en: "Please enter a question.",
    },
    "toast.error.chatbot-answer.missing-answer": {
        he: "יש להזין תשובה",
        en: "Please enter an answer.",
    },
    "toast.error.chatbot-answer.save-with-error": {
        he: "קרתה שגיאה: {error}",
        en: "An error occurred: {error}",
    },
    "toast.success.chatbot-answer.saved": {
        he: "התשובה נשמרה בהצלחה!",
        en: "Answer saved successfully!",
    },
    "toast.success.chatbot-answer.dismissed": {
        he: "השאלה הוסרה מהרשימה",
        en: "Question dismissed",
    },
    "toast.success.course-update": {
        he: "הקורס עודכן בהצלחה!",
        en: "Course updated successfully!",
    },
    "toast.error.course-update": {
        he: "ניסיון עדכון קורס נכשל",
        en: "Failed to update course.",
    },
    "toast.success.create-chapter": {
        he: "הפרק נוצר בהצלחה!",
        en: "Chapter created successfully!",
    },
    "toast.error.create-chapter": {
        he: "ניסיון יצירת פרק נכשל",
        en: "Failed to create chapter.",
    },
    "toast.success.update-chapter": {
        he: "הפרק עודכן בהצלחה!",
        en: "Chapter updated successfully!",
    },
    "toast.error.update-chapter": {
        he: "ניסיון עדכון פרק נכשל",
        en: "Failed to update chapter.",
    },
    "toast.success.delete-chapter": {
        he: "הפרק נמחק בהצלחה!",
        en: "Chapter deleted successfully!",
    },
    "toast.error.delete-chapter": {
        he: "ניסיון מחיקת פרק נכשל",
        en: "Failed to delete chapter.",
    },
    "toast.success.create-lesson": {
        he: "השיעור נוצר בהצלחה בסוף הפרק!",
        en: "Lesson created successfully at the end of the chapter!",
    },
    "toast.error.create-lesson": {
        he: "ניסיון יצירת שיעור נכשל",
        en: "Failed to create lesson.",
    },
    "toast.success.update-lesson": {
        he: "השיעור עודכן בהצלחה!",
        en: "Lesson updated successfully!",
    },
    "toast.error.update-lesson": {
        he: "ניסיון עדכון שיעור נכשל",
        en: "Failed to update lesson.",
    },
    "toast.success.delete-lesson": {
        he: "השיעור נמחק בהצלחה!",
        en: "Lesson deleted successfully!",
    },
    "toast.error.delete-lesson": {
        he: "ניסיון מחיקת שיעור נכשל",
        en: "Failed to delete lesson.",
    },
    "toast.success.create-question": {
        he: "השאלה נוצרה בהצלחה!",
        en: "Question created successfully!",
    },
    "toast.error.create-question": {
        he: "אירעה שגיאה בניסיון יצירת שאלה",
        en: "Failed to create question.",
    },
    "toast.success.create-comment": {
        he: "התגובה נוצרה בהצלחה!",
        en: "Comment created successfully!",
    },
    "toast.error.create-comment": {
        he: "אירעה שגיאה בניסיון יצירת תגובה",
        en: "Failed to create comment.",
    },
    "toast.error.load-questions": {
        he: "אירעה שגיאה בניסיון טעינת השאלות",
        en: "Failed to load questions.",
    },
    "toast.error.load-comments": {
        he: "אירעה שגיאה בניסיון טעינת התגובות",
        en: "Failed to load comments.",
    },
    "toast.error.create-student": {
        he: "ניסיון יצירת תלמיד נכשל",
        en: "Failed to create student.",
    },
    "toast.error.create-lesson-no-chapters": {
        he: 'כדי ליצור שיעור צריך לפחות פרק אחד. לחצו: "פרק חדש".',
        en: 'To create a lesson, you need at least one chapter. Click "New chapter".',
    },
    "toast.error.course-settings.no-name": {
        he: "שם הקורס חסר",
        en: "Course name is required.",
    },
    "toast.error.course-settings.no-url": {
        he: "כתובת הקורס חסרה",
        en: "Course URL is required.",
    },
    "toast.error.course-settings.invalid-enrollment-type": {
        he: "סוג הרשמה לא תקין",
        en: "Invalid enrollment type.",
    },
    "toast.error.course-settings.negative-price": {
        he: "המחיר לא יכול להיות שלילי",
        en: "Price cannot be negative.",
    },
    "toast.error.course-settings.invalid-access-period": {
        he: "תקופת גישה לא תקינה",
        en: "Invalid access period.",
    },
    "toast.error.storage.allowed-referrers-validation": {
        he: "חלק מהדומיינים שהוזנו לא חוקיים",
        en: "Some of the domains you entered are invalid.",
    },
    "toast.error.storage.netfree.folder-already-selected": {
        he: "התיקייה כבר נבחרה",
        en: "The folder is already selected.",
    },
    "toast.error.update-captions": {
        he: "אירעה שגיאה בניסיון עדכון כתוביות",
        en: "Failed to update captions.",
    },
    "toast.error.update-video": {
        he: "אירעה שגיאה בניסיון עדכון סרטון",
        en: "Failed to update video.",
    },
    "toast.error.start-before-end": {
        he: "זמן ההתחלה חייב להיות לפני זמן הסיום",
        en: "Start time must be before end time.",
    },
    "toast.error.exceeds-video-length": {
        he: "הזמן חורג מאורך הסרטון",
        en: "Time exceeds video length.",
    },
    "toast.error.overlapping-chapter": {
        he: "זמן התחלה של פרק חייב להיות אחרי זמן סיום של הפרק הקודם",
        en: "Chapter start must be after the previous chapter end.",
    },
    "toast.error.overlapping-next-chapter": {
        he: "זמן סיום של פרק חייב להיות לפני זמן התחלה של הפרק הבא",
        en: "Chapter end must be before the next chapter start.",
    },
    "toast.error.no-selected-languages": {
        he: "לא נבחרו שפות",
        en: "No languages selected.",
    },
    "toast.error.no-selected-language": {
        he: "לא נבחרה שפה",
        en: "No language selected.",
    },
    "toast.error.no-selected-translations": {
        he: "לא נבחרו תרגומים",
        en: "No translations selected.",
    },
    "toast.error.dubbings.translation-required": {
        he: "כדי לייצר דיבוב לשפה מסויימת חובה קודם לייצר לה תרגום",
        en: "To generate dubbing for a language, you must first create its translation.",
    },
    "toast.error.interaction-not-found": {
        he: "אינטראקציה לא נמצאה",
        en: "Interaction not found.",
    },
    "toast.error.interaction-timestamp-out-of-bounds": {
        he: "הזמן חייב להיות בתוך גבולות הסרטון",
        en: "Time must be within the video duration.",
    },
    "toast.error.interaction-timestamp-is-taken": {
        he: "הזמן שבחרת תפוס על ידי אינטראקציה אחרת",
        en: "That time is already taken by another interaction.",
    },
    "toast.error.save-interactions": {
        he: "אירעה שגיאה בניסיון שמירת אינטראקציות",
        en: "Failed to save interactions.",
    },
    "toast.error.hotwords.invalid-format": {
        he: 'אנא ודאו שכל המילים בשדה המילים החמות מופרדות בפסיק, למשל: "dog, cat, bird"',
        en: 'Please separate hotwords with commas, e.g. "dog, cat, bird".',
    },
    "toast.error.generate.no-selected-languages": {
        he: "בחרו שפות",
        en: "Please select languages.",
    },
    "toast.error.generate-captions-no-srclng": {
        he: "בחרו את השפה בה מדברים בסרטון",
        en: "Choose the spoken language in the video.",
    },
    "toast.error.retry-failed-video-workflow": {
        he: "ניסיון חוזר נכשל",
        en: "Retry failed.",
    },
    "toast.error.download-failed": {
        he: "שגיאה בהורדה",
        en: "Download failed.",
    },
    "toast.error.delete-video": {
        he: "אירעה שגיאה בנסיון מחיקת הסרטון",
        en: "Failed to delete video.",
    },
    "toast.error.update-folder": {
        he: "אירעה שגיאה בנסיון עדכון התיקייה",
        en: "Failed to update folder.",
    },
    "toast.error.update-lomda": {
        he: "אירעה שגיאה בניסיון עדכון לומדה",
        en: "Failed to update learning module.",
    },
    "toast.error.update-presentation": {
        he: "אירעה שגיאה בניסיון עדכון מצגת",
        en: "Failed to update presentation.",
    },
    "toast.error.delete-presentation": {
        he: "אירעה שגיאה בנסיון מחיקת המצגת",
        en: "Failed to delete presentation.",
    },
    "toast.error.duplicate-presentation": {
        he: "אירעה שגיאה בנסיון שכפול המצגת",
        en: "Failed to duplicate presentation.",
    },
    "toast.error.duplicate-lomda": {
        he: "קרתה שגיאה בהעתקת הלומדה",
        en: "Failed to duplicate learning module.",
    },
    "toast.error.delete-lomda": {
        he: "אירעה שגיאה בנסיון מחיקת הלומדה",
        en: "Failed to delete learning module.",
    },
    "toast.error.export-folder": {
        he: "קרתה שגיאה",
        en: "Something went wrong.",
    },
    "toast.success.download-captions": {
        he: "הכתוביות הורדו",
        en: "Captions downloaded",
    },
    "toast.error.download-captions": {
        he: "הורדת הכתוביות נכשלה",
        en: "Failed to download captions",
    },
    "toast.error.create-yedalms-course": {
        he: "אירעה שגיאה ביצירת הקורס ב-YedaLMS",
        en: "Failed to create the YedaLMS course.",
    },
    "toast.error.change-video-privacy": {
        he: "קרתה שגיאה",
        en: "Something went wrong.",
    },
    "toast.error.generate-lomda-from-file.too-long": {
        he: "הקובץ שבחרתם ארוך מידי, האורך המקסימלי הוא 50 עמודים.",
        en: "The file you selected is too long. The maximum length is 50 pages.",
    },
    "toast.error.generate-presentation-from-file.too-long": {
        he: "הקובץ שבחרתם ארוך מידי, האורך המקסימלי הוא 30 עמודים.",
        en: "The file you selected is too long. The maximum length is 30 pages.",
    },
    "toast.error.upload-video-from-gdrive.too-many-videos": {
        he: "לא ניתן לבחור יותר מ-100 סרטונים בהעלאה בודדת",
        en: "You can’t select more than 100 videos per upload.",
    },
    "toast.error.upload-video-from-gdrive.no-files-selected": {
        he: "לא נבחרו סרטונים",
        en: "No videos were selected.",
    },
    "toast.error.dropzone-video-upload.invalid-files": {
        he: "לא נבחרו סרטונים תקינים",
        en: "No valid videos were selected.",
    },
    "toast.error.import-from-vimeo.no-videos-selected": {
        he: "לא נבחרו סרטונים",
        en: "No videos were selected.",
    },
    "toast.error.vimeo-error": {
        he: "קרתה שגיאה עם וימאו, נסו שוב בעוד דקה",
        en: "There was a Vimeo error. Please try again in a minute.",
    },
    "toast.success.generate-lomda": {
        he: "הלומדה נוצרה בהצלחה!",
        en: "Lomda created successfully!",
    },
    "toast.success.generate-presentation": {
        he: "המצגת נוצרה בהצלחה!",
        en: "Presentation created successfully!",
    },
    "toast.success.generate-syllabus": {
        he: "הסילאבוס נוצר בהצלחה!",
        en: "Syllabus created successfully!",
    },
    "toast.error.file-upload.exceeded-max-file-size": {
        he: "הקובץ שהועלה כבד מידי",
        en: "The uploaded file is too large.",
    },
    "toast.error.unexpected": {
        he: "קרתה שגיאה לא צפויה",
        en: "An unexpected error occurred.",
    },
    "toast.error.upload-file.already-uploading": {
        he: "יש לחכות שההעלאה הנוכחית תסתיים.",
        en: "Please wait for the current upload to finish.",
    },
    "toast.error.upload-file.failed": {
        he: "אירעה שגיאה בהעלאה",
        en: "Upload failed.",
    },
    "toast.error.upload-file.multiple-files-attempt": {
        he: "ניתן להעלות קובץ אחד בלבד בכל פעם.",
        en: "Only one file can be uploaded at a time.",
    },
    "toast.error.upload-file.type-not-supported": {
        he: "סוג הקובץ לא נתמך.",
        en: "File type is not supported.",
    },
    "toast.error.upload-video-failed": {
        he: "ההעלאה נכשלה",
        en: "Upload failed.",
    },
    "toast.error.video-modal.storage-form.no-folder": {
        he: "יש לבחור תיקייה",
        en: "Please select a folder",
    },
    "toast.error.video-modal.storage-form.no-video-name": {
        he: "יש לבחור שם לסרטון",
        en: "Please enter a video name",
    },
    "toast.error.video-duration-render-limit": {
        he: "אורך הסרטון חייב להיות קטן מ-60 דקות",
        en: "Video length must be under 60 minutes",
    },
    "toast.success.create-folder": {
        he: "התיקייה נוצרה בהצלחה!",
        en: "Folder created successfully!",
    },
    "toast.success.create-yedalms-course": {
        he: "הקורס נוצר בהצלחה ב-YedaLMS!",
        en: "YedaLMS course created successfully!",
    },
    "toast.success.delete-folder": {
        he: "התיקייה נמחקה בהצלחה!",
        en: "Folder deleted successfully!",
    },
    "toast.success.payment-successful": {
        he: "התשלום בוצע בהצלחה!",
        en: "Payment was made successfully!",
    },
    "toast.success.upload-file": {
        he: "הקובץ הועלה בהצלחה!",
        en: "File uploaded successfully!",
    },
    "toast.loading.export-folder": {
        he: "מכין את הקובץ...",
        en: "Preparing the file...",
    },
    "toast.loading.change-video-privacy": {
        he: "טוען...",
        en: "Loading...",
    },
    "toast.success.export-folder": {
        he: "הקובץ ירד בהצלחה!",
        en: "File downloaded successfully!",
    },
    "toast.success.file-created": {
        he: "הקובץ נוצר בהצלחה!",
        en: "File created successfully!",
    },
    "toast.success.changes-saved": {
        he: "השינויים נשמרו בהצלחה!",
        en: "Changes saved successfully!",
    },
    "toast.success.save-changes": {
        he: "השינויים נשמרו בהצלחה!",
        en: "Changes saved successfully!",
    },
    "toast.success.chapters-generated": {
        he: "הפרקים נוצרו בהצלחה!",
        en: "Chapters generated successfully!",
    },
    "toast.success.interactions-generated": {
        he: "האינטראקציות נוצרו בהצלחה!",
        en: "Interactions generated successfully!",
    },
    "toast.success.download-started": {
        he: "ההורדה החלה!",
        en: "Download started!",
    },
    "toast.success.delete-video": {
        he: "הסרטון נמחק בהצלחה!",
        en: "Video deleted successfully!",
    },
    "toast.success.delete-presentation": {
        he: "המצגת נמחקה בהצלחה!",
        en: "Presentation deleted successfully!",
    },
    "toast.success.delete-lomda": {
        he: "הלומדה נמחקה בהצלחה!",
        en: "Learning module deleted successfully!",
    },
    "toast.success.duplicate-presentation": {
        he: "המצגת שוכפלה בהצלחה!",
        en: "Presentation duplicated successfully!",
    },
    "toast.success.duplicate-lomda": {
        he: "הלומדה הועתקה בהצלחה!",
        en: "Learning module duplicated successfully!",
    },
    "toast.success.update-folder": {
        he: "התיקייה עודכנה בהצלחה!",
        en: "Folder updated successfully!",
    },
    "toast.success.create-storage-preset": {
        he: "התבנית נוצרה בהצלחה!",
        en: "Preset created successfully!",
    },
    "toast.error.create-storage-preset": {
        he: "שגיאה ביצירת התבנית",
        en: "Failed to create preset.",
    },
    "toast.success.update-storage-preset": {
        he: "התבנית עודכנה בהצלחה!",
        en: "Preset updated successfully!",
    },
    "toast.error.update-storage-preset": {
        he: "שגיאה בעדכון התבנית",
        en: "Failed to update preset.",
    },
    "toast.success.update-zoom-storage-preset": {
        he: "תבנית הזום עודכנה בהצלחה!",
        en: "Zoom preset updated successfully!",
    },
    "toast.error.update-zoom-storage-preset": {
        he: "שגיאה בעדכון תבנית הזום",
        en: "Failed to update Zoom preset.",
    },
    "toast.success.delete-storage-preset": {
        he: "התבנית נמחקה בהצלחה!",
        en: "Preset deleted successfully!",
    },
    "toast.error.delete-storage-preset": {
        he: "שגיאה במחיקת התבנית",
        en: "Failed to delete preset.",
    },
    "toast.success.detach-storage-preset": {
        he: "התבנית נותקה מהתיקייה בהצלחה!",
        en: "Preset detached from folder successfully!",
    },
    "toast.error.detach-storage-preset": {
        he: "שגיאה בניתוק התבנית",
        en: "Failed to detach preset.",
    },
    "toast.success.dubbing-deleted": {
        he: "הדיבוב נמחק בהצלחה!",
        en: "Dubbing deleted successfully!",
    },
    "toast.success.translation-deleted": {
        he: "התרגום נמחק בהצלחה!",
        en: "Translation deleted successfully!",
    },
    "toast.success.retry-failed-video-workflow": {
        he: "ניסיון חוזר הופעל בהצלחה!",
        en: "Retry started successfully!",
    },
    "toast.success.change-video-privacy": {
        he: "הגדרות הפרטיות עודכנו בהצלחה!",
        en: "Privacy settings updated successfully!",
    },
    "upload-csv.selected-file": { he: "הקובץ הנבחר:", en: "Selected file:" },
    "upload-csv.subtitle": {
        he: "גררו לכאן קובץ, או לחצו כדי לבחור קובץ",
        en: "Drag a file here, or click to choose a file",
    },
    "upload-csv.title": { he: "העלאת קובץ", en: "Upload file" },
    "upload-file.default.subtitle": {
        he: "גררו לכאן קובץ, או לחצו כדי לבחור קובץ",
        en: "Drag a file here, or click to choose a file",
    },
    "upload-file.default.title": { he: "העלאת קובץ", en: "Upload file" },
    "upload-file.preview.alt": { he: "תצוגה מקדימה", en: "Preview" },
    "upload-file.selected-file": { he: "הקובץ הנבחר:", en: "Selected file:" },
    "upload-file.selected-files": {
        he: "הקבצים שנבחרו:",
        en: "Selected files:",
    },
    "upload-file.batch.progress": {
        he: "מעלה {done} מתוך {total}",
        en: "Uploading {done} of {total}",
    },
    "upload-file.batch.done": {
        he: "{count} קבצים הועלו בהצלחה",
        en: "{count} files uploaded",
    },
    "upload-file.batch.partial": {
        he: "הועלו {done} מתוך {total} קבצים. השאר נכשלו, נסו להעלות אותם שוב.",
        en: "Uploaded {done} of {total} files. The rest failed, try them again.",
    },
    "upload-file.batch.skipped": {
        he: "קבצים שדולגו: {count} ({reasons})",
        en: "Files skipped: {count} ({reasons})",
    },
    "upload-file.batch.skipped-partial": {
        he: "קבצים שדולגו: לפחות {count} ({reasons})",
        en: "Files skipped: {count}+ ({reasons})",
    },
    "upload-file.batch.reason.type": { he: "סוג לא נתמך", en: "wrong type" },
    "upload-file.batch.reason.size": { he: "גדול מדי", en: "too big" },
    "upload-file.batch.reason.batch": {
        he: "יותר מ־{max} בבת אחת",
        en: "more than {max} at once",
    },
    "upload-file.batch.reason.room": {
        he: "אין מקום לעוד",
        en: "no room for more",
    },
    "upload-file.batch.reason.scan": {
        he: "תיקייה גדולה מדי",
        en: "folder too large",
    },
    "upload-file.batch.already-listed": {
        he: "כבר ברשימה: {count}",
        en: "Already in the list: {count}",
    },
    "upload-file.batch.subtitle-icons": {
        he: "PNG, SVG · אפשר כמה קבצים או תיקייה",
        en: "PNG, SVG · several files or a folder",
    },
    "upload-file.batch.scan-stopped": {
        he: "התיקייה גדולה מדי: נקראו רק {max} הפריטים הראשונים",
        en: "Folder too large: only the first {max} items were read",
    },
    "upload-file.batch.scanning": {
        he: "קורא את התיקייה…",
        en: "Reading the folder…",
    },
    "upload-file.batch.cancel": {
        he: "ביטול ההעלאה",
        en: "Cancel upload",
    },
    "upload-file.batch.cancelled": {
        he: "ההעלאה בוטלה. הועלו {done} מתוך {total} קבצים.",
        en: "Upload cancelled. Uploaded {done} of {total} files.",
    },
    "voice-selector.placeholder": { he: "בחרו קול", en: "Select a voice" },
    "storage.lomda-card.num-of-scenes-badge.multiple": {
        he: "סצנות",
        en: "Scenes",
    },
    "storage.header.actions": { he: "פעולות", en: "Actions" },
    "storage.header.actions.create-folder": {
        he: "תיקייה חדשה",
        en: "New folder",
    },
    "storage.header.actions.settings": {
        he: "הגדרות האחסון",
        en: "Storage settings",
    },
    "storage.delete-folder-modal.body.confirm": {
        he: "אתם בטוחים שתרצו למחוק את התיקייה: {folderName}? פעולה זו תמחק את כל הקבצים בתיקייה ולא ניתן יהיה לשחזר אותם.",
        en: "Are you sure you want to delete the folder: {folderName}? This will delete all files in the folder and cannot be undone.",
    },
    "storage.delete-folder-modal.body.multi-folder-warning": {
        he: "שימו לב שאם קובץ מהתיקייה מופיע ביותר מתיקייה אחת, פעולה זו תמחק גם את כל העותקים של הקובץ בשאר התיקיות שהוא נמצא בהן.",
        en: "Note: if a file appears in more than one folder, this action will delete all its copies from other folders as well.",
    },
    "storage.header.actions.upload-videos": {
        he: "העלאת סרטונים",
        en: "Upload videos",
    },
    "storage.header.title": { he: "האחסון שלי", en: "My storage" },
    "storage.explorer.select-folder.video.description": {
        he: "נווטו אל התיקייה שבה תרצו לשמור את הסרטון ולחצו אישור.",
        en: "Navigate to the folder where you want to save the video and click Confirm.",
    },
    "storage.explorer.select-folder.lomda.description": {
        he: "נווטו אל התיקייה שבה תרצו לשמור את הלומדה ולחצו אישור.",
        en: "Navigate to the folder where you want to save the learning module and click Confirm.",
    },
    "storage.explorer.select-folder.presentation.description": {
        he: "נווטו אל התיקייה שבה תרצו לשמור את המצגת ולחצו אישור.",
        en: "Navigate to the folder where you want to save the presentation and click Confirm.",
    },
    "storage.folder-dropdown.no-selection": {
        he: "לא נבחרו תיקיות",
        en: "No folders selected",
    },
    "storage.folder-dropdown.add-folder": {
        he: "הוספת תיקייה",
        en: "Add folder",
    },
    "storage.export.csv.header.id": { he: "מזהה", en: "ID" },
    "storage.export.csv.header.name": { he: "שם", en: "Name" },
    "storage.export.csv.header.embed-url": {
        he: "לינק הטמעה",
        en: "Embed URL",
    },
    "storage.export.csv.header.folder-path": {
        he: "נתיב תיקייה",
        en: "Folder path",
    },
    "storage.export.csv.filename": {
        he: "folder-embeds.csv",
        en: "folder-embeds.csv",
    },
    "storage.export.captions.header.video-name": {
        he: "שם הסרטון",
        en: "Video name",
    },
    "storage.export.captions.header.captions": {
        he: "כתוביות",
        en: "Captions",
    },
    "storage.export.captions.filename": {
        he: "folder-subtitles.txt",
        en: "folder-subtitles.txt",
    },
    "storage.download.xlsx.header.video-id": {
        he: "מזהה סרטון",
        en: "Video ID",
    },
    "storage.download.xlsx.header.video-title": {
        he: "כותרת סרטון",
        en: "Video Title",
    },
    "storage.download.xlsx.header.video-length": {
        he: "אורך סרטון",
        en: "Video Length",
    },
    "storage.download.xlsx.header.video-folder-name": {
        he: "שם תיקייה",
        en: "Video Folder Name",
    },
    "storage.download.xlsx.header.video-download-url": {
        he: "קישור הורדה",
        en: "Video Download URL",
    },
    "storage.download.xlsx.sheet-name": {
        he: "דוח סרטונים",
        en: "Videos Report",
    },
    "storage.download.xlsx.filename": {
        he: "{folderName}-videos-report.xlsx",
        en: "{folderName}-videos-report.xlsx",
    },
    "storage.selected-items.error.move-different-locations": {
        he: "לא ניתן להעביר תיקיות ממיקומים שונים בו-זמנית.",
        en: "Cannot move folders from different locations at the same time.",
    },
    "storage.selected-items.move.description": {
        he: "בחרו תיקיית יעד להעברת הפריטים.",
        en: "Choose a destination folder to move the items.",
    },
    "storage.selected-items.move.confirm": { he: "אישור", en: "Confirm" },
    "storage.selected-items.label.folder": {
        he: "תיקייה: {name}",
        en: "Folder: {name}",
    },
    "storage.selected-items.label.file": {
        he: "קובץ: {name}",
        en: "File: {name}",
    },
    "storage.selected-items.label.mixed": {
        he: "{folders} תיקיות · {files} קבצים",
        en: "{folders} folders · {files} files",
    },
    "storage.selected-items.label.files": {
        he: "{files} קבצים",
        en: "{files} files",
    },
    "storage.selected-items.label.folders": {
        he: "{folders} תיקיות",
        en: "{folders} folders",
    },
    "storage.selected-items.move.action": { he: "העברה", en: "Move" },
    "storage.selected-items.cancel": { he: "ביטול", en: "Cancel" },
    "storage.overview-tab.empty-state.subtitle": {
        he: "נסו חיפוש אחר או העלו לאחסון סרטונים.",
        en: "Try a different search or upload videos to storage.",
    },
    "storage.overview-tab.empty-state.title": {
        he: "לא נמצאו תוצאות",
        en: "No results found",
    },
    "storage.presentation-card.num-of-slides-badge.multiple": {
        he: "שקופיות",
        en: "Slides",
    },
    "not-found.title": { he: "העמוד לא קיים", en: "Page not found" },
    "not-found.description": {
        he: "מתנצלים, לא הצלחנו למצוא את העמוד שחיפשתם.",
        en: "Sorry, we couldn't find the page you were looking for.",
    },
    "not-found.home-link": { he: "לעמוד הראשי", en: "Back to home" },
    "chatbot.lms-demo.greeting": { he: "דמו", en: "Demo" },
    "ui.breadcrumb.more": { he: "עוד", en: "More" },
    "ui.ai": { he: "AI", en: "AI" },
    "ui.logo": { he: "לוגו", en: "Logo" },
    "ui.illustration": { he: "איור", en: "Illustration" },
    "ui.close": { he: "סגירה", en: "Close" },
    "ui.sidebar.toggle": { he: "פתיחת סרגל צד", en: "Toggle sidebar" },
    "timestamp-input.popover.seconds": { he: "שניות", en: "Seconds" },
    "timestamp-input.popover.minutes": { he: "דקות", en: "Minutes" },
    "timestamp-input.popover.hours": { he: "שעות", en: "Hours" },
    "timestamp-input.popover.ms": { he: "עשיריות שנייה", en: "Tenths" },
    "pricing.title": { he: "מחירון", en: "Pricing" },
    "pricing.billing-table.title": { he: "מחירון", en: "Pricing" },
    "pricing.credits-table.title": {
        he: "מחירון פעולות קרדיטים",
        en: "Credits Actions Pricing",
    },
    "pricing.header.bank-type": { he: "סוג", en: "Type" },
    "pricing.header.price": { he: "מחיר", en: "Price" },
    "pricing.header.unit": { he: "יחידה", en: "Unit" },
    "pricing.header.discount": { he: "הנחה", en: "Discount" },
    "pricing.header.credit-action": {
        he: "פעולת קרדיטים",
        en: "Credit action",
    },
    "pricing.header.credits-amount": {
        he: "כמות קרדיטים",
        en: "Credits amount",
    },
    "pricing.unit.storage": { he: "ל-TB לחודש", en: "Per TB per month" },
    "pricing.unit.bandwidth": { he: "ל-TB", en: "Per TB" },
    "pricing.unit.credits": { he: "לקרדיט", en: "Per credit" },
    "pricing.label.storage": { he: "אחסון", en: "Storage" },
    "pricing.label.bandwidth": { he: "תעבורה", en: "Bandwidth" },
    "pricing.label.credits": { he: "קרדיטים", en: "Credits" },
    "pricing.label.video_transcribe_hour": {
        he: "תמלול וידאו (לשעה)",
        en: "Video Transcription (per hour)",
    },
    "pricing.label.video_interactions_hour": {
        he: "אינטראקציות וידאו (לשעה)",
        en: "Video Interactions (per hour)",
    },
    "pricing.label.video_chapters_hour": {
        he: "פרקי וידאו (לשעה)",
        en: "Video Chapters (per hour)",
    },
    "pricing.label.video_translation_hour": {
        he: "תרגום וידאו (לשעה)",
        en: "Video Translation (per hour)",
    },
    "pricing.label.video_dubbing_hour": {
        he: "דיבוב וידאו (לשעה)",
        en: "Video Dubbing (per hour)",
    },
    "pricing.label.presentation": { he: "מצגת", en: "Presentation" },
    "pricing.label.syllabus": { he: "סילבוס", en: "Syllabus" },
    "pricing.label.lomda": { he: "לומדה", en: "Lomda" },
    "pricing.label.generate_lomda_scene_audio": {
        he: "אודיו סצנת לומדה",
        en: "Lomda Scene Audio",
    },
    "pricing.label.generate_lomda_video": {
        he: "וידאו לומדה",
        en: "Lomda Video",
    },
    "pricing.label.chatbot_message": {
        he: "הודעת צ'אטבוט",
        en: "Chatbot Message",
    },
    "pricing.label.edit_video": { he: "עריכת וידאו", en: "Video Editing" },
    "pricing.action-unit.video_transcribe_hour": {
        he: "לשעה",
        en: "Per hour",
    },
    "pricing.action-unit.video_interactions_hour": {
        he: "לשעה",
        en: "Per hour",
    },
    "pricing.action-unit.video_chapters_hour": {
        he: "לשעה",
        en: "Per hour",
    },
    "pricing.action-unit.video_translation_hour": {
        he: "לשעה",
        en: "Per hour",
    },
    "pricing.action-unit.video_dubbing_hour": {
        he: "לשעה",
        en: "Per hour",
    },
    "pricing.action-unit.presentation": {
        he: "למצגת",
        en: "Per presentation",
    },
    "pricing.action-unit.syllabus": {
        he: "לסילבוס",
        en: "Per syllabus",
    },
    "pricing.action-unit.lomda": {
        he: "-",
        en: "-",
    },
    "pricing.action-unit.generate_lomda_scene_audio": {
        he: "-",
        en: "-",
    },
    "pricing.action-unit.generate_lomda_video": {
        he: "-",
        en: "-",
    },
    "pricing.action-unit.chatbot_message": {
        he: "להודעה",
        en: "Per message",
    },
    "pricing.action-unit.edit_video": {
        he: "-",
        en: "-",
    },
    "pricing.action-unit.lomda_v2_ai_creation": {
        he: "לסצנה",
        en: "Per scene",
    },
    "pricing.action-unit.lomda_v3_ai_creation": {
        he: "לעמוד",
        en: "Per page",
    },
    "pricing.action-unit.lomda_v2_rendering": {
        he: "לדקה",
        en: "Per minute",
    },
    "pricing.action-unit.lomda_v2_avatar_generation": {
        he: "לדקת אווטאר",
        en: "Per avatar minute",
    },
    "pricing.action-unit.lomda_v2_voice_over_generation": {
        he: "לדקת קריינות",
        en: "Per voice-over minute",
    },

    // ── Brand kits ─────────────────────────────────────────────────────
    "brand-kits.picker.your-brand-kits": {
        he: "ערכות המיתוג שלך",
        en: "Your brand kits",
    },
    "brand-kits.picker.templates": { he: "תבניות", en: "Templates" },
    "brand-kits.picker.create-new": { he: "ערכה חדשה", en: "Create new" },
    "brand-kits.picker.loading": { he: "טוען…", en: "Loading…" },
    "brand-kits.picker.delete-confirm.title": {
        he: 'למחוק את "{name}"?',
        en: 'Delete "{name}"?',
    },
    "brand-kits.picker.delete-confirm.description": {
        he: "ערכת המיתוג תוסר מהחשבון שלך. לומדות שכבר נוצרו עם הערכה הזו ישמרו על המראה שלהן.",
        en: "This brand kit will be removed from your account. Lomdas you already created with it keep their look unchanged.",
    },
    "brand-kits.picker.delete-confirm.cancel": { he: "ביטול", en: "Cancel" },
    "brand-kits.picker.delete-confirm.confirm": { he: "מחיקה", en: "Delete" },

    "brand-kits.modal.title.create": {
        he: "יצירת ערכת מיתוג",
        en: "Create brand kit",
    },
    "brand-kits.modal.title.edit": {
        he: "עריכת ערכת מיתוג",
        en: "Edit brand kit",
    },

    "brand-kits.form.preview.heading": {
        he: "כותרת לדוגמה",
        en: "Heading sample",
    },
    "brand-kits.form.preview.body": {
        he: "טקסט גוף לדוגמה",
        en: "Body text sample",
    },
    "brand-kits.form.name.label": { he: "שם", en: "Name" },
    "brand-kits.form.name.placeholder": { he: "המותג שלי", en: "My brand" },
    "brand-kits.tab.general": { he: "כללי", en: "General" },
    "brand-kits.tab.video-lomda": { he: "לומדת וידאו", en: "Video lomda" },
    "brand-kits.tab.html-lomda": { he: "לומדת HTML", en: "HTML lomda" },
    "brand-kits.tab.backgrounds": { he: "רקעים", en: "Backgrounds" },
    "brand-kits.tab.images": { he: "תמונות", en: "Images" },
    "brand-kits.tab.icons": { he: "אייקונים", en: "Icons" },
    "brand-kits.icons.hint": {
        he: "העלו את האייקונים של המותג (PNG או SVG). הם יופיעו בכל בחירת אייקון בלומדה, לצד האייקונים המובנים, בדיוק בצבעים שהעליתם.",
        en: "Upload your brand's icons (PNG or SVG). They appear in every icon picker in the lomda, next to the built-in ones, exactly in the colors you uploaded.",
    },
    "brand-kits.icons.remove": { he: "הסרה", en: "Remove" },
    "brand-kits.icons.duplicate": {
        he: "האייקון הזה כבר נמצא ברשימה",
        en: "This icon is already in the list",
    },
    "brand-kits.icons.limit": {
        he: "אפשר להעלות עד 60 אייקונים",
        en: "You can upload up to 60 icons",
    },
    "brand-kits.icons.upload-title": {
        he: "העלאת אייקון",
        en: "Upload an icon",
    },
    "brand-kits.icons.upload-subtitle": { he: "PNG, SVG", en: "PNG, SVG" },
    "lomda-v3-editor.icons.default": {
        he: "אייקונים מובנים",
        en: "Default icons",
    },
    "lomda-v3-editor.icons.custom": {
        he: "האייקונים שלי",
        en: "Custom icons",
    },
    "lomda-v3-editor.icons.upload": { he: "העלאת אייקון", en: "Upload icon" },
    "lomda-v3-editor.flip.card-appearance": {
        he: "עיצוב הכרטיס",
        en: "Card appearance",
    },
    "lomda-v3-editor.flip.card-bg-image": {
        he: "תמונת רקע לכרטיס",
        en: "Card background image",
    },
    "lomda-v3-editor.flip.card-bg-upload": {
        he: "העלאת תמונה",
        en: "Upload an image",
    },
    "lomda-v3-editor.flip.card-bg-remove": { he: "הסרה", en: "Remove" },
    "lomda-v3-editor.interactive.static-cards": { he: "כרטיסים סטטיים", en: "Static cards" },
    "lomda-v3-editor.block-card.static-cards.title": { he: "כרטיסים סטטיים", en: "Static Cards" },
    "lomda-v3-editor.block-card.static-cards.description": {
        he: "הוסף כרטיסים עם אייקון, כותרת ותיאור",
        en: "Add cards with an icon, a title and a description",
    },
    "brand-kits.reference-images.hint": {
        he: "העלו אלמנטים מהמיתוג שלכם — איורים, אייקונים, סטיקרים. התמונות שה-AI ייצר בלומדות יתאימו לסגנון ולצבעים שלהם.",
        en: "Upload your brand elements — illustrations, icons, stickers. AI-generated lomda images will match their style and colors.",
    },
    "brand-kits.reference-images.remove": { he: "הסרה", en: "Remove" },
    "brand-kits.reference-images.limit": {
        he: "אפשר להעלות עד 8 תמונות",
        en: "You can upload up to 8 images",
    },
    "brand-kits.reference-images.duplicate": {
        he: "התמונה הזו כבר נמצאת ברשימה",
        en: "This image is already in the list",
    },
    "brand-kits.reference-images.upload-title": {
        he: "העלאת תמונת מותג",
        en: "Upload a brand image",
    },
    "brand-kits.reference-images.upload-subtitle": {
        he: "PNG, JPG, WEBP · אפשר כמה תמונות או תיקייה",
        en: "PNG, JPG, WEBP · several images or a folder",
    },
    "brand-kits.reference-images.style-notes": {
        he: "הנחיות סגנון",
        en: "Style notes",
    },
    "brand-kits.reference-images.style-notes-placeholder": {
        he: "למשל: תמיד איור בקו יד, בלי להשתמש בלוגו עצמו",
        en: "e.g. always hand-drawn line art, never use the logo itself",
    },
    "brand-kits.reference-images.style-notes-hint": {
        he: "טקסט חופשי שיצורף להנחיות יצירת התמונות.",
        en: "Free text appended to the image generation instructions.",
    },
    "brand-kits.backgrounds.hint": {
        he: "העלו תמונת רקע לכל יחס מסך; הלומדה תבחר אוטומטית את המתאימה ביותר למסך הצופה.",
        en: "Upload a background per screen ratio; the lomda automatically picks the best fit for the viewer's screen.",
    },
    "brand-kits.backgrounds.remove": { he: "הסרה", en: "Remove" },
    "brand-kits.backgrounds.custom-invalid": {
        he: "הזינו רוחב וגובה חיוביים",
        en: "Enter a positive width and height",
    },
    "brand-kits.backgrounds.custom-exists": {
        he: "היחס הזה כבר קיים ברשימה",
        en: "This ratio already exists in the list",
    },
    "brand-kits.backgrounds.add-custom": {
        he: "יחס מותאם",
        en: "Custom ratio",
    },
    "brand-kits.backgrounds.upload-title": {
        he: "העלאת תמונה",
        en: "Upload image",
    },
    "brand-kits.backgrounds.upload-subtitle": {
        he: "PNG, JPG, WEBP",
        en: "PNG, JPG, WEBP",
    },
    "brand-kits.backgrounds.scrim": {
        he: "כהות רקע",
        en: "Overlay",
    },
    "brand-kits.backgrounds.surface": {
        he: "משטח תוכן",
        en: "Content surface",
    },
    "brand-kits.html-lomda.preview-heading": { he: "כותרת לדוגמה", en: "Sample heading" },
    "brand-kits.html-lomda.preview-body": { he: "כך ייראה טקסט בלומדה שלך.", en: "This is how body text looks in your lomda." },
    "brand-kits.html-lomda.preview-button": { he: "כפתור", en: "Button" },
    "brand-kits.html-lomda.preview-card": { he: "כרטיס", en: "Card" },
    "brand-kits.form.logo.label": { he: "לוגו", en: "Logo" },
    "brand-kits.form.logo.upload-title": {
        he: "העלאת לוגו",
        en: "Upload logo",
    },
    "brand-kits.form.logo.upload-subtitle": {
        he: "PNG, JPG, WEBP",
        en: "PNG, JPG, WEBP",
    },
    "brand-kits.form.titles-font.label": {
        he: "פונט כותרות",
        en: "Titles font",
    },
    "brand-kits.form.body-font.label": { he: "פונט גוף", en: "Body font" },
    "brand-kits.form.text-color.label": { he: "צבע טקסט", en: "Text color" },
    "brand-kits.form.lomda-bg-color.label": {
        he: "צבע רקע ללומדה",
        en: "Lomda background color",
    },
    "brand-kits.form.lomda-bg-color.description": {
        he: "צבע אחיד שבו ישתמש נגן הוידאו כרקע למסכי הלומדה ולשכבות אינטראקציה. מומלץ להתאים לצבע הרקע הראשי.",
        en: "Solid color the video player uses as the background for lomda screens and interaction overlays. Recommended to match your main background color.",
    },
    "brand-kits.form.primary-button.label": {
        he: "כפתור ראשי",
        en: "Primary button",
    },
    "brand-kits.form.secondary-button.label": {
        he: "כפתור משני",
        en: "Secondary button",
    },
    "brand-kits.form.button.bg": { he: "רקע", en: "Background" },
    "brand-kits.form.button.text": { he: "טקסט", en: "Text" },
    "brand-kits.form.button.border": { he: "מסגרת", en: "Border" },
    "brand-kits.form.button.reset": { he: "איפוס", en: "Reset" },
    "brand-kits.form.cancel": { he: "ביטול", en: "Cancel" },
    "brand-kits.form.wait-for-uploads": {
        he: "אפשר לשמור כשההעלאה תסתיים",
        en: "You can save once the upload finishes",
    },
    "brand-kits.form.submit.create": { he: "יצירה", en: "Create" },
    "brand-kits.form.submit.edit": {
        he: "שמירת שינויים",
        en: "Save changes",
    },

    "brand-kits.font-selector.choose": {
        he: "בחירת פונט",
        en: "Choose a font",
    },
    "brand-kits.font-selector.custom-fonts": {
        he: "פונטים שהועלו",
        en: "Uploaded fonts",
    },
    "brand-kits.font-selector.upload-custom": {
        he: "העלאת פונט מותאם",
        en: "Upload custom font",
    },
    "brand-kits.font-selector.upload.title": {
        he: "גררו קובץ ‎.ttf‎ או ‎.otf",
        en: "Drop a .ttf or .otf",
    },
    "brand-kits.font-selector.upload.subtitle": {
        he: "עד 2MB",
        en: "Max 2 MB",
    },
    "brand-kits.font-selector.sign-in-required": {
        he: "נדרשת התחברות להעלאה",
        en: "Sign in required to upload",
    },

    "brand-kits.background.label": { he: "רקע", en: "Background" },
    "brand-kits.background.solid": { he: "צבע אחיד", en: "Solid" },
    "brand-kits.background.gradient": { he: "גרדיאנט", en: "Gradient" },
    "brand-kits.background.image": { he: "תמונה", en: "Image" },
    "brand-kits.background.color": { he: "צבע רקע", en: "Background color" },
    "brand-kits.background.from": { he: "מ", en: "From" },
    "brand-kits.background.to": { he: "עד", en: "To" },
    "brand-kits.background.pick-color": {
        he: "בחירת צבע",
        en: "Pick a color",
    },
    "brand-kits.background.image.upload-title": {
        he: "העלאת תמונת רקע",
        en: "Upload background image",
    },
    "brand-kits.background.image.upload-subtitle": {
        he: "PNG, JPG, WEBP",
        en: "PNG, JPG, WEBP",
    },

    "brand-kits.card.edit": { he: "עריכה", en: "Edit" },
    "brand-kits.card.delete": { he: "מחיקה", en: "Delete" },

    // Rich-text editor block-type picker
    "rte.block.paragraph": { he: "פסקה", en: "Paragraph" },
    "rte.block.heading1": { he: "כותרת 1", en: "Heading 1" },
    "rte.block.heading2": { he: "כותרת 2", en: "Heading 2" },
    "rte.block.heading3": { he: "כותרת 3", en: "Heading 3" },
    "rte.block.heading6": { he: "תווית", en: "Label" },
    "rte.align.label": { he: "יישור", en: "Alignment" },
    "rte.align.start": { he: "יישור להתחלה", en: "Align start" },
    "rte.align.end": { he: "יישור לסוף", en: "Align end" },
    "rte.direction.label": { he: "כיוון טקסט", en: "Text direction" },
    "rte.valign.label": { he: "יישור אנכי", en: "Vertical align" },

    "brand-kits.font-control.brand-fonts": {
        he: "פונטים מותאמים",
        en: "Brand fonts",
    },
    "brand-kits.font-control.builtin": { he: "פונטים מובנים", en: "Built-in" },
    "brand-kits.font-control.titles": { he: "כותרות", en: "Titles" },
    "brand-kits.font-control.body": { he: "גוף", en: "Body" },
    "brand-kits.font-control.font-label": { he: "פונט", en: "Font" },
    "brand-kits.font-control.aria": { he: "פונט", en: "Font family" },
    "brand-kits.font-control.reset": { he: "איפוס", en: "Reset (inherit)" },

    // Default brand kit names
    "brand-kits.defaults.professional-blue": {
        he: "כחול מקצועי",
        en: "Professional Blue",
    },
    "brand-kits.defaults.dynamic-blue": {
        he: "כחול דינמי",
        en: "Dynamic Blue",
    },
    "brand-kits.defaults.soft-indigo": { he: "אינדיגו רך", en: "Soft Indigo" },
    "brand-kits.defaults.cool-mist": { he: "ערפל קריר", en: "Cool Mist" },
    "brand-kits.defaults.light-blue": { he: "תכלת", en: "Light Blue" },
    "brand-kits.defaults.warm-sand": { he: "חול חמים", en: "Warm Sand" },

    // Toasts
    "toast.success.create-brand-kit": {
        he: "ערכת המיתוג נוצרה",
        en: "Brand kit created",
    },
    "toast.error.create-brand-kit": {
        he: "יצירת ערכת המיתוג נכשלה",
        en: "Failed to create brand kit",
    },
    "toast.success.update-brand-kit": {
        he: "ערכת המיתוג עודכנה",
        en: "Brand kit updated",
    },
    "toast.error.update-brand-kit": {
        he: "עדכון ערכת המיתוג נכשל",
        en: "Failed to update brand kit",
    },
    "toast.success.delete-brand-kit": {
        he: "ערכת המיתוג נמחקה",
        en: "Brand kit deleted",
    },
    "toast.error.delete-brand-kit": {
        he: "מחיקת ערכת המיתוג נכשלה",
        en: "Failed to delete brand kit",
    },

    // billing dashboards
    "platform.sidebar.admin.dashboards": { he: "דשבורדים", en: "Dashboards" },
    "platform.sidebar.admin.billing-reports": {
        he: "דוחות חיוב",
        en: "Billing reports",
    },
    "analytics.tabs.usage": { he: "שימוש", en: "Usage" },
    "home.usage-transactions.columns.details.guide_recording_hour": {
        he: "הקלטת מדריך",
        en: "Guide recording",
    },
    "admin.billing.dashboard.title": {
        he: "דשבורד חיובים",
        en: "Billing dashboard",
    },
    "admin.billing.dashboard.description": {
        he: "שימוש, חידושים ותחזיות בכל הפלטפורמה",
        en: "Usage, renewals and forecasts across the platform",
    },
    "admin.billing.error": {
        he: "טעינת נתוני החיוב נכשלה",
        en: "Failed to load billing data",
    },
    "admin.billing.filters.all-users": { he: "כל המשתמשים", en: "All users" },
    "admin.billing.filters.search-user": {
        he: "חיפוש לפי אימייל",
        en: "Search by email",
    },
    "admin.billing.filters.no-user": {
        he: "לא נמצא משתמש",
        en: "No user found",
    },
    "admin.billing.filters.only-active": {
        he: "רק משתמשים פעילים",
        en: "Only active users",
    },
    "admin.billing.filters.active-count": {
        he: "{active} מתוך {total} פעילים",
        en: "{active} of {total} active",
    },
    "admin.billing.filters.thresholds": {
        he: "קריטריון פעילות",
        en: "Active criteria",
    },
    "admin.billing.filters.thresholds.description": {
        he: "משתמש נחשב פעיל כאשר לפחות סף אחד נחצה ב-12 החודשים האחרונים",
        en: "A user is active when any one minimum is crossed over the last 12 months",
    },
    "admin.billing.filters.thresholds.storage": {
        he: "מינימום אחסון (GB-חודשים)",
        en: "Min storage (GB-months)",
    },
    "admin.billing.filters.thresholds.bandwidth": {
        he: "מינימום תעבורה (GB)",
        en: "Min bandwidth (GB)",
    },
    "admin.billing.filters.thresholds.credits": {
        he: "מינימום קרדיטים",
        en: "Min credits",
    },
    "admin.billing.filters.thresholds.apply": { he: "החל", en: "Apply" },
    "admin.billing.filters.thresholds.reset": {
        he: "איפוס לברירת מחדל",
        en: "Reset to defaults",
    },
    "admin.billing.usage.title": {
        he: "שימוש ב-12 החודשים האחרונים",
        en: "Usage over the last 12 months",
    },
    "admin.billing.usage.description": {
        he: "אחסון מאוחסן (ממוצע חודשי), תעבורה וקרדיטים שנוצלו בכל חודש",
        en: "Storage stored (monthly average), bandwidth and credits used per month",
    },
    "admin.billing.units.stored-now": {
        he: "TB מאוחסנים כעת",
        en: "TB stored now",
    },
    "admin.billing.units.tb": { he: "TB", en: "TB" },
    "admin.billing.units.credits": { he: "קרדיטים", en: "credits" },
    "admin.billing.users.title": {
        he: "חידושים קרובים",
        en: "Upcoming renewals",
    },
    "admin.billing.users.description": {
        he: 'תחזית החידוש = השימוש ב-12 החודשים האחרונים, מוקרן לשנה מלאה כשההיסטוריה קצרה יותר (לפחות 30 יום), במחירי המחירון הנוכחיים אחרי ההנחות של המשתמש, לפני מע"מ: אחסון ₪{storage} ל-TB-חודש, תעבורה ₪{bandwidth} ל-TB, קרדיט ₪{credits}.',
        en: "Renewal forecast = usage over the last 12 months, scaled to a full year when the history is shorter (at least 30 days), at current list prices after the user's discounts, excl. VAT: storage ₪{storage} per TB-month, bandwidth ₪{bandwidth} per TB, ₪{credits} per credit.",
    },
    "admin.billing.users.columns.email": { he: "אימייל", en: "Email" },
    "admin.billing.users.columns.renewal-date": {
        he: "מועד חידוש",
        en: "Renewal date",
    },
    "admin.billing.users.columns.forecast": {
        he: 'תחזית (₪, לפני מע"מ)',
        en: "Forecast (NIS, excl. VAT)",
    },
    "admin.billing.users.empty": {
        he: "אין משתמשים התואמים לסינון",
        en: "No users match the filters",
    },
    "admin.billing.forecast.history": {
        he: "מבוסס על {days} ימי שימוש",
        en: "Based on {days} days of usage",
    },
    "admin.billing.pie.title": {
        he: "קרדיטים לפי פעולה",
        en: "Credits by action",
    },
    "admin.billing.pie.description": {
        he: "חלק מכלל הקרדיטים שנוצלו ב-12 החודשים האחרונים",
        en: "Share of all credits used in the last 12 months",
    },
    "admin.billing.pie.unknown": { he: "לא ידוע", en: "Unknown" },
    "admin.billing.pie.empty": {
        he: "לא נוצלו קרדיטים",
        en: "No credits used",
    },
    "admin.billing.renewals.title": {
        he: "תחזית חידושים, 12 החודשים הבאים",
        en: "Renewal forecast, next 12 months",
    },
    "admin.billing.renewals.description": {
        he: 'סכום חידושים חזוי לחודש, לפני מע"מ. רחפו על חודש כדי לראות מי מתחדש',
        en: "Forecasted renewal sum per month, excl. VAT. Hover a month to see who renews",
    },
    "admin.billing.renewals.users-count": {
        he: "{count} משתמשים",
        en: "{count} users",
    },
    "admin.billing.renewals.more": {
        he: "+{count} נוספים",
        en: "+{count} more",
    },
    "admin.billing.renewals.none": { he: "אין חידושים", en: "No renewals" },
    "admin.billing.reports.title": { he: "דוחות חיוב", en: "Billing reports" },
    "admin.billing.reports.description": {
        he: "דוח אחד לחודש, נוצר ב-28 בחודש, עם המשתמשים שיש לחייב באותו חודש",
        en: "One report per month, generated on the 28th, listing who should be billed that month",
    },
    "admin.billing.reports.columns.month": { he: "חודש", en: "Month" },
    "admin.billing.reports.columns.users": {
        he: "משתמשים לחיוב",
        en: "Users to bill",
    },
    "admin.billing.reports.columns.total": {
        he: 'סה"כ (₪, לפני מע"מ)',
        en: "Total (NIS, excl. VAT)",
    },
    "admin.billing.reports.columns.generated": { he: "נוצר", en: "Generated" },
    "admin.billing.reports.empty": {
        he: "אין דוחות עדיין. הראשון ייווצר ב-28 בחודש",
        en: "No reports yet. The first one is generated on the 28th of this month",
    },
    "admin.billing.reports.back": { he: "כל הדוחות", en: "All reports" },
    "admin.billing.report.title": {
        he: "דוח חיוב עבור {month}",
        en: "Billing report for {month}",
    },
    "admin.billing.report.generated": {
        he: "נוצר ב-{date}",
        en: "Generated {date}",
    },
    "admin.billing.report.not-found": {
        he: "הדוח לא נמצא",
        en: "Report not found",
    },
    "admin.billing.report.columns.email": { he: "אימייל", en: "Email" },
    "admin.billing.report.columns.renewal-date": {
        he: "מועד חידוש",
        en: "Renewal date",
    },
    "admin.billing.report.columns.storage": {
        he: "אחסון (TB-חודשים)",
        en: "Storage (TB-months)",
    },
    "admin.billing.report.columns.bandwidth": {
        he: "תעבורה (TB)",
        en: "Bandwidth (TB)",
    },
    "admin.billing.report.columns.credits": { he: "קרדיטים", en: "Credits" },
    "admin.billing.report.columns.total": {
        he: 'סה"כ (₪, לפני מע"מ)',
        en: "Total (NIS, excl. VAT)",
    },
    "admin.billing.report.empty": {
        he: "אף משתמש לא מתחדש בחודש זה",
        en: "Nobody renews in this month",
    },
    "admin.billing.report.summary.users": { he: "משתמשים", en: "Users" },
    "admin.billing.report.summary.total": { he: 'סה"כ', en: "Total" },
    "analytics.usage.title": { he: "שימוש", en: "Usage" },
    "analytics.usage.chart-title": { he: "שימוש לפי חודש", en: "Usage by month" },
    "analytics.usage.description": {
        he: "האחסון שלך (ממוצע חודשי), התעבורה והקרדיטים בכל חודש",
        en: "Your storage (monthly average), bandwidth and credits per month",
    },
    "analytics.usage.range.cycle": {
        he: "מחזור החיוב הנוכחי",
        en: "Current billing cycle",
    },
    "analytics.usage.range.12m": {
        he: "12 החודשים האחרונים",
        en: "Last 12 months",
    },
    "analytics.usage.next-renewal": {
        he: "החידוש הבא: {date}",
        en: "Next renewal: {date}",
    },
    "analytics.usage.error": {
        he: "טעינת השימוש נכשלה",
        en: "Failed to load usage",
    },
};

export type TranslationKey = keyof typeof translations;

export const getDir = (locale?: Locale | null): "rtl" | "ltr" => {
    const language = locale || defaultLocale;

    if (rtlLanguages.includes(language)) {
        return "rtl";
    } else {
        return "ltr";
    }
};

export const resolveLomdaLanguage = (contentLanguage?: string | null): Locale =>
    contentLanguage === "he" ? "he" : "en";

export const t = (
    key: TranslationKey | string,
    language?: Locale,
    params: Record<string, string | number> = {}
): string => {
    const loc =
        language && languages.some((l) => l.code === language)
            ? language
            : (currentLocale ?? defaultLocale);

    // pick the template (or fallback)
    const entry = translations?.[key];
    const tmpl = entry?.[loc] ?? entry?.[defaultLocale] ?? "";

    // now just iteratively replace each `{param}` in the string
    let result: string = tmpl;
    for (const [param, value] of Object.entries(params)) {
        // replaceAll is fine in modern JS/TS
        result = result.replaceAll(`{${param}}`, String(value));
    }

    return result;
};

export const getLanguageLabel = (code: string, locale?: Locale): string => {
    return translations[code] ? t(code, locale) : code;
};

export const localeCookieName = "locale";
